import React, { useEffect, useMemo, useRef, useState } from "react";
import jsPDF from "jspdf";
import {
  CREATOR,
  MASKS,
  ROLES,
  WORK_STATUS,
  WORK_STATUS_LABELS,
} from "./board/board.constants";
import { addDays, calcProgress, fmtDate, parseSubtasks } from "./board/board.utils";
import { filterAndSortCards, setWorkStatus } from "./board/board.logic";
import SignaturePad from "./components/SignaturePad";
import { supabase } from "./lib/supabase";
import {
  CUSTOMER_PRIVACY_CONSENT_VERSION,
  buildConfirmationUrl,
  createQrSvgDataUrl,
  getCustomerSignatureStorageKey,
  getFriendlyProcessError,
  hasReusablePrivacyConsent,
  isValidSignatureDataUrl,
  mergeActiveDamageEntries,
  shouldWarnMileage,
} from "./lib/customerProcess";

const EXTRA_VIEWS = ["Leihwagen", "Leihwagen Admin", "Prozess Admin", "Benutzer Admin", "Produktivtest", "Systemstatus", "Protokoll"];
const BLOCKER_OPTIONS = [
  { value: "", label: "Kein Blocker" },
  { value: "customer", label: "Wartet auf Kunde" },
  { value: "parts", label: "Wartet auf Teil" },
  { value: "approval", label: "Wartet auf Freigabe" },
  { value: "loaner", label: "Wartet auf Leihwagen" },
  { value: "internal", label: "Wartet intern" },
  { value: "other", label: "Sonstiges" },
];
const INTAKE_REQUEST_TYPES = [
  { value: "change_request", label: "Änderung anfordern" },
  { value: "question", label: "Rückfrage stellen" },
  { value: "handover_request", label: "Weitergabe anfordern" },
  { value: "escalation_request", label: "Priorität eskalieren" },
];
const SPECIAL_VIEWS = ["Leitstand"];
const MANAGEMENT_VIEWS = ["Management", "Werkstättenleitung", "Werkstattplanung", "Tagesboard"];
const FUEL_OPTIONS = ["Leer", "1/4", "1/2", "3/4", "Voll"];
const BRANCH_OPTIONS = [
  { value: "21", label: "21 - Floridsdorf" },
  { value: "22", label: "22 - Donaustadt" },
];
const normalizeBranchFilter = (value) => {
  const branch = String(value || "");
  return BRANCH_OPTIONS.some((entry) => entry.value === branch) ? branch : "";
};
const getDefaultLoanerHistoryFilters = (branch = "") => ({
  branch: normalizeBranchFilter(branch),
  plate: "",
  name: "",
  handoverEmployee: "",
  returnEmployee: "",
  dateFrom: "",
  dateTo: "",
  status: "",
});
const CASE_TYPE_OPTIONS = [
  { value: "annual_service", label: "Jahresservice" },
  { value: "inspection_57a", label: "§57A Überprüfung" },
  { value: "boxenstop_depot", label: "Boxenstop 1 (Räder umstecken und Deponieren)" },
  { value: "boxenstop_wash", label: "Boxenstop 2 (Räder umstecken, Deponieren und Waschen der Räder)" },
  { value: "boxenstop_balance", label: "Boxenstop 3 (Räder umstecken, Deponieren, Waschen der Räder und Wuchten)" },
  { value: "body_inspection", label: "Karosserieschaden - Besichtigung" },
  { value: "body_repair", label: "Karosserieschaden - Reparatur" },
  { value: "mechanic_inspection", label: "Mechanikschaden - Besichtigung" },
  { value: "mechanic_repair", label: "Mechanikschaden - Reparatur" },
  { value: "loaner", label: "Leihwagen" },
  { value: "other", label: "Sonstiges" },
];
const ORDER_TYPE_OPTIONS = [
  { value: "external", label: "Externer Auftrag" },
  { value: "internal", label: "Interner Auftrag" },
];
const USER_ROLE_OPTIONS = [
  { value: "staff", label: "Staff" },
  { value: "workshop_lead", label: "Werkstättenleitung" },
  { value: "technician", label: "Techniker" },
  { value: "admin", label: "Admin" },
];
const getUserRoleLabel = (value) =>
  USER_ROLE_OPTIONS.find((entry) => entry.value === value)?.label || value || "Staff";
const getUserInviteStatusLabel = (value) =>
  ({
    created: "Einladung offen",
    sent: "Einladung versendet",
    accepted: "Zugang aktiviert",
  })[value] || value || "Einladung offen";
const ALLOWED_NEW_USER_EMAIL_DOMAIN = "teamfraenkel.at";
const NEW_USER_EMAIL_DOMAIN_ERROR =
  "Neue Nutzer können aktuell nur mit einer @teamfraenkel.at E-Mail-Adresse angelegt werden.";
const PROTECTED_DISABLED_USER_EMAILS = new Set([
  "suayip.seringec@evolution-data.com",
  "t.pichler@teamfraenkel.com",
]);
const isAllowedNewUserEmail = (value) => {
  const email = String(value || "").trim().toLowerCase();
  const parts = email.split("@");
  return (
    parts.length === 2 &&
    Boolean(parts[0]) &&
    parts[1] === ALLOWED_NEW_USER_EMAIL_DOMAIN
  );
};
const TECHNICIAN_SETTINGS_KEY = "technician_settings";
const TECHNICIAN_SETTINGS_STORAGE_KEY = "teamFraenkelTechnicianSettings";
const PROCESS_CONFIG_STORAGE_KEY = "teamFraenkelProcessConfig";
const normalizeLookupText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
const DEFAULT_TECH_CAPACITY_HOURS = 6;
const CASE_TYPE_ESTIMATED_HOURS = {
  annual_service: 2,
  inspection_57a: 1,
  boxenstop_depot: 0.5,
  boxenstop_wash: 0.75,
  boxenstop_balance: 1,
  body_inspection: 1,
  body_repair: 4,
  mechanic_inspection: 1.5,
  mechanic_repair: 3,
  loaner: 0.5,
  other: 1,
};
const ENGINE_TYPE_OPTIONS = [
  "Benzin",
  "Diesel",
  "Hybrid-Benzin",
  "Hybrid-Diesel",
  "Plug-In HEV",
  "EV",
];
const TRANSMISSION_OPTIONS = ["Schaltgetriebe", "Automatik"];
const VEHICLE_BRAND_SUGGESTIONS = [
  "Hyundai",
  "Mitsubishi",
  "Volkswagen",
  "BMW",
  "Mercedes",
  "Audi",
];
const LOANER_USAGE_OPTIONS = [
  { value: "customer_loaner", label: "Leihwagen für Kunden" },
  { value: "employee_vehicle", label: "Dienstwagen Mitarbeiter" },
  { value: "long_term_assignment", label: "Langzeitvermittlung" },
];
const TIRE_TYPE_OPTIONS = ["Sommer", "Winter", "Allwetter"];
const getDefaultLoanerDraft = () => ({
  id: null,
  plate: "",
  brand: "",
  model: "",
  vin: "",
  vehicleType: "",
  usageType: "customer_loaner",
  homeBranch: "",
  registrationStatus: "Angemeldet",
  tireType: "",
  hasDepot: false,
  vignetteDate: "",
  hasParkingPermit: false,
  engineType: "",
  status: "available",
  permanentlyCheckedOut: false,
  isActive: true,
  notes: "",
});
const GENDER_OPTIONS = ["männlich", "weiblich", "divers"];
const INSURANCE_OPTIONS = [
  { value: "with_deductible", label: "Mit Selbstbehalt" },
  { value: "without_deductible", label: "Ohne Selbstbehalt (+ 19,90 EUR / Tag)" },
];
const APP_BUILD_LABEL = "2026-07-31.3";
const APP_NAVIGATION_STATE_KEY = "teamFraenkelNavigation";
const APP_ENV_LABEL =
  process.env.REACT_APP_APP_MODE ||
  process.env.REACT_APP_ENV ||
  "Produktivtest";
const APP_MODE_IS_FALLBACK = !process.env.REACT_APP_APP_MODE && !process.env.REACT_APP_ENV;
const INTAKE_DRAFT_STORAGE_KEY = "teamFraenkelIntakeDraft";
const WIZARD_DRAFT_STORAGE_KEY = "teamFraenkelLoanerWizardDraft";
const RETURN_DRAFT_STORAGE_KEY = "teamFraenkelLoanerReturnDraft";
const OFFLINE_RETURN_STORAGE_KEY = "pendingLoanerReturns";
const DRAFT_MAX_AGE_MS = 48 * 60 * 60 * 1000;
const RESEND_COOLDOWN_MS = 60_000;
const USER_INVITE_RESEND_COOLDOWN_MS = 30_000;
const PRODUCTION_TEST_RESET_SQL = `begin;

truncate table public.loaner_bookings, public.cards restart identity cascade;
truncate table public.audit_events restart identity;

update public.loaners
set status = 'available'
where coalesce(usage_type, 'customer_loaner') = 'customer_loaner'
  and coalesce(is_active, true) = true;

commit;`;
const LOANER_MEDIA_BUCKET = "loaner-media";
const BOOKING_STATUS_LABELS = {
  awaiting_customer_confirmation: "Wartet auf Kundenbestätigung",
  ready_for_checkout: "Bereit zur Übergabe",
  checked_out: "Ausgegeben",
  returned: "Zurückgenommen",
  cancelled: "Abgebrochen",
};
const LIVE_ACCEPTANCE_STEPS = [
  {
    id: "schema",
    title: "Supabase final prüfen",
    detail: "Systemstatus starten und sicherstellen, dass card_uuid, confirmation_events, Storage und Audit grün sind.",
  },
  {
    id: "intake",
    title: "Auftrag in der Annahme anlegen",
    detail: "Pflichtfelder absichtlich leer lassen, Feldmarkierung prüfen, danach Auftrag vollständig speichern.",
  },
  {
    id: "loaner",
    title: "Leihwagenbedarf fortsetzen",
    detail: "Offenen Bedarf im Leihwagenmodul öffnen, Mitarbeiter wählen, Fahrzeug wählen, Fotos aufnehmen.",
  },
  {
    id: "mobile-link",
    title: "Bestätigungslink am Smartphone öffnen",
    detail: "E-Mail-Link öffnen, lange Daten prüfen, unterschreiben und bestätigen.",
  },
  {
    id: "handover",
    title: "Bereit zur Ausgabe prüfen",
    detail: "Vorgang muss in Bereit zur Ausgabe erscheinen und über Fahrzeug jetzt ausgeben weiterlaufen.",
  },
  {
    id: "return",
    title: "Rücknahme am Tablet abschließen",
    detail: "Rückgabefotos aufnehmen, KM/Tank prüfen, Rücknahmeprotokoll in Historie prüfen.",
  },
  {
    id: "audit",
    title: "Protokoll filtern",
    detail: "Nach Kunden-E-Mail, Auftrag und confirmation suchen. Versand, Ansicht und Bestätigung müssen nachvollziehbar sein.",
  },
  {
    id: "package",
    title: "Dokumentationspaket exportieren",
    detail: "PDF und JSON-Paket exportieren und prüfen, ob Auftrag, Leihwagen, Fotos, Protokoll und Audit-Bezug enthalten sind.",
  },
  {
    id: "data-quality",
    title: "Fahrzeugdatenqualität bereinigen",
    detail: "Fahrzeugbestand öffnen, doppelte Kennzeichen/VINs und fehlende Kernfelder bereinigen.",
  },
  {
    id: "approval-center",
    title: "Freigabe-Center testen",
    detail: "Werkstatt-Freigabe anfordern, in Annahme/Admin genehmigen oder ablehnen und Verlauf prüfen.",
  },
];
const PILOT_TEST_CASES = [
  "Jahresservice mit Leihwagenbedarf bis Kundenbestätigung",
  "§57A ohne Leihwagen bis Rechnung und Übergabe",
  "Karosserieschaden über Spenglerei und Aufbereitung",
  "Boxenstop mit Reifen-To-dos und Aufbereitung",
  "Mechanikreparatur mit Freigabeanforderung aus der Werkstatt",
];
const PHOTO_LABELS = [
  { key: "front", label: "Vorne" },
  { key: "left", label: "Fahrerseite" },
  { key: "rear", label: "Hinten" },
  { key: "right", label: "Beifahrerseite" },
];
const LOANER_TERMS = [
  "Im Fahrzeug herrscht absolutes Rauchverbot.",
  "Bei außerordentlicher Verschmutzung werden zusätzliche Reinigungskosten verrechnet.",
  "Das Fahrzeug darf nicht zur gewerblichen Personenbeförderung genutzt werden.",
  "Auslandsfahrten sind nur nach vorheriger Absprache erlaubt.",
  "Organstrafen und sonstige Gebühren im Nutzungszeitraum sind vom ausborgenden Kunden zu übernehmen.",
  "Diebstahl, sonstiger Verlust oder Beschädigung sind unverzüglich dem Händler zu melden.",
  "Die Vollkaskoversicherung beinhaltet standardmäßig 1.000 EUR Selbstbehalt, sofern nicht anders gewählt.",
  "Das Leihfahrzeug ist bis zu sieben Tage nach Erhalt kostenlos; ab dem achten Tag fallen jedenfalls 19,90 EUR pro Tag an.",
  "Das Fahrzeug ist vollgetankt zurückzubringen, andernfalls wird eine Tankpauschale von 50 EUR verrechnet.",
  "Bitte die Tankrechnung bei Fahrzeugrückgabe mitbringen.",
];
const EXTERNAL_OPERATIONS_TODOS = [
  {
    title: "SPF für teamfraenkel.app prüfen",
    detail: "DNS muss Hostinger als erlaubten Mailversender enthalten.",
  },
  {
    title: "DKIM bei Hostinger aktivieren",
    detail: "DKIM-Schlüssel im Hostinger-Mailpanel kopieren und im DNS hinterlegen.",
  },
  {
    title: "DMARC für teamfraenkel.app setzen",
    detail: "Mindestens p=none zum Start, danach Zustellung prüfen und schärfen.",
  },
  {
    title: "Absender sauber angleichen",
    detail: "MAIL_FROM, SMTP_USER und Reply-To sollen zur Domain teamfraenkel.app passen.",
  },
  {
    title: "Spam-Test nach Redeploy durchführen",
    detail: "Eine Bestätigung an Gmail/Outlook/Smartphone senden und Header/Zustellung prüfen.",
  },
];
const WIZARD_STEPS = [
  { id: "employee", label: "Mitarbeiter" },
  { id: "vehicle", label: "Fahrzeug" },
  { id: "customer", label: "Kunde" },
  { id: "license", label: "Führerschein" },
  { id: "insurance", label: "Tarif" },
  { id: "walkaround", label: "Rundgang" },
  { id: "damage", label: "Schäden" },
  { id: "handover", label: "Abschluss" },
];
const DETAIL_TABS = [
  { id: "work", label: "To-dos & Rückfragen" },
  { id: "info", label: "Infos & Verlauf" },
];
const DEPARTMENT_TODO_PRESETS = {
  Werkstatt: [
    "Bremsen prüfen",
    "Bremsen austauschen",
    "Bremsflüssigkeit prüfen / nachfüllen",
    "Scheibenwischerflüssigkeit nachfüllen",
    "Ölstand prüfen",
    "Ölwechsel durchführen",
    "Filter prüfen / tauschen",
    "Reifen umstecken",
    "Räder wuchten",
    "Probefahrt durchführen",
    "Diagnose durchführen",
    "Kostenvoranschlag / Freigabe Annahme anfordern",
  ],
  Spenglerei: [
    "Schadenbild prüfen",
    "Fotos ergänzen",
    "Kostenvoranschlag erstellen",
    "Freigabe Annahme anfordern",
    "Karosseriearbeit durchführen",
    "Lackierung vorbereiten",
    "An Fahrzeugaufbereitung übergeben",
  ],
  Fahrzeugaufbereitung: [
    "Fahrzeugaußenwäsche durchführen",
    "Fahrzeuginnenreinigung durchführen",
    "Innenraum saugen",
    "Scheiben reinigen",
    "Endkontrolle durchführen",
  ],
  Rechnung: [
    "Leistungen kontrollieren",
    "Material / Teile prüfen",
    "Rechnung erstellen",
    "Zahlungsstatus dokumentieren",
  ],
  "Übergabe": [
    "Kunde informieren",
    "Rechnung bereitlegen",
    "Fahrzeugschlüssel vorbereiten",
    "Fahrzeug übergeben",
    "Abschluss dokumentieren",
  ],
};
const CASE_TYPE_DEPARTMENT_CHECKLISTS = {
  annual_service: {
    Werkstatt: [
      "Auftrag, Servicehistorie und Annahme-To-dos prüfen",
      "Motoröl und Ölfilter prüfen / wechseln",
      "Innenraumfilter prüfen / wechseln",
      "Bremsanlage prüfen",
      "Flüssigkeitsstände prüfen und ergänzen",
      "Fehlerspeicher auslesen / dokumentieren",
      "Serviceintervall zurücksetzen",
      "Probefahrt / Endkontrolle durchführen",
    ],
    Fahrzeugaufbereitung: [
      "Fahrzeugaußenwäsche durchführen",
      "Innenraum grob prüfen",
      "Serviceaufkleber / Unterlagen sichtbar bereitlegen",
      "Endkontrolle vor Rechnung dokumentieren",
    ],
    Rechnung: [
      "Servicepositionen und Material prüfen",
      "Arbeitszeit kontrollieren",
      "Rechnung erstellen",
      "Zahlungsstatus / Freigabe dokumentieren",
    ],
  },
  inspection_57a: {
    Werkstatt: [
      "Auftrag und Fahrzeugdaten prüfen",
      "§57A-Prüfpositionen durchführen",
      "Bremsenprüfstand durchführen",
      "Lichtanlage prüfen",
      "Fahrwerk / Achsen prüfen",
      "Abgas / OBD prüfen",
      "Pickerl-Ergebnis dokumentieren",
    ],
    Fahrzeugaufbereitung: [
      "Prüfbericht und Fahrzeugunterlagen bereitlegen",
      "Fahrzeugaußenwäsche bei Bedarf durchführen",
      "Endkontrolle vor Rechnung dokumentieren",
    ],
    Rechnung: [
      "§57A-Position verrechnen",
      "Zusatzarbeiten kontrollieren",
      "Rechnung und Prüfbericht vorbereiten",
    ],
  },
  boxenstop_depot: {
    Werkstatt: [
      "Auftrag und Reifensatz prüfen",
      "Räder umstecken",
      "Reifendruck prüfen",
      "Radschrauben nach Vorgabe anziehen",
      "Räder einlagern / Depot dokumentieren",
      "Nachziehhinweis dokumentieren",
    ],
    Rechnung: [
      "Boxenstop 1 verrechnen",
      "Depotstatus kontrollieren",
      "Kundenhinweis für Nachziehen vorbereiten",
    ],
  },
  boxenstop_wash: {
    Werkstatt: [
      "Auftrag und Reifensatz prüfen",
      "Räder umstecken",
      "Reifendruck prüfen",
      "Radschrauben nach Vorgabe anziehen",
      "Räder einlagern / Depot dokumentieren",
      ],
    Fahrzeugaufbereitung: [
      "Räder waschen",
      "Räder trocknen / Sichtprüfung durchführen",
      "Waschstatus dokumentieren",
    ],
    Rechnung: [
      "Boxenstop 2 verrechnen",
      "Depot- und Waschstatus kontrollieren",
      "Kundenhinweis für Nachziehen vorbereiten",
    ],
  },
  boxenstop_balance: {
    Werkstatt: [
      "Auftrag und Reifensatz prüfen",
      "Räder umstecken",
      "Reifendruck prüfen",
      "Räder wuchten",
      "Radschrauben nach Vorgabe anziehen",
      "Räder einlagern / Depot dokumentieren",
      "Nachziehhinweis dokumentieren",
    ],
    Fahrzeugaufbereitung: [
      "Räder waschen",
      "Räder trocknen / Sichtprüfung durchführen",
      "Waschstatus dokumentieren",
    ],
    Rechnung: [
      "Boxenstop 3 verrechnen",
      "Wucht- und Depotstatus kontrollieren",
      "Kundenhinweis für Nachziehen vorbereiten",
    ],
  },
  mechanic_inspection: {
    Werkstatt: [
      "Fehlerbild laut Annahme prüfen",
      "Diagnose durchführen",
      "Prüfergebnis mit Ursache dokumentieren",
      "Kostenvoranschlag / Freigabe Annahme anfordern",
      "Ergebnis dokumentieren",
    ],
    Fahrzeugaufbereitung: [
      "Fahrzeugaußenwäsche bei Bedarf durchführen",
      "Endkontrolle vor Rechnung dokumentieren",
    ],
  },
  mechanic_repair: {
    Werkstatt: [
      "Reparaturumfang prüfen",
      "Teilebedarf prüfen",
      "Reparatur durchführen",
      "Probefahrt / Endkontrolle durchführen",
      "Rückmeldung für Rechnung dokumentieren",
    ],
    Fahrzeugaufbereitung: [
      "Fahrzeugaußenwäsche durchführen",
      "Innenraum bei Werkstattverschmutzung reinigen",
      "Endkontrolle vor Rechnung dokumentieren",
    ],
  },
  body_inspection: {
    Spenglerei: [
      "Schadenbild prüfen",
      "Schadenfotos prüfen / ergänzen",
      "Kostenvoranschlag erstellen",
      "Freigabe Annahme anfordern",
    ],
    Rechnung: [
      "Besichtigungsunterlagen prüfen",
      "Kostenpositionen vorbereiten",
      "Kunden- / Versicherungsfreigabe dokumentieren",
    ],
  },
  body_repair: {
    Spenglerei: [
      "Reparaturfreigabe prüfen",
      "Karosseriearbeiten durchführen",
      "Lackvorbereitung durchführen",
      "Endkontrolle Spenglerei durchführen",
      "Reparaturumfang für Aufbereitung dokumentieren",
    ],
    Fahrzeugaufbereitung: [
      "Fahrzeugaußenwäsche durchführen",
      "Innenraum bei Bedarf reinigen",
      "Lack- / Karosserie-Endkontrolle dokumentieren",
    ],
    Rechnung: [
      "Karosserie- und Lackpositionen prüfen",
      "Material / Fremdleistungen kontrollieren",
      "Rechnung erstellen",
    ],
  },
};
const DEFAULT_DEPARTMENT = "Annahme";
const CASE_TYPE_FLOW = {
  annual_service: ["Annahme", "Werkstatt", "Fahrzeugaufbereitung", "Rechnung", "Übergabe"],
  inspection_57a: ["Annahme", "Werkstatt", "Fahrzeugaufbereitung", "Rechnung", "Übergabe"],
  boxenstop_depot: ["Annahme", "Werkstatt", "Rechnung", "Übergabe"],
  boxenstop_wash: ["Annahme", "Werkstatt", "Fahrzeugaufbereitung", "Rechnung", "Übergabe"],
  boxenstop_balance: ["Annahme", "Werkstatt", "Rechnung", "Übergabe"],
  body_inspection: ["Annahme", "Spenglerei", "Rechnung", "Übergabe"],
  body_repair: ["Annahme", "Spenglerei", "Fahrzeugaufbereitung", "Rechnung", "Übergabe"],
  mechanic_inspection: ["Annahme", "Werkstatt", "Fahrzeugaufbereitung", "Rechnung", "Übergabe"],
  mechanic_repair: ["Annahme", "Werkstatt", "Fahrzeugaufbereitung", "Rechnung", "Übergabe"],
  loaner: ["Annahme", "Leihwagen", "Übergabe"],
  other: ["Annahme", "Werkstatt", "Rechnung", "Übergabe"],
};
const CASE_TYPE_CHECKLISTS = {
  service: {
    Annahme: [
      "Kundenanliegen aufnehmen",
      "Fahrzeug- und Servicedaten prüfen",
      "Optional: Kostenrahmen abstimmen",
    ],
    Werkstatt: [
      "Arbeitsauftrag und Annahme-To-dos prüfen",
      "Serviceumfang durchführen",
      "Flüssigkeiten prüfen und ergänzen",
      "Qualitätskontrolle abschließen",
      "Rückmeldung für Rechnung dokumentieren",
    ],
    Rechnung: [
      "Leistungen kontrollieren",
      "Rechnung vorbereiten",
      "Kundenfreigabe dokumentieren",
    ],
    "Übergabe": [
      "Fahrzeug und Rechnung bereitlegen",
      "Kunde informieren",
      "Kundenübergabe dokumentieren",
    ],
  },
  damage: {
    Annahme: [
      "Schadenaufnahme dokumentieren",
      "Fotos und Kundenangaben erfassen",
      "Spenglerei einplanen",
    ],
    Spenglerei: [
      "Schadenbild prüfen",
      "Maßnahmen planen",
      "Karosseriearbeiten abschließen",
      "Fahrzeugaufbereitung vorbereiten",
    ],
    Fahrzeugaufbereitung: [
      "Fahrzeugaußenwäsche durchführen",
      "Sichtkontrolle dokumentieren",
      "Optional Innenreinigung prüfen",
      "Rückmeldung für Rechnung dokumentieren",
    ],
    Rechnung: [
      "Schadenfall abrechnen",
      "Unterlagen vervollständigen",
      "Übergabeunterlagen vorbereiten",
    ],
    "Übergabe": [
      "Kundenkommunikation vorbereiten",
      "Fahrzeugerklärung abschließen",
      "Fall übergeben",
    ],
  },
  repair: {
    Annahme: [
      "Reparaturbedarf aufnehmen",
      "Fehlerbild dokumentieren",
      "Werkstatt beauftragen",
    ],
    Werkstatt: [
      "Diagnose durchführen",
      "Reparatur umsetzen",
      "Probefahrt / Kontrolle abschließen",
      "Rechnung informieren",
    ],
    Rechnung: [
      "Reparaturpositionen prüfen",
      "Rechnung erstellen",
      "Übergabeunterlagen vorbereiten",
    ],
    "Übergabe": [
      "Kunde informieren",
      "Rechnung und Fahrzeug übergeben",
      "Abschluss dokumentieren",
    ],
  },
  loaner: {
    Annahme: [
      "Leihwagenbedarf aufnehmen",
      "Kundendaten und Fahrzeugdaten prüfen",
      "Leihwagen im Modul vorbereiten",
    ],
    Leihwagen: [
      "Fahrzeug auswählen",
      "Übergabedokumentation am Tablet abschließen",
      "Kundenbestätigung versenden",
    ],
    "Übergabe": [
      "Rückgabe planen",
      "Rücknahme dokumentieren",
      "Vorgang abschließen",
    ],
  },
  other: {
    Annahme: [
      "Anliegen aufnehmen",
      "Passende Abteilung festlegen",
      "Auftrag intern übergeben",
    ],
    Werkstatt: [
      "Vorgang sichten",
      "Nächsten Bearbeitungsschritt abstimmen",
      "Rückmeldung geben",
    ],
    Rechnung: [
        "Unterlagen prüfen",
      "Abschluss vorbereiten",
    ],
    "Übergabe": [
      "Kundenabschluss dokumentieren",
      "Fall abschließen",
    ],
  },
};
const buildDefaultProcessConfig = () =>
  CASE_TYPE_OPTIONS.reduce((config, caseOption) => {
    const route = CASE_TYPE_FLOW[caseOption.value] || CASE_TYPE_FLOW.other;
    config[caseOption.value] = route.reduce((departments, department) => {
      departments[department] = (CASE_TYPE_DEPARTMENT_CHECKLISTS[caseOption.value]?.[department] ||
        CASE_TYPE_CHECKLISTS[getChecklistCaseType(caseOption.value)]?.[department] ||
        CASE_TYPE_CHECKLISTS.other?.[department] ||
        ["Nächsten Bearbeitungsschritt dokumentieren"]).join("\n");
      return departments;
    }, {});
    return config;
  }, {});
const SHOW_DEBUG_PANEL = String(process.env.REACT_APP_ENABLE_DEBUG_PANEL || "false") === "true";
const LOANER_BOOKING_LIST_SELECT = `
  id,
  loaner_id,
  loaner_plate,
  loaner_brand,
  loaner_model,
  loaner_home_branch,
  loaner_usage_type,
  card_id,
  card_uuid,
  order_number,
  customer_name,
  customer_first_name,
  customer_last_name,
  customer_gender,
  customer_email,
  planned_pickup_date,
  expected_return_date,
  actual_return_date,
  status,
  insurance_option,
  branch_warning_acknowledged,
  terms_accepted,
  no_other_damage_confirmed,
  km_start,
  fuel_start,
  km_end,
  fuel_end,
  return_damage_notes,
  customer_token,
  customer_confirmation_status,
  customer_viewed_at,
  customer_email_sent_at,
  customer_confirmed_at,
  confirmation_events,
  checked_out_at,
  checked_in_at,
  handover_employee_name,
  return_employee_name,
  created_at,
  created_by
`;

const createDemoTechnicians = () => [
  { id: "service_sonja_friedlmayer", name: "Sonja Friedlmayer", type: "Kundendienstberater", branch: "21" },
  { id: "service_christian_buchinger", name: "Christian Buchinger", type: "Kundendienstberater", branch: "21" },
  { id: "service_nadine_staud", name: "Nadine Staud", type: "Kundendienstberater", branch: "22" },
  { id: "service_dominik_seckl_swoboda", name: "Dominik Seckl-Swoboda", type: "Kundendienstberater", branch: "22" },
  { id: "service_thomas_pichler", name: "Thomas Pichler", type: "Kundendienstberater", branch: "22" },
  { id: "tech_lukas_bambas", name: "Lukas Bambas", type: "Techniker", branch: "21", capacityHours: 6 },
  { id: "tech_hesam_hajavi", name: "Hesam Hajavi", type: "Techniker", branch: "21", capacityHours: 6 },
  { id: "tech_martin_hauser", name: "Martin Hauser", type: "Techniker", branch: "21", capacityHours: 6 },
  { id: "tech_franz_schuller", name: "Franz Schuller", type: "Techniker", branch: "21", capacityHours: 6 },
  { id: "prep_zlatoje_kostic", name: "Zlatoje Kostic", type: "Aufbereitung", branch: "21" },
  { id: "tech_andreas_leidenfrost", name: "Andreas Leidenfrost", type: "Techniker", branch: "22", capacityHours: 6 },
  { id: "tech_mario_marinkovic", name: "Mario Marinkovic", type: "Techniker", branch: "22", capacityHours: 6 },
  { id: "tech_kevin_moerth", name: "Kevin Mörth", type: "Techniker", branch: "22", capacityHours: 6 },
  { id: "paint_julia_lackermayer", name: "Julia Lackermayer", type: "Lackierer", branch: "22" },
  { id: "paint_christian_paschek", name: "Christian Paschek", type: "Lackierer", branch: "22" },
  { id: "prep_robert_panholzer", name: "Robert Panholzer", type: "Aufbereitung", branch: "22" },
];

const createDemoCards = () => [
  {
    id: 1001,
    orderNumber: "AU-240101",
    customerName: "Max Müller",
    customerEmail: "max.mueller@example.com",
    licensePlate: "W-12345A",
    title: "Hyundai i30 - Bremsen & Service",
    role: "Werkstatt",
    prio: "high",
    due: addDays(0),
    needsSpenglerei: false,
    needsLoaner: false,
    assignedTech: "tech_1",
    invoice: { total: 420, paid: false },
    parts: [
      {
        id: "p1",
        name: "Bremsscheiben vorne",
        ordered: true,
        delivered: false,
        installed: false,
      },
      {
        id: "p2",
        name: "Ölfilter",
        ordered: true,
        delivered: true,
        installed: false,
      },
    ],
    subs: parseSubtasks([
      "Arbeitsauftrag prüfen",
      "Fahrzeug auf Bühne",
      "Bremsscheiben prüfen",
      "Bremsbeläge wechseln",
      "Ölwechsel durchführen",
      "Probefahrt durchführen",
    ]),
    watchers: ["Werkstättenleitung"],
    workStatus: WORK_STATUS.IN_WORK,
    history: [],
    media: [],
  },
  {
    id: 1002,
    orderNumber: "AU-240102",
    customerName: "Sabine Koch",
    customerEmail: "sabine.koch@example.com",
    licensePlate: "W-BIENE1",
    title: "Hyundai Tucson - Inspektion 60.000",
    role: "Annahme",
    prio: "normal",
    due: addDays(2),
    needsSpenglerei: false,
    needsLoaner: true,
    assignedTech: null,
    parts: [],
    subs: parseSubtasks([
      "Kundenauftrag anlegen",
      "Schäden dokumentieren",
      "Kilometerstand erfassen",
      "Werkstattauftrag erstellen",
    ]),
    watchers: [],
    workStatus: WORK_STATUS.NEW,
    history: [],
    media: [],
  },
  {
    id: 1003,
    orderNumber: "AU-240103",
    customerName: "Thomas Berger",
    customerEmail: "thomas.berger@example.com",
    licensePlate: "G-54321Z",
    title: "Hyundai Kona - Karosserieschaden",
    role: "Spenglerei",
    prio: "urgent",
    due: addDays(-1),
    needsSpenglerei: true,
    needsLoaner: false,
    assignedTech: null,
    parts: [],
    subs: parseSubtasks([
      "[S] Schaden dokumentieren",
      "[S] Karosserie richten",
      "[S] Spachteln & Schleifen",
      "[S] Lack vorbereiten",
    ]),
    watchers: ["Verkauf"],
    workStatus: WORK_STATUS.IN_WORK,
    history: [],
    media: [],
  },
];

const createDemoLoaners = () => [
  {
    id: "loaner_1",
    plate: "W-101LW",
    brand: "Hyundai",
    model: "i20",
    status: "available",
    isActive: true,
    notes: "",
  },
  {
    id: "loaner_2",
    plate: "W-102LW",
    brand: "Hyundai",
    model: "Kona",
    status: "available",
    isActive: true,
    notes: "",
  },
];

const getDefaultWizardState = (card = null) => ({
  cardId: card?.id || null,
  orderNumber: card?.orderNumber || "",
  handoverEmployeeId: "",
  handoverEmployeeName: "",
  customerName: card?.customerName || "",
  customerFirstName: card?.customerFirstName || "",
  customerLastName: card?.customerLastName || "",
  customerGender: card?.customerGender || "",
  customerEmail: card?.customerEmail || "",
  loanerId: "",
  branchWarningAccepted: false,
  plannedPickupDate: new Date().toISOString().slice(0, 10),
  expectedReturnDate: "",
  licenseFrontPhoto: "",
  licenseBackPhoto: "",
  insuranceOption: "with_deductible",
  frontPhoto: "",
  leftPhoto: "",
  rearPhoto: "",
  rightPhoto: "",
  damageEntries: [],
  noOtherDamageConfirmed: false,
  termsAccepted: false,
  kmStart: "",
  fuelStart: "Voll",
  dashboardPhotoPrimary: "",
  dashboardPhotoSecondary: "",
  signature: "",
  submissionError: "",
  submissionSuccess: "",
  isSubmitting: false,
});

const createEmptyReturnDamageEntry = () => ({
  id: `return_damage_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  description: "",
  photos: [],
});

const ensureTrailingReturnDamageEntry = (entries = []) => {
  const nextEntries = Array.isArray(entries) && entries.length > 0
    ? entries
    : [createEmptyReturnDamageEntry()];
  const lastEntry = nextEntries[nextEntries.length - 1];
  const lastEntryIsUsed =
    Boolean(String(lastEntry?.description || "").trim()) ||
    (Array.isArray(lastEntry?.photos) && lastEntry.photos.length > 0);
  return lastEntryIsUsed
    ? [...nextEntries, createEmptyReturnDamageEntry()]
    : nextEntries;
};

const getRelevantReturnDamageEntries = (entries = []) =>
  (Array.isArray(entries) ? entries : []).filter(
    (entry) =>
      Boolean(String(entry?.description || "").trim()) ||
      (Array.isArray(entry?.photos) && entry.photos.length > 0)
  );

const getIncompleteReturnDamageEntries = (entries = []) =>
  getRelevantReturnDamageEntries(entries).filter(
    (entry) =>
      !String(entry?.description || "").trim() ||
      !Array.isArray(entry?.photos) ||
      entry.photos.length === 0
  );

const getDefaultReturnState = (booking = null) => ({
  bookingId: booking?.id || null,
  returnEmployeeId: "",
  returnEmployeeName: "",
  kmEnd: "",
  correctedKmStart: "",
  fuelEnd: "Voll",
  fuelReceiptReceived: false,
  fuelReceiptPhoto: "",
  comparisonPhotos: {
    front: "",
    left: "",
    rear: "",
    right: "",
  },
  returnPhotos: [],
  returnDamageEntries: [createEmptyReturnDamageEntry()],
  returnDamageNotes: "",
  isSubmitting: false,
  error: "",
});

const getBlockerLabel = (value) =>
  BLOCKER_OPTIONS.find((entry) => entry.value === value)?.label || "Kein Blocker";
const getCaseTypeLabel = (value) =>
  CASE_TYPE_OPTIONS.find((entry) => entry.value === value)?.label ||
  {
    service: "Service",
    damage: "Schaden",
    repair: "Reparatur",
  }[value] ||
  "Sonstiges";
const getDepartmentFlow = (caseType) => CASE_TYPE_FLOW[caseType] || CASE_TYPE_FLOW.other;
const getDepartmentRoute = (card) => getDepartmentFlow(card?.caseType || "other");
const getApprovalRequestTarget = (card) => {
  const structuredTarget = [...(card?.questions || [])]
    .reverse()
    .find((question) => !question.answered && question.requiresApproval && question.targetDepartment)?.targetDepartment;
  if (structuredTarget && ROLES.includes(structuredTarget)) return structuredTarget;
  const entries = [...(card?.questions || []), ...(card?.history || [])]
    .map((entry) => entry.text || entry.action || "")
    .reverse();
  const match = entries
    .map((text) => String(text).match(/(?:an|für manuelle Übergabe an) ([A-Za-zÄÖÜäöüß]+(?: [A-Za-zÄÖÜäöüß]+)?)/))
    .find(Boolean);
  return match?.[1] && ROLES.includes(match[1]) ? match[1] : "";
};
const getApprovalRequestEmployee = (card, employees = []) => {
  const entries = [...(card?.questions || []), ...(card?.history || [])]
    .map((entry) => entry.text || entry.action || "")
    .reverse();
  const request = entries
    .map((text) => String(text).match(/Abgabe an (.+?) angefordert/i))
    .find(Boolean);
  const name = request?.[1]?.trim();
  if (!name) return null;
  return employees.find((employee) => employee.name === name) || null;
};
const getEmployeeById = (employees = [], id = "") =>
  employees.find((employee) => employee.id === id) || null;
const getEmployeeNameById = (employees = [], id = "") =>
  getEmployeeById(employees, id)?.name || "";
const getApprovalRequestType = (card) => {
  const request = [...(card?.questions || [])]
    .reverse()
    .find((question) => !question.answered && question.requiresApproval);
  if (request?.requestType === "handover_request") return "Weitergabe angefordert";
  if (request?.requestType === "escalation_request") return "Priorität / Eskalation";
  if (request?.requestType === "change_request") return "Änderung angefordert";
  const text = [
    card?.blockerType ? getBlockerLabel(card.blockerType) : "",
    ...((card?.questions || []).map((question) => question.text || "")),
    ...((card?.history || []).map((entry) => entry.action || "")),
  ]
    .join(" ")
    .toLowerCase();
  if (card?.loanerFlow?.assignmentApproval?.status === "pending_workshop_lead" || /abgabe an/.test(text)) {
    return "Techniker-Zuweisung";
  }
  if (/übergabe|uebergabe|weitergeleitet|manuelle/.test(text)) return "Manuelle Übergabe";
  if (/kosten|kostenvoranschlag|rechnung/.test(text)) return "Kostenfreigabe";
  if (/kunde|kundenfreigabe/.test(text)) return "Kundenfreigabe";
  if (/blocker|wartet/.test(text) || card?.blockerType) return "Blocker";
  return "Sonstige Freigabe";
};
const getOperationalStatusLabel = (card) => {
  if (card?.workStatus === WORK_STATUS.CANCELLED) return "Abgebrochen";
  if (card?.workStatus === WORK_STATUS.DELIVERED) return "Übergeben";
  if (card?.workStatus === WORK_STATUS.INVOICED) return "Abgerechnet";
  if (card?.loanerFlow?.assignmentApproval?.status === "pending_workshop_lead") {
    return "Wartet auf Werkstättenleitung";
  }
  if (card?.blockerType) return getBlockerLabel(card.blockerType);
  if (card?.workStatus === WORK_STATUS.DONE) return "Technisch fertig";
  if (card?.workStatus === WORK_STATUS.IN_WORK) return "In Arbeit";
  return "Angelegt";
};
const getEstimatedHours = (cardOrDraft) => {
  const explicit =
    cardOrDraft?.estimatedHours ||
    cardOrDraft?.vehicleData?.estimatedHours ||
    cardOrDraft?.planning?.estimatedHours;
  const parsed = Number(String(explicit || "").replace(",", "."));
  if (parsed > 0) return parsed;
  return CASE_TYPE_ESTIMATED_HOURS[cardOrDraft?.caseType] || CASE_TYPE_ESTIMATED_HOURS.other;
};
const formatHours = (value) => {
  const hours = Number(value || 0);
  return `${hours.toLocaleString("de-DE", { maximumFractionDigits: 1 })} h`;
};
const formatResendWait = (cooldownUntil, now = Date.now()) => {
  const seconds = Math.max(0, Math.ceil((Number(cooldownUntil || 0) - now) / 1000));
  if (seconds >= 60) {
    const minutes = Math.ceil(seconds / 60);
    return `${minutes} ${minutes === 1 ? "Minute" : "Minuten"}`;
  }
  return `${seconds} ${seconds === 1 ? "Sekunde" : "Sekunden"}`;
};
const getFriendlyStatusText = (message) => {
  const text = String(message || "");
  if (!text) return "";
  if (/failed to fetch|network|fetch/i.test(text)) {
    return "Verbindung instabil. Das System versucht weiter zu synchronisieren.";
  }
  if (/statement timeout|canceling statement|timeout/i.test(text)) {
    return "Die Datenbank antwortet langsam. Bitte kurz warten, der Vorgang wird geprüft.";
  }
  if (/invalid input syntax|uuid|bigint|schema cache|column .* does not exist/i.test(text)) {
    return "Datenbankschema prüfen. Bitte Systemstatus öffnen und die angezeigte Migration ausführen.";
  }
  if (/email|smtp|mail/i.test(text) && /konnte|fehler|error|failed/i.test(text)) {
    return "E-Mail konnte nicht sicher bestätigt werden. Vorgang ist gespeichert, Versand bitte im Modul prüfen.";
  }
  return text;
};
const canRestoreIntakeDraft = (draft) =>
  Boolean(
    draft &&
      typeof draft === "object" &&
      (draft.title ||
        draft.customerFirstName ||
        draft.customerLastName ||
        draft.customerEmail ||
        draft.licensePlate ||
        draft.vehicleBrand ||
        draft.vehicleModel ||
        draft.notes)
  );
const canRestoreWizardDraft = (draft) =>
  Boolean(
    draft &&
      typeof draft === "object" &&
      (draft.cardId ||
        draft.orderNumber ||
        draft.loanerId ||
        draft.customerFirstName ||
        draft.customerLastName ||
        draft.licenseFrontPhoto ||
        draft.frontPhoto ||
        draft.kmStart)
  );
const buildSharedWizardDraft = (draft, wizardStep = 0) => {
  if (!draft || typeof draft !== "object") return null;
  const {
    isSubmitting,
    submissionError,
    submissionSuccess,
    licenseFrontPhoto,
    licenseBackPhoto,
    frontPhoto,
    leftPhoto,
    rearPhoto,
    rightPhoto,
    dashboardPhotoPrimary,
    dashboardPhotoSecondary,
    damagePhotos,
    damageEntries,
    signature,
    ...safeDraft
  } = draft;
  return {
    ...safeDraft,
    damageEntries: Array.isArray(damageEntries)
      ? damageEntries.map((entry) => ({
          id: entry.id,
          description: entry.description || "",
        }))
      : [],
    hasLocalMedia: Boolean(
      licenseFrontPhoto ||
        licenseBackPhoto ||
        frontPhoto ||
        leftPhoto ||
        rearPhoto ||
        rightPhoto ||
        dashboardPhotoPrimary ||
        dashboardPhotoSecondary ||
        signature ||
        (Array.isArray(damagePhotos) && damagePhotos.length > 0)
    ),
    wizardStep,
  };
};
const canRestoreReturnDraft = (draft) =>
  Boolean(
    draft &&
      typeof draft === "object" &&
      (draft.bookingId ||
        draft.returnEmployeeId ||
        draft.kmEnd ||
        draft.correctedKmStart ||
        draft.returnDamageNotes ||
        getRelevantReturnDamageEntries(draft.returnDamageEntries).length > 0 ||
        draft.fuelReceiptPhoto ||
        Object.values(draft.comparisonPhotos || {}).some(Boolean))
  );
const isStoredDraftFresh = (savedAt) => {
  if (!savedAt) return true;
  const timestamp = new Date(savedAt).getTime();
  return Number.isFinite(timestamp) && Date.now() - timestamp <= DRAFT_MAX_AGE_MS;
};
const isRecentActivity = (value, minutes = 10) => {
  if (!value) return false;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) && Date.now() - timestamp <= minutes * 60_000;
};
const getLoanerConfirmationUrl = (booking) =>
  buildConfirmationUrl(window.location.origin, booking?.confirmationToken);
const makeQrSvgDataUrl = (text) => {
  const version = 5;
  const size = version * 4 + 17;
  const dataCodewords = 108;
  const ecCodewords = 26;
  const bytes = Array.from(new TextEncoder().encode(String(text || "")));
  if (!bytes.length || bytes.length > 106) return "";
  const modules = Array.from({ length: size }, () => Array(size).fill(null));
  const reserved = Array.from({ length: size }, () => Array(size).fill(false));
  const setModule = (x, y, value, lock = true) => {
    if (x < 0 || y < 0 || x >= size || y >= size) return;
    modules[y][x] = Boolean(value);
    if (lock) reserved[y][x] = true;
  };
  const addFinder = (x, y) => {
    for (let dy = -1; dy <= 7; dy += 1) {
      for (let dx = -1; dx <= 7; dx += 1) {
        const xx = x + dx;
        const yy = y + dy;
        const inCore = dx >= 0 && dx <= 6 && dy >= 0 && dy <= 6;
        const dark =
          inCore &&
          (dx === 0 || dx === 6 || dy === 0 || dy === 6 || (dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4));
        setModule(xx, yy, dark);
      }
    }
  };
  const addAlignment = (cx, cy) => {
    for (let dy = -2; dy <= 2; dy += 1) {
      for (let dx = -2; dx <= 2; dx += 1) {
        setModule(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
      }
    }
  };
  addFinder(0, 0);
  addFinder(size - 7, 0);
  addFinder(0, size - 7);
  addAlignment(30, 30);
  for (let i = 8; i < size - 8; i += 1) {
    setModule(i, 6, i % 2 === 0);
    setModule(6, i, i % 2 === 0);
  }
  setModule(8, version * 4 + 9, true);
  for (let i = 0; i < 9; i += 1) {
    setModule(8, i, false);
    setModule(i, 8, false);
  }
  for (let i = size - 8; i < size; i += 1) {
    setModule(8, i, false);
    setModule(i, 8, false);
  }

  const bits = [];
  const appendBits = (value, length) => {
    for (let i = length - 1; i >= 0; i -= 1) bits.push((value >>> i) & 1);
  };
  appendBits(0b0100, 4);
  appendBits(bytes.length, 8);
  bytes.forEach((byte) => appendBits(byte, 8));
  appendBits(0, Math.min(4, dataCodewords * 8 - bits.length));
  while (bits.length % 8) bits.push(0);
  const data = [];
  for (let i = 0; i < bits.length; i += 8) {
    data.push(bits.slice(i, i + 8).reduce((acc, bit) => (acc << 1) | bit, 0));
  }
  for (let pad = 0xec; data.length < dataCodewords; pad = pad === 0xec ? 0x11 : 0xec) data.push(pad);

  const gfExp = Array(512).fill(0);
  const gfLog = Array(256).fill(0);
  let value = 1;
  for (let i = 0; i < 255; i += 1) {
    gfExp[i] = value;
    gfLog[value] = i;
    value <<= 1;
    if (value & 0x100) value ^= 0x11d;
  }
  for (let i = 255; i < 512; i += 1) gfExp[i] = gfExp[i - 255];
  const gfMul = (a, b) => (a && b ? gfExp[gfLog[a] + gfLog[b]] : 0);
  let generator = [1];
  for (let degree = 0; degree < ecCodewords; degree += 1) {
    const next = Array(generator.length + 1).fill(0);
    generator.forEach((coef, index) => {
      next[index] ^= gfMul(coef, gfExp[degree]);
      next[index + 1] ^= coef;
    });
    generator = next;
  }
  const ecc = Array(ecCodewords).fill(0);
  data.forEach((byte) => {
    const factor = byte ^ ecc.shift();
    ecc.push(0);
    generator.slice(1).forEach((coef, index) => {
      ecc[index] ^= gfMul(coef, factor);
    });
  });
  const codewordBits = [...data, ...ecc].flatMap((byte) =>
    Array.from({ length: 8 }, (_, index) => (byte >> (7 - index)) & 1)
  );
  let bitIndex = 0;
  let upward = true;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right -= 1;
    for (let row = 0; row < size; row += 1) {
      const y = upward ? size - 1 - row : row;
      for (let col = 0; col < 2; col += 1) {
        const x = right - col;
        if (reserved[y][x]) continue;
        const raw = bitIndex < codewordBits.length ? codewordBits[bitIndex] : 0;
        bitIndex += 1;
        setModule(x, y, Boolean(raw ^ (((x + y) % 2 === 0) ? 1 : 0)), false);
      }
    }
    upward = !upward;
  }
  const formatBits = (() => {
    let dataBits = (0b01 << 3) | 0;
    let rem = dataBits << 10;
    const poly = 0x537;
    for (let i = 14; i >= 10; i -= 1) {
      if ((rem >>> i) & 1) rem ^= poly << (i - 10);
    }
    return (((dataBits << 10) | rem) ^ 0x5412) & 0x7fff;
  })();
  const formatBit = (i) => Boolean((formatBits >>> i) & 1);
  for (let i = 0; i <= 5; i += 1) setModule(8, i, formatBit(i));
  setModule(8, 7, formatBit(6));
  setModule(8, 8, formatBit(7));
  setModule(7, 8, formatBit(8));
  for (let i = 9; i < 15; i += 1) setModule(14 - i, 8, formatBit(i));
  for (let i = 0; i < 8; i += 1) setModule(size - 1 - i, 8, formatBit(i));
  for (let i = 8; i < 15; i += 1) setModule(8, size - 15 + i, formatBit(i));
  const cells = [];
  modules.forEach((row, y) =>
    row.forEach((dark, x) => {
      if (dark) cells.push(`<rect x="${x}" y="${y}" width="1" height="1"/>`);
    })
  );
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-4 -4 ${size + 8} ${size + 8}" shape-rendering="crispEdges"><rect x="-4" y="-4" width="${size + 8}" height="${size + 8}" fill="#fff"/><g fill="#10293a">${cells.join("")}</g></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};
const getLoanerConfirmationQrUrl = (booking) => {
  const url = getLoanerConfirmationUrl(booking);
  if (!url) return "";
  try {
    return createQrSvgDataUrl(url, { quiet: 4, color: "#000000" });
  } catch {
    return makeQrSvgDataUrl(url);
  }
};
const getLoanerExternalQrUrl = (booking) => {
  const url = getLoanerConfirmationUrl(booking);
  if (!url) return "";
  return `https://api.qrserver.com/v1/create-qr-code/?size=360x360&margin=24&ecc=Q&data=${encodeURIComponent(url)}`;
};
const CASE_TYPE_SKILL_HINTS = {
  annual_service: ["service", "wartung", "hyundai", "mitsubishi"],
  inspection_57a: ["57a", "§57a", "pickerl", "überprüfung", "ueberpruefung"],
  boxenstop_depot: ["reifen", "räder", "raeder", "boxenstop"],
  boxenstop_wash: ["reifen", "räder", "raeder", "boxenstop"],
  boxenstop_balance: ["reifen", "räder", "raeder", "wuchten", "boxenstop"],
  body_inspection: ["karosserie", "spenglerei", "schaden"],
  body_repair: ["karosserie", "spenglerei", "schaden"],
  mechanic_inspection: ["mechanik", "diagnose", "fehlersuche"],
  mechanic_repair: ["mechanik", "reparatur", "bremsen"],
  loaner: ["service", "annahme"],
  other: [],
};
const getHandoverIssues = (card) => {
  const issues = [];
  const openTodos = (card?.subs || []).filter(isBlockingSubtask);
  const openQuestions = (card?.questions || []).filter((question) => !question.answered);
  if (card?.blockerType) issues.push(`Blocker aktiv: ${getBlockerLabel(card.blockerType)}`);
  if (openTodos.length > 0) issues.push(`${openTodos.length} offene Pflicht-To-do(s)`);
  if (openQuestions.length > 0) issues.push(`${openQuestions.length} offene Rückfrage(n)`);
  if (card?.role === "Werkstatt" && !card?.assignedTech) issues.push("kein Techniker zugewiesen");
  if (card?.role === "Rechnung" && card?.invoice && !card.invoice.paid) issues.push("Rechnung noch offen");
  if (card?.loanerFlow?.assignmentApproval?.status === "pending_workshop_lead") {
    issues.push("Techniker-Zuweisung wartet auf Freigabe");
  }
  return issues;
};
const getChecklistCaseType = (caseType) => {
  if (["body_inspection", "body_repair"].includes(caseType)) return "damage";
  if (["mechanic_inspection", "mechanic_repair"].includes(caseType)) return "repair";
  if (
    [
      "annual_service",
      "inspection_57a",
      "boxenstop_depot",
      "boxenstop_wash",
      "boxenstop_balance",
    ].includes(caseType)
  ) {
    return "service";
  }
  return caseType;
};
const INTAKE_FIELD_TABS = {
  orderType: "core",
  title: "core",
  branch: "core",
  licensePlate: "core",
  vehicleBrand: "core",
  vehicleModel: "core",
  firstRegistrationDate: "core",
  vin: "core",
  mileage: "core",
  customerFirstName: "customer",
  customerLastName: "customer",
  customerEmail: "customer",
};

const getRequiredIntakeFieldKeys = (draft) => {
  const internal = isInternalOrder(draft);
  const required = ["title", "branch", "licensePlate"];

  if (!internal) {
    required.push("customerFirstName", "customerLastName");
  }

  if (
    [
      "annual_service",
      "inspection_57a",
      "body_inspection",
      "body_repair",
      "mechanic_inspection",
      "mechanic_repair",
    ].includes(draft.caseType)
  ) {
    required.push("vehicleBrand", "vehicleModel");
  }

  if (draft.caseType === "inspection_57a") {
    required.push("firstRegistrationDate", "vin");
  }

  if (!internal) {
    required.push("customerEmail");
  }
  return required.filter((field) => !String(draft[field] || "").trim());
};

const getRequiredIntakeFields = (draft) => {
  const labels = {
    title: "Titel / Anliegen",
    customerFirstName: "Vorname",
    customerLastName: "Nachname",
    branch: "Filiale",
    licensePlate: "Kennzeichen",
    vehicleBrand: "Fahrzeugmarke",
    vehicleModel: "Fahrzeugmodell",
    firstRegistrationDate: "Erstzulassung",
    vin: "Fahrgestellnummer",
    mileage: "Kilometerstand",
    customerEmail: "Kunden-E-Mail",
    orderType: "Auftragstyp",
  };
  return getRequiredIntakeFieldKeys(draft).map((field) => labels[field] || field);
};
const getNextDepartmentForCard = (card) => {
  const route = getDepartmentFlow(card.caseType || "other");
  const currentIndex = route.indexOf(card.role || DEFAULT_DEPARTMENT);
  if (currentIndex === -1) return route[1] || null;
  return route[currentIndex + 1] || null;
};
const buildChecklistForDepartment = (caseType, department) => {
  const exact = CASE_TYPE_DEPARTMENT_CHECKLISTS[caseType]?.[department];
  if (exact) return parseSubtasks(exact);
  const byCase = CASE_TYPE_CHECKLISTS[getChecklistCaseType(caseType)] || CASE_TYPE_CHECKLISTS.other;
  const checklist = parseSubtasks(byCase[department] || ["Nächsten Bearbeitungsschritt dokumentieren"]);
  if (department === DEFAULT_DEPARTMENT) {
    return checklist.map((sub) =>
      ["Kundenanliegen aufnehmen", "Fahrzeug- und Servicedaten prüfen"].includes(sub.text)
        ? { ...sub, done: true }
        : sub
    );
  }
  return checklist;
};
const isBlockingSubtask = (sub) => !sub.done && !/^Optional:/i.test(String(sub.text || ""));
const getEscalationLevel = (card) => {
  const dueTs = card.due ? new Date(card.due).getTime() : null;
  const nextActionTs = card.nextActionDue ? new Date(card.nextActionDue).getTime() : null;
  const now = Date.now();
  if (card.blockerType && card.workStatus === WORK_STATUS.BLOCKED) return 2;
  if ((dueTs && dueTs < now) || (nextActionTs && nextActionTs < now)) return 1;
  return 0;
};
const getEscalationLabel = (level) =>
  level >= 2 ? "kritisch" : level === 1 ? "beobachten" : "stabil";

const mapSupabaseCard = (row) => ({
  id: row.id,
  orderNumber: row.order_number || "",
  customerFirstName: row.customer_first_name || "",
  customerLastName: row.customer_last_name || "",
  customerName:
    row.customer_name ||
    [row.customer_first_name, row.customer_last_name].filter(Boolean).join(" "),
  customerGender: row.customer_gender || "",
  customerEmail: row.customer_email || "",
  licensePlate: row.license_plate || "",
  vehicleData: row.vehicle_data || {},
  title: row.title || row.order_number || "Auftrag",
  role: row.role || "Annahme",
  prio: row.prio || "normal",
  due: row.due || "",
  workStatus: row.work_status || WORK_STATUS.NEW,
  needsLoaner: Boolean(row.needs_loaner),
  needsSpenglerei: Boolean(row.needs_spenglerei),
  assignedTech: row.assigned_tech || null,
  caseType: row.case_type || "service",
  blockerType: row.blocker_type || "",
  nextAction: row.next_action || "",
  nextActionDue: row.next_action_due || "",
  responsibleLabel: row.responsible_label || "",
  escalationLevel: Number(row.escalation_level || 0),
  branch: row.branch || "",
  managementNotes: row.management_notes || "",
  nextDepartment: row.next_department || "",
  sourceLabel: row.source_label || "manuell",
  externalSystem: row.external_system || "",
  externalReference: row.external_reference || "",
  questions: Array.isArray(row.questions) ? row.questions : [],
  loanerFlow: row.loaner_flow || {},
  parts: Array.isArray(row.parts) ? row.parts : [],
  media: Array.isArray(row.media) ? row.media : [],
  history: Array.isArray(row.history) ? row.history : [],
  subs: Array.isArray(row.subs) ? row.subs : [],
  invoice: row.invoice || null,
});

const cardToDbRow = (card) => ({
  id: card.id,
  order_number: card.orderNumber || null,
  customer_name: card.customerName || null,
  customer_first_name: card.customerFirstName || null,
  customer_last_name: card.customerLastName || null,
  customer_gender: card.customerGender || null,
  customer_email: card.customerEmail || null,
  license_plate: card.licensePlate || null,
  vehicle_data: card.vehicleData || {},
  title: card.title || null,
  role: card.role || "Annahme",
  prio: card.prio || "normal",
  due: card.due || null,
  work_status: card.workStatus || WORK_STATUS.NEW,
  needs_loaner: Boolean(card.needsLoaner),
  needs_spenglerei: Boolean(card.needsSpenglerei),
  assigned_tech: card.assignedTech || null,
  case_type: card.caseType || "service",
  blocker_type: card.blockerType || null,
  next_action: card.nextAction || null,
  next_action_due: card.nextActionDue || null,
  responsible_label: card.responsibleLabel || null,
  escalation_level: Number(card.escalationLevel || 0),
  branch: card.branch || null,
  management_notes: card.managementNotes || null,
  next_department: card.nextDepartment || null,
  source_label: card.sourceLabel || null,
  external_system: card.externalSystem || null,
  external_reference: card.externalReference || null,
  questions: Array.isArray(card.questions) ? card.questions : [],
  loaner_flow: card.loanerFlow || {},
  parts: card.parts || [],
  media: card.media || [],
  history: card.history || [],
  subs: card.subs || [],
  invoice: card.invoice || null,
});

const mapSupabaseLoaner = (row) => ({
  id: row.id,
  plate: row.plate || "",
  brand: row.brand || "",
  model: row.model || "",
  vin: row.vin || "",
  vehicleType: row.vehicle_type || "",
  registrationStatus: row.registration_status || "",
  assignmentNote: row.assignment_note || "",
  usageType: row.usage_type || "customer_loaner",
  homeBranch: row.home_branch || "",
  vignetteUntil: row.vignette_until || "",
  vignetteDate: row.vignette_date || "",
  engineType: row.engine_type || "",
  depot: row.depot || "",
  hasDepot:
    typeof row.has_depot === "boolean"
      ? row.has_depot
      : String(row.depot || "").toLowerCase() === "ja",
  tireType: row.tire_type || "",
  summerTiresNote: row.summer_tires_note || "",
  winterTiresNote: row.winter_tires_note || "",
  parkingPermitUntil: row.parking_permit_until || "",
  hasParkingPermit:
    typeof row.has_parking_permit === "boolean"
      ? row.has_parking_permit
      : Boolean(row.parking_permit_until),
  status: row.status || "available",
  permanentlyCheckedOut:
    typeof row.permanently_checked_out === "boolean"
      ? row.permanently_checked_out
      : row.usage_type === "employee_vehicle" || row.usage_type === "long_term_assignment",
  isActive: row.is_active !== false,
  notes: row.notes || "",
  createdAt: row.created_at || null,
});

const loanerToDbRow = (loaner) => ({
  id: loaner.id,
  plate: loaner.plate,
  brand: loaner.brand,
  model: loaner.model,
  vin: loaner.vin || "",
  vehicle_type: loaner.vehicleType || "",
  registration_status: loaner.registrationStatus || "",
  assignment_note: loaner.assignmentNote || "",
  usage_type: loaner.usageType || "customer_loaner",
  home_branch: loaner.homeBranch || "",
  vignette_until: loaner.vignetteUntil || "",
  vignette_date: loaner.vignetteDate || null,
  engine_type: loaner.engineType || "",
  depot: loaner.depot || "",
  has_depot: Boolean(loaner.hasDepot),
  tire_type: loaner.tireType || null,
  summer_tires_note: loaner.summerTiresNote || "",
  winter_tires_note: loaner.winterTiresNote || "",
  parking_permit_until: loaner.parkingPermitUntil || "",
  has_parking_permit: Boolean(loaner.hasParkingPermit),
  status: loaner.status,
  permanently_checked_out: Boolean(loaner.permanentlyCheckedOut),
  is_active: loaner.isActive,
  notes: loaner.notes || "",
});

const mapSupabaseBluePlate = (row) => ({
  id: row.id,
  plate: row.plate || "",
  isActive: row.is_active !== false,
  notes: row.notes || "",
});

const BOOKING_MEDIA_ROW_FIELDS = [
  "condition_photos",
  "driver_license_front",
  "driver_license_back",
  "damage_entries",
  "return_photos",
  "signature_data",
];
const rowHasField = (row, field) =>
  Object.prototype.hasOwnProperty.call(row || {}, field);

const mapSupabaseBooking = (row) => ({
  id: row.id,
  loanerId: row.loaner_id,
  loanerPlate: row.loaner_plate || "",
  loanerBrand: row.loaner_brand || "",
  loanerModel: row.loaner_model || "",
  loanerHomeBranch: row.loaner_home_branch || "",
  loanerUsageType: row.loaner_usage_type || "customer_loaner",
  cardId: row.card_uuid || row.card_id,
  orderNumber: row.order_number || "",
  customerName:
    row.customer_name ||
    [row.customer_first_name, row.customer_last_name].filter(Boolean).join(" "),
  customerFirstName: row.customer_first_name || "",
  customerLastName: row.customer_last_name || "",
  customerGender: row.customer_gender || "",
  customerEmail: row.customer_email || "",
  plannedPickupDate: row.planned_pickup_date || "",
  expectedReturnDate: row.expected_return_date || "",
  actualReturnDate: row.actual_return_date || "",
  status: row.status || "awaiting_customer_confirmation",
  conditionPhotos: row.condition_photos || {},
  driverLicenseFront: row.driver_license_front || "",
  driverLicenseBack: row.driver_license_back || "",
  driverLicenseFrontPresent: Boolean(row.driver_license_front),
  driverLicenseBackPresent: Boolean(row.driver_license_back),
  licensePresenceLoaded:
    rowHasField(row, "driver_license_front") &&
    rowHasField(row, "driver_license_back"),
  insuranceOption: row.insurance_option || "with_deductible",
  branchWarningAcknowledged: Boolean(row.branch_warning_acknowledged),
  termsAccepted: Boolean(row.terms_accepted),
  damageEntries: Array.isArray(row.damage_entries)
    ? row.damage_entries
    : Array.isArray(row.damage_photos)
      ? row.damage_photos.map((photo, index) => ({
          id: photo.id || `legacy_${index}`,
          url: photo.url || "",
          description: row.damage_notes || "",
        }))
      : [],
  noOtherDamageConfirmed: Boolean(row.no_other_damage_confirmed),
  kmStart: row.km_start || "",
  fuelStart: row.fuel_start || "",
  kmEnd: row.km_end || "",
  fuelEnd: row.fuel_end || "",
  returnPhotos: Array.isArray(row.return_photos) ? row.return_photos : [],
  returnDamageNotes: row.return_damage_notes || "",
  signature: row.signature_data || "",
  mediaLoaded: BOOKING_MEDIA_ROW_FIELDS.every((field) =>
    rowHasField(row, field)
  ),
  confirmationToken: row.customer_token || "",
  confirmationStatus: row.customer_confirmation_status || "pending",
  confirmationViewedAt: row.customer_viewed_at || null,
  confirmationSentAt: row.customer_email_sent_at || null,
  confirmationAcceptedAt: row.customer_confirmed_at || null,
  confirmationEvents: Array.isArray(row.confirmation_events) ? row.confirmation_events : [],
  checkedOutAt: row.checked_out_at || null,
  checkedInAt: row.checked_in_at || null,
  handoverEmployeeName: row.handover_employee_name || "",
  returnEmployeeName: row.return_employee_name || "",
  createdAt: row.created_at || null,
  createdBy: row.created_by || null,
});

const mapSupabaseLoanerDamage = (row) => ({
  id: row.id,
  loanerId: row.loaner_id,
  bookingId: row.booking_id || "",
  status: row.status || "active",
  url: row.photo_url || "",
  description: row.description || "",
  reportedBy: row.reported_by || "",
  reportedAt: row.reported_at || row.created_at || "",
  deletedBy: row.deleted_by || "",
  deletedAt: row.deleted_at || "",
  deleteReason: row.delete_reason || "",
});
const getCentralLoanerDamageId = (loanerId, entryId) =>
  `${loanerId}_${entryId}`.replace(/[^A-Za-z0-9_-]/g, "_");

const mergeBookingMedia = (incoming, existing) => {
  if (!existing) return incoming;
  const mergedLicenseMedia = {
    driverLicenseFront:
      existing.driverLicenseFront || incoming.driverLicenseFront || "",
    driverLicenseBack:
      existing.driverLicenseBack || incoming.driverLicenseBack || "",
    driverLicenseFrontPresent: Boolean(
      existing.driverLicenseFrontPresent ||
        existing.driverLicenseFront ||
        incoming.driverLicenseFrontPresent ||
        incoming.driverLicenseFront
    ),
    driverLicenseBackPresent: Boolean(
      existing.driverLicenseBackPresent ||
        existing.driverLicenseBack ||
        incoming.driverLicenseBackPresent ||
        incoming.driverLicenseBack
    ),
    licensePresenceLoaded: Boolean(
      existing.licensePresenceLoaded || incoming.licensePresenceLoaded
    ),
  };
  if (!existing.mediaLoaded) {
    return {
      ...incoming,
      ...mergedLicenseMedia,
    };
  }
  return {
    ...incoming,
    conditionPhotos: existing.conditionPhotos || incoming.conditionPhotos || {},
    ...mergedLicenseMedia,
    damageEntries: existing.damageEntries?.length ? existing.damageEntries : incoming.damageEntries || [],
    returnPhotos: existing.returnPhotos?.length ? existing.returnPhotos : incoming.returnPhotos || [],
    signature: existing.signature || incoming.signature || "",
    mediaLoaded: Boolean(incoming.mediaLoaded || existing.mediaLoaded),
  };
};

const isBigintCompatibleId = (value) => /^\d+$/.test(String(value || "").trim());

const bookingToDbRow = (booking) => ({
  id: booking.id,
  loaner_id: booking.loanerId,
  loaner_plate: booking.loanerPlate,
  loaner_brand: booking.loanerBrand || "",
  loaner_model: booking.loanerModel || "",
  card_id: isBigintCompatibleId(booking.cardId) ? booking.cardId : null,
  card_uuid: isBigintCompatibleId(booking.cardId) ? null : booking.cardId || null,
  order_number: booking.orderNumber,
  customer_name: booking.customerName,
  customer_first_name: booking.customerFirstName || null,
  customer_last_name: booking.customerLastName || null,
  customer_gender: booking.customerGender || null,
  customer_email: booking.customerEmail,
  planned_pickup_date: booking.plannedPickupDate || null,
  expected_return_date: booking.expectedReturnDate,
  actual_return_date: booking.actualReturnDate || null,
  status: booking.status,
  condition_photos: booking.conditionPhotos || {},
  driver_license_front: booking.driverLicenseFront || null,
  driver_license_back: booking.driverLicenseBack || null,
  insurance_option: booking.insuranceOption || "with_deductible",
  branch_warning_acknowledged: Boolean(booking.branchWarningAcknowledged),
  terms_accepted: Boolean(booking.termsAccepted),
  damage_entries: booking.damageEntries || [],
  damage_photos: (booking.damageEntries || []).map((entry) => ({
    id: entry.id,
    url: entry.url,
    name: entry.name || "Schaden",
  })),
  damage_notes: (booking.damageEntries || [])
    .map((entry) => entry.description)
    .filter(Boolean)
    .join(" | "),
  no_other_damage_confirmed: Boolean(booking.noOtherDamageConfirmed),
  km_start: booking.kmStart || null,
  fuel_start: booking.fuelStart || null,
  km_end: booking.kmEnd || null,
  fuel_end: booking.fuelEnd || null,
  return_photos: booking.returnPhotos || [],
  return_damage_notes: booking.returnDamageNotes || "",
  signature_data: booking.signature || "",
  customer_token: booking.confirmationToken || null,
  customer_confirmation_status: booking.confirmationStatus || "pending",
  customer_viewed_at: booking.confirmationViewedAt || null,
  customer_email_sent_at: booking.confirmationSentAt || null,
  customer_confirmed_at: booking.confirmationAcceptedAt || null,
  confirmation_events: booking.confirmationEvents || [],
  checked_out_at: booking.checkedOutAt || null,
  checked_in_at: booking.checkedInAt || null,
  handover_employee_name: booking.handoverEmployeeName || null,
  return_employee_name: booking.returnEmployeeName || null,
  created_by: booking.createdBy || null,
  loaner_home_branch: booking.loanerHomeBranch || null,
  loaner_usage_type: booking.loanerUsageType || "customer_loaner",
});

const buildBookingPdf = (booking) => {
  const pdf = new jsPDF();
  let y = 20;

  pdf.text(`Leihwagen Übergabe ${booking.loanerPlate}`, 20, y);
  y += 10;
  pdf.text(`Auftrag: ${booking.orderNumber}`, 20, y);
  y += 10;
  pdf.text(`Kunde: ${booking.customerName}`, 20, y);
  y += 10;
  pdf.text(`Verleih geplant ab: ${fmtDate(booking.plannedPickupDate)}`, 20, y);
  y += 10;
  pdf.text(`Rückgabe geplant: ${fmtDate(booking.expectedReturnDate)}`, 20, y);
  y += 10;
  pdf.text(`KM Start: ${booking.kmStart || "-"}`, 20, y);
  y += 10;
  pdf.text(`Tank Start: ${booking.fuelStart || "-"}`, 20, y);
  y += 10;

  if (booking.status === "returned" || booking.checkedInAt) {
    pdf.text("Rücknahmeprotokoll:", 20, y);
    y += 8;
    getReturnProtocolLines(booking).forEach((line) => {
      pdf.text(line.slice(0, 95), 20, y);
      y += 7;
    });
    y += 4;
  }

  if (booking.signature) {
    pdf.text("Unterschrift:", 20, y);
    y += 4;
    pdf.addImage(booking.signature, "PNG", 20, y, 80, 40);
    y += 50;
  }

  pdf.save(`Leihwagen_${booking.loanerPlate || booking.orderNumber}.pdf`);
};

const buildBookingPackage = ({ booking, card, auditEvents = [], includeSensitiveMedia = false }) => ({
  exportedAt: new Date().toISOString(),
  order: card
    ? {
        id: card.id,
        orderNumber: card.orderNumber,
        title: card.title,
        customerName: card.customerName,
        customerEmail: card.customerEmail,
        licensePlate: card.licensePlate,
        vehicleData: card.vehicleData,
        status: card.workStatus,
        gateStatus: getOrderGateStatus(card, booking),
        history: card.history || [],
      }
    : null,
  booking: {
    id: booking.id,
    orderNumber: booking.orderNumber,
    status: booking.status,
    statusLabel: getBookingStatusLabel(booking.status),
    customerName: booking.customerName,
    customerEmail: booking.customerEmail,
    loanerPlate: booking.loanerPlate,
    loanerBrand: booking.loanerBrand,
    loanerModel: booking.loanerModel,
    plannedPickupDate: booking.plannedPickupDate,
    expectedReturnDate: booking.expectedReturnDate,
    actualReturnDate: booking.actualReturnDate,
    kmStart: booking.kmStart,
    kmEnd: booking.kmEnd,
    fuelStart: booking.fuelStart,
    fuelEnd: booking.fuelEnd,
    handoverEmployee: getBookingHandoverEmployee(booking),
    returnEmployee: getBookingReturnEmployee(booking),
    photoChecklist: getBookingPhotoChecklist(booking),
    confirmationProtocol: buildConfirmationProtocol({ card, booking }),
    returnProtocol: getReturnProtocolLines(booking),
    media: {
      ...(includeSensitiveMedia
        ? {
            driverLicenseFront: booking.driverLicenseFront,
            driverLicenseBack: booking.driverLicenseBack,
          }
        : {
            driverLicenseNotice:
              "Führerscheinfotos sind aus Datenschutzgründen nur für Admins sichtbar und in diesem Export ausgeblendet.",
          }),
      conditionPhotos: booking.conditionPhotos || {},
      damageEntries: booking.damageEntries || [],
      returnPhotos: booking.returnPhotos || [],
      signature: booking.signature,
    },
  },
  auditEvents,
});

const isActiveBooking = (booking) =>
  ["awaiting_customer_confirmation", "ready_for_checkout", "checked_out"].includes(
    booking.status
  );

const isBookingLinkedToCard = (booking, card) =>
  Boolean(
    booking &&
      card &&
      ((booking.cardId && booking.cardId === card.id) ||
        (booking.orderNumber && booking.orderNumber === card.orderNumber))
  );

const isPermanentlyCheckedOutLoaner = (loaner) =>
  Boolean(loaner?.permanentlyCheckedOut) ||
  loaner?.usageType === "employee_vehicle" ||
  loaner?.usageType === "long_term_assignment";

const isLoanerUnavailable = (loaner, availabilityMap = {}, unavailableLoanerIds = new Set()) =>
  Boolean(
    !loaner?.isActive ||
      loaner?.status !== "available" ||
      isPermanentlyCheckedOutLoaner(loaner) ||
      availabilityMap[loaner?.id] ||
      unavailableLoanerIds.has(loaner?.id)
  );

const getEmployeeBranch = (profile, fallbackBranch = "") =>
  profile?.branch || fallbackBranch || "";

const getEmployeesForBranch = (employees, branch) => {
  const branchEmployees = employees.filter((employee) => !branch || employee.branch === branch);
  return branchEmployees.length > 0 ? branchEmployees : employees;
};
const getEmployeesForBranchAndTypes = (employees, branch, types = []) => {
  const filtered = employees.filter(
    (employee) =>
      (!branch || employee.branch === branch) &&
      (types.length === 0 || types.includes(employee.type))
  );
  return filtered.length > 0 ? filtered : getEmployeesForBranch(employees, branch);
};

const extractEmployeeFromNote = (notes, prefix) => {
  const match = String(notes || "").match(new RegExp(`${prefix} ([^.|]+)`, "i"));
  return match?.[1]?.trim() || "";
};

const getBookingHandoverEmployee = (booking) =>
  booking.handoverEmployeeName ||
  extractEmployeeFromNote(booking.returnDamageNotes, "Übergabe durchgeführt durch") ||
  "-";

const getBookingReturnEmployee = (booking) =>
  booking.returnEmployeeName ||
  extractEmployeeFromNote(booking.returnDamageNotes, "Rücknahme durchgeführt durch") ||
  "-";

const countTruthyValues = (values) => values.filter(Boolean).length;
const getReturnComparisonPhotos = (booking) =>
  (booking?.returnPhotos || []).filter(
    (entry) =>
      entry?.type === "return_comparison" ||
      PHOTO_LABELS.some((photoLabel) => photoLabel.key === entry?.key)
  );
const isReturnDamagePhoto = (entry) =>
  entry?.type === "return_damage" ||
  Boolean(entry?.damageId) ||
  String(entry?.key || "").startsWith("returnDamage_") ||
  /rückgabeschaden|rueckgabeschaden/i.test(
    String(entry?.name || entry?.label || "")
  );
const getReturnDamagePhotos = (booking) =>
  (booking?.returnPhotos || []).filter(isReturnDamagePhoto);
const getReturnDamageGroupId = (photo, index) => {
  if (photo?.damageId) return photo.damageId;
  const key = String(photo?.key || "");
  if (key.startsWith("returnDamage_")) {
    return key
      .replace(/^returnDamage_/, "")
      .replace(/_\d+$/, "");
  }
  return key || `return_damage_${index}`;
};
const getReturnDamageGroups = (booking) => {
  const groups = new Map();
  getReturnDamagePhotos(booking).forEach((photo, index) => {
    const damageId = getReturnDamageGroupId(photo, index);
    const current = groups.get(damageId) || {
      id: damageId,
      description: photo.description || "",
      photos: [],
    };
    current.description = current.description || photo.description || "";
    current.photos.push(photo);
    groups.set(damageId, current);
  });
  return [...groups.values()];
};
const getDriverLicensePhotoCount = (booking) =>
  countTruthyValues([
    booking?.driverLicenseFrontPresent || booking?.driverLicenseFront,
    booking?.driverLicenseBackPresent || booking?.driverLicenseBack,
  ]);
const getDetailedPhotoChecklistValue = (booking, count, total = null) => {
  if (booking?.mediaLoaded === false) return "nach Öffnen prüfen";
  return total === null ? String(count) : `${count}/${total}`;
};

const getBookingPhotoChecklist = (booking) => [
  {
    label: "Führerschein",
    value:
      booking.licensePresenceLoaded === false
        ? "Prüfung offen"
        : `${getDriverLicensePhotoCount(booking)}/2`,
  },
  {
    label: "Übergabefotos",
    value: getDetailedPhotoChecklistValue(
      booking,
      PHOTO_LABELS.filter((entry) => booking.conditionPhotos?.[entry.key]).length,
      4
    ),
  },
  {
    label: "Tacho/Tank",
    value: getDetailedPhotoChecklistValue(
      booking,
      countTruthyValues([
        booking.conditionPhotos?.dashboardPrimary,
        booking.conditionPhotos?.dashboardSecondary,
      ]),
      2
    ),
  },
  {
    label: "Rücknahmefotos",
    value: getDetailedPhotoChecklistValue(
      booking,
      getReturnComparisonPhotos(booking).filter((entry) => entry?.url).length,
      4
    ),
  },
  {
    label: "Neue Schadenfotos",
    value: getDetailedPhotoChecklistValue(
      booking,
      getReturnDamagePhotos(booking).filter((entry) => entry?.url).length
    ),
  },
];

const getBookingMediaGallery = (booking, { includeSensitive = false } = {}) => {
  if (!booking) return [];
  const conditionPhotos = booking.conditionPhotos || {};
  const gallery = [];
  const addPhoto = (url, label, group = "Dokumentation", note = "") => {
    if (!url) return;
    gallery.push({ url, label, group, note, storage: getMediaStorageLabel(url) });
  };

  if (includeSensitive) {
    addPhoto(booking.driverLicenseFront, "Führerschein Vorderseite", "Führerschein");
    addPhoto(booking.driverLicenseBack, "Führerschein Rückseite", "Führerschein");
  }
  PHOTO_LABELS.forEach((entry) => addPhoto(conditionPhotos[entry.key], entry.label, "Übergabe"));
  addPhoto(conditionPhotos.dashboardPrimary, "Tankstand / Kilometerstand 1", "Übergabe");
  addPhoto(conditionPhotos.dashboardSecondary, "Tankstand / Kilometerstand 2", "Übergabe");
  (booking.damageEntries || []).forEach((entry, index) => {
    addPhoto(entry.url, `Schaden ${index + 1}`, "Schäden", entry.description || "Keine Beschreibung hinterlegt");
  });
  (booking.returnPhotos || []).forEach((entry, index) => {
    addPhoto(
      entry?.url,
      entry.name || entry.label || `Rücknahme ${index + 1}`,
      isReturnDamagePhoto(entry)
        ? "Neue Schäden bei Rücknahme"
        : "Rücknahme",
      entry?.description || ""
    );
  });
  addPhoto(booking.signature, "Kundenunterschrift", "Unterschrift");

  return gallery;
};

const getBookingDamageEntries = (booking) =>
  (booking?.damageEntries || []).filter((entry) => !entry?.deletedAt && (entry?.url || entry?.description));

const getDeletedBookingDamageEntries = (booking) =>
  (booking?.damageEntries || []).filter((entry) => entry?.deletedAt);

const bookingHasInlineMedia = (booking, { includeSensitive = false } = {}) =>
  getBookingMediaGallery(booking, { includeSensitive }).some((photo) => isInlineMedia(photo.url));

const getCorrectionChangeSummary = (event) =>
  Array.isArray(event?.changes)
    ? event.changes.filter((change) => change?.field)
    : (event?.changedFields || []).map((field) => ({ field, before: "", after: "" }));

const getPendingCorrectionEvents = (booking) =>
  (booking?.confirmationEvents || []).filter((event) => event?.type === "confirmation_reset");

const isBookingPendingCorrectionApproval = (booking) =>
  booking?.confirmationStatus === "pending" && getPendingCorrectionEvents(booking).length > 0;

const getBookingEventTimeline = (booking) => {
  const events = [
    { at: booking.createdAt, label: "Vorgang angelegt", detail: booking.orderNumber || booking.loanerPlate || "" },
    { at: booking.confirmationSentAt, label: "Bestätigung gesendet", detail: booking.customerEmail || "" },
    { at: booking.confirmationViewedAt, label: "Bestätigungslink geöffnet", detail: "" },
    { at: booking.confirmationAcceptedAt, label: "Kundenseitig bestätigt", detail: "" },
    { at: booking.checkedOutAt, label: "Fahrzeug ausgegeben", detail: getBookingHandoverEmployee(booking) },
    { at: booking.checkedInAt, label: "Fahrzeug zurückgenommen", detail: getBookingReturnEmployee(booking) },
  ];
  (booking.confirmationEvents || []).forEach((event) => {
    events.push({
      at: event.at || event.ts || event.createdAt,
      label:
        event.type === "loaner_correction"
          ? "Daten korrigiert"
          : event.type === "confirmation_reset"
            ? "Kundenfreigabe zurückgesetzt"
            : event.type || "Ereignis",
      detail: [
        event.by,
        event.reason,
        getCorrectionChangeSummary(event).length
          ? `Felder: ${getCorrectionChangeSummary(event).map((change) => change.field).join(", ")}`
          : "",
      ]
        .filter(Boolean)
        .join(" · "),
    });
  });
  return events
    .filter((event) => event.at || event.detail)
    .sort((a, b) => new Date(a.at || 0) - new Date(b.at || 0));
};

const getBookingAuditTrail = (booking) =>
  [
    { label: "Vorgang angelegt", value: booking.createdAt },
    { label: "Bestätigung gesendet", value: booking.confirmationSentAt },
    { label: "Bestätigung angesehen", value: booking.confirmationViewedAt },
    { label: "Bestätigung angenommen", value: booking.confirmationAcceptedAt },
    {
      label: `Ausgabe durch ${getBookingHandoverEmployee(booking)}`,
      value: booking.checkedOutAt,
    },
    {
      label: `Rücknahme durch ${getBookingReturnEmployee(booking)}`,
      value: booking.checkedInAt,
    },
  ].filter((entry) => entry.value);

const getReturnProtocolLines = (booking) => {
  const kmStart = Number(booking.kmStart || 0);
  const kmEnd = Number(booking.kmEnd || 0);
  const kmDiff = kmStart > 0 && kmEnd > 0 ? Math.max(0, kmEnd - kmStart) : null;
  const returnPhotoCount = getReturnComparisonPhotos(booking).filter((entry) => entry?.url).length;
  const returnDamagePhotoCount = getReturnDamagePhotos(booking).filter((entry) => entry?.url).length;
  const returnDamageCount = new Set(
    getReturnDamagePhotos(booking).map((entry) => entry.damageId).filter(Boolean)
  ).size;
  return [
    `Rücknahme am ${booking.checkedInAt ? fmtDateTime(booking.checkedInAt) : fmtDateTime(booking.actualReturnDate)}`,
    `Mitarbeiter: ${getBookingReturnEmployee(booking)}`,
    `KM Übergabe: ${booking.kmStart || "-"}`,
    `KM Rückgabe: ${booking.kmEnd || "-"}`,
    kmDiff !== null ? `Gefahrene Kilometer: ${kmDiff}` : "",
    `Tank Übergabe: ${booking.fuelStart || "-"}`,
    `Tank Rückgabe: ${booking.fuelEnd || "-"}`,
    `Rückgabe-Fotos: ${returnPhotoCount}/4`,
    returnDamageCount > 0
      ? `Neue Schäden: ${returnDamageCount} mit ${returnDamagePhotoCount} zusätzlichem Foto${returnDamagePhotoCount === 1 ? "" : "s"}`
      : "",
    booking.returnDamageNotes ? `Notizen: ${booking.returnDamageNotes}` : "",
  ].filter(Boolean);
};

const findDuplicateValues = (items, getter) => {
  const counts = new Map();
  items.forEach((item) => {
    const value = String(getter(item) || "").trim().toLowerCase();
    if (!value) return;
    counts.set(value, (counts.get(value) || 0) + 1);
  });
  return new Set([...counts.entries()].filter(([, count]) => count > 1).map(([value]) => value));
};

const buildLoanerDataQuality = (loaners = []) => {
  const duplicatePlates = findDuplicateValues(loaners, (loaner) => loaner.plate);
  const duplicateVins = findDuplicateValues(loaners, (loaner) => loaner.vin);
  return loaners
    .map((loaner) => {
      const issues = [];
      if (!loaner.plate) issues.push("Kennzeichen fehlt");
      if (!loaner.brand) issues.push("Marke fehlt");
      if (!loaner.model) issues.push("Modell fehlt");
      if (!loaner.vin) issues.push("VIN fehlt");
      if (!loaner.usageType) issues.push("Nutzung fehlt");
      if (!loaner.homeBranch) issues.push("Filiale fehlt");
      if (duplicatePlates.has(String(loaner.plate || "").trim().toLowerCase())) {
        issues.push("Kennzeichen doppelt");
      }
      if (duplicateVins.has(String(loaner.vin || "").trim().toLowerCase())) {
        issues.push("VIN doppelt");
      }
      return { loaner, issues };
    })
    .filter((entry) => entry.issues.length > 0);
};

const toDateOnlyString = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
};

const getBookingStartDate = (booking) =>
  toDateOnlyString(booking.plannedPickupDate) ||
  toDateOnlyString(booking.checkedOutAt) ||
  toDateOnlyString(booking.createdAt) ||
  toDateOnlyString(booking.expectedReturnDate);

const getBookingEndDate = (booking) =>
  toDateOnlyString(booking.expectedReturnDate) ||
  toDateOnlyString(booking.actualReturnDate) ||
  getBookingStartDate(booking);

const rangesOverlap = (startA, endA, startB, endB) => {
  if (!startA || !endA || !startB || !endB) return false;
  return startA <= endB && startB <= endA;
};

const isBookingActiveOnDate = (booking, dateValue) =>
  isActiveBooking(booking) &&
  rangesOverlap(
    getBookingStartDate(booking),
    getBookingEndDate(booking),
    dateValue,
    dateValue
  );

const makeToken = () => {
  const buffer = new Uint8Array(24);
  window.crypto.getRandomValues(buffer);
  return Array.from(buffer, (value) => value.toString(16).padStart(2, "0")).join("");
};

const fmtDateTime = (value) => {
  if (!value) return "noch nie";
  return new Date(value).toLocaleString("de-DE");
};

const isStatementTimeoutError = (error) =>
  String(error?.message || error || "")
    .toLowerCase()
    .includes("statement timeout");

const isTransientSaveError = (error) => {
  const message = String(error?.message || error || "").toLowerCase();
  return (
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("statement timeout") ||
    message.includes("timeout")
  );
};

const fmtCurrency = (value) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value || 0));

const getLoanerDurationDays = (start, end) => {
  if (!start || !end) return 0;
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return 0;
  const diffDays = Math.ceil((endDate - startDate) / 86400000);
  return Math.max(1, diffDays + 1);
};

const getLoanerManagementDate = (booking) =>
  booking.plannedPickupDate ||
  booking.checkedOutAt ||
  booking.createdAt ||
  booking.expectedReturnDate ||
  "";

const getLoanerEstimatedCost = (booking, referenceDate = new Date()) => {
  const start = booking.checkedOutAt || booking.plannedPickupDate || booking.createdAt;
  const end =
    booking.actualReturnDate ||
    booking.checkedInAt ||
    booking.expectedReturnDate ||
    referenceDate.toISOString();
  const durationDays = getLoanerDurationDays(start, end);
  const chargeableDays = Math.max(0, durationDays - 7);
  const longUseCost = chargeableDays * 19.9;
  const insuranceCost =
    booking.insuranceOption === "without_deductible" ? durationDays * 19.9 : 0;
  const fuelPenalty =
    booking.status === "returned" &&
    String(booking.fuelEnd || "").trim() &&
    /leer|nicht voll|halb|1\/2|¼|1\/4/i.test(String(booking.fuelEnd || ""))
      ? 50
      : 0;
  return {
    durationDays,
    chargeableDays,
    total: longUseCost + insuranceCost + fuelPenalty,
    parts: { longUseCost, insuranceCost, fuelPenalty },
  };
};

const getLoanerWatchdogAssessment = (booking, referenceDate = new Date()) => {
  const warnings = [];
  const today = toDateOnlyString(referenceDate);
  const addWarning = (type, level, label, detail, recommendation, urgency = "Heute prüfen") =>
    warnings.push({
      type,
      level,
      label,
      title: label,
      detail,
      cause: detail,
      recommendation,
      urgency,
      deadline: urgency,
    });
  const expectedReturn = toDateOnlyString(booking.expectedReturnDate);
  const isActive = isActiveBooking(booking);
  const overdueDays =
    isActive && expectedReturn && expectedReturn < today
      ? getLoanerDurationDays(expectedReturn, today) - 1
      : 0;
  const handoverPhotoCount = PHOTO_LABELS.filter(
    (entry) => booking.conditionPhotos?.[entry.key]
  ).length;
  const dashboardPhotoCount = countTruthyValues([
    booking.conditionPhotos?.dashboardPrimary,
    booking.conditionPhotos?.dashboardSecondary,
  ]);
  const returnPhotoCount = getReturnComparisonPhotos(booking).filter(
    (entry) => entry?.url
  ).length;
  const licenseCount = getDriverLicensePhotoCount(booking);
  const cost = getLoanerEstimatedCost(booking, referenceDate);
  const confirmationAgeHours = booking.confirmationSentAt
    ? Math.round((referenceDate.getTime() - new Date(booking.confirmationSentAt).getTime()) / 3600000)
    : 0;
  const createdAgeHours = booking.createdAt
    ? Math.round((referenceDate.getTime() - new Date(booking.createdAt).getTime()) / 3600000)
    : 0;

  if (overdueDays > 0) {
    addWarning(
      "overdue_return",
      overdueDays >= 2 ? "critical" : "warn",
      "Rückgabe überfällig",
      `${overdueDays} Tag(e) über Termin`,
      "Kundenkontakt prüfen und Rückgabe aktiv nachfassen.",
      overdueDays >= 2 ? "Sofort" : "Heute"
    );
  }
  if (booking.status === "awaiting_customer_confirmation" && confirmationAgeHours >= 24) {
    addWarning(
      "open_approval",
      confirmationAgeHours >= 48 ? "critical" : "warn",
      "Kundenfreigabe offen",
      `${confirmationAgeHours} Stunden seit Versand`,
      "Bestätigungslink erneut senden oder telefonisch klären.",
      confirmationAgeHours >= 48 ? "Sofort" : "Heute"
    );
  }
  if (booking.status === "ready_for_checkout" && createdAgeHours >= 24) {
    addWarning(
      "ready_waiting",
      "warn",
      "Bereit, aber nicht ausgegeben",
      "Der Vorgang wartet ungewöhnlich lange auf Ausgabe.",
      "Prüfen, ob der Bedarf noch besteht oder storniert werden soll.",
      "Heute klären"
    );
  }
  if (
    booking.licensePresenceLoaded !== false &&
    licenseCount < 2 &&
    booking.status !== "cancelled"
  ) {
    addWarning(
      "missing_documents",
      booking.status === "checked_out" ? "critical" : "warn",
      "Führerschein unvollständig",
      `${licenseCount}/2 Fotos vorhanden`,
      "Dokumentation vervollständigen oder Vorgang prüfen.",
      booking.status === "checked_out" ? "Sofort" : "Vor Ausgabe"
    );
  }
  if (
    booking.mediaLoaded !== false &&
    handoverPhotoCount < PHOTO_LABELS.length &&
    ["ready_for_checkout", "checked_out", "returned"].includes(booking.status)
  ) {
    addWarning(
      "handover_photos",
      booking.status === "checked_out" ? "critical" : "warn",
      "Übergabefotos fehlen",
      `${handoverPhotoCount}/${PHOTO_LABELS.length} Fahrzeugseiten vorhanden`,
      "Fehlende Fotos nachdokumentieren, bevor der Fall abgeschlossen wird.",
      booking.status === "checked_out" ? "Sofort" : "Vor Ausgabe"
    );
  }
  if (
    booking.mediaLoaded !== false &&
    dashboardPhotoCount < 1 &&
    ["ready_for_checkout", "checked_out", "returned"].includes(booking.status)
  ) {
    addWarning(
      "dashboard_photo",
      "warn",
      "Tacho/Tankfoto fehlt",
      "Kein Pflichtfoto für Kilometerstand oder Tankstand vorhanden.",
      "Foto nachholen oder Begründung im Vorgang dokumentieren.",
      "Vor Abschluss"
    );
  }
  if (
    booking.mediaLoaded !== false &&
    booking.status === "returned" &&
    returnPhotoCount < PHOTO_LABELS.length
  ) {
    addWarning(
      "return_photos",
      "warn",
      "Rücknahmefotos unvollständig",
      `${returnPhotoCount}/${PHOTO_LABELS.length} Rücknahmefotos vorhanden`,
      "Rücknahmeprotokoll prüfen und fehlende Fotos ergänzen.",
      "Vor Archivierung"
    );
  }
  if (cost.durationDays > 14) {
    addWarning(
      "long_usage",
      "critical",
      "Sehr lange Nutzung",
      `${cost.durationDays} Tage Nutzungsdauer`,
      "Kosten, Rückgabetermin und Ersatzbedarf prüfen.",
      "Heute entscheiden"
    );
  } else if (cost.durationDays > 7) {
    addWarning(
      "long_usage",
      "warn",
      "Lange Nutzung",
      `${cost.durationDays} Tage Nutzungsdauer`,
      "Abrechnung ab Tag 8 prüfen.",
      "Heute prüfen"
    );
  }
  if (cost.total >= 250) {
    addWarning(
      "cost",
      "critical",
      "Kosten auffällig",
      fmtCurrency(cost.total),
      "Abrechnung und Kundenfreigabe prüfen.",
      "Sofort"
    );
  } else if (cost.total >= 100) {
    addWarning(
      "cost",
      "warn",
      "Kosten beobachten",
      fmtCurrency(cost.total),
      "Kostenrahmen im Blick behalten.",
      "Diese Woche"
    );
  }
  if (
    booking.loanerHomeBranch &&
    booking.branch &&
    booking.loanerHomeBranch !== booking.branch &&
    !booking.branchWarningAcknowledged
  ) {
    addWarning(
      "branch",
      "warn",
      "Standort abweichend",
      `${getBranchLabel(booking.loanerHomeBranch)} statt ${getBranchLabel(booking.branch)}`,
      "Fahrzeugstandort vor Ausgabe verifizieren.",
      "Vor Ausgabe"
    );
  }

  const score = warnings.reduce(
    (sum, warning) => sum + (warning.level === "critical" ? 35 : 15),
    0
  );
  const level = score >= 50 ? "critical" : score >= 15 ? "warn" : "normal";
  return {
    level,
    score,
    overdueDays,
    cost,
    warnings,
    label:
      level === "critical"
        ? "Kritisch"
        : level === "warn"
          ? "Warnung"
          : "Normal",
  };
};

const normalizeRole = (value) => {
  const role = String(value || "").toLowerCase();
  if (["admin", "workshop_lead", "technician"].includes(role)) return role;
  return "staff";
};
const normalizeDepartment = (value) =>
  ROLES.includes(value) ? value : DEFAULT_DEPARTMENT;
const getBranchLabel = (value) =>
  BRANCH_OPTIONS.find((entry) => entry.value === value)?.label || "keine Zuordnung";
const getLoanerUsageLabel = (value) =>
  LOANER_USAGE_OPTIONS.find((entry) => entry.value === value)?.label || value || "-";
const getOrderTypeLabel = (value) =>
  ORDER_TYPE_OPTIONS.find((entry) => entry.value === value)?.label || "Externer Auftrag";
const isInternalOrder = (cardOrDraft) =>
  (cardOrDraft?.orderType || cardOrDraft?.loanerFlow?.orderType || "external") === "internal";
const getInsuranceLabel = (value) =>
  INSURANCE_OPTIONS.find((entry) => entry.value === value)?.label || value || "-";
const getBookingStatusLabel = (value) => BOOKING_STATUS_LABELS[value] || value || "-";
const getOrderGateStatus = (card, relatedBooking = null) => {
  const flow = card?.loanerFlow || {};
  if (isInternalOrder(card)) return "Interner Auftrag - keine Kundenbestätigung nötig";
  if (relatedBooking?.status === "awaiting_customer_confirmation") return "Wartet auf gemeinsame Kundenbestätigung";
  if (relatedBooking?.status === "ready_for_checkout") return "Bestätigt, bereit zur Leihwagenübergabe";
  if (relatedBooking?.status === "checked_out") return "Leihwagen ausgegeben, Auftrag freigegeben";
  if (relatedBooking?.status === "returned") return "Leihwagen zurückgenommen";
  if (card?.needsLoaner) return "Leihwagenbedarf offen";
  if (flow.orderConfirmationStatus === "accepted") return "Auftrag vom Kunden bestätigt";
  if (flow.orderConfirmationStatus === "sent") return "Wartet auf Kundenbestätigung";
  if (flow.orderConfirmationStatus === "pending_loaner_confirmation") return "Wartet auf gemeinsame Leihwagenbestätigung";
  return "Annahme erfasst";
};

const buildConfirmationProtocol = ({ card = null, booking = null }) => {
  const flow = card?.loanerFlow || {};
  if (isInternalOrder(card)) return [];
  return [
    { label: "Auftragslink erstellt", value: flow.orderConfirmationCreatedAt },
    { label: "Auftragslink gesendet", value: flow.orderConfirmationSentAt },
    { label: "Auftragslink geöffnet", value: flow.orderConfirmationViewedAt },
    { label: "Auftrag bestätigt", value: flow.orderConfirmationAcceptedAt },
    { label: "Leihwagenlink gesendet", value: booking?.confirmationSentAt || flow.loanerConfirmationSentAt },
    { label: "Leihwagenlink geöffnet", value: booking?.confirmationViewedAt },
    { label: "Gemeinsam bestätigt", value: booking?.confirmationAcceptedAt || flow.loanerConfirmationAcceptedAt },
  ].filter((entry) => entry.value);
};

const getDepartmentTodoPresets = (department) => DEPARTMENT_TODO_PRESETS[department] || [];

const getRelevantDepartmentTabs = (department, isAdminView = false) => {
  if (department === DEFAULT_DEPARTMENT || isAdminView) return DETAIL_TABS;
  if (department === "Werkstatt") return DETAIL_TABS.filter((tab) => tab.id === "work");
  return DETAIL_TABS;
};

const buildBookingConfirmationSteps = (booking) => [
  {
    label: "E-Mail gesendet",
    done: Boolean(booking?.confirmationSentAt),
    value: booking?.confirmationSentAt ? fmtDateTime(booking.confirmationSentAt) : "ausstehend",
  },
  {
    label: "Kunde hat geöffnet",
    done: Boolean(booking?.confirmationViewedAt),
    value: booking?.confirmationViewedAt ? fmtDateTime(booking.confirmationViewedAt) : "noch nicht geöffnet",
  },
  {
    label: "Kunde hat unterschrieben",
    done: Boolean(booking?.confirmationAcceptedAt),
    value: booking?.confirmationAcceptedAt ? fmtDateTime(booking.confirmationAcceptedAt) : "wartet",
  },
  {
    label: "Freigabe",
    done: ["ready_for_checkout", "checked_out", "returned"].includes(booking?.status),
    value: getBookingStatusLabel(booking?.status),
  },
];

const buildOrderConfirmationSteps = (card, relatedBooking = null) => {
  const flow = card?.loanerFlow || {};
  if (isInternalOrder(card)) {
    return [
      {
        label: "Auftragstyp",
        done: true,
        value: "Interner Auftrag - keine Kundenbestätigung",
      },
    ];
  }
  if (relatedBooking) return buildBookingConfirmationSteps(relatedBooking);
  return [
    {
      label: "E-Mail gesendet",
      done: Boolean(flow.orderConfirmationSentAt),
      value: flow.orderConfirmationSentAt ? fmtDateTime(flow.orderConfirmationSentAt) : "ausstehend",
    },
    {
      label: "Kunde hat geöffnet",
      done: Boolean(flow.orderConfirmationViewedAt),
      value: flow.orderConfirmationViewedAt ? fmtDateTime(flow.orderConfirmationViewedAt) : "noch nicht geöffnet",
    },
    {
      label: "Kunde hat unterschrieben",
      done: Boolean(flow.orderConfirmationAcceptedAt),
      value: flow.orderConfirmationAcceptedAt ? fmtDateTime(flow.orderConfirmationAcceptedAt) : "wartet",
    },
    {
      label: "Freigabe",
      done: flow.orderConfirmationStatus === "accepted",
      value: getOrderGateStatus(card, relatedBooking),
    },
  ];
};
const getEmailStatusLabel = (card, relatedBooking = null) => {
  if (isInternalOrder(card)) return "E-Mail nicht nötig";
  if (relatedBooking) {
    if (relatedBooking.confirmationAcceptedAt || relatedBooking.confirmationStatus === "accepted") return "Kunde bestätigt";
    if (relatedBooking.confirmationViewedAt) return "Link geöffnet";
    if (relatedBooking.confirmationSentAt) return "E-Mail gesendet";
    if (relatedBooking.status === "awaiting_customer_confirmation") return "Versand prüfen";
    return "E-Mail offen";
  }
  const flow = card?.loanerFlow || {};
  if (flow.orderConfirmationStatus === "accepted" || flow.orderConfirmationAcceptedAt) return "Kunde bestätigt";
  if (flow.orderConfirmationViewedAt) return "Link geöffnet";
  if (flow.orderConfirmationSentAt || flow.orderConfirmationStatus === "sent") return "E-Mail gesendet";
  if (flow.orderConfirmationStatus === "pending_email") return "E-Mail vorbereitet";
  if (flow.orderConfirmationStatus === "pending_loaner_confirmation") return "wartet auf Leihwagen-Mail";
  return card?.customerEmail ? "E-Mail offen" : "keine E-Mail";
};

const buildMailDiagnosticSteps = (booking) => [
  {
    label: "E-Mail",
    value: booking?.confirmationSentAt ? fmtDateTime(booking.confirmationSentAt) : "noch nicht versendet",
    ok: Boolean(booking?.confirmationSentAt),
  },
  {
    label: "Link geöffnet",
    value: booking?.confirmationViewedAt ? fmtDateTime(booking.confirmationViewedAt) : "offen",
    ok: Boolean(booking?.confirmationViewedAt),
  },
  {
    label: "Kunde bestätigt",
    value: booking?.confirmationAcceptedAt ? fmtDateTime(booking.confirmationAcceptedAt) : "offen",
    ok: Boolean(booking?.confirmationAcceptedAt || booking?.confirmationStatus === "accepted"),
  },
  {
    label: "QR / Link-Fallback",
    value: booking?.confirmationToken ? "verfügbar" : "nicht verfügbar",
    ok: Boolean(booking?.confirmationToken),
  },
];

const getConfirmationHealth = (bookings = [], cards = []) => {
  const waitingBookings = bookings.filter((booking) => booking.status === "awaiting_customer_confirmation");
  const readyBookings = bookings.filter((booking) => booking.status === "ready_for_checkout");
  const unsignedOrders = cards.filter(
    (card) => !card.needsLoaner && card.loanerFlow?.orderConfirmationStatus === "sent"
  );
  const missingSentAt = waitingBookings.filter((booking) => !booking.confirmationSentAt);
  return [
    {
      label: "Wartet auf Kunden",
      value: waitingBookings.length + unsignedOrders.length,
      tone: waitingBookings.length + unsignedOrders.length > 0 ? "warn" : "ok",
    },
    {
      label: "Bereit zur Ausgabe",
      value: readyBookings.length,
      tone: readyBookings.length > 0 ? "ok" : "neutral",
    },
    {
      label: "Versand prüfen",
      value: missingSentAt.length,
      tone: missingSentAt.length > 0 ? "danger" : "ok",
    },
  ];
};

const getStorageSizeKb = (value) => {
  try {
    return Math.round((new Blob([JSON.stringify(value || [])]).size / 1024) * 10) / 10;
  } catch {
    return 0;
  }
};

const buildMailOperationalDiagnostics = (bookings = [], cards = []) => {
  const waitingBookings = bookings.filter((booking) => booking.status === "awaiting_customer_confirmation");
  const sentButNotOpened = waitingBookings.filter((booking) => booking.confirmationSentAt && !booking.confirmationViewedAt);
  const openedButNotAccepted = waitingBookings.filter((booking) => booking.confirmationViewedAt && !booking.confirmationAcceptedAt);
  const missingToken = waitingBookings.filter((booking) => !booking.confirmationToken);
  const waitingOrders = cards.filter((card) => card.loanerFlow?.orderConfirmationStatus === "sent");
  return [
    {
      label: "Mail gesendet, nicht geöffnet",
      value: sentButNotOpened.length,
      tone: sentButNotOpened.length > 0 ? "warn" : "ok",
      detail: "Bei Häufung SPF/DKIM/DMARC, Spamordner und Absender-Reputation prüfen.",
    },
    {
      label: "Geöffnet, nicht bestätigt",
      value: openedButNotAccepted.length,
      tone: openedButNotAccepted.length > 0 ? "warn" : "ok",
      detail: "Kunde hat den Link gesehen, aber noch nicht unterschrieben.",
    },
    {
      label: "Bestätigungslink fehlt",
      value: missingToken.length,
      tone: missingToken.length > 0 ? "danger" : "ok",
      detail: "Diese Vorgänge benötigen sofortige Prüfung, sonst gibt es keinen QR-/Mail-Fallback.",
    },
    {
      label: "Auftragsbestätigung offen",
      value: waitingOrders.length,
      tone: waitingOrders.length > 0 ? "warn" : "ok",
      detail: "Aufträge ohne Leihwagen warten noch auf Kundenfreigabe.",
    },
  ];
};

const getDefaultIntakeSubtasks = () =>
  [
    "Kundenauftrag anlegen",
    "Fahrzeugdaten erfassen",
    "Zustand dokumentieren",
    "Werkstattauftrag weitergeben",
  ].join("\n");

const getDefaultIntakeDraft = () => ({
  mode: "task",
  orderType: "external",
  orderNumber: "",
  externalReference: "",
  externalSystem: "motiondata",
  sourceLabel: "manuell",
  caseType: "annual_service",
  customerFirstName: "",
  customerLastName: "",
  customerGender: "",
  customerEmail: "",
  branch: "",
  licensePlate: "",
  vehicleBrand: "",
  vehicleModel: "",
  firstRegistrationDate: "",
  vin: "",
  engineType: "",
  transmissionType: "",
  mileage: "",
  nationalCode: "",
  curbWeight: "",
  grossWeight: "",
  estimatedHours: "",
  assignedTech: "",
  title: "",
  due: addDays(0),
  prio: "normal",
  needsLoaner: false,
  notes: "",
  subtasksText: getDefaultIntakeSubtasks(),
  saving: false,
  error: "",
  success: "",
});

const makeCardId = () => {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const value = Math.floor(Math.random() * 16);
    const next = char === "x" ? value : (value & 0x3) | 0x8;
    return next.toString(16);
  });
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const compressImageFileToDataUrl = async (file, { maxSize = 760, quality = 0.5 } = {}) => {
  if (!file?.type?.startsWith("image/")) return readFileAsDataUrl(file);

  const source = await readFileAsDataUrl(file);
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const scale = Math.min(1, maxSize / Math.max(image.width, image.height));
      const width = Math.max(1, Math.round(image.width * scale));
      const height = Math.max(1, Math.round(image.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      if (!context) {
        resolve(source);
        return;
      }
      context.drawImage(image, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    image.onerror = () => resolve(source);
    image.src = source;
  });
};

const fileToDataUrl = (file) => compressImageFileToDataUrl(file);

const dataUrlToBlob = async (dataUrl) => {
  const response = await fetch(dataUrl);
  return response.blob();
};

const isInlineMedia = (value) => typeof value === "string" && value.startsWith("data:");
const isRemoteMedia = (value) => /^https?:\/\//i.test(String(value || ""));
const getMediaStorageLabel = (url) => {
  if (!url) return "fehlt";
  if (isInlineMedia(url)) return "lokal, Sync offen";
  if (isRemoteMedia(url)) return "synchronisiert";
  return "gespeichert";
};

const CameraCaptureButton = ({
  label,
  value,
  onChange,
  onOpenCamera,
  sequenceHint = "",
}) => {
  if (onOpenCamera) {
    return (
      <div className="cameraButtonCard">
        <div className="cameraButtonText">
          <strong>{label}</strong>
          <span>
            {value
              ? "Foto einzeln ersetzen"
              : sequenceHint || "Foto aufnehmen"}
          </span>
        </div>
        <button type="button" className="btn primary" onClick={onOpenCamera}>
          {value ? "Neu aufnehmen" : "Kamera öffnen"}
        </button>
        <label className="cameraUploadFallback">
          Datei auswählen
          <input
            type="file"
            accept="image/*"
            onClick={(event) => {
              event.currentTarget.value = "";
            }}
            onChange={onChange}
          />
        </label>
        {value && <img src={value} alt={label} />}
      </div>
    );
  }

  return (
    <label className="cameraButtonCard">
      <div className="cameraButtonText">
        <strong>{label}</strong>
        <span>Foto aufnehmen</span>
      </div>
      <input
        type="file"
        accept="image/*"
        capture="environment"
        onClick={(event) => {
          event.currentTarget.value = "";
        }}
        onChange={onChange}
      />
      {value && <img src={value} alt={label} />}
    </label>
  );
};

const PhotoSequenceCamera = ({ sequence, onCapture, onClose }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraError, setCameraError] = useState("");
  const [isCapturing, setIsCapturing] = useState(false);
  const currentItem = sequence.items[sequence.index];

  useEffect(() => {
    let cancelled = false;

    const startCamera = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError(
          "Die Kamera kann in diesem Browser nicht direkt geöffnet werden. Bitte den Datei-Upload verwenden."
        );
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch {
        setCameraError(
          "Kamerazugriff nicht möglich. Bitte die Kameraberechtigung erlauben oder den Datei-Upload verwenden."
        );
      }
    };

    startCamera();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, []);

  const captureCurrentPhoto = async () => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video?.videoHeight || !currentItem) {
      setCameraError("Die Kamera ist noch nicht bereit. Bitte kurz erneut versuchen.");
      return;
    }

    const maxDimension = 1600;
    const scale = Math.min(
      1,
      maxDimension / Math.max(video.videoWidth, video.videoHeight)
    );
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
    canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
    const context = canvas.getContext("2d");
    if (!context) {
      setCameraError("Das Foto konnte nicht verarbeitet werden.");
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    setIsCapturing(true);
    setCameraError("");
    try {
      await onCapture(currentItem, canvas.toDataURL("image/jpeg", 0.82));
    } catch (error) {
      setCameraError(`Foto konnte nicht gespeichert werden: ${error.message}`);
    } finally {
      setIsCapturing(false);
    }
  };

  return (
    <div className="modalOverlay photoSequenceOverlay">
      <div
        className="photoSequenceModal"
        role="dialog"
        aria-modal="true"
        aria-label={sequence.title}
      >
        <div className="photoSequenceHeader">
          <div>
            <span className="sectionTag">
              Foto {sequence.index + 1} von {sequence.items.length}
            </span>
            <h3>{currentItem?.label || sequence.title}</h3>
            <p>
              Nach der Aufnahme wird automatisch das nächste vorgesehene Foto
              angezeigt.
            </p>
          </div>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
        </div>

        <div className="photoSequenceViewport">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            aria-label={`Kameravorschau für ${currentItem?.label || sequence.title}`}
          />
          {cameraError && <div className="photoSequenceError">{cameraError}</div>}
        </div>

        <div className="photoSequenceProgress" aria-label="Fortschritt Fotoreihe">
          {sequence.items.map((item, index) => (
            <span
              key={item.field}
              className={
                index < sequence.index
                  ? "complete"
                  : index === sequence.index
                    ? "active"
                    : ""
              }
            >
              {item.label}
            </span>
          ))}
        </div>

        <button
          type="button"
          className="btn primary photoSequenceShutter"
          onClick={captureCurrentPhoto}
          disabled={isCapturing}
        >
          {isCapturing ? "Foto wird gespeichert ..." : "Foto aufnehmen"}
        </button>
      </div>
    </div>
  );
};

const InfoHint = ({ text }) => (
  <span className="infoHint" title={text} aria-label={text}>
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="10" x2="12" y2="16" />
      <circle cx="12" cy="7.25" r="1.2" />
    </svg>
  </span>
);

const UploadFallbackCard = ({ label, description, onChange, multiple = false }) => (
  <label className="uploadCard">
    <span>{label}</span>
    <small>{description}</small>
    <input type="file" accept="image/*" multiple={multiple} onChange={onChange} />
  </label>
);

function CustomerConfirmationView({ token }) {
  const signatureRef = useRef("");
  const privacyConsentRef = useRef(null);
  const signatureStorageKey = getCustomerSignatureStorageKey(token);
  const [state, setState] = useState({
    loading: true,
    error: "",
    booking: null,
    submitting: false,
    accepted: false,
    customerSignature: "",
    signatureSaving: false,
    signatureSaveMessage: "",
    privacyAccepted: false,
    privacyError: "",
  });

  useEffect(() => {
    let active = true;

    const loadBooking = async () => {
      try {
        const response = await fetch(`/api/loaner-confirmation?token=${token}`);
        const payload = await response.json();

        if (!active) return;

        if (!response.ok) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: payload.error || "Dokumentation konnte nicht geladen werden.",
          }));
          return;
        }

        let locallySavedSignature = "";
        try {
          locallySavedSignature = localStorage.getItem(signatureStorageKey) || "";
        } catch {
          locallySavedSignature = "";
        }
        const backendSignature = payload.card?.loanerFlow?.customerConfirmationSignature || "";
        const restoredSignature = isValidSignatureDataUrl(backendSignature)
          ? backendSignature
          : isValidSignatureDataUrl(locallySavedSignature)
            ? locallySavedSignature
            : "";
        signatureRef.current = restoredSignature;

        setState({
          loading: false,
          error: "",
          booking: payload.booking,
          card: payload.card || null,
          submitting: false,
          accepted: payload.booking.customerConfirmationStatus === "accepted",
          customerSignature: restoredSignature,
          signatureSaving: false,
          signatureSaveMessage: restoredSignature
            ? backendSignature
              ? "Unterschrift wurde aus der bestätigten Dokumentation geladen."
              : "Unterschrift wurde aus diesem Gerät wiederhergestellt."
            : "",
          privacyAccepted: hasReusablePrivacyConsent(payload.booking),
          privacyError: "",
        });
      } catch (error) {
        if (!active) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          error: getFriendlyProcessError(error, "Dokumentation konnte nicht geladen werden. Bitte Link prüfen oder Team Fraenkel kontaktieren."),
        }));
      }
    };

    loadBooking();
    return () => {
      active = false;
    };
  }, [token, signatureStorageKey]);

  const handleConfirm = async () => {
    const currentSignature = signatureRef.current || state.customerSignature;
    if (!isValidSignatureDataUrl(currentSignature)) {
      setState((prev) => ({
        ...prev,
        error: state.signatureSaving
          ? "Die Unterschrift wird noch gespeichert. Bitte kurz warten."
          : "Bitte unterschreiben und die Unterschrift speichern.",
      }));
      return;
    }
    if (!state.privacyAccepted) {
      setState((prev) => ({
        ...prev,
        error: "",
        privacyError:
          "Bitte bestätigen Sie die Datenschutzbestimmungen, bevor Sie fortfahren.",
      }));
      window.requestAnimationFrame(() => {
        privacyConsentRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
      return;
    }

    setState((prev) => ({
      ...prev,
      submitting: true,
      error: "",
      privacyError: "",
    }));

    try {
      const response = await fetch("/api/loaner-confirmation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          signature: currentSignature,
          privacyConsentVersion: CUSTOMER_PRIVACY_CONSENT_VERSION,
        }),
      });

      const payload = await response.json();

      if (!response.ok) {
        setState((prev) => ({
          ...prev,
          submitting: false,
          error: payload.error || "Bestätigung fehlgeschlagen.",
        }));
        return;
      }

      setState((prev) => ({
        ...prev,
        submitting: false,
        accepted: true,
        booking: payload.booking,
        card: payload.card || prev.card || null,
        customerSignature:
          payload.card?.loanerFlow?.customerConfirmationSignature || signatureRef.current || prev.customerSignature,
        signatureSaving: false,
        signatureSaveMessage: "Unterschrift wurde bestätigt und gespeichert.",
        privacyAccepted: true,
        privacyError: "",
      }));
      try {
        localStorage.removeItem(signatureStorageKey);
      } catch {
        // Die Bestätigung ist bereits serverseitig gespeichert; lokaler Cache ist nur Komfort.
      }
    } catch (error) {
      setState((prev) => ({
          ...prev,
          submitting: false,
          error: getFriendlyProcessError(error, "Bestätigung fehlgeschlagen. Bitte Verbindung prüfen und erneut versuchen."),
      }));
    }
  };

  const saveCustomerSignature = (signature) => {
    setState((prev) => ({ ...prev, signatureSaving: true, error: "", signatureSaveMessage: "" }));
    window.setTimeout(() => {
      if (!isValidSignatureDataUrl(signature)) {
        signatureRef.current = "";
        setState((prev) => ({
          ...prev,
          customerSignature: "",
          signatureSaving: false,
          signatureSaveMessage: "",
          error: "Die Unterschrift konnte nicht gespeichert werden. Bitte erneut unterschreiben.",
        }));
        return;
      }

      signatureRef.current = signature;
      try {
        localStorage.setItem(signatureStorageKey, signature);
      } catch {
        setState((prev) => ({
          ...prev,
          customerSignature: signature,
          signatureSaving: false,
          signatureSaveMessage:
            "Unterschrift gespeichert. Hinweis: Dieses Gerät konnte sie nicht lokal zwischenspeichern.",
          error: "",
        }));
        return;
      }
      setState((prev) => ({
        ...prev,
        customerSignature: signature,
        signatureSaving: false,
        signatureSaveMessage: "Unterschrift gespeichert. Sie können jetzt bestätigen.",
        error: "",
      }));
    }, 0);
  };

  if (state.loading) {
    return (
      <div className="authShell">
        <div className="authCard">
          <h1>Leihwagen Dokumentation</h1>
          <p>Dokumentation wird geladen ...</p>
        </div>
      </div>
    );
  }

  if (!state.booking) {
    return (
      <div className="authShell">
        <div className="authCard">
          <h1>Leihwagen Dokumentation</h1>
          <div className="authError">{state.error || "Dokumentation nicht gefunden."}</div>
        </div>
      </div>
    );
  }

  const booking = state.booking;
  const confirmationCard = state.card;
  const hasCustomerSignature = isValidSignatureDataUrl(signatureRef.current || state.customerSignature);
  const canCustomerConfirm = Boolean(hasCustomerSignature && state.privacyAccepted && !state.signatureSaving);
  const customerConfirmHint = state.privacyAccepted
    ? state.signatureSaving
      ? "Unterschrift wird gespeichert ..."
      : hasCustomerSignature
      ? "Alles bereit. Bitte final bestätigen."
      : "Bitte unten noch unterschreiben und speichern."
    : "Bitte zuerst die Datenschutzbestätigung aktiv anhaken.";
  const correctionEvents = getPendingCorrectionEvents({
    confirmationEvents: booking.confirmationEvents || [],
    confirmationStatus: booking.customerConfirmationStatus,
  });
  const latestCorrectionEvent = correctionEvents[correctionEvents.length - 1] || null;
  const vehiclePhotos = PHOTO_LABELS.map((entry) => ({
    key: entry.key,
    label: entry.label,
    url: booking.conditionPhotos?.[entry.key] || "",
  })).filter((entry) => entry.url);
  const durationDays = getLoanerDurationDays(
    booking.plannedPickupDate,
    booking.expectedReturnDate
  );
  const dailySurcharge = booking.insuranceOption === "without_deductible" ? 19.9 : 0;
  const surchargeTotal = dailySurcharge > 0 ? dailySurcharge * durationDays : 0;
  const costHighlights = [
    {
      label: "Tarif",
      value: getInsuranceLabel(booking.insuranceOption),
    },
    {
      label: "Verleihdauer",
      value: `${durationDays || 0} Tag(e)`,
    },
    {
      label: "Zusatzkosten laut Tarif",
      value: dailySurcharge > 0 ? `${fmtCurrency(dailySurcharge)} / Tag` : "Keine",
    },
    {
      label: "Voraussichtliche Zusatzkosten",
      value: fmtCurrency(surchargeTotal),
      highlight: true,
    },
  ];
  const documentMeta = [
    { label: "Kunde", value: booking.customerName || "-" },
    { label: "E-Mail", value: booking.customerEmail || "-" },
    { label: "Fahrzeug", value: booking.loanerPlate || "-" },
    { label: "Verleihbeginn", value: fmtDate(booking.plannedPickupDate) },
    { label: "Geplante Rückgabe", value: fmtDate(booking.expectedReturnDate) },
    { label: "Auftrag", value: booking.orderNumber || "-" },
  ];
  const handoverMeta = [
    { label: "Kilometerstand", value: booking.kmStart || "-" },
    { label: "Tankstand", value: booking.fuelStart || "-" },
    {
      label: "Schadensdokumentation",
      value: booking.noOtherDamageConfirmed
        ? "Abgesehen von den oben angeführten Schäden wurden bei der Übergabe keine weiteren Schäden dokumentiert."
        : "Noch nicht abschließend bestätigt.",
    },
    {
      label: "Erstellt am",
      value: booking.createdAt ? fmtDateTime(booking.createdAt) : "-",
    },
  ];

  return (
    <div className="authShell">
      <div className="customerCard customerDocument">
          <div className="documentHero">
            <div>
              <p className="eyebrow">Team Fraenkel</p>
            <h1>Leihwagen-Dokumentation zur Bestätigung</h1>
            <p className="documentLead">
              Bitte prüfen Sie die Übergabedaten, die Fotodokumentation und die
              Vertragsbedingungen. Mit Ihrer Bestätigung wird das Fahrzeug intern
              zur Übergabe freigegeben.
            </p>
          </div>
            <div className={`documentStatus ${state.accepted ? "accepted" : "pending"}`}>
              {state.accepted ? "Bereits bestätigt" : "Bestätigung offen"}
            </div>
          </div>

          <div className="documentStepRail">
            {["1. Auftrag prüfen", "2. Leihwagen prüfen", "3. Bedingungen", "4. Unterschreiben"].map(
              (step) => (
                <span key={step}>{step}</span>
              )
            )}
          </div>

          {!state.accepted && (
            <div className="documentTopConfirm">
              <div>
                <strong>Bestätigung erforderlich</strong>
                <span>{customerConfirmHint}</span>
              </div>
              <button
                type="button"
                className="btn primary"
                onClick={handleConfirm}
                disabled={state.submitting || state.signatureSaving || !hasCustomerSignature}
              >
                {state.submitting ? "Bestätigung läuft ..." : canCustomerConfirm ? "Jetzt bestätigen" : "Noch Angaben offen"}
              </button>
            </div>
          )}

          {latestCorrectionEvent && !state.accepted && (
            <section className="documentPanel correctionReviewPanel">
              <div className="documentPanelHeader">
                <h3>Änderung seit der letzten Bestätigung</h3>
                <p>
                  Diese Daten wurden nachträglich korrigiert und müssen erneut kundenseitig bestätigt werden.
                </p>
              </div>
              <div className="correctionReviewList">
                {getCorrectionChangeSummary(latestCorrectionEvent).map((change) => (
                  <div key={`${change.field}_${change.before}_${change.after}`} className="correctionReviewRow">
                    <strong>{change.field}</strong>
                    <span>{change.before || "-"}</span>
                    <span>{change.after || "-"}</span>
                  </div>
                ))}
              </div>
              <p className="mutedText">
                Grund: {latestCorrectionEvent.reason || "Korrektur der aufgenommenen Daten"}
              </p>
            </section>
          )}

          <div className="documentGrid">
          {confirmationCard && (
            <section className="documentPanel">
              <div className="documentPanelHeader">
                <h3>Werkstattauftrag</h3>
                <p>Der zuvor in der Annahme erfasste Auftrag.</p>
              </div>
              <div className="documentMetaGrid">
                <div className="documentMetaItem">
                  <span>Auftrag</span>
                  <strong>{confirmationCard.orderNumber || "-"}</strong>
                </div>
                <div className="documentMetaItem">
                  <span>Anliegen</span>
                  <strong>{confirmationCard.title || "-"}</strong>
                </div>
                <div className="documentMetaItem">
                  <span>Kundenfahrzeug</span>
                  <strong>
                    {[confirmationCard.vehicleData?.brand, confirmationCard.vehicleData?.model, confirmationCard.licensePlate]
                      .filter(Boolean)
                      .join(" - ") || "-"}
                  </strong>
                </div>
                <div className="documentMetaItem">
                  <span>Kilometerstand</span>
                  <strong>{confirmationCard.vehicleData?.mileage || "-"}</strong>
                </div>
              </div>
              {confirmationCard.loanerFlow?.intakeSignature && (
                <div className="signatureDocumentBox">
                  <img
                    src={confirmationCard.loanerFlow.intakeSignature}
                    alt="Unterschrift Werkstattauftrag"
                    className="signatureImage"
                  />
                </div>
              )}
              {confirmationCard.loanerFlow?.signatureUnavailable && (
                <p className="mutedText">
                  Die Unterschrift war bei der Annahme nicht einholbar. Die E-Mail-Bestätigung gilt als Bestätigung des Auftrags.
                </p>
              )}
            </section>
          )}
          <section className="documentPanel">
            <div className="documentPanelHeader">
              <h3>Dokumentübersicht</h3>
              <p>Alle relevanten Daten auf einen Blick.</p>
            </div>
            <div className="documentMetaGrid">
              {documentMeta.map((entry) => (
                <div key={entry.label} className="documentMetaItem">
                  <span>{entry.label}</span>
                  <strong>{entry.value}</strong>
                </div>
              ))}
            </div>
          </section>

          <section className="documentPanel costPanel">
            <div className="documentPanelHeader">
              <h3>Kosten und Tarif</h3>
              <p>Zusammenfassung der gewählten Konditionen.</p>
            </div>
            <div className="costList">
              {costHighlights.map((entry) => (
                <div
                  key={entry.label}
                  className={`costRow${entry.highlight ? " highlight" : ""}`}
                >
                  <span>{entry.label}</span>
                  <strong>{entry.value}</strong>
                </div>
              ))}
            </div>
            <div className="costFootnote">
              Ab dem achten Tag fallen jedenfalls 19,90 EUR pro Tag an. Bei
              nicht vollgetankter Rückgabe wird eine Tankpauschale von 50 EUR
              verrechnet.
            </div>
          </section>
        </div>

        <section className="documentPanel">
          <div className="documentPanelHeader">
            <h3>Übergabedaten</h3>
            <p>Dokumentierter Zustand bei Übergabe.</p>
          </div>
          <div className="customerSummary documentSummaryGrid">
            {handoverMeta.map((entry) => (
              <div key={entry.label} className="summaryRow stacked">
                <strong>{entry.label}</strong>
                <span>{entry.value}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="documentPanel">
          <div className="documentPanelHeader">
            <h3>Fahrzeugfotos bei Übergabe</h3>
            <p>Vorher-Dokumentation zur rechtlichen Absicherung.</p>
          </div>
          <div className="documentPhotoGrid">
            {vehiclePhotos.map((entry) => (
              <div key={entry.key} className="documentPhotoCard">
                <div className="documentPhotoLabel">{entry.label}</div>
                <div className="mediaPreview documentPhotoPreview">
                  <img src={entry.url} alt={`Übergabe ${entry.label}`} />
                </div>
              </div>
            ))}
          </div>
        </section>

        {booking.damageEntries?.length > 0 && (
          <section className="documentPanel">
            <div className="documentPanelHeader">
              <h3>Bereits dokumentierte Schäden</h3>
              <p>Diese Schäden waren vor der Übergabe bereits sichtbar.</p>
            </div>
            <div className="documentDamageGrid">
              {booking.damageEntries.map((entry, index) => (
                <div key={`dmg_${entry.id || index}`} className="damageEntryCard documentDamageCard">
                  <div className="mediaPreview damage documentDamagePreview">
                    <img src={entry.url} alt={`Schaden ${index + 1}`} />
                  </div>
                  <div className="documentDamageText">
                    <strong>Schaden {index + 1}</strong>
                    <p>{entry.description || "Dokumentierter Schaden"}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="documentPanel">
          <div className="documentPanelHeader">
            <h3>Vertragsbedingungen</h3>
            <p>Bitte lesen Sie die Bedingungen vor der Bestätigung.</p>
          </div>
          <ul className="documentTerms">
            {LOANER_TERMS.map((term) => (
              <li key={term}>{term}</li>
            ))}
          </ul>
        </section>

        {!state.accepted && !booking.privacyConsentReused && (
          <section
            className="documentPanel privacyConsentPanel"
            ref={privacyConsentRef}
          >
            <div className="documentPanelHeader">
              <h3>Datenschutzbestätigung</h3>
              <p>Die Zustimmung ist erforderlich, bevor die Dokumentation bestätigt wird.</p>
            </div>
            <label className="checkRow">
              <input
                type="checkbox"
                checked={state.privacyAccepted}
                onChange={(event) =>
                  setState((prev) => ({
                    ...prev,
                    privacyAccepted: event.target.checked,
                    error: "",
                    privacyError: "",
                  }))
                }
              />
              <span>
                Ich bin damit einverstanden, dass Daten zu meinem Fahrzeug, meinem Führerschein sowie weitere für diesen
                Vorgang notwendige Daten gespeichert und bei Bedarf verarbeitet werden. Ich kann jederzeit Auskunft über
                meine gespeicherten Daten verlangen und habe das Recht auf Einsicht, Berichtigung oder Löschung im Rahmen
                der gesetzlichen Vorgaben.
              </span>
            </label>
            {state.privacyError && (
              <div className="authError">{state.privacyError}</div>
            )}
          </section>
        )}
        {!state.accepted && booking.privacyConsentReused && (
          <section className="documentPanel privacyConsentPanel">
            <div className="documentPanelHeader">
              <h3>Datenschutzbestätigung</h3>
              <p>Eine frühere Datenschutzbestätigung zu dieser E-Mail-Adresse liegt bereits vor.</p>
            </div>
          </section>
        )}

        {booking.signature && (
          <section className="documentPanel signatureDocumentPanel">
            <div className="documentPanelHeader">
              <h3>Unterschrift bei Übergabe</h3>
              <p>Die aufgenommenen Daten wurden bei der Fahrzeugübergabe kundenseitig bestätigt sowie unterzeichnet.</p>
            </div>
            <div className="signatureDocumentBox">
              <img src={booking.signature} alt="Kundenunterschrift" className="signatureImage" />
            </div>
          </section>
        )}

        {state.accepted ? (
          <section className="documentPanel signatureDocumentPanel">
            <div className="documentPanelHeader">
              <h3>Bestätigungsunterschrift</h3>
              <p>Die aufgenommenen Daten wurden bei der Auftragserteilung kundenseitig bestätigt sowie unterzeichnet.</p>
            </div>
            {state.customerSignature ? (
              <div className="signatureDocumentBox">
                <img
                  src={state.customerSignature}
                  alt="Digitale Bestätigungsunterschrift"
                  className="signatureImage"
                />
              </div>
            ) : (
              <p className="mutedText">Diese ältere Bestätigung enthält noch keine separate Bestätigungsunterschrift.</p>
            )}
          </section>
        ) : (
          <section className="documentPanel signatureDocumentPanel">
            <div className="documentPanelHeader">
              <h3>Bestätigungsunterschrift</h3>
              <p>Bitte unterschreiben und die Unterschrift speichern.</p>
            </div>
            <SignaturePad
              onSave={saveCustomerSignature}
              onClear={() => {
                signatureRef.current = "";
                try {
                  localStorage.removeItem(signatureStorageKey);
                } catch {
                  // Lokaler Cache ist optional.
                }
                setState((prev) => ({
                  ...prev,
                  customerSignature: "",
                  signatureSaving: false,
                  signatureSaveMessage: "",
                  error: "",
                }));
              }}
            />
            {state.signatureSaving && (
              <div className="customerNotice">Unterschrift wird gespeichert ...</div>
            )}
            {hasCustomerSignature && !state.signatureSaving && (
              <div className="customerSuccess">{state.signatureSaveMessage || "Unterschrift gespeichert."}</div>
            )}
          </section>
        )}

        {state.error && <div className="authError">{state.error}</div>}

        {state.accepted ? (
          <div className="customerSuccess">
            Die Dokumentation wurde bestätigt. Das Fahrzeug ist jetzt intern zur
            Übergabe freigegeben.
          </div>
        ) : (
          <div className="documentActions">
            <div className="documentActionText">
              Mit Klick auf den Button bestätigen Sie die Fotos, die
              Vertragsbedingungen und die dokumentierten Übergabedaten.
            </div>
            <button
              type="button"
              className="btn primary"
              onClick={handleConfirm}
              disabled={state.submitting || state.signatureSaving || !hasCustomerSignature}
            >
              {state.submitting ? "Bestätigung läuft ..." : "Dokumentation verbindlich bestätigen"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function OrderConfirmationView({ token }) {
  const signatureRef = useRef("");
  const signatureStorageKey = `${getCustomerSignatureStorageKey(token)}:order`;
  const [state, setState] = useState({
    loading: true,
    error: "",
    card: null,
    submitting: false,
    accepted: false,
    signature: "",
    signatureSaving: false,
    signatureSaveMessage: "",
  });

  useEffect(() => {
    let active = true;

    const loadCard = async () => {
      try {
        const response = await fetch(`/api/order-confirmation?token=${token}`);
        const payload = await response.json();

        if (!active) return;

        if (!response.ok) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: payload.error || "Auftragsbestätigung konnte nicht geladen werden.",
          }));
          return;
        }

        let locallySavedSignature = "";
        try {
          locallySavedSignature = localStorage.getItem(signatureStorageKey) || "";
        } catch {
          locallySavedSignature = "";
        }
        const backendSignature = payload.card?.loanerFlow?.customerConfirmationSignature || "";
        const restoredSignature = isValidSignatureDataUrl(backendSignature)
          ? backendSignature
          : isValidSignatureDataUrl(locallySavedSignature)
            ? locallySavedSignature
            : "";
        signatureRef.current = restoredSignature;

        setState({
          loading: false,
          error: "",
          card: payload.card,
          submitting: false,
          accepted: payload.card?.loanerFlow?.orderConfirmationStatus === "accepted",
          signature: restoredSignature,
          signatureSaving: false,
          signatureSaveMessage: restoredSignature
            ? backendSignature
              ? "Unterschrift wurde aus der bestätigten Dokumentation geladen."
              : "Unterschrift wurde aus diesem Gerät wiederhergestellt."
            : "",
        });
      } catch (error) {
        if (!active) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error.message || "Auftragsbestätigung konnte nicht geladen werden.",
        }));
      }
    };

    loadCard();
    return () => {
      active = false;
    };
  }, [token, signatureStorageKey]);

  const handleConfirm = async () => {
    const currentSignature = signatureRef.current || state.signature;
    if (!isValidSignatureDataUrl(currentSignature)) {
      setState((prev) => ({
        ...prev,
        error: state.signatureSaving
          ? "Die Unterschrift wird noch gespeichert. Bitte kurz warten."
          : "Bitte unterschreiben und die Unterschrift speichern.",
      }));
      return;
    }

    setState((prev) => ({ ...prev, submitting: true, error: "" }));

    try {
      const response = await fetch("/api/order-confirmation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, signature: currentSignature }),
      });

      const payload = await response.json();

      if (!response.ok) {
        setState((prev) => ({
          ...prev,
          submitting: false,
          error: payload.error || "Bestätigung fehlgeschlagen.",
        }));
        return;
      }

      setState((prev) => ({
        ...prev,
        submitting: false,
        accepted: true,
        card: payload.card,
        signature: payload.card?.loanerFlow?.customerConfirmationSignature || signatureRef.current || prev.signature,
        signatureSaving: false,
        signatureSaveMessage: "Unterschrift wurde bestätigt und gespeichert.",
      }));
      try {
        localStorage.removeItem(signatureStorageKey);
      } catch {
        // Die Bestätigung ist bereits serverseitig gespeichert; lokaler Cache ist nur Komfort.
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        submitting: false,
        error: getFriendlyProcessError(error, "Bestätigung fehlgeschlagen. Bitte Verbindung prüfen und erneut versuchen."),
      }));
    }
  };

  const saveOrderSignature = (signature) => {
    setState((prev) => ({ ...prev, signatureSaving: true, error: "", signatureSaveMessage: "" }));
    window.setTimeout(() => {
      if (!isValidSignatureDataUrl(signature)) {
        signatureRef.current = "";
        setState((prev) => ({
          ...prev,
          signature: "",
          signatureSaving: false,
          signatureSaveMessage: "",
          error: "Die Unterschrift konnte nicht gespeichert werden. Bitte erneut unterschreiben.",
        }));
        return;
      }
      signatureRef.current = signature;
      try {
        localStorage.setItem(signatureStorageKey, signature);
      } catch {
        setState((prev) => ({
          ...prev,
          signature,
          signatureSaving: false,
          signatureSaveMessage:
            "Unterschrift gespeichert. Hinweis: Dieses Gerät konnte sie nicht lokal zwischenspeichern.",
          error: "",
        }));
        return;
      }
      setState((prev) => ({
        ...prev,
        signature,
        signatureSaving: false,
        signatureSaveMessage: "Unterschrift gespeichert. Sie können jetzt bestätigen.",
        error: "",
      }));
    }, 0);
  };

  if (state.loading) {
    return (
      <div className="authShell">
        <div className="authCard">
          <h1>Werkstattauftrag</h1>
          <p>Auftragsdaten werden geladen ...</p>
        </div>
      </div>
    );
  }

  if (state.error && !state.card) {
    return (
      <div className="authShell">
        <div className="authCard">
          <h1>Werkstattauftrag</h1>
          <div className="authError">{state.error}</div>
        </div>
      </div>
    );
  }

  const card = state.card;
  const vehicleLabel = [
    card.vehicleData?.brand,
    card.vehicleData?.model,
    card.licensePlate,
  ]
    .filter(Boolean)
    .join(" - ");
  const meta = [
    { label: "Kunde", value: card.customerName || "-" },
    { label: "Auftrag", value: card.orderNumber || "-" },
    { label: "Anliegen", value: card.title || "-" },
    { label: "Kundenfahrzeug", value: vehicleLabel || "-" },
    { label: "Erstzulassung", value: fmtDate(card.vehicleData?.firstRegistrationDate) },
    { label: "Kilometerstand", value: card.vehicleData?.mileage || "-" },
    { label: "Fahrgestellnummer", value: card.vehicleData?.vin || "-" },
  ];
  const hasOrderSignature = isValidSignatureDataUrl(signatureRef.current || state.signature);
  const orderConfirmHint = state.signatureSaving
    ? "Unterschrift wird gespeichert ..."
    : hasOrderSignature
      ? "Alles bereit. Bitte final bestätigen."
      : "Bitte unten noch unterschreiben und speichern.";

  return (
    <div className="authShell">
      <div className="customerCard customerDocument">
        <div className="documentHero">
          <div>
            <p className="eyebrow">Team Fraenkel</p>
            <h1>Werkstattauftrag bestätigen</h1>
            <p className="documentLead">
              Bitte prüfen Sie die aufgenommenen Auftrags- und Fahrzeugdaten.
              Mit Ihrer Bestätigung wird der Auftrag verbindlich dokumentiert.
            </p>
          </div>
          <div className={`documentStatus ${state.accepted ? "accepted" : "pending"}`}>
            {state.accepted ? "Bereits bestätigt" : "Bestätigung offen"}
          </div>
        </div>

        <div className="documentStepRail">
          {["1. Auftrag prüfen", "2. Daten bestätigen", "3. Unterschreiben"].map((step) => (
            <span key={step}>{step}</span>
          ))}
        </div>

        {!state.accepted && (
          <div className="documentTopConfirm">
            <div>
              <strong>Bestätigung erforderlich</strong>
              <span>{orderConfirmHint}</span>
            </div>
            <button
              type="button"
              className="btn primary"
              onClick={handleConfirm}
              disabled={state.submitting || state.signatureSaving || !hasOrderSignature}
            >
              {state.submitting ? "Bestätigung läuft ..." : hasOrderSignature ? "Jetzt bestätigen" : "Noch Angaben offen"}
            </button>
          </div>
        )}

        <section className="documentPanel">
          <div className="documentPanelHeader">
            <h3>Auftragsdaten</h3>
            <p>Zusammenfassung der Annahme.</p>
          </div>
          <div className="documentMetaGrid">
            {meta.map((entry) => (
              <div key={entry.label} className="documentMetaItem">
                <span>{entry.label}</span>
                <strong>{entry.value}</strong>
              </div>
            ))}
          </div>
          {card.notes && <p className="mutedText">{card.notes}</p>}
        </section>

        {state.accepted && state.signature ? (
          <section className="documentPanel signatureDocumentPanel">
            <div className="documentPanelHeader">
              <h3>Unterschrift</h3>
              <p>Der Auftrag wurde digital bestätigt.</p>
            </div>
            <div className="signatureDocumentBox">
              <img src={state.signature} alt="Kundenunterschrift" className="signatureImage" />
            </div>
          </section>
        ) : (
          <section className="documentPanel signatureDocumentPanel">
            <div className="documentPanelHeader">
              <h3>Unterschrift</h3>
              <p>Bitte unterschreiben und die Unterschrift speichern.</p>
            </div>
            <SignaturePad
              onSave={saveOrderSignature}
              onClear={() => {
                signatureRef.current = "";
                try {
                  localStorage.removeItem(signatureStorageKey);
                } catch {
                  // Lokaler Cache ist optional.
                }
                setState((prev) => ({
                  ...prev,
                  signature: "",
                  signatureSaving: false,
                  signatureSaveMessage: "",
                  error: "",
                }));
              }}
            />
            {state.signatureSaving && <div className="customerNotice">Unterschrift wird gespeichert ...</div>}
            {hasOrderSignature && !state.signatureSaving && (
              <div className="customerSuccess">{state.signatureSaveMessage || "Unterschrift gespeichert."}</div>
            )}
          </section>
        )}

        {state.error && <div className="authError">{state.error}</div>}

        {state.accepted ? (
          <div className="customerSuccess">
            Der Werkstattauftrag wurde bestätigt. Vielen Dank.
          </div>
        ) : (
          <div className="documentActions">
            <div className="documentActionText">
              Mit Klick auf den Button bestätigen Sie die aufgenommenen Auftragsdaten.
            </div>
            <button
              type="button"
              className="btn primary"
              onClick={handleConfirm}
              disabled={state.submitting || state.signatureSaving || !hasOrderSignature}
            >
              {state.submitting ? "Bestätigung läuft ..." : "Auftrag verbindlich bestätigen"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function App() {
  const confirmationToken = new URLSearchParams(window.location.search).get(
    "confirmation"
  );
  const orderConfirmationToken = new URLSearchParams(window.location.search).get(
    "orderConfirmation"
  );
  const isPublicConfirmation = Boolean(confirmationToken || orderConfirmationToken);

  const [cards, setCards] = useState([]);
  const [technicians] = useState(createDemoTechnicians);
  const [technicianSettings, setTechnicianSettings] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(TECHNICIAN_SETTINGS_STORAGE_KEY) || "{}");
    } catch {
      return {};
    }
  });
  const [technicianSettingsStatus, setTechnicianSettingsStatus] = useState("lokal");
  const [loaners, setLoaners] = useState([]);
  const [bluePlates, setBluePlates] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loanerDamages, setLoanerDamages] = useState([]);
  const [profile, setProfile] = useState(null);
  const [profileDebug, setProfileDebug] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [adminUsersMessage, setAdminUsersMessage] = useState("");
  const [systemStatus, setSystemStatus] = useState(null);
  const [systemStatusMessage, setSystemStatusMessage] = useState("");
  const [systemStatusLoading, setSystemStatusLoading] = useState(false);
  const [systemTestEmail, setSystemTestEmail] = useState("");
  const [systemSmokeTest, setSystemSmokeTest] = useState([]);
  const [liveAcceptance, setLiveAcceptance] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("teamFraenkelLiveAcceptance") || "{}");
    } catch {
      return {};
    }
  });
  const [auditFilters, setAuditFilters] = useState({
    query: "",
    action: "",
    entityType: "",
    dateFrom: "",
    dateTo: "",
  });
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [userDraft, setUserDraft] = useState({
    email: "",
    fullName: "",
    role: "staff",
    branch: "",
    department: DEFAULT_DEPARTMENT,
  });
  const [intakeDraft, setIntakeDraft] = useState(() => {
    const fallback = getDefaultIntakeDraft();
    try {
      const saved = JSON.parse(localStorage.getItem(INTAKE_DRAFT_STORAGE_KEY) || "null");
      const savedDraft = saved?.draft || saved;
      const savedAt = saved?.savedAt || null;
      if (savedAt && !isStoredDraftFresh(savedAt)) {
        localStorage.removeItem(INTAKE_DRAFT_STORAGE_KEY);
        return fallback;
      }
      return canRestoreIntakeDraft(savedDraft) ? { ...fallback, ...savedDraft, saving: false, error: "", success: "Entwurf wurde automatisch wiederhergestellt." } : fallback;
    } catch {
      return fallback;
    }
  });
  const [intakeDraftSavedAt, setIntakeDraftSavedAt] = useState(null);
  const [intakeFieldErrors, setIntakeFieldErrors] = useState({});
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(!isPublicConfirmation);
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLockUntil, setAuthLockUntil] = useState(null);
  const [authFailures, setAuthFailures] = useState(0);
  const [dbStatus, setDbStatus] = useState("Warte auf Anmeldung ...");
  const [ui, setUi] = useState({
    activeView: "Annahme",
    search: "",
    statusFilter: "",
    prioFilter: "",
    sortBy: "due_asc",
    dayMode: "today_overdue",
  });
  const [globalSearch, setGlobalSearch] = useState("");
  const [processConfig, setProcessConfig] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(PROCESS_CONFIG_STORAGE_KEY) || "null");
      return saved && typeof saved === "object" ? saved : buildDefaultProcessConfig();
    } catch {
      return buildDefaultProcessConfig();
    }
  });
  const [processConfigDraft, setProcessConfigDraft] = useState({
    caseType: "annual_service",
    department: "Werkstatt",
    text: (() => {
      try {
        const saved = JSON.parse(localStorage.getItem(PROCESS_CONFIG_STORAGE_KEY) || "null");
        const config = saved && typeof saved === "object" ? saved : buildDefaultProcessConfig();
        return config.annual_service?.Werkstatt || "";
      } catch {
        return buildDefaultProcessConfig().annual_service?.Werkstatt || "";
      }
    })(),
    message: "",
  });
  const [partDrafts, setPartDrafts] = useState({});
  const [questionDrafts, setQuestionDrafts] = useState({});
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizard, setWizard] = useState(getDefaultWizardState());
  const [loanerCompletion, setLoanerCompletion] = useState(null);
  const [loanerCorrection, setLoanerCorrection] = useState(null);
  const [imageViewer, setImageViewer] = useState({ open: false, photos: [], index: 0 });
  const [loanerDraft, setLoanerDraft] = useState(getDefaultLoanerDraft());
  const [loanerEditorOpen, setLoanerEditorOpen] = useState(false);
  const [loanerFormMessage, setLoanerFormMessage] = useState("");
  const [returnState, setReturnState] = useState(getDefaultReturnState());
  const [returnDraftSavedAt, setReturnDraftSavedAt] = useState(null);
  const [returnConflict, setReturnConflict] = useState(null);
  const [passwordState, setPasswordState] = useState({
    open: false,
    nextPassword: "",
    confirmPassword: "",
    error: "",
    success: "",
    saving: false,
    required: false,
    passwordUpdated: false,
  });
  const [sidebarWidth, setSidebarWidth] = useState(320);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarResizing, setSidebarResizing] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [expandedCards, setExpandedCards] = useState({});
  const [taskDetailCardId, setTaskDetailCardId] = useState(null);
  const [detailTabs, setDetailTabs] = useState({});
  const [approvalComments, setApprovalComments] = useState({});
  const [undoAction, setUndoAction] = useState(null);
  const [planningMove, setPlanningMove] = useState({
    open: false,
    cardId: "",
    techId: "",
    due: "",
    estimatedHours: "",
  });
  const [workshopBlockerModal, setWorkshopBlockerModal] = useState({
    open: false,
    cardId: "",
    reason: "parts",
    note: "",
  });
  const [workshopHandoverModal, setWorkshopHandoverModal] = useState({
    open: false,
    cardId: "",
    target: "",
    note: "",
    acknowledgeWarnings: false,
  });
  const [workshopPhotoModal, setWorkshopPhotoModal] = useState({
    open: false,
    cardId: "",
    subId: "",
    category: "Arbeit dokumentiert",
    note: "",
    fileName: "",
    saving: false,
  });
  const [workshopSearch, setWorkshopSearch] = useState("");
  const [showAllWorkshopCards, setShowAllWorkshopCards] = useState(false);
  const [intakeTab, setIntakeTab] = useState("core");
  const [intakeWorkspaceTab, setIntakeWorkspaceTab] = useState("new");
  const [loanerBoardTab, setLoanerBoardTab] = useState("overview");
  const [fleetUsageFilter, setFleetUsageFilter] = useState("customer_loaner");
  const [returnTabs, setReturnTabs] = useState({});
  const [loanerHistorySearch, setLoanerHistorySearch] = useState("");
  const [loanerHistoryFilters, setLoanerHistoryFilters] = useState(
    getDefaultLoanerHistoryFilters
  );
  const [managementLoanerFilters, setManagementLoanerFilters] = useState({
    period: "30",
    branch: "",
    status: "",
    vehicle: "",
    responsible: "",
    risk: "",
  });
  const [expandedLoanerHistory, setExpandedLoanerHistory] = useState(null);
  const [loanerHistoryLimit, setLoanerHistoryLimit] = useState(40);
  const [resendCooldowns, setResendCooldowns] = useState({});
  const [resendSending, setResendSending] = useState({});
  const [resendClock, setResendClock] = useState(Date.now());
  const [offlineQueueCount, setOfflineQueueCount] = useState(0);
  const [offlineReturnQueueCount, setOfflineReturnQueueCount] = useState(0);
  const [offlineQueueSizeKb, setOfflineQueueSizeKb] = useState(0);
  const [offlineSyncBusy, setOfflineSyncBusy] = useState(false);
  const [lastOfflineSyncAt, setLastOfflineSyncAt] = useState(null);
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  const [mediaSyncStatus, setMediaSyncStatus] = useState("");
  const [lastLiveSyncAt, setLastLiveSyncAt] = useState(null);
  const [photoCaptureSequence, setPhotoCaptureSequence] = useState({
    open: false,
    title: "",
    items: [],
    index: 0,
  });
  const liveRefreshTimerRef = useRef(null);
  const landingAppliedRef = useRef(false);
  const loanerHistoryDefaultContextRef = useRef("");
  const photoCaptureSaveRef = useRef(null);
  const browserNavigationInitializedRef = useRef(false);
  const browserNavigationPositionRef = useRef(0);
  const browserNavigationKeyRef = useRef("");
  const browserNavigationRuntimeRef = useRef({});
  const browserNavigationRevertingRef = useRef(false);
  const [wizardUsageFilter, setWizardUsageFilter] = useState("customer_loaner");
  const [showExtendedLoanerInventory, setShowExtendedLoanerInventory] = useState(false);
  const [availabilityInspector, setAvailabilityInspector] = useState({
    open: false,
    loanerId: "",
    startDate: "",
    endDate: "",
    error: "",
  });

  const isAdmin = profile?.role === "admin";
  const isWorkshopLead =
    isAdmin ||
    profile?.role === "workshop_lead" ||
    profile?.department === "Werkstättenleitung" ||
    profile?.department === "Werkstattplanung";
  const isTechnicianRole = profile?.role === "technician";
  const browserNavigationReady = Boolean(profile && landingAppliedRef.current);
  const browserNavigationSnapshot = {
    activeView: ui.activeView,
    intakeWorkspaceTab,
    intakeTab,
    loanerBoardTab,
    wizardOpen,
    wizardStep,
    returnBookingId: returnState.bookingId || "",
    returnTab: returnState.bookingId
      ? returnTabs[returnState.bookingId] || "compare"
      : "",
    taskDetailCardId: taskDetailCardId || "",
    expandedLoanerHistory: expandedLoanerHistory || "",
  };
  const browserNavigationKey = JSON.stringify(browserNavigationSnapshot);
  browserNavigationRuntimeRef.current = {
    snapshot: browserNavigationSnapshot,
    returnDamageEntries: returnState.returnDamageEntries,
    returnFuelReceiptPhoto: returnState.fuelReceiptPhoto,
    photoCaptureOpen: photoCaptureSequence.open,
    imageViewerOpen: imageViewer.open,
    passwordPanelOpen: passwordState.open,
    passwordChangeRequired: passwordState.required,
  };
  const getTechnicianSetting = (techId) => technicianSettings.technicians?.[techId] || {};
  const getConfiguredTechnician = (technician) => {
    const setting = getTechnicianSetting(technician.id);
    const capacityHours = Number(setting.capacityHours || technician.capacityHours || DEFAULT_TECH_CAPACITY_HOURS);
    return {
      ...technician,
      capacityHours: Number.isFinite(capacityHours) && capacityHours > 0 ? capacityHours : DEFAULT_TECH_CAPACITY_HOURS,
      specialties: Array.isArray(setting.specialties) ? setting.specialties : technician.specialties || [],
    };
  };
  const configuredTechnicians = useMemo(
    () => technicians.map((technician) => getConfiguredTechnician(technician)),
    [technicians, technicianSettings]
  );
  const getProfileTechnicianId = (nextProfile = profile) => {
    if (!nextProfile) return "";
    const mapped = technicianSettings.userMap?.[nextProfile.id];
    if (mapped) return mapped;
    if (nextProfile.employee_id) return nextProfile.employee_id;
    const profileName = normalizeLookupText(nextProfile.full_name);
    const emailName = normalizeLookupText(String(nextProfile.email || "").split("@")[0]);
    return (
      configuredTechnicians.find(
        (technician) =>
          technician.type === "Techniker" &&
          (normalizeLookupText(technician.name) === profileName ||
            normalizeLookupText(technician.name) === emailName)
      )?.id || ""
    );
  };
  const profileTechnicianId = useMemo(
    () => getProfileTechnicianId(profile),
    [profile, configuredTechnicians, technicianSettings]
  );
  const getConfiguredChecklistForDepartment = (caseType, department) => {
    const configured = String(processConfig?.[caseType]?.[department] || "").trim();
    if (configured) {
      return parseSubtasks(
        configured
          .split("\n")
          .map((entry) => entry.trim())
          .filter(Boolean)
      );
    }
    return buildChecklistForDepartment(caseType, department);
  };
  const saveProcessConfigDraft = () => {
    const next = {
      ...processConfig,
      [processConfigDraft.caseType]: {
        ...(processConfig[processConfigDraft.caseType] || {}),
        [processConfigDraft.department]: processConfigDraft.text,
      },
    };
    setProcessConfig(next);
    localStorage.setItem(PROCESS_CONFIG_STORAGE_KEY, JSON.stringify(next));
    setProcessConfigDraft((prev) => ({ ...prev, message: "Standard-To-dos gespeichert. Neue Aufträge nutzen diese Liste." }));
  };
  const resetProcessConfigDraft = () => {
    const defaults = buildDefaultProcessConfig();
    setProcessConfig(defaults);
    localStorage.setItem(PROCESS_CONFIG_STORAGE_KEY, JSON.stringify(defaults));
    setProcessConfigDraft((prev) => ({
      ...prev,
      text: defaults[prev.caseType]?.[prev.department] || "",
      message: "Standard-To-dos wurden auf Werkseinstellung zurückgesetzt.",
    }));
  };
  const persistTechnicianSettings = async (nextSettings) => {
    try {
      localStorage.setItem(TECHNICIAN_SETTINGS_STORAGE_KEY, JSON.stringify(nextSettings));
    } catch {
      // Die Seite bleibt weiter nutzbar; nur die lokale Betriebskonfiguration konnte nicht persistiert werden.
    }
    if (!session) return;
    const { error } = await supabase
      .from("app_settings")
      .upsert({
        key: TECHNICIAN_SETTINGS_KEY,
        value: nextSettings,
        updated_at: new Date().toISOString(),
        updated_by: profile?.email || session?.user?.email || null,
      });
    if (error) {
      setTechnicianSettingsStatus("lokaler Fallback");
      return;
    }
    setTechnicianSettingsStatus("zentral synchronisiert");
  };
  const commitTechnicianSettings = (updater) => {
    setTechnicianSettings((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      persistTechnicianSettings(next);
      return next;
    });
  };
  const updateTechnicianSetting = (techId, patch) => {
    commitTechnicianSettings((prev) => ({
      ...prev,
      technicians: {
        ...(prev.technicians || {}),
        [techId]: {
          ...(prev.technicians?.[techId] || {}),
          ...patch,
        },
      },
    }));
  };
  const updateUserTechnicianMapping = (userId, techId) => {
    commitTechnicianSettings((prev) => ({
      ...prev,
      userMap: {
        ...(prev.userMap || {}),
        [userId]: techId,
      },
    }));
    setAdminUsersMessage(techId ? "Techniker-Zuordnung wurde lokal gespeichert." : "Techniker-Zuordnung wurde entfernt.");
  };

  useEffect(() => {
    if (!sidebarResizing) return undefined;

    const clampWidth = (nextWidth) => {
      const bounded = Math.max(92, Math.min(420, nextWidth));
      setSidebarWidth(bounded);
      setSidebarCollapsed(bounded < 168);
    };

    const handlePointerMove = (event) => {
      if (typeof event.clientX === "number") {
        clampWidth(event.clientX);
      }
    };

    const handleTouchMove = (event) => {
      const touch = event.touches?.[0];
      if (touch) {
        clampWidth(touch.clientX);
      }
    };

    const stopResize = () => setSidebarResizing(false);

    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseup", stopResize);
    window.addEventListener("touchmove", handleTouchMove);
    window.addEventListener("touchend", stopResize);

    return () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", stopResize);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", stopResize);
    };
  }, [sidebarResizing]);

  useEffect(() => {
    try {
      localStorage.setItem(TECHNICIAN_SETTINGS_STORAGE_KEY, JSON.stringify(technicianSettings));
    } catch {
      // Die Seite bleibt weiter nutzbar; nur die lokale Betriebskonfiguration konnte nicht persistiert werden.
    }
  }, [technicianSettings]);

  useEffect(() => {
    if (!session) return undefined;
    let cancelled = false;
    const loadCentralTechnicianSettings = async () => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", TECHNICIAN_SETTINGS_KEY)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        setTechnicianSettingsStatus("lokaler Fallback");
        return;
      }
      if (data?.value && typeof data.value === "object") {
        setTechnicianSettings(data.value);
        setTechnicianSettingsStatus("zentral synchronisiert");
        try {
          localStorage.setItem(TECHNICIAN_SETTINGS_STORAGE_KEY, JSON.stringify(data.value));
        } catch {
          // Zentral geladene Einstellungen bleiben in dieser Sitzung aktiv.
        }
      } else {
        setTechnicianSettingsStatus("zentral bereit");
      }
    };
    loadCentralTechnicianSettings();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const refreshAdminUsers = async ({ silent = false } = {}) => {
    if (!session) return;
    if (!silent) setAdminUsersLoading(true);
    try {
      const result = await runAdminUserAction({ action: "listUsers" });
      setAdminUsers(result.users || []);
    } catch (error) {
      if (silent) {
        setDbStatus(`Benutzerliste konnte im Hintergrund nicht aktualisiert werden (${error.message})`);
      } else {
        setAdminUsersMessage(error.message);
      }
    } finally {
      if (!silent) setAdminUsersLoading(false);
    }
  };

  const readOfflineBookings = () => {
    try {
      const items = JSON.parse(localStorage.getItem("pendingLoanerBookings") || "[]");
      setOfflineQueueSizeKb((prev) => {
        const returnSize = readOfflineReturns().length ? getStorageSizeKb(readOfflineReturns()) : 0;
        return getStorageSizeKb(items) + returnSize;
      });
      return items;
    } catch {
      return [];
    }
  };

  const writeOfflineBookings = (items) => {
    try {
      localStorage.setItem("pendingLoanerBookings", JSON.stringify(items));
      setOfflineQueueCount(items.length);
      setOfflineQueueSizeKb(getStorageSizeKb(items) + getStorageSizeKb(readOfflineReturns()));
      return true;
    } catch (error) {
      setDbStatus(
        "Offline-Speicher ist voll. Der Vorgang wurde nicht offline zwischengespeichert, damit keine Fotos verloren gehen. Bitte Internetverbindung prüfen und erneut speichern."
      );
      return false;
    }
  };

  const queueOfflineBooking = (booking) => {
    const items = readOfflineBookings();
    const nextItems = [
      ...items.filter((item) => item.id !== booking.id),
      booking,
    ];
    return writeOfflineBookings(nextItems);
  };
  const readOfflineReturns = () => {
    try {
      return JSON.parse(localStorage.getItem(OFFLINE_RETURN_STORAGE_KEY) || "[]");
    } catch {
      return [];
    }
  };

  const writeOfflineReturns = (items) => {
    try {
      localStorage.setItem(OFFLINE_RETURN_STORAGE_KEY, JSON.stringify(items));
      setOfflineReturnQueueCount(items.length);
      setOfflineQueueSizeKb(getStorageSizeKb(readOfflineBookings()) + getStorageSizeKb(items));
      return true;
    } catch {
      setDbStatus("Offline-Speicher ist voll. Rücknahme konnte nicht lokal zwischengespeichert werden.");
      return false;
    }
  };

  const queueOfflineReturn = (payload) => {
    const items = readOfflineReturns();
    return writeOfflineReturns([
      ...items.filter((item) => item.booking?.id !== payload.booking?.id),
      { ...payload, queuedAt: new Date().toISOString() },
    ]);
  };

  const flushOfflineBookings = async () => {
    const items = readOfflineBookings();
    if (items.length === 0) {
      setOfflineQueueCount(0);
      return;
    }

    const remaining = [];
    for (const booking of items) {
      const { error } = await supabase.from("loaner_bookings").upsert(bookingToDbRow(booking));
      if (error) {
        remaining.push(booking);
      }
    }

    writeOfflineBookings(remaining);
    if (remaining.length === 0) {
      setDbStatus("Offline gespeicherte Leihwagenvorgänge wurden synchronisiert.");
      setLastOfflineSyncAt(new Date().toISOString());
      loadWorkspaceData().catch(() => undefined);
    }
  };

  const flushOfflineReturns = async () => {
    const items = readOfflineReturns();
    if (items.length === 0) {
      setOfflineReturnQueueCount(0);
      return;
    }

    const remaining = [];
    for (const item of items) {
      try {
        if (item.booking) await persistBooking(item.booking);
        if (item.loaner) await persistLoaner(item.loaner);
      } catch {
        remaining.push(item);
      }
    }
    writeOfflineReturns(remaining);
    if (remaining.length === 0) {
      setDbStatus("Offline gespeicherte Rücknahmen wurden synchronisiert.");
      setLastOfflineSyncAt(new Date().toISOString());
      loadWorkspaceData().catch(() => undefined);
    }
  };

  const runOfflineSyncNow = async () => {
    if (offlineSyncBusy) return;
    if (!navigator.onLine) {
      setDbStatus("Tablet ist offline. Sync startet automatisch, sobald WLAN wieder da ist.");
      return;
    }
    setOfflineSyncBusy(true);
    setDbStatus("Offline-Warteschlange wird synchronisiert ...");
    try {
      await flushOfflineBookings();
      await flushOfflineReturns();
      setLastOfflineSyncAt(new Date().toISOString());
    } finally {
      setOfflineSyncBusy(false);
    }
  };

  const loadWorkspaceData = async ({ silent = false } = {}) => {
    if (!silent) setDbStatus("Lade aktuelle Daten aus Supabase ...");

    let [
      profileResponse,
      sharedLoanerDraftsResponse,
      cardsResult,
      loanersResult,
      bookingsResult,
      bluePlatesResult,
      damagesResult,
      licenseFrontPresenceResult,
      licenseBackPresenceResult,
    ] = await Promise.all([
      fetch("/api/session-profile", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      }),
      fetch("/api/open-loaner-drafts", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      }),
      supabase.from("cards").select("*").order("id", { ascending: true }),
      supabase.from("loaners").select("*").order("plate", { ascending: true }),
      supabase
        .from("loaner_bookings")
        .select(LOANER_BOOKING_LIST_SELECT)
        .order("created_at", { ascending: false }),
      supabase.from("blue_plates").select("*").order("plate", { ascending: true }),
      supabase.from("loaner_damages").select("*").order("reported_at", { ascending: false }),
      supabase
        .from("loaner_bookings")
        .select("id")
        .not("driver_license_front", "is", null)
        .neq("driver_license_front", ""),
      supabase
        .from("loaner_bookings")
        .select("id")
        .not("driver_license_back", "is", null)
        .neq("driver_license_back", ""),
    ]);

    if (
      bookingsResult.error &&
      /(handover_employee_name|return_employee_name|card_uuid|confirmation_events)/i.test(bookingsResult.error.message || "")
    ) {
      bookingsResult = await supabase
        .from("loaner_bookings")
        .select(
          LOANER_BOOKING_LIST_SELECT.replace("handover_employee_name,", "")
            .replace("return_employee_name,", "")
            .replace("card_uuid,", "")
            .replace("confirmation_events,", "")
        )
        .order("created_at", { ascending: false });
    }

    const profilePayload = await profileResponse.json();
    if (!profileResponse.ok || !profilePayload.profile) {
      setDbStatus(
        `Profil konnte nicht geladen werden (${profilePayload.error || "unbekannter Fehler"})`
      );
      return;
    }

    const nextProfile = {
      ...profilePayload.profile,
      role: normalizeRole(profilePayload.profile.role),
      branch: profilePayload.profile.branch || "",
      department:
        ["admin", "workshop_lead"].includes(profilePayload.profile.role)
          ? profilePayload.profile.department || ""
          : normalizeDepartment(profilePayload.profile.department),
    };

    if (!nextProfile.is_active) {
      await supabase.auth.signOut();
      setAuthError("Dieses Konto ist derzeit deaktiviert.");
      setAuthLoading(false);
      setDbStatus("Konto deaktiviert");
      return;
    }

    setProfile(nextProfile);
    if (nextProfile.invite_status === "sent") {
      setPasswordState((prev) => ({
        ...prev,
        open: true,
        required: true,
        success: "",
      }));
    }
    setProfileDebug({
      sessionUserId: session.user.id,
      sessionEmail: session.user.email,
      initialProfileId: nextProfile.id || null,
      initialProfileRole: nextProfile.role || null,
      initialProfileEmail: nextProfile.email || null,
      savedProfileId: nextProfile.id || null,
      savedProfileRole: nextProfile.role || null,
      savedProfileEmail: nextProfile.email || null,
      finalProfileRole: nextProfile.role,
      isAdmin: nextProfile.role === "admin",
      profileUpsertError: "",
    });

    let sharedLoanerDraftRows = [];
    if (sharedLoanerDraftsResponse.ok) {
      const sharedLoanerDraftsPayload =
        await sharedLoanerDraftsResponse.json();
      sharedLoanerDraftRows = Array.isArray(sharedLoanerDraftsPayload.cards)
        ? sharedLoanerDraftsPayload.cards
        : [];
    }
    const mergedCardRows = [
      ...(Array.isArray(cardsResult.data) ? cardsResult.data : []),
      ...sharedLoanerDraftRows,
    ].reduce((rowsById, row) => {
      rowsById.set(row.id, row);
      return rowsById;
    }, new Map());
    const nextCardRows = [...mergedCardRows.values()];

    if (nextCardRows.length > 0) {
      setCards(nextCardRows.map(mapSupabaseCard));
      if (!silent) setDbStatus(`${nextCardRows.length} Aufträge geladen`);
    } else if (cardsResult.error) {
      setDbStatus(`Aufträge konnten nicht geladen werden (${cardsResult.error.message})`);
    } else {
      setCards([]);
      if (!silent) setDbStatus("Keine Aufträge im System");
    }

    if (!loanersResult.error && Array.isArray(loanersResult.data)) {
      setLoaners(loanersResult.data.map(mapSupabaseLoaner));
    }

    if (!bookingsResult.error && Array.isArray(bookingsResult.data)) {
      const licensePresenceLoaded =
        !licenseFrontPresenceResult.error &&
        !licenseBackPresenceResult.error;
      const licenseFrontBookingIds = new Set(
        (licenseFrontPresenceResult.data || []).map((entry) => entry.id)
      );
      const licenseBackBookingIds = new Set(
        (licenseBackPresenceResult.data || []).map((entry) => entry.id)
      );
      const nextBookings = bookingsResult.data.map((row) => {
        const booking = mapSupabaseBooking(row);
        return {
          ...booking,
          driverLicenseFrontPresent: licenseFrontBookingIds.has(booking.id),
          driverLicenseBackPresent: licenseBackBookingIds.has(booking.id),
          licensePresenceLoaded,
        };
      });
      setBookings((prev) =>
        nextBookings.map((incoming) =>
          mergeBookingMedia(
            incoming,
            prev.find((existing) => existing.id === incoming.id)
          )
        )
      );
    }

    if (!damagesResult.error && Array.isArray(damagesResult.data)) {
      setLoanerDamages(damagesResult.data.map(mapSupabaseLoanerDamage));
    }

    if (!bluePlatesResult.error && Array.isArray(bluePlatesResult.data)) {
      setBluePlates(bluePlatesResult.data.map(mapSupabaseBluePlate));
    }
    setLastLiveSyncAt(new Date().toISOString());

    if (nextProfile.role === "admin") {
      await refreshAdminUsers({ silent });
    } else {
      setAdminUsers([]);
    }
  };

  useEffect(() => {
    if (isPublicConfirmation) {
      return undefined;
    }

    let active = true;

    const bootstrap = async () => {
      const { data } = await supabase.auth.getSession();
      if (!active) return;

      setSession(data.session);
      setAuthLoading(false);
    };

    const { data: subscription } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        if (!active) return;
        setSession(nextSession);
        setAuthLoading(false);
        setAuthError("");
        if (event === "PASSWORD_RECOVERY") {
          setPasswordState({
            open: true,
            nextPassword: "",
            confirmPassword: "",
            error: "",
            success: "",
            saving: false,
            required: true,
            passwordUpdated: false,
          });
        }
      }
    );

    bootstrap();

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, [isPublicConfirmation]);

  useEffect(() => {
    if (!session || isPublicConfirmation) return;

    loadWorkspaceData().catch((error) => {
      setDbStatus(`Daten konnten nicht geladen werden (${error.message})`);
    });
  }, [session, isPublicConfirmation]);

  useEffect(() => {
    if (!session || isPublicConfirmation) return undefined;
    if (!canRestoreIntakeDraft(intakeDraft) || intakeDraft.saving) return undefined;
    const timeout = window.setTimeout(() => {
      try {
        const { saving, error, success, ...persistableDraft } = intakeDraft;
        const savedAt = new Date().toISOString();
        localStorage.setItem(INTAKE_DRAFT_STORAGE_KEY, JSON.stringify({ draft: persistableDraft, savedAt }));
        setIntakeDraftSavedAt(savedAt);
      } catch {
        setDbStatus("Annahme-Entwurf konnte lokal nicht gespeichert werden.");
      }
    }, 500);
    return () => window.clearTimeout(timeout);
  }, [intakeDraft, session, isPublicConfirmation]);

  useEffect(() => {
    if (!wizardOpen || wizard.isSubmitting) return undefined;
    if (!canRestoreWizardDraft(wizard)) return undefined;
    const timeout = window.setTimeout(() => {
      const sharedDraft = buildSharedWizardDraft(wizard, wizardStep);
      try {
        localStorage.setItem(
          WIZARD_DRAFT_STORAGE_KEY,
          JSON.stringify({
            wizard: sharedDraft,
            wizardStep,
            savedAt: new Date().toISOString(),
          })
        );
      } catch {
        setDbStatus("Leihwagen-Entwurf konnte lokal nicht gespeichert werden.");
      }
      if (wizard.cardId && sharedDraft) {
        updateCard(wizard.cardId, (current) => ({
          ...current,
          loanerFlow: {
            ...(current.loanerFlow || {}),
            loanerWizardDraft: {
              ...sharedDraft,
              savedAt: new Date().toISOString(),
              savedBy: profile?.full_name || session?.user?.email || "",
            },
            loanerWizardDraftStatus: "in_progress",
            loanerWizardLock: {
              by: profile?.full_name || session?.user?.email || "Benutzer",
              email: session?.user?.email || "",
              at: new Date().toISOString(),
            },
          },
        }));
      }
    }, 1200);
    return () => window.clearTimeout(timeout);
  }, [wizard, wizardOpen, wizardStep]);

  useEffect(() => {
    if (!returnState.bookingId || returnState.isSubmitting) return undefined;
    if (!canRestoreReturnDraft(returnState)) return undefined;
    const timeout = window.setTimeout(() => {
      try {
        const { isSubmitting, error, ...persistableReturn } = returnState;
        const savedAt = new Date().toISOString();
        localStorage.setItem(
          RETURN_DRAFT_STORAGE_KEY,
          JSON.stringify({
            returnState: persistableReturn,
            activeTab: returnTabs[returnState.bookingId] || "employee",
            savedAt,
          })
        );
        setReturnDraftSavedAt(savedAt);
      } catch {
        setDbStatus("Rücknahme-Entwurf konnte lokal nicht gespeichert werden.");
      }
    }, 500);
    return () => window.clearTimeout(timeout);
  }, [returnState, returnTabs]);

  useEffect(() => {
    if (
      !returnState.bookingId ||
      getIncompleteReturnDamageEntries(returnState.returnDamageEntries).length === 0
    ) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [returnState.bookingId, returnState.returnDamageEntries]);

  useEffect(() => {
    if (!session || isPublicConfirmation) return undefined;

    setOfflineQueueCount(readOfflineBookings().length);
    setOfflineReturnQueueCount(readOfflineReturns().length);
    setOfflineQueueSizeKb(getStorageSizeKb(readOfflineBookings()) + getStorageSizeKb(readOfflineReturns()));
    flushOfflineBookings().catch(() => undefined);
    flushOfflineReturns().catch(() => undefined);

    const handleOnline = () => {
      setIsOnline(true);
      flushOfflineBookings().catch(() => undefined);
      flushOfflineReturns().catch(() => undefined);
    };
    const handleOffline = () => {
      setIsOnline(false);
      setDbStatus("Tablet ist offline. Neue Leihwagenvorgänge werden lokal zwischengespeichert.");
    };
    const interval = window.setInterval(() => {
      if (navigator.onLine) {
        setIsOnline(true);
        flushOfflineBookings().catch(() => undefined);
        flushOfflineReturns().catch(() => undefined);
      } else {
        setIsOnline(false);
      }
    }, 30_000);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.clearInterval(interval);
    };
  }, [session, isPublicConfirmation]);

  useEffect(() => {
    if (!session || isPublicConfirmation) return undefined;

    const scheduleRefresh = () => {
      window.clearTimeout(liveRefreshTimerRef.current);
      liveRefreshTimerRef.current = window.setTimeout(() => {
        loadWorkspaceData({ silent: true }).catch((error) => {
          setDbStatus(`Live-Aktualisierung fehlgeschlagen (${error.message})`);
        });
      }, 700);
    };

    const channel = supabase
      .channel("teamfraenkel-live-workspace")
      .on("postgres_changes", { event: "*", schema: "public", table: "cards" }, scheduleRefresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "loaners" }, scheduleRefresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "loaner_bookings" }, scheduleRefresh)
      .on("postgres_changes", { event: "*", schema: "public", table: "blue_plates" }, scheduleRefresh)
      .subscribe();

    return () => {
      window.clearTimeout(liveRefreshTimerRef.current);
      supabase.removeChannel(channel);
    };
  }, [session, isPublicConfirmation]);

  useEffect(() => {
    if (!session || isPublicConfirmation) return undefined;

    const interval = window.setInterval(() => {
      loadWorkspaceData({ silent: true }).catch((error) => {
        setDbStatus(`Aktualisierung fehlgeschlagen (${error.message})`);
      });
    }, 5_000);

    const handleFocus = () => {
      loadWorkspaceData({ silent: true }).catch(() => undefined);
    };

    window.addEventListener("focus", handleFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
    };
  }, [session, isPublicConfirmation]);

  useEffect(() => {
    if (!profile?.branch) return;
    setIntakeDraft((prev) => ({
      ...prev,
      branch: prev.branch || profile.branch,
    }));
  }, [profile]);

  useEffect(() => {
    const hasActiveCooldown = Object.values(resendCooldowns).some(
      (cooldownUntil) => Number(cooldownUntil || 0) > Date.now()
    );
    if (!hasActiveCooldown) return undefined;

    const interval = window.setInterval(() => {
      const now = Date.now();
      setResendClock(now);
      setResendCooldowns((prev) => {
        const activeEntries = Object.entries(prev).filter(
          ([, cooldownUntil]) => Number(cooldownUntil || 0) > now
        );
        if (activeEntries.length === Object.keys(prev).length) return prev;
        return Object.fromEntries(activeEntries);
      });
    }, 1000);

    return () => window.clearInterval(interval);
  }, [resendCooldowns]);

  useEffect(() => {
    if (!session?.user?.id || !profile?.id) {
      loanerHistoryDefaultContextRef.current = "";
      return;
    }

    const defaultBranch = normalizeBranchFilter(profile.branch);
    const contextKey = `${session.user.id}:${profile.id}:${defaultBranch}`;
    if (loanerHistoryDefaultContextRef.current === contextKey) return;

    loanerHistoryDefaultContextRef.current = contextKey;
    setLoanerHistoryFilters(getDefaultLoanerHistoryFilters(defaultBranch));
    setLoanerHistoryLimit(40);
  }, [profile?.id, profile?.branch, session?.user?.id]);

  useEffect(() => {
    if (!session || !isAdmin || !["Produktivtest", "Systemstatus", "Protokoll"].includes(ui.activeView) || systemStatus) return;
    refreshSystemStatus().catch(() => undefined);
  }, [session, isAdmin, ui.activeView, systemStatus]);

  useEffect(() => {
    if (!profile || landingAppliedRef.current) return;
    landingAppliedRef.current = true;
    const departmentView = normalizeDepartment(profile.department);
    const nextView =
      profile.role === "admin"
        ? "Produktivtest"
        : isWorkshopLead
          ? "Werkstattplanung"
          : profile.role === "technician"
            ? "Werkstatt"
            : departmentView === DEFAULT_DEPARTMENT
              ? "Annahme"
              : departmentView || DEFAULT_DEPARTMENT;
    setUi((prev) => ({
      ...prev,
      activeView: nextView,
    }));
    if (nextView === "Annahme") {
      setIntakeWorkspaceTab("new");
    }
  }, [profile, isWorkshopLead]);

  useEffect(() => {
    if (!session || isPublicConfirmation || !browserNavigationReady) {
      browserNavigationInitializedRef.current = false;
      browserNavigationPositionRef.current = 0;
      browserNavigationKeyRef.current = "";
      return;
    }

    const currentSnapshot = JSON.parse(browserNavigationKey);

    if (!browserNavigationInitializedRef.current) {
      const existingState =
        window.history.state && typeof window.history.state === "object"
          ? window.history.state
          : {};
      window.history.replaceState(
        {
          ...existingState,
          [APP_NAVIGATION_STATE_KEY]: {
            ...currentSnapshot,
            position: 0,
          },
        },
        "",
        window.location.href
      );
      browserNavigationInitializedRef.current = true;
      browserNavigationPositionRef.current = 0;
      browserNavigationKeyRef.current = browserNavigationKey;
      return;
    }

    if (browserNavigationKeyRef.current === browserNavigationKey) return;

    const nextPosition = browserNavigationPositionRef.current + 1;
    const existingState =
      window.history.state && typeof window.history.state === "object"
        ? window.history.state
        : {};
    window.history.pushState(
      {
        ...existingState,
        [APP_NAVIGATION_STATE_KEY]: {
          ...currentSnapshot,
          position: nextPosition,
        },
      },
      "",
      window.location.href
    );
    browserNavigationPositionRef.current = nextPosition;
    browserNavigationKeyRef.current = browserNavigationKey;
  }, [
    browserNavigationKey,
    browserNavigationReady,
    isPublicConfirmation,
    session,
  ]);

  useEffect(() => {
    if (!session || isPublicConfirmation || !browserNavigationReady) return undefined;

    const restoreCurrentHistoryEntry = (targetPosition) => {
      const delta =
        browserNavigationPositionRef.current - Number(targetPosition || 0);
      if (delta === 0) return;
      browserNavigationRevertingRef.current = true;
      window.history.go(delta);
    };

    const handlePopState = (event) => {
      const target = event.state?.[APP_NAVIGATION_STATE_KEY];
      if (!target) return;

      if (browserNavigationRevertingRef.current) {
        browserNavigationRevertingRef.current = false;
        return;
      }

      const runtime = browserNavigationRuntimeRef.current;
      const current = runtime.snapshot || {};
      const targetPosition = Number(target.position || 0);

      if (runtime.passwordPanelOpen && runtime.passwordChangeRequired) {
        window.alert(
          "Die erforderliche Passwortänderung muss zuerst abgeschlossen werden."
        );
        restoreCurrentHistoryEntry(targetPosition);
        return;
      }

      if (runtime.passwordPanelOpen) {
        setPasswordState((prev) => ({ ...prev, open: false }));
        restoreCurrentHistoryEntry(targetPosition);
        return;
      }

      if (runtime.photoCaptureOpen) {
        setPhotoCaptureSequence({
          open: false,
          title: "",
          items: [],
          index: 0,
        });
        photoCaptureSaveRef.current = null;
        restoreCurrentHistoryEntry(targetPosition);
        return;
      }

      if (runtime.imageViewerOpen) {
        setImageViewer({ open: false, photos: [], index: 0 });
        restoreCurrentHistoryEntry(targetPosition);
        return;
      }

      const currentReturnBookingId = current.returnBookingId || "";
      const targetReturnBookingId = target.returnBookingId || "";
      const returnStepOrder = ["employee", "compare", "condition", "finish"];
      const leavesCurrentReturn =
        Boolean(currentReturnBookingId) &&
        targetReturnBookingId !== currentReturnBookingId;
      const movesReturnForward =
        Boolean(currentReturnBookingId) &&
        targetReturnBookingId === currentReturnBookingId &&
        returnStepOrder.indexOf(target.returnTab || "compare") >
          returnStepOrder.indexOf(current.returnTab || "compare");
      const hasIncompleteReturnDamage =
        getIncompleteReturnDamageEntries(runtime.returnDamageEntries).length > 0;
      const movesToReturnFinish =
        movesReturnForward && target.returnTab === "finish";

      if (
        hasIncompleteReturnDamage &&
        (leavesCurrentReturn || movesReturnForward)
      ) {
        setReturnState((prev) => ({
          ...prev,
          error:
            "Zu jedem dokumentierten Schaden sind eine Beschreibung und mindestens ein zusätzliches Foto erforderlich.",
        }));
        window.alert(
          "Die Rücknahme kann noch nicht verlassen werden. Zu jedem dokumentierten Schaden sind eine Beschreibung und mindestens ein Foto erforderlich."
        );
        restoreCurrentHistoryEntry(targetPosition);
        return;
      }

      if (movesToReturnFinish && !runtime.returnFuelReceiptPhoto) {
        setReturnState((prev) => ({
          ...prev,
          error:
            "Bitte hinterlegen Sie ein Foto der Tankrechnung, bevor Sie fortfahren.",
        }));
        window.alert(
          "Bitte hinterlegen Sie ein Foto der Tankrechnung, bevor Sie fortfahren."
        );
        restoreCurrentHistoryEntry(targetPosition);
        return;
      }

      const normalizedTarget = {
        activeView: target.activeView || "Annahme",
        intakeWorkspaceTab: target.intakeWorkspaceTab || "new",
        intakeTab: target.intakeTab || "core",
        loanerBoardTab: target.loanerBoardTab || "overview",
        wizardOpen: Boolean(target.wizardOpen),
        wizardStep: Math.min(
          Math.max(Number(target.wizardStep || 0), 0),
          WIZARD_STEPS.length - 1
        ),
        returnBookingId: targetReturnBookingId,
        returnTab: targetReturnBookingId ? target.returnTab || "compare" : "",
        taskDetailCardId: target.taskDetailCardId || "",
        expandedLoanerHistory: target.expandedLoanerHistory || "",
      };

      browserNavigationPositionRef.current = targetPosition;
      browserNavigationKeyRef.current = JSON.stringify(normalizedTarget);
      setUi((prev) => ({ ...prev, activeView: normalizedTarget.activeView }));
      setIntakeWorkspaceTab(normalizedTarget.intakeWorkspaceTab);
      setIntakeTab(normalizedTarget.intakeTab);
      setLoanerBoardTab(normalizedTarget.loanerBoardTab);
      setWizardOpen(normalizedTarget.wizardOpen);
      setWizardStep(normalizedTarget.wizardStep);
      setReturnState((prev) => ({
        ...prev,
        bookingId: normalizedTarget.returnBookingId || null,
      }));
      if (normalizedTarget.returnBookingId) {
        setReturnTabs((prev) => ({
          ...prev,
          [normalizedTarget.returnBookingId]: normalizedTarget.returnTab,
        }));
      }
      setTaskDetailCardId(normalizedTarget.taskDetailCardId || null);
      setExpandedLoanerHistory(
        normalizedTarget.expandedLoanerHistory || null
      );
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [
    browserNavigationReady,
    isPublicConfirmation,
    session,
  ]);

  const technicianWorkload = useMemo(() => {
    const load = {};
    cards.forEach((card) => {
      if (!card.assignedTech) return;
      if (
        card.workStatus === WORK_STATUS.DONE ||
        card.workStatus === WORK_STATUS.CANCELLED
      ) {
        return;
      }
      load[card.assignedTech] = (load[card.assignedTech] || 0) + 1;
    });
    return load;
  }, [cards]);

  const departmentCapacity = useMemo(() => {
    const map = {};
    cards.forEach((card) => {
      if (
        card.workStatus === WORK_STATUS.DONE ||
        card.workStatus === WORK_STATUS.CANCELLED
      ) {
        return;
      }
      map[card.role] = (map[card.role] || 0) + 1;
    });
    return map;
  }, [cards]);

  const executiveSignals = useMemo(() => {
    const activeBookings = bookings.filter((booking) => isActiveBooking(booking)).length;
    const readyForPickup = bookings.filter(
      (booking) => booking.status === "ready_for_checkout"
    ).length;
    const blockedCards = cards.filter((card) => card.workStatus === WORK_STATUS.BLOCKED).length;
    const overdueCards = cards.filter((card) => {
      if (!card.due) return false;
      return new Date(card.due) < new Date() && card.workStatus !== WORK_STATUS.DELIVERED;
    }).length;
    const list = [
      {
        type: "ok",
        text: `${loaners.filter((loaner) => loaner.isActive).length} aktive Leihwagen`,
      },
      {
        type: activeBookings > 0 ? "warn" : "ok",
        text: `${activeBookings} laufende Leihwagenvorgänge`,
      },
      {
        type: blockedCards > 0 ? "warn" : "ok",
        text: `${blockedCards} blockierte Aufträge`,
      },
    ];

    if (readyForPickup > 0) {
      list.push({
        type: "danger",
        text: `${readyForPickup} Freigaben warten auf Übergabe`,
      });
    }

    if (overdueCards > 0) {
      list.push({
        type: "danger",
        text: `${overdueCards} Aufträge sind überfällig`,
      });
    }

    return list;
  }, [bookings, cards, loaners]);

  const effectiveDepartment =
    profile?.role === "admin" || isWorkshopLead
      ? ""
      : isTechnicianRole
        ? "Werkstatt"
        : normalizeDepartment(profile?.department);
  const isIntakeStaff = profile?.role !== "admin" && !isWorkshopLead && effectiveDepartment === DEFAULT_DEPARTMENT;
  const canEditDepartmentCard = (card) => {
    if (isAdmin) return true;
    if (isWorkshopLead && (card?.role === "Werkstatt" || card?.assignedTech)) return true;
    const cardDepartment = card?.role || DEFAULT_DEPARTMENT;
    if (isIntakeStaff) return cardDepartment === DEFAULT_DEPARTMENT;
    return cardDepartment === effectiveDepartment;
  };
  const visibleCards = useMemo(() => {
    if (profile?.role === "admin") return cards;
    if (isWorkshopLead) {
      return cards.filter((card) => card.role === "Werkstatt" || card.assignedTech);
    }
    if (isTechnicianRole) {
      return cards.filter(
        (card) =>
          card.role === "Werkstatt" &&
          (!profileTechnicianId || !card.assignedTech || card.assignedTech === profileTechnicianId) &&
          card.loanerFlow?.assignmentApproval?.status !== "pending_workshop_lead"
      );
    }
    return cards.filter((card) => {
      const isSharedLoanerDraft =
        effectiveDepartment === "Leihwagen" &&
        card.needsLoaner &&
        canRestoreWizardDraft(card.loanerFlow?.loanerWizardDraft) &&
        ["in_progress", "ready_for_device_handover"].includes(
          card.loanerFlow?.loanerWizardDraftStatus
        );
      const departmentMatches =
        isIntakeStaff ||
        isSharedLoanerDraft ||
        (card.role || DEFAULT_DEPARTMENT) === effectiveDepartment;
      const branchMatches =
        isIntakeStaff ||
        !profile?.branch ||
        !card.branch ||
        String(card.branch) === String(profile.branch);
      return departmentMatches && branchMatches;
    });
  }, [cards, effectiveDepartment, isIntakeStaff, isTechnicianRole, isWorkshopLead, profile, profileTechnicianId]);
  const cardsView = useMemo(() => filterAndSortCards(visibleCards, ui), [visibleCards, ui]);
  const now = Date.now();
  const activeCards = useMemo(
    () => visibleCards.filter((card) => card.workStatus !== WORK_STATUS.CANCELLED),
    [visibleCards]
  );
  const allActiveCards = useMemo(
    () => cards.filter((card) => card.workStatus !== WORK_STATUS.CANCELLED && card.workStatus !== WORK_STATUS.DELIVERED),
    [cards]
  );
  const workshopMission = useMemo(() => {
    const today = addDays(0);
    const tomorrow = addDays(1);
    const workshopCards = activeCards
      .filter((card) => card.role === "Werkstatt" || card.assignedTech)
      .sort((left, right) => String(left.due || "").localeCompare(String(right.due || "")));
    const actionable = workshopCards.filter(
      (card) =>
        card.workStatus !== WORK_STATUS.BLOCKED &&
        !card.blockerType &&
        card.loanerFlow?.assignmentApproval?.status !== "pending_workshop_lead"
    );
    const readyForHandover = actionable.filter(
      (card) => (card.subs || []).length > 0 && !(card.subs || []).some(isBlockingSubtask)
    );
    return {
      cards: workshopCards,
      actionable,
      next: actionable[0] || null,
      today: workshopCards.filter((card) => (card.due || "") <= today),
      tomorrow: workshopCards.filter((card) => card.due === tomorrow),
      overdue: workshopCards.filter((card) => card.due && card.due < today),
      blocked: workshopCards.filter((card) => card.workStatus === WORK_STATUS.BLOCKED || card.blockerType),
      unassigned: workshopCards.filter((card) => !card.assignedTech),
      approvals: workshopCards.filter(
        (card) => card.loanerFlow?.assignmentApproval?.status === "pending_workshop_lead" || card.blockerType === "approval"
      ),
      readyForHandover,
    };
  }, [activeCards]);
const getWorkshopCardSignal = (card) => {
    const today = addDays(0);
    const isMine = !profileTechnicianId || card.assignedTech === profileTechnicianId;
    if (card.workStatus === WORK_STATUS.DONE) return { label: "Erledigt", tone: "done" };
    if (!isMine && card.assignedTech) return { label: "Nicht zuständig", tone: "muted" };
    if (card.due && card.due < today && card.workStatus !== WORK_STATUS.DONE) return { label: "Überfällig", tone: "danger" };
    if (card.blockerType === "parts") return { label: "Teil fehlt", tone: "warn" };
    if (card.blockerType === "approval") return { label: "Freigabe fehlt", tone: "warn" };
    if (card.blockerType) return { label: getBlockerLabel(card.blockerType), tone: "danger" };
    if (card.workStatus === WORK_STATUS.IN_WORK) return { label: "In Arbeit", tone: "active" };
    if (!card.assignedTech) return { label: "Bereit", tone: "ok" };
    return { label: "Bereit", tone: "ok" };
  };
const shouldOfferTodoPhoto = (text) =>
  /schaden|foto|teil|arbeit|endkontrolle|rückfrage|diagnose|probefahrt|prüfen|dokument/i.test(String(text || ""));
  const getWorkshopNextCard = (sourceCards = workshopMission.cards) => {
    const today = addDays(0);
    const score = (card) => {
      let value = 0;
      const isMine = profileTechnicianId && card.assignedTech === profileTechnicianId;
      if (isMine && card.due && card.due <= today) value += 1000;
      if (isMine) value += 700;
      if (!card.blockerType && card.workStatus !== WORK_STATUS.BLOCKED) value += 350;
      if (card.prio === "urgent" || card.prio === "high") value += 160;
      if (card.prio === "customer_waiting") value += 160;
      if (!card.assignedTech) value += 80;
      if (card.due) value += Math.max(0, 40 - Math.abs(new Date(card.due).getTime() - new Date(today).getTime()) / 86400000);
      return value;
    };
    return sourceCards
      .filter(
        (card) =>
          card.workStatus !== WORK_STATUS.DONE &&
          card.workStatus !== WORK_STATUS.BLOCKED &&
          !card.blockerType &&
          card.loanerFlow?.assignmentApproval?.status !== "pending_workshop_lead"
      )
      .sort((left, right) => score(right) - score(left) || String(left.due || "").localeCompare(String(right.due || "")))[0] || null;
  };
  const workshopNextCard = useMemo(
    () => getWorkshopNextCard(workshopMission.cards),
    [workshopMission.cards, profileTechnicianId]
  );
  const globalSearchResults = useMemo(() => {
    const term = normalizeLookupText(globalSearch);
    if (term.length < 2) return [];
    const match = (values) => normalizeLookupText(values.filter(Boolean).join(" ")).includes(term);
    const cardResults = cards
      .filter((card) =>
        match([
          card.orderNumber,
          card.title,
          card.customerName,
          card.customerFirstName,
          card.customerLastName,
          card.customerEmail,
          card.licensePlate,
          card.vehicleData?.brand,
          card.vehicleData?.model,
          card.vehicleData?.vin,
          getEmployeeNameById(technicians, card.assignedTech),
        ])
      )
      .slice(0, 6)
      .map((card) => ({
        id: `card_${card.id}`,
        type: "Auftrag",
        title: card.orderNumber || card.title || "Auftrag",
        detail: `${[card.vehicleData?.brand, card.vehicleData?.model].filter(Boolean).join(" ") || card.licensePlate || "-"} · ${card.customerName || "-"}`,
        hint: getOperationalStatusLabel(card),
        action: () => openTaskDetail(card.id),
      }));
    const bookingResults = bookings
      .filter((booking) =>
        match([
          booking.orderNumber,
          booking.customerName,
          booking.customerEmail,
          booking.loanerPlate,
          booking.licensePlate,
          booking.loanerBrand,
          booking.loanerModel,
        ])
      )
      .slice(0, 4)
      .map((booking) => ({
        id: `booking_${booking.id}`,
        type: "Leihwagen",
        title: booking.loanerPlate || booking.orderNumber || "Leihwagen",
        detail: `${booking.customerName || "-"} · ${booking.expectedReturnDate ? fmtDate(booking.expectedReturnDate) : "ohne Rückgabe"}`,
        hint: getBookingStatusLabel(booking.status),
        action: () => {
          setUi((prev) => ({ ...prev, activeView: "Leihwagen" }));
          setLoanerHistorySearch(booking.loanerPlate || booking.customerName || "");
        },
      }));
    const loanerResults = loaners
      .filter((loaner) =>
        match([
          loaner.plate,
          loaner.brand,
          loaner.model,
          loaner.vin,
          loaner.assignmentNote,
          getLoanerUsageLabel(loaner.usageType),
        ])
      )
      .slice(0, 4)
      .map((loaner) => ({
        id: `loaner_${loaner.id}`,
        type: "Fahrzeug",
        title: loaner.plate || "Fahrzeug",
        detail: `${loaner.brand || "-"} ${loaner.model || ""}`.trim(),
        hint: getLoanerUsageLabel(loaner.usageType),
        action: () => {
          setUi((prev) => ({ ...prev, activeView: "Leihwagen" }));
          setLoanerHistorySearch(loaner.plate || loaner.model || "");
        },
      }));
    return [...cardResults, ...bookingResults, ...loanerResults].slice(0, 10);
  }, [globalSearch, cards, bookings, loaners, technicians]);
  const getTechnicianActiveHours = (techId, sourceCards = allActiveCards) =>
    sourceCards
      .filter(
        (card) =>
          card.assignedTech === techId &&
          card.workStatus !== WORK_STATUS.DONE &&
          card.loanerFlow?.assignmentApproval?.status !== "pending_workshop_lead"
      )
      .reduce((sum, card) => sum + getEstimatedHours(card), 0);
  const getTechnicianRecommendationMeta = (technician, draftOrCard = {}) => {
    const branch = draftOrCard.branch || profile?.branch || "";
    const caseType = draftOrCard.caseType || "other";
    const capacity = technician.capacityHours || DEFAULT_TECH_CAPACITY_HOURS;
    const activeHours = getTechnicianActiveHours(technician.id);
    const freeHours = Math.max(0, capacity - activeHours);
    const skillText = normalizeLookupText([technician.name, ...(technician.specialties || [])].join(" "));
    const skillHints = CASE_TYPE_SKILL_HINTS[caseType] || [];
    const skillMatches = skillHints.filter((hint) => skillText.includes(normalizeLookupText(hint))).length;
    const branchScore = !branch || technician.branch === branch ? 40 : 0;
    const capacityScore = Math.min(35, freeHours * 5);
    const skillScore = skillMatches * 12;
    const score = branchScore + capacityScore + skillScore;
    const reasons = [
      technician.branch === branch ? "gleiche Filiale" : "",
      skillMatches > 0 ? "passender Schwerpunkt" : "",
      freeHours > 0 ? `${formatHours(freeHours)} frei` : "voll ausgelastet",
    ].filter(Boolean);
    return { score, activeHours, freeHours, capacity, reasons };
  };
  const getRecommendedTechnicians = (draftOrCard = {}) =>
    configuredTechnicians
      .filter((technician) => technician.type === "Techniker")
      .map((technician) => ({
        ...technician,
        recommendation: getTechnicianRecommendationMeta(technician, draftOrCard),
      }))
      .sort(
        (left, right) =>
          right.recommendation.score - left.recommendation.score ||
          left.recommendation.activeHours - right.recommendation.activeHours ||
          left.name.localeCompare(right.name)
      );
  const blockedCards = useMemo(
    () => activeCards.filter((card) => card.workStatus === WORK_STATUS.BLOCKED || card.blockerType),
    [activeCards]
  );
  const overdueCards = useMemo(
    () =>
      activeCards.filter((card) => {
        if (!card.due) return false;
        return new Date(card.due).getTime() < now && card.workStatus !== WORK_STATUS.DELIVERED;
      }),
    [activeCards, now]
  );
  const nextActionCritical = useMemo(
    () =>
      activeCards.filter((card) => {
        if (!card.nextActionDue) return false;
        return new Date(card.nextActionDue).getTime() < now;
      }),
    [activeCards, now]
  );
  const approvalRequests = useMemo(
    () =>
      activeCards
        .filter(
          (card) =>
            card.blockerType === "approval" ||
            (card.questions || []).some(
              (question) =>
                !question.answered &&
                (question.requiresApproval ||
                  /freigabe|abgabe|übergabe|uebergabe|blocker|erinnerung/i.test(String(question.text || "")))
            )
        )
        .sort((left, right) => new Date(left.due || 0) - new Date(right.due || 0)),
    [activeCards]
  );
  const departmentMetrics = useMemo(
    () =>
      ROLES.map((role) => {
        const items = activeCards.filter((card) => card.role === role);
        const blocked = items.filter(
          (card) => card.workStatus === WORK_STATUS.BLOCKED || card.blockerType
        ).length;
        const overdue = items.filter((card) => {
          if (!card.due) return false;
          return new Date(card.due).getTime() < now && card.workStatus !== WORK_STATUS.DELIVERED;
        }).length;
        const dueToday = items.filter((card) => {
          if (!card.due) return false;
          const dueDate = new Date(card.due);
          const today = new Date();
          return (
            dueDate.getFullYear() === today.getFullYear() &&
            dueDate.getMonth() === today.getMonth() &&
            dueDate.getDate() === today.getDate()
          );
        }).length;
        return {
          role,
          total: items.length,
          blocked,
          overdue,
          dueToday,
          active: items.filter((card) => card.workStatus === WORK_STATUS.IN_WORK).length,
          done: items.filter((card) => card.workStatus === WORK_STATUS.DONE).length,
          items,
        };
      }),
    [activeCards, now]
  );
  const managementKpis = useMemo(
    () => [
      { label: "Offene Aufträge", value: activeCards.length },
      { label: "Blocker", value: blockedCards.length },
      { label: "Überfällig", value: overdueCards.length },
      {
        label: "Werkstatt ohne Techniker",
        value: activeCards.filter((card) => card.role === "Werkstatt" && !card.assignedTech).length,
      },
      {
        label: "Techniker-Freigaben",
        value: activeCards.filter((card) => card.loanerFlow?.assignmentApproval?.status === "pending_workshop_lead").length,
      },
      {
        label: "Interne Aufträge",
        value: activeCards.filter((card) => isInternalOrder(card)).length,
      },
      {
        label: "Leihwagen aktiv",
        value: bookings.filter((booking) => isActiveBooking(booking)).length,
      },
      {
        label: "Freigaben offen",
        value: bookings.filter((booking) => booking.status === "ready_for_checkout").length,
      },
      { label: "Nächste Aktionen überfällig", value: nextActionCritical.length },
      {
        label: "Wartet auf Kunde",
        value:
          bookings.filter((booking) => booking.status === "awaiting_customer_confirmation").length +
          activeCards.filter((card) => card.loanerFlow?.orderConfirmationStatus === "sent").length,
      },
      {
        label: "Auslastung Leihwagen",
        value: `${bookings.filter((booking) => isActiveBooking(booking)).length}/${loaners.filter((loaner) => loaner.isActive && loaner.usageType === "customer_loaner").length || 0}`,
      },
    ],
    [activeCards, blockedCards.length, overdueCards.length, bookings, nextActionCritical.length, loaners]
  );
  const morningBriefing = useMemo(() => {
    const today = addDays(0);
    const dueToday = activeCards
      .filter((card) => card.due && card.due <= today && card.workStatus !== WORK_STATUS.DONE)
      .sort((left, right) => String(left.due || "").localeCompare(String(right.due || "")));
    const stuckCards = blockedCards
      .map((card) => {
        const latest =
          [...(card.history || [])].reverse().find((entry) => entry.ts)?.ts ||
          card.loanerFlow?.assignmentApproval?.requestedAt ||
          card.createdAt;
        const ageHours = latest ? Math.max(0, Math.round((Date.now() - new Date(latest).getTime()) / 3600000)) : 0;
        return { ...card, stuckHours: ageHours };
      })
      .sort((left, right) => right.stuckHours - left.stuckHours);
    const techLoad = configuredTechnicians
      .filter((technician) => technician.type === "Techniker")
      .map((technician) => {
        const assigned = activeCards.filter(
          (card) => card.assignedTech === technician.id && card.workStatus !== WORK_STATUS.DONE
        );
        const hours = assigned.reduce((sum, card) => sum + getEstimatedHours(card), 0);
        return {
          ...technician,
          assignedCount: assigned.length,
          hours,
          overloaded: hours > (technician.capacityHours || DEFAULT_TECH_CAPACITY_HOURS),
        };
      })
      .sort((left, right) => right.hours - left.hours);
    const missingParts = blockedCards.filter((card) => card.blockerType === "parts");
    const readyVehicles = activeCards.filter(
      (card) =>
        card.workStatus !== WORK_STATUS.BLOCKED &&
        !card.blockerType &&
        (card.subs || []).length > 0 &&
        !(card.subs || []).some(isBlockingSubtask)
    );
    return {
      dueToday,
      stuckCards,
      techLoad,
      missingParts,
      readyVehicles,
      approvalCards: approvalRequests,
    };
  }, [activeCards, blockedCards, approvalRequests, configuredTechnicians]);
  const approvalGroups = useMemo(() => {
    const groups = {};
    approvalRequests.forEach((card) => {
      const type = getApprovalRequestType(card);
      groups[type] = [...(groups[type] || []), card];
    });
    return groups;
  }, [approvalRequests]);
  const operationAlerts = useMemo(() => {
    const alerts = [];
    const ageHours = (card) => {
      const ts =
        card?.loanerFlow?.assignmentApproval?.requestedAt ||
        [...(card?.history || [])].reverse().find((entry) => entry.ts)?.ts ||
        card?.createdAt;
      if (!ts) return 0;
      return (Date.now() - new Date(ts).getTime()) / 3600000;
    };
    const assignmentApprovals = activeCards.filter(
      (card) => card.loanerFlow?.assignmentApproval?.status === "pending_workshop_lead"
    ).length;
    if (assignmentApprovals > 0) {
      alerts.push({
        tone: "warn",
        text: `${assignmentApprovals} Techniker-Zuweisung(en) warten auf Freigabe`,
        target: "Werkstattplanung",
      });
    }
    if (overdueCards.length > 0) {
      alerts.push({
        tone: "danger",
        text: `${overdueCards.length} Auftrag/Aufträge überfällig`,
        target: "Management",
      });
    }
    if (blockedCards.length > 0) {
      alerts.push({
        tone: "warn",
        text: `${blockedCards.length} Blocker offen`,
        target: "Management",
      });
    }
    const oldBlockers = blockedCards.filter((card) => ageHours(card) >= 2).length;
    if (oldBlockers > 0) {
      alerts.push({
        tone: "danger",
        text: `${oldBlockers} Blocker länger als 2h offen`,
        target: "Management",
      });
    }
    const unassignedWorkshop = activeCards.filter(
      (card) => card.role === "Werkstatt" && !card.assignedTech && ageHours(card) >= 1
    ).length;
    if (unassignedWorkshop > 0) {
      alerts.push({
        tone: "warn",
        text: `${unassignedWorkshop} Werkstattauftrag/Aufträge ohne Techniker`,
        target: "Werkstattplanung",
      });
    }
    const dueLoaners = bookings.filter((booking) => {
      if (!isActiveBooking(booking) || !booking.expectedReturnDate) return false;
      return booking.expectedReturnDate <= toDateOnlyString(new Date());
    }).length;
    if (dueLoaners > 0) {
      alerts.push({
        tone: "warn",
        text: `${dueLoaners} Leihwagen heute fällig/überfällig`,
        target: "Leihwagen",
      });
    }
    return alerts.slice(0, 4);
  }, [activeCards, blockedCards.length, bookings, overdueCards.length]);

  const loanerManagement = useMemo(() => {
    const referenceDate = new Date();
    const periodDays = managementLoanerFilters.period === "all"
      ? null
      : Number(managementLoanerFilters.period || 30);
    const periodStart =
      periodDays === null
        ? null
        : new Date(referenceDate.getTime() - periodDays * 86400000);
    const normalizedVehicle = normalizeLookupText(managementLoanerFilters.vehicle);
    const normalizedResponsible = normalizeLookupText(managementLoanerFilters.responsible);
    const today = toDateOnlyString(referenceDate);
    const customerLoanerCount = loaners.filter(
      (loaner) => loaner.isActive && loaner.usageType === "customer_loaner"
    ).length;
    const activeLoanerIds = new Set(
      bookings.filter((booking) => isActiveBooking(booking)).map((booking) => booking.loanerId)
    );
    const groupCounts = (items, keyGetter) =>
      items.reduce((acc, item) => {
        const key = keyGetter(item) || "Nicht zugeordnet";
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
    const makeRecurring = (items, keyGetter, labelGetter) =>
      Object.entries(groupCounts(items, keyGetter))
        .map(([key, count]) => ({
          key,
          label: labelGetter(key),
          count,
        }))
        .filter((entry) => entry.count >= 2)
        .sort((left, right) => right.count - left.count)
        .slice(0, 6);

    const allEntries = bookings
      .map((booking) => {
        const linkedCard = cards.find((card) => isBookingLinkedToCard(booking, card));
        const assessment = getLoanerWatchdogAssessment(
          { ...booking, branch: linkedCard?.branch || booking.loanerHomeBranch },
          referenceDate
        );
        const responsible = [
          getBookingHandoverEmployee(booking),
          getBookingReturnEmployee(booking),
          linkedCard?.responsibleLabel,
          linkedCard?.assignedTech
            ? configuredTechnicians.find((tech) => tech.id === linkedCard.assignedTech)?.name
            : "",
        ]
          .filter((value) => value && value !== "-")
          .join(" · ");
        return {
          booking,
          linkedCard,
          assessment,
          branch: linkedCard?.branch || booking.loanerHomeBranch || "",
          responsible,
          date: getLoanerManagementDate(booking),
        };
      });

    const matchesNonPeriodFilters = (entry) => {
        if (managementLoanerFilters.branch && entry.branch !== managementLoanerFilters.branch) {
          return false;
        }
        if (managementLoanerFilters.status && entry.booking.status !== managementLoanerFilters.status) {
          return false;
        }
        if (managementLoanerFilters.risk && entry.assessment.level !== managementLoanerFilters.risk) {
          return false;
        }
        if (normalizedVehicle) {
          const vehicleText = normalizeLookupText(
            [
              entry.booking.loanerPlate,
              entry.booking.loanerBrand,
              entry.booking.loanerModel,
              entry.booking.orderNumber,
            ].join(" ")
          );
          if (!vehicleText.includes(normalizedVehicle)) return false;
        }
        if (normalizedResponsible) {
          const responsibleText = normalizeLookupText(
            [entry.responsible, entry.booking.customerName, entry.booking.customerEmail].join(" ")
          );
          if (!responsibleText.includes(normalizedResponsible)) return false;
        }
        return true;
    };

    const entryDateValue = (entry) => {
      const entryDate = entry.date ? new Date(entry.date) : null;
      return entryDate && !Number.isNaN(entryDate.getTime()) ? entryDate : null;
    };
    const enriched = allEntries
      .filter(matchesNonPeriodFilters)
      .filter((entry) => {
        if (!periodStart) return true;
        const entryDate = entryDateValue(entry);
        return !entryDate || entryDate >= periodStart;
      })
      .sort((left, right) => right.assessment.score - left.assessment.score);

    const previousPeriodStart =
      periodDays === null || !periodStart
        ? null
        : new Date(periodStart.getTime() - periodDays * 86400000);
    const previousEntries =
      previousPeriodStart && periodStart
        ? allEntries
            .filter(matchesNonPeriodFilters)
            .filter((entry) => {
              const entryDate = entryDateValue(entry);
              return entryDate && entryDate >= previousPeriodStart && entryDate < periodStart;
            })
        : [];

    const active = enriched.filter((entry) => isActiveBooking(entry.booking));
    const returned = enriched.filter((entry) => entry.booking.status === "returned");
    const critical = enriched.filter((entry) => entry.assessment.level === "critical");
    const warnings = enriched.filter((entry) => entry.assessment.level === "warn");
    const overdue = enriched.filter((entry) => entry.assessment.overdueDays > 0);
    const dueToday = enriched.filter(
      (entry) => isActiveBooking(entry.booking) && toDateOnlyString(entry.booking.expectedReturnDate) === today
    );
    const openApprovals = enriched.filter(
      (entry) => entry.booking.status === "awaiting_customer_confirmation"
    );
    const totalCosts = enriched.reduce((sum, entry) => sum + entry.assessment.cost.total, 0);
    const previousTotalCosts = previousEntries.reduce(
      (sum, entry) => sum + entry.assessment.cost.total,
      0
    );
    const avgDuration =
      enriched.length === 0
        ? 0
        : enriched.reduce((sum, entry) => sum + entry.assessment.cost.durationDays, 0) /
          enriched.length;
    const statusDistribution = Object.entries(
      groupCounts(enriched, (entry) => getBookingStatusLabel(entry.booking.status))
    )
      .map(([label, count]) => ({
        label,
        count,
        percentage: enriched.length ? Math.round((count / enriched.length) * 100) : 0,
      }))
      .sort((left, right) => right.count - left.count);
    const warningEntries = enriched.flatMap((entry) =>
      entry.assessment.warnings.map((warning) => ({ ...entry, warning }))
    );
    const criticalWarningEntries = warningEntries.filter(
      (entry) => entry.warning.level === "critical"
    );
    const topCauses = Object.entries(
      warningEntries.reduce((acc, entry) => {
        const key = entry.warning.type;
        const current = acc[key] || {
          key,
          label: entry.warning.label,
          count: 0,
          critical: 0,
          recommendation: entry.warning.recommendation,
        };
        current.count += 1;
        if (entry.warning.level === "critical") current.critical += 1;
        acc[key] = current;
        return acc;
      }, {})
    )
      .map(([, value]) => value)
      .sort((left, right) => right.critical - left.critical || right.count - left.count)
      .slice(0, 6);
    const availabilityRate = customerLoanerCount
      ? Math.max(0, Math.round(((customerLoanerCount - activeLoanerIds.size) / customerLoanerCount) * 100))
      : 0;
    const lowAvailability =
      customerLoanerCount > 0 && activeLoanerIds.size / customerLoanerCount >= 0.8;
    const managementAlerts = [
      ...criticalWarningEntries.slice(0, 12).map((entry) => ({
        id: `${entry.booking.id}_${entry.warning.type}`,
        severity: entry.warning.level,
        title: entry.warning.label,
        target: entry.booking.loanerPlate || entry.booking.orderNumber || "Leihwagenvorgang",
        cause: entry.warning.cause,
        recommendation: entry.warning.recommendation,
        urgency: entry.warning.urgency,
        entry,
      })),
      ...(lowAvailability
        ? [
            {
              id: "fleet_availability",
              severity: "warn",
              title: "Fahrzeugverfügbarkeit niedrig",
              target: "Leihwagenflotte",
              cause: `${activeLoanerIds.size}/${customerLoanerCount} Kundenleihfahrzeuge aktiv gebunden`,
              recommendation: "Rückgaben priorisieren und Langläufer prüfen.",
              urgency: "Heute prüfen",
              entry: null,
            },
          ]
        : []),
      ...(openApprovals.length >= 3
        ? [
            {
              id: "approval_bottleneck",
              severity: "warn",
              title: "Freigabe-Engpass",
              target: "Kundenbestätigungen",
              cause: `${openApprovals.length} Vorgänge warten auf Bestätigung`,
              recommendation: "Offene Links nachfassen und Versand prüfen.",
              urgency: "Heute",
              entry: openApprovals[0],
            },
          ]
        : []),
    ].slice(0, 10);
    const processHealthScore = Math.max(
      0,
      Math.min(
        100,
        Math.round(
          100 -
            critical.length * 10 -
            warnings.length * 4 -
            overdue.length * 7 -
            openApprovals.length * 3 -
            (lowAvailability ? 8 : 0)
        )
      )
    );
    const processHealthLabel =
      processHealthScore >= 85
        ? "Stabil"
        : processHealthScore >= 65
          ? "Beobachten"
          : "Handlungsbedarf";
    const previousCritical = previousEntries.filter(
      (entry) => entry.assessment.level === "critical"
    ).length;
    const previousOverdue = previousEntries.filter((entry) => entry.assessment.overdueDays > 0).length;
    const periodComparison = {
      cases: enriched.length - previousEntries.length,
      critical: critical.length - previousCritical,
      overdue: overdue.length - previousOverdue,
      costs: totalCosts - previousTotalCosts,
    };
    const trendDays = Array.from({ length: 7 }, (_, index) => {
      const date = new Date(referenceDate);
      date.setDate(referenceDate.getDate() - (6 - index));
      const key = toDateOnlyString(date);
      const dayEntries = enriched.filter(
        (entry) => toDateOnlyString(entry.date) === key
      );
      return {
        key,
        label: date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }),
        total: dayEntries.length,
        critical: dayEntries.filter((entry) => entry.assessment.level === "critical").length,
      };
    });

    return {
      items: enriched,
      criticalCases: critical.slice(0, 10),
      warningCases: warnings.slice(0, 8),
      managementAlerts,
      topCauses,
      processHealth: {
        score: processHealthScore,
        label: processHealthLabel,
        availabilityRate,
        lowAvailability,
      },
      periodComparison,
      statusDistribution,
      trends: trendDays,
      recurring: {
        vehicles: makeRecurring(
          warningEntries,
          (entry) => entry.booking.loanerPlate || entry.booking.loanerId,
          (key) => key
        ),
        branches: makeRecurring(
          warningEntries,
          (entry) => entry.branch || entry.booking.loanerHomeBranch,
          (key) => getBranchLabel(key)
        ),
        employees: makeRecurring(
          warningEntries,
          (entry) => entry.responsible || "Nicht zugeordnet",
          (key) => key
        ),
      },
      kpis: [
        {
          label: "Health-Score",
          value: `${processHealthScore}%`,
          tone: processHealthScore >= 85 ? "ok" : processHealthScore >= 65 ? "warn" : "danger",
          delta: processHealthLabel,
        },
        { label: "Aktive Vorgänge", value: active.length, tone: "normal", delta: `${periodComparison.cases >= 0 ? "+" : ""}${periodComparison.cases} zur Vorperiode` },
        { label: "Abgeschlossen", value: returned.length, tone: "ok" },
        { label: "Überfällig", value: overdue.length, tone: overdue.length ? "danger" : "ok", delta: `${periodComparison.overdue >= 0 ? "+" : ""}${periodComparison.overdue}` },
        { label: "Kritisch", value: critical.length, tone: critical.length ? "danger" : "ok", delta: `${periodComparison.critical >= 0 ? "+" : ""}${periodComparison.critical}` },
        { label: "Heute fällig", value: dueToday.length, tone: dueToday.length ? "warn" : "normal" },
        { label: "Freigaben offen", value: openApprovals.length, tone: openApprovals.length ? "warn" : "ok" },
        {
          label: "Auslastung",
          value: `${activeLoanerIds.size}/${customerLoanerCount || 0}`,
          tone: lowAvailability ? "warn" : "normal",
          delta: `${availabilityRate}% frei`,
        },
        {
          label: "Kostenindikator",
          value: fmtCurrency(totalCosts),
          tone: totalCosts >= 250 ? "warn" : "normal",
          delta: `${periodComparison.costs >= 0 ? "+" : ""}${fmtCurrency(periodComparison.costs)} zur Vorperiode`,
        },
        { label: "Ø Dauer", value: `${avgDuration.toFixed(1).replace(".", ",")} Tage`, tone: avgDuration > 7 ? "warn" : "normal" },
      ],
    };
  }, [bookings, cards, configuredTechnicians, loaners, managementLoanerFilters]);

  const availabilityMap = useMemo(() => {
    const activeByLoaner = {};
    const today = toDateOnlyString(new Date());
    bookings.forEach((booking) => {
      if (!isBookingActiveOnDate(booking, today)) return;
      activeByLoaner[booking.loanerId] = booking;
    });
    return activeByLoaner;
  }, [bookings]);

  const getLoanerConflictsForRange = (loanerId, startDate, endDate, ignoreBookingId = "") =>
    bookings.filter(
      (booking) =>
        booking.loanerId === loanerId &&
        booking.id !== ignoreBookingId &&
        isActiveBooking(booking) &&
        rangesOverlap(
          getBookingStartDate(booking),
          getBookingEndDate(booking),
          startDate,
          endDate
        )
    );

  const getLoanerAvailabilityDate = (loanerId) => {
    const relevantBookings = bookings
      .filter((booking) => booking.loanerId === loanerId && isActiveBooking(booking))
      .sort((left, right) =>
        getBookingEndDate(left).localeCompare(getBookingEndDate(right))
      );

    if (relevantBookings.length === 0) {
      return toDateOnlyString(new Date());
    }

    const latestEnd = getBookingEndDate(relevantBookings[relevantBookings.length - 1]);
    if (!latestEnd) return toDateOnlyString(new Date());
    const nextDate = new Date(`${latestEnd}T00:00:00`);
    nextDate.setDate(nextDate.getDate() + 1);
    return toDateOnlyString(nextDate);
  };

  const getActiveDamagesForLoaner = (loanerId, ignoreBookingId = "") => {
    const seen = new Set();
    const centralLifecycleById = new Map(
      loanerDamages
        .filter((entry) => entry.loanerId === loanerId)
        .map((entry) => [entry.id, entry])
    );
    const centralDamages = loanerDamages
      .filter((entry) => entry.loanerId === loanerId && entry.status === "active")
      .map((entry) => ({ ...entry, sourceBookingId: entry.bookingId, sourceDate: entry.reportedAt }));
    const bookingDamages = bookings
      .filter((booking) => booking.loanerId === loanerId && booking.id !== ignoreBookingId)
      .sort((left, right) => String(right.createdAt || "").localeCompare(String(left.createdAt || "")))
      .flatMap((booking) => {
        const handoverDamages = getBookingDamageEntries(booking).map((entry) => ({
          ...entry,
          loanerId,
          centralDamageId: getCentralLoanerDamageId(loanerId, entry.id),
          sourceBookingId: booking.id,
          sourceDate: booking.createdAt,
        }));
        const returnDamages = getReturnDamageGroups(booking).map((entry) => ({
          ...entry,
          loanerId,
          url: entry.photos[0]?.url || "",
          centralDamageId: getCentralLoanerDamageId(loanerId, entry.id),
          sourceBookingId: booking.id,
          sourceDate:
            booking.checkedInAt ||
            booking.actualReturnDate ||
            booking.createdAt,
        }));
        return [...handoverDamages, ...returnDamages];
      })
      .filter((entry) => {
        const lifecycleEntry = centralLifecycleById.get(entry.centralDamageId);
        return !lifecycleEntry || lifecycleEntry.status === "active";
      });
    return mergeActiveDamageEntries(centralDamages, bookingDamages)
      .filter((entry) => {
        const key = `${entry.url || entry.photoUrl || ""}_${entry.description || ""}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  const getLastReturnedKmForLoaner = (loanerId) => {
    const latestReturn = bookings
      .filter((booking) => booking.loanerId === loanerId && booking.kmEnd)
      .sort((left, right) =>
        String(right.checkedInAt || right.actualReturnDate || right.createdAt || "").localeCompare(
          String(left.checkedInAt || left.actualReturnDate || left.createdAt || "")
        )
      )[0];
    return latestReturn?.kmEnd || "";
  };

  const openAvailabilityInspector = (loanerId) => {
    const selectedLoaner = loaners.find((entry) => entry.id === loanerId);
    const defaultStart =
      wizard.loanerId === loanerId && wizard.plannedPickupDate
        ? wizard.plannedPickupDate
        : getLoanerAvailabilityDate(loanerId);
    const defaultEnd =
      wizard.loanerId === loanerId && wizard.expectedReturnDate
        ? wizard.expectedReturnDate
        : defaultStart;

    setWizard((prev) => ({
      ...prev,
      loanerId,
      branchWarningAccepted:
        selectedLoaner &&
        profile?.branch &&
        selectedLoaner.homeBranch &&
        profile.branch !== selectedLoaner.homeBranch
          ? prev.branchWarningAccepted
          : false,
      submissionError: "",
    }));

    setAvailabilityInspector({
      open: true,
      loanerId,
      startDate: defaultStart,
      endDate: defaultEnd,
      error: "",
    });
  };

  const closeAvailabilityInspector = () =>
    setAvailabilityInspector((prev) => ({ ...prev, open: false, error: "" }));

  const applyAvailabilityInspector = () => {
    if (!availabilityInspector.loanerId) {
      setAvailabilityInspector((prev) => ({
        ...prev,
        error: "Bitte zuerst ein Fahrzeug wählen.",
      }));
      return;
    }

    const selectedLoaner = loaners.find((loaner) => loaner.id === availabilityInspector.loanerId);
    const permanentlyCheckedOut = selectedLoaner ? isPermanentlyCheckedOutLoaner(selectedLoaner) : false;
    if (selectedLoaner && isPermanentlyCheckedOutLoaner(selectedLoaner)) {
      setAvailabilityInspector((prev) => ({
        ...prev,
        error:
          "Dieses Fahrzeug ist dauerhaft verliehen und kann nicht für eine neue Übergabe reserviert werden.",
      }));
      return;
    }

    if (!availabilityInspector.startDate || !availabilityInspector.endDate) {
      setAvailabilityInspector((prev) => ({
        ...prev,
        error: "Bitte Verleihbeginn und Rückgabe setzen.",
      }));
      return;
    }

    if (availabilityInspector.endDate < availabilityInspector.startDate) {
      setAvailabilityInspector((prev) => ({
        ...prev,
        error: "Die Rückgabe muss am oder nach dem Verleihbeginn liegen.",
      }));
      return;
    }

    const conflicts = getLoanerConflictsForRange(
      availabilityInspector.loanerId,
      availabilityInspector.startDate,
      availabilityInspector.endDate
    );

    if (conflicts.length > 0) {
      setAvailabilityInspector((prev) => ({
        ...prev,
        error:
          "Das Fahrzeug ist im gewählten Zeitraum nicht verfügbar. Bitte einen anderen Zeitraum oder ein anderes Fahrzeug wählen.",
      }));
      return;
    }

    setWizard((prev) => ({
      ...prev,
      loanerId: availabilityInspector.loanerId,
      plannedPickupDate: availabilityInspector.startDate,
      expectedReturnDate: availabilityInspector.endDate,
      submissionError: "",
    }));
    closeAvailabilityInspector();
  };

  const customerReplacementLoaners = useMemo(
    () =>
      loaners.filter(
        (loaner) =>
          loaner.isActive &&
          loaner.usageType === "customer_loaner" &&
          !isLoanerUnavailable(loaner, availabilityMap) &&
          !availabilityMap[loaner.id]
      ),
    [availabilityMap, loaners]
  );

  const secondaryAvailableLoaners = useMemo(
    () =>
      loaners.filter(
        (loaner) =>
          loaner.isActive &&
          (loaner.usageType === "employee_vehicle" ||
            loaner.usageType === "long_term_assignment") &&
          !isLoanerUnavailable(loaner, availabilityMap) &&
          !availabilityMap[loaner.id]
      ),
    [availabilityMap, loaners]
  );

  const unavailableCustomerLoaners = useMemo(
    () =>
      loaners.filter(
        (loaner) =>
          loaner.isActive &&
          loaner.usageType === "customer_loaner" &&
          isLoanerUnavailable(loaner, availabilityMap)
      ),
    [availabilityMap, loaners]
  );

  const handleLogin = async (event) => {
    event.preventDefault();
    if (authLockUntil && authLockUntil > Date.now()) {
      setAuthError("Zu viele Anmeldeversuche. Bitte kurz warten und erneut versuchen.");
      return;
    }
    setAuthSubmitting(true);
    setAuthError("");

    try {
      const response = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: authEmail.trim(),
          password: authPassword,
        }),
      });
      const payload = await response.json();

      if (!response.ok || !payload.session) {
        const retryAfter = Number(response.headers.get("Retry-After") || 0);
        if (response.status === 429) {
          setAuthLockUntil(Date.now() + Math.max(60, retryAfter) * 1000);
          setAuthError("Zu viele Anmeldeversuche. Bitte kurz warten und erneut versuchen.");
        } else {
          setAuthError("Login fehlgeschlagen. Bitte Zugangsdaten prüfen.");
        }
        const nextFailures = authFailures + 1;
        setAuthFailures(nextFailures);
        if (nextFailures >= 5) {
          setAuthLockUntil(Date.now() + 60_000);
        }
        setAuthSubmitting(false);
        return;
      }

      const { error } = await supabase.auth.setSession({
        access_token: payload.session.access_token,
        refresh_token: payload.session.refresh_token,
      });

      if (error) {
        throw error;
      }

      setAuthFailures(0);
      setAuthLockUntil(null);
    } catch (error) {
      setAuthError("Login fehlgeschlagen. Bitte Zugangsdaten prüfen.");
      const nextFailures = authFailures + 1;
      setAuthFailures(nextFailures);
      if (nextFailures >= 5) {
        setAuthLockUntil(Date.now() + 60_000);
      }
    }

    setAuthSubmitting(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setCards([]);
    setLoaners([]);
    setBluePlates([]);
    setBookings([]);
    setProfile(null);
    setProfileDebug(null);
    setAdminUsers([]);
    setAdminUsersMessage("");
    setDbStatus("Abgemeldet");
    setIntakeDraft(getDefaultIntakeDraft());
    setPasswordState({
      open: false,
      nextPassword: "",
      confirmPassword: "",
      error: "",
      success: "",
      saving: false,
      required: false,
      passwordUpdated: false,
    });
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    setPasswordState((prev) => ({ ...prev, error: "", success: "" }));

    if (passwordState.nextPassword.length < 10) {
      setPasswordState((prev) => ({
        ...prev,
        error: "Das neue Passwort sollte mindestens 10 Zeichen haben.",
      }));
      return;
    }

    if (passwordState.nextPassword !== passwordState.confirmPassword) {
      setPasswordState((prev) => ({
        ...prev,
        error: "Die Passwörter stimmen nicht überein.",
      }));
      return;
    }

    setPasswordState((prev) => ({ ...prev, saving: true }));
    if (!passwordState.passwordUpdated) {
      const { error } = await supabase.auth.updateUser({
        password: passwordState.nextPassword,
      });

      if (error) {
        setPasswordState((prev) => ({
          ...prev,
          saving: false,
          error: error.message,
        }));
        return;
      }
      setPasswordState((prev) => ({
        ...prev,
        passwordUpdated: true,
      }));
    }

    try {
      const activationResponse = await fetch(
        "/api/session-profile?completeActivation=true",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );
      const activationResult = await activationResponse.json();
      if (!activationResponse.ok) {
        throw new Error(
          activationResult.error || "Aktivierung konnte nicht bestätigt werden."
        );
      }
      if (activationResult.activationNotification?.retryPending) {
        throw new Error(
          "Die Aktivierungsbenachrichtigung konnte noch nicht versendet werden."
        );
      }
      if (activationResult.profile) {
        setProfile(activationResult.profile);
      }
    } catch (activationError) {
      setPasswordState((prev) => ({
        ...prev,
        saving: false,
        error: `Das Passwort wurde gespeichert. ${activationError.message} Bitte erneut auf „Passwort aktualisieren“ klicken.`,
      }));
      return;
    }

    setPasswordState({
      open: false,
      nextPassword: "",
      confirmPassword: "",
      error: "",
      success: "Passwort erfolgreich geändert.",
      saving: false,
      required: false,
      passwordUpdated: false,
    });
  };

  const runAdminUserAction = async (payload) => {
    const response = await fetch("/api/admin-users", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json();
    if (!response.ok) {
      const error = new Error(result.error || "Benutzeraktion fehlgeschlagen.");
      error.retryAfterSeconds = Number(result.retryAfterSeconds || 0);
      throw error;
    }

    return result;
  };

  const runSystemAction = async (payload) => {
    const response = await fetch("/api/system-status", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || "Systemaktion fehlgeschlagen.");
    }

    return result;
  };

  const refreshSystemStatus = async () => {
    if (!session || !isAdmin) return;
    setSystemStatusLoading(true);
    setSystemStatusMessage("Systemstatus wird geprüft ...");
    try {
      const result = await runSystemAction({ action: "status" });
      setSystemStatus(result);
      setSystemStatusMessage(`Systemstatus aktualisiert: ${fmtDateTime(result.checkedAt)}`);
    } catch (error) {
      setSystemStatusMessage(error.message);
    } finally {
      setSystemStatusLoading(false);
    }
  };

  const sendSystemTestEmail = async () => {
    setSystemStatusLoading(true);
    setSystemStatusMessage("Sende SMTP-Testmail ...");
    try {
      const result = await runSystemAction({
        action: "testEmail",
        recipient: systemTestEmail || session?.user?.email,
      });
      setSystemStatusMessage(`Testmail wurde an ${result.recipient} gesendet.`);
      await refreshSystemStatus();
    } catch (error) {
      setSystemStatusMessage(error.message);
    } finally {
      setSystemStatusLoading(false);
    }
  };

  const runLocalSmokeTest = async () => {
    const checks = [
      {
        label: "Daten geladen",
        ok: Array.isArray(cards) && Array.isArray(loaners) && Array.isArray(bookings),
        detail: `${cards.length} Aufträge, ${loaners.length} Fahrzeuge, ${bookings.length} Leihvorgänge`,
      },
      {
        label: "SMTP konfiguriert",
        ok: Boolean(systemStatus?.smtp?.configured),
        detail: systemStatus?.smtp?.configured ? "bereit" : "bitte Systemprüfung laden",
      },
      {
        label: "Foto-Storage",
        ok: Boolean(systemStatus?.storage?.configured) || Boolean(LOANER_MEDIA_BUCKET),
        detail: systemStatus?.storage?.configured ? "Bucket bereit" : "Fallback aktiv",
      },
      {
        label: "Bestätigungslinks",
        ok: bookings.every((booking) => booking.status !== "awaiting_customer_confirmation" || booking.confirmationToken),
        detail: "offene Kundenbestätigungen haben Token",
      },
      {
        label: "Auftrag-Leihwagen-Verknüpfung",
        ok: bookings.every((booking) => !booking.orderNumber || cards.some((card) => isBookingLinkedToCard(booking, card)) || !booking.cardId),
        detail: "Verknüpfung per UUID oder Auftragsnummer geprüft",
      },
      {
        label: "Testdatensatz möglich",
        ok: Boolean(cards.length > 0 && loaners.length > 0),
        detail: cards.length > 0 && loaners.length > 0
          ? "E2E-Test kann mit bestehendem Auftrag und Fahrzeug simuliert werden"
          : "Für einen echten Testlauf bitte mindestens einen Auftrag und ein Fahrzeug anlegen",
      },
      {
        label: "Fahrzeugdatenqualität",
        ok: buildLoanerDataQuality(loaners).length === 0,
        detail:
          buildLoanerDataQuality(loaners).length === 0
            ? "keine doppelten oder fehlenden Kernfelder gefunden"
            : `${buildLoanerDataQuality(loaners).length} Fahrzeuge mit Datenhinweisen`,
      },
      {
        label: "CSV/PDF Basis",
        ok: typeof exportBoardCsv === "function" && typeof buildBookingPdf === "function",
        detail: "Exportfunktionen verfügbar",
      },
    ];
    setSystemSmokeTest(checks);
    setSystemStatusMessage(
      checks.every((check) => check.ok)
        ? "Testlauf erfolgreich. Keine lokalen Auffälligkeiten gefunden."
        : "Testlauf abgeschlossen. Bitte markierte Punkte prüfen."
    );
  };

  const exportE2ETestPackage = () => {
    const sampleCard = cards[0] || null;
    const sampleBooking = bookings[0] || null;
    downloadJson("admin-e2e-testlauf", {
      exportedAt: new Date().toISOString(),
      purpose: "Manueller End-to-End-Test fuer Team Fraenkel",
      sampleOrder: sampleCard
        ? {
            id: sampleCard.id,
            orderNumber: sampleCard.orderNumber,
            title: sampleCard.title,
            gateStatus: getOrderGateStatus(sampleCard, sampleBooking),
          }
        : null,
      sampleBooking: sampleBooking
        ? {
            id: sampleBooking.id,
            orderNumber: sampleBooking.orderNumber,
            status: sampleBooking.status,
            confirmationLink: sampleBooking.confirmationToken
              ? `${window.location.origin}?confirmation=${sampleBooking.confirmationToken}`
              : "",
          }
        : null,
      checklist: [
        "1. Auftrag in der Annahme anlegen",
        "2. Falls ohne Leihwagen: E-Mail-Link öffnen, unterschreiben und bestätigen",
        "3. Falls mit Leihwagen: offenen Bedarf fortsetzen und Fotos vollständig aufnehmen",
        "4. Gemeinsame Bestätigungs-E-Mail öffnen, unterschreiben und bestätigen",
        "5. Leihwagenmodul: Vorgang muss in Bereit zur Ausgabe erscheinen",
        "6. Fahrzeug jetzt ausgeben klicken",
        "7. Rücknahme starten, Vergleichsfotos aufnehmen und abschließen",
        "8. Historie öffnen, PDF und Dokumentationspaket exportieren",
        "9. Protokoll nach Auftrag/E-Mail filtern und Ereignisse prüfen",
      ],
    });
    setSystemStatusMessage("E2E-Testpaket wurde exportiert.");
  };

  const updateLiveAcceptance = (stepId, checked) => {
    setLiveAcceptance((prev) => {
      const next = {
        ...prev,
        [stepId]: {
          checked,
          checkedAt: checked ? new Date().toISOString() : null,
          by: session?.user?.email || "",
        },
      };
      localStorage.setItem("teamFraenkelLiveAcceptance", JSON.stringify(next));
      return next;
    });
  };

  const resetLiveAcceptance = () => {
    localStorage.removeItem("teamFraenkelLiveAcceptance");
    setLiveAcceptance({});
    setSystemStatusMessage("Live-Abnahme wurde zurückgesetzt.");
  };

  const exportLiveAcceptance = () => {
    const steps = LIVE_ACCEPTANCE_STEPS.map((step) => ({
      ...step,
      checked: Boolean(liveAcceptance[step.id]?.checked),
      checkedAt: liveAcceptance[step.id]?.checkedAt || null,
      by: liveAcceptance[step.id]?.by || "",
    }));
    downloadJson("team-fraenkel-live-abnahme", {
      exportedAt: new Date().toISOString(),
      version: APP_BUILD_LABEL,
      completed: steps.filter((step) => step.checked).length,
      total: steps.length,
      steps,
    });
    setSystemStatusMessage("Live-Abnahmeprotokoll wurde exportiert.");
  };

  const copyProductionResetSql = () => {
    navigator.clipboard?.writeText(PRODUCTION_TEST_RESET_SQL);
    setSystemStatusMessage("Produktivtest-Reset SQL wurde in die Zwischenablage kopiert.");
  };

  const buildProductionReadinessChecks = () => {
    const schemaEntries = Object.entries(systemStatus?.schema || {});
    const schemaOk = schemaEntries.length > 0 && schemaEntries.every(([, status]) => status.ok);
    const appSettingsOk = systemStatus?.schema?.app_settings?.ok || technicianSettingsStatus.includes("zentral");
    const activeLoanerCount = loaners.filter((loaner) => loaner.isActive).length;
    const configuredTodoCount = Object.values(processConfig || {}).reduce(
      (sum, departments) =>
        sum + Object.values(departments || {}).filter((text) => String(text || "").trim().length > 0).length,
      0
    );
    return [
      {
        label: "Datenbankschema",
        ok: schemaOk,
        detail: schemaOk ? "Alle Pflichttabellen und Spalten vorhanden." : "Systemstatus prüfen und fehlende SQL-Migrationen ausführen.",
      },
      {
        label: "Zentrale Planung",
        ok: appSettingsOk,
        detail: appSettingsOk ? "Techniker-Konfiguration ist zentral bereit." : "Migration app_settings einspielen.",
      },
      {
        label: "Standard-To-dos",
        ok: configuredTodoCount >= 10,
        detail: `${configuredTodoCount} Falltyp-/Abteilungslisten gepflegt. Pflege unter Prozess Admin möglich.`,
      },
      {
        label: "E-Mail-Versand",
        ok: Boolean(systemStatus?.smtp?.verified || systemStatus?.smtp?.configured),
        detail: systemStatus?.smtp?.verified ? "SMTP getestet." : "SMTP ist konfiguriert, Testversand im Systemstatus prüfen.",
      },
      {
        label: "Foto-Speicher",
        ok: Boolean(systemStatus?.storage?.configured),
        detail: systemStatus?.storage?.configured ? "Storage ist bereit." : "Storage/Fallback prüfen, bevor echte Kundendaten verwendet werden.",
      },
      {
        label: "Testdaten-Reset",
        ok: cards.length === 0 && bookings.length === 0,
        detail: cards.length === 0 && bookings.length === 0
          ? "Keine offenen Test-Aufträge oder Leihwagenvorgänge geladen."
          : `${cards.length} Auftrag/Aufträge und ${bookings.length} Leihwagenvorgang/Vorgänge vorhanden.`,
      },
      {
        label: "Fahrzeugbestand",
        ok: activeLoanerCount >= 30,
        detail: `${activeLoanerCount} aktive Fahrzeuge geladen.`,
      },
      {
        label: "Live-Aktualisierung",
        ok: true,
        detail: "Realtime plus Polling-Fallback ist im Client aktiv.",
      },
      {
        label: "Offline-Warteschlange",
        ok: offlineQueueCount === 0 && offlineReturnQueueCount === 0,
        detail:
          offlineQueueCount === 0 && offlineReturnQueueCount === 0
            ? "Keine lokalen Vorgänge warten auf Synchronisierung."
            : `${offlineQueueCount} Übergabe(n), ${offlineReturnQueueCount} Rücknahme(n) warten auf Synchronisierung.`,
      },
      {
        label: "Betriebsmodus",
        ok: Boolean(APP_ENV_LABEL),
        detail: APP_MODE_IS_FALLBACK
          ? `Fallback ${APP_ENV_LABEL} aktiv. Vercel-Variable ist optional.`
          : `Aktuell sichtbar als ${APP_ENV_LABEL}.`,
      },
    ];
  };

  const exportProductionReadiness = (checks = buildProductionReadinessChecks()) => {
    downloadJson("team-fraenkel-produktivtest-freigabe", {
      exportedAt: new Date().toISOString(),
      version: APP_BUILD_LABEL,
      appMode: APP_ENV_LABEL,
      exportedBy: session?.user?.email || "",
      decision: checks.every((check) => check.ok) ? "GO" : "NO_GO",
      checks,
      counts: {
        cards: cards.length,
        bookings: bookings.length,
        loaners: loaners.length,
        offlineQueue: offlineQueueCount,
        offlineReturnQueue: offlineReturnQueueCount,
      },
    });
    setSystemStatusMessage("Produktivtest-Protokoll wurde exportiert.");
  };

  const exportEmergencyPackage = () => {
    downloadJson("team-fraenkel-notfall-export", {
      exportedAt: new Date().toISOString(),
      version: APP_BUILD_LABEL,
      appMode: APP_ENV_LABEL,
      exportedBy: session?.user?.email || "",
      cards,
      loaners,
      bookings,
      bluePlates,
      users: adminUsers.map((user) => ({
        id: user.id,
        email: user.email,
        fullName: user.full_name,
        role: user.role,
        branch: user.branch,
        department: user.department,
        isActive: user.is_active,
      })),
      recentAudit: systemStatus?.recentAudit || [],
      localQueues: {
        loanerBookings: readOfflineBookings(),
        loanerReturns: readOfflineReturns(),
      },
    });
    setSystemStatusMessage("Notfall-Export wurde erstellt.");
  };

  const healLoanerStatuses = async () => {
    if (!isAdmin) return;
    const confirmed = window.confirm(
      "Leihwagenstatus-Heilung starten?\n\nFahrzeuge ohne aktiven Leihwagenvorgang werden wieder auf verfügbar gesetzt. Dauerhaft verliehene Fahrzeuge bleiben unverändert."
    );
    if (!confirmed) return;
    const activeLoanerIds = new Set(bookings.filter(isActiveBooking).map((booking) => booking.loanerId));
    const candidates = loaners.filter(
      (loaner) =>
        loaner.isActive &&
        !isPermanentlyCheckedOutLoaner(loaner) &&
        !activeLoanerIds.has(loaner.id) &&
        loaner.status !== "available"
    );
    let updated = 0;
    for (const loaner of candidates) {
      const ok = await persistLoaner({ ...loaner, status: "available" });
      if (ok) updated += 1;
    }
    setSystemStatusMessage(
      updated > 0
        ? `${updated} Fahrzeug(e) wurden wieder auf verfügbar gesetzt.`
        : "Keine inkonsistenten Leihwagenstatus gefunden."
    );
  };

  const resetLoanerDemoState = async () => {
    const confirmed = window.confirm(
      "Sollen alle aktiven Leihwagenvorgänge für die Demo zurückgesetzt werden?"
    );

    if (!confirmed) return;

    setLoanerFormMessage("Setze Demo-Leihwagenstatus zurück ...");

    try {
      const response = await fetch("/api/reset-loaner-demo", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || "Demo-Reset fehlgeschlagen.");
      }

      setLoanerFormMessage(
        `Reset abgeschlossen: ${result.cancelledBookings} Buchungen zurückgesetzt, ${result.updatedLoaners} Leihwagen freigegeben.`
      );
      await loadWorkspaceData();
    } catch (error) {
      setLoanerFormMessage(error.message);
    }
  };

  const provisionPresetUsers = async () => {
    setAdminUsersMessage("Lege fehlende Annahme- und Techniker-Benutzer an ...");
    try {
      const result = await runAdminUserAction({
        action: "provisionPresetUsers",
        redirectTo: window.location.origin,
      });
      setAdminUsersMessage(
        `${result.created.length} Nutzer neu angelegt, ${result.invited.length} eingeladen, ${result.skipped.length} bereits aktiv oder eingeladen.`
      );
      await refreshAdminUsers();
    } catch (error) {
      setAdminUsersMessage(error.message);
    }
  };

  const createSingleUser = async () => {
    if (!userDraft.email) {
      setAdminUsersMessage("Bitte eine E-Mail-Adresse eintragen.");
      return;
    }
    if (!isAllowedNewUserEmail(userDraft.email)) {
      setAdminUsersMessage(NEW_USER_EMAIL_DOMAIN_ERROR);
      return;
    }

    setAdminUsersMessage("Lege Nutzer an ...");
    try {
      await runAdminUserAction({
        action: "createUser",
        email: userDraft.email.trim(),
        fullName: userDraft.fullName.trim(),
        role: userDraft.role,
        branch: userDraft.branch,
        department: userDraft.department,
        redirectTo: window.location.origin,
      });
      setAdminUsersMessage(
        `Nutzer ${userDraft.email.trim()} wurde angelegt und die Einladung wurde versendet.`
      );
      setUserDraft({
        email: "",
        fullName: "",
        role: "staff",
        branch: "",
        department: DEFAULT_DEPARTMENT,
      });
      await refreshAdminUsers();
    } catch (error) {
      setAdminUsersMessage(error.message);
    }
  };

  const updateUserProfile = async (user, patch) => {
    setAdminUsersMessage(`${user.email} wird aktualisiert ...`);
    try {
      await runAdminUserAction({
        action: "updateUserProfile",
        id: user.id,
        ...patch,
      });
      setAdminUsersMessage(`${user.email} wurde aktualisiert.`);
      await refreshAdminUsers();
    } catch (error) {
      setAdminUsersMessage(error.message);
    }
  };

  const toggleUserActive = async (user) => {
    setAdminUsersMessage(
      `${user.email} wird ${user.is_active ? "deaktiviert" : "aktiviert"} ...`
    );
    try {
      await runAdminUserAction({
        action: "toggleActive",
        id: user.id,
        isActive: !user.is_active,
      });
      setAdminUsersMessage(
        `${user.email} ist jetzt ${user.is_active ? "deaktiviert" : "aktiv"}.`
      );
      await refreshAdminUsers();
    } catch (error) {
      setAdminUsersMessage(error.message);
    }
  };

  const deleteDisabledUsers = async () => {
    const deletableUsers = adminUsers.filter(
      (user) =>
        user.is_active === false &&
        !PROTECTED_DISABLED_USER_EMAILS.has(
          String(user.email || "").trim().toLowerCase()
        )
    );
    const confirmed = window.confirm(
      `Alle deaktivierten Konten werden dauerhaft gelöscht.\n\nAktuell löschbar: ${
        deletableUsers.length
      }\n${
        deletableUsers.length
          ? `\n${deletableUsers.map((user) => user.email).join("\n")}\n`
          : ""
      }\nGeschützt und von der Löschung ausgeschlossen:\n${[
        ...PROTECTED_DISABLED_USER_EMAILS,
      ].join("\n")}\n\nFortfahren?`
    );
    if (!confirmed) return;

    setAdminUsersMessage("Prüfe und lösche alle deaktivierten Konten ...");
    try {
      const result = await runAdminUserAction({
        action: "deleteDisabledUsers",
      });
      setAdminUsersMessage(
        `${result.deleted.length} deaktivierte(s) Konto/Konten wurden dauerhaft gelöscht.${
          result.notFound.length
            ? ` ${result.notFound.length} Konto/Konten konnten nicht zugeordnet werden.`
            : ""
        }`
      );
      await refreshAdminUsers();
    } catch (error) {
      setAdminUsersMessage(error.message);
    }
  };

  const sendUserInvite = async (user) => {
    const cooldownKey = `user-invite:${user.id}`;
    const cooldownUntil = resendCooldowns[cooldownKey] || 0;
    if (cooldownUntil > Date.now()) {
      setAdminUsersMessage(
        `E-Mail wurde versendet. Ein erneuter Versand ist in ${formatResendWait(
          cooldownUntil
        )} möglich.`
      );
      return;
    }
    if (resendSending[cooldownKey]) return;

    setResendSending((prev) => ({ ...prev, [cooldownKey]: true }));
    setAdminUsersMessage(`Bereite Einladung für ${user.email} vor ...`);
    try {
      await runAdminUserAction({
        action: "sendInvite",
        id: user.id,
        email: user.email,
        redirectTo: window.location.origin,
      });

      const nextCooldownUntil =
        Date.now() + USER_INVITE_RESEND_COOLDOWN_MS;
      setResendClock(Date.now());
      setResendCooldowns((prev) => ({
        ...prev,
        [cooldownKey]: nextCooldownUntil,
      }));
      setAdminUsersMessage(
        `E-Mail wurde versendet. Ein erneuter Versand ist in ${formatResendWait(
          nextCooldownUntil
        )} möglich.`
      );

      await refreshAdminUsers();
    } catch (error) {
      if (error.retryAfterSeconds > 0) {
        const nextCooldownUntil =
          Date.now() + error.retryAfterSeconds * 1000;
        setResendClock(Date.now());
        setResendCooldowns((prev) => ({
          ...prev,
          [cooldownKey]: nextCooldownUntil,
        }));
        setAdminUsersMessage(
          `E-Mail wurde versendet. Ein erneuter Versand ist in ${formatResendWait(
            nextCooldownUntil
          )} möglich.`
        );
      } else {
        setAdminUsersMessage(error.message);
      }
    } finally {
      setResendSending((prev) => ({ ...prev, [cooldownKey]: false }));
    }
  };

  const persistCard = async (card) => {
    setCards((prev) => {
      const exists = prev.some((item) => item.id === card.id);
      return exists ? prev.map((item) => (item.id === card.id ? card : item)) : [card, ...prev];
    });

    const response = await fetch("/api/save-card", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ cardRow: cardToDbRow(card) }),
    });
    const payload = await response.json();

    if (!response.ok) {
      const errorMessage = payload.error || "unbekannter Fehler";
      setDbStatus(`Auftrag konnte nicht gespeichert werden (${errorMessage})`);
      card.lastSaveError = errorMessage;
      return false;
    }

    if (payload.card) {
      const savedCard = mapSupabaseCard(payload.card);
      setCards((prev) => {
        const exists = prev.some((item) => item.id === savedCard.id);
        return exists
          ? prev.map((item) => (item.id === savedCard.id ? savedCard : item))
          : [savedCard, ...prev];
      });
    }

    if (payload.removedColumns?.length) {
      setDbStatus(
        `Auftrag gespeichert. Einige optionale Felder fehlen noch in Supabase: ${payload.removedColumns.join(", ")}.`
      );
    }

    return payload.card ? mapSupabaseCard(payload.card) : card;
  };

  const buildUndoRestorePatch = (previousCard, updatedCard) => {
    const watchedKeys = [
      "role",
      "workStatus",
      "assignedTech",
      "due",
      "nextActionDue",
      "blockerType",
      "nextDepartment",
      "subs",
      "loanerFlow",
      "vehicleData",
      "history",
    ];
    return watchedKeys.reduce((patch, key) => {
      if (JSON.stringify(previousCard?.[key] ?? null) !== JSON.stringify(updatedCard?.[key] ?? null)) {
        patch[key] = previousCard?.[key] ?? null;
      }
      return patch;
    }, {});
  };

  const offerUndo = (label, previousCard, updatedCard = null) => {
    if (!previousCard?.id) return;
    const restorePatch = updatedCard ? buildUndoRestorePatch(previousCard, updatedCard) : previousCard;
    if (updatedCard && Object.keys(restorePatch).length === 0) return;
    const id = `${previousCard.id}_${Date.now()}`;
    setUndoAction({
      id,
      label,
      cardId: previousCard.id,
      restorePatch,
      expiresAt: Date.now() + 15_000,
    });
    window.setTimeout(() => {
      setUndoAction((current) => (current?.id === id ? null : current));
    }, 15_000);
  };

  const runUndoAction = async () => {
    if (!undoAction?.cardId || !undoAction?.restorePatch) return;
    const current = cards.find((card) => card.id === undoAction.cardId);
    if (!current) {
      setUndoAction(null);
      return;
    }
    const restored = {
      ...current,
      ...undoAction.restorePatch,
    };
    setUndoAction(null);
    await persistCard({
      ...restored,
      history: [
        ...(restored.history || []),
        {
          ts: new Date().toISOString(),
          by: profile?.full_name || session?.user?.email || CREATOR,
          action: `Änderung rückgängig gemacht: ${undoAction.label}`,
        },
      ],
    });
    setDbStatus(`Rückgängig gemacht: ${undoAction.label}`);
  };

  const persistLoaner = async (loaner) => {
    const row = loanerToDbRow(loaner);
    let { error } = await supabase.from("loaners").upsert(row);
    if (
      error &&
      String(error.message || "").toLowerCase().includes("column") &&
      /(tire_type|has_depot|vignette_date|has_parking_permit|permanently_checked_out)/i.test(
        error.message || ""
      )
    ) {
      const fallbackRow = { ...row };
      delete fallbackRow.tire_type;
      delete fallbackRow.has_depot;
      delete fallbackRow.vignette_date;
      delete fallbackRow.has_parking_permit;
      delete fallbackRow.permanently_checked_out;
      const retry = await supabase.from("loaners").upsert(fallbackRow);
      error = retry.error;
      if (!error) {
        setLoanerFormMessage(
          "Leihwagen gespeichert. Einige neue Fahrzeugfelder fehlen noch in Supabase und werden nach SQL-Migration vollständig gespeichert."
        );
      }
    }
    if (error) {
      setLoanerFormMessage(error.message);
      return false;
    }

    setLoaners((prev) => {
      const exists = prev.some((item) => item.id === loaner.id);
      return exists ? prev.map((item) => (item.id === loaner.id ? loaner : item)) : [...prev, loaner];
    });
    return true;
  };

  const uploadLoanerMediaValue = async (bookingId, slot, value) => {
    if (!isInlineMedia(value)) return value;
    try {
      setMediaSyncStatus(`Foto ${slot} wird hochgeladen ...`);
      const blob = await dataUrlToBlob(value);
      const path = `${bookingId}/${slot}_${Date.now()}.jpg`;
      const { error } = await supabase.storage
        .from(LOANER_MEDIA_BUCKET)
        .upload(path, blob, { contentType: "image/jpeg", upsert: true });
      if (error) throw error;
      const { data } = supabase.storage.from(LOANER_MEDIA_BUCKET).getPublicUrl(path);
      setMediaSyncStatus(`Foto ${slot} synchronisiert.`);
      return data?.publicUrl || value;
    } catch {
      setMediaSyncStatus(`Foto ${slot} bleibt lokal im Vorgang gespeichert.`);
      return value;
    }
  };

  const persistCentralLoanerDamage = async ({ booking, entry, status = "active", deletedPatch = {} }) => {
    if (!booking?.loanerId || !entry?.id) return;
    const row = {
      id: entry.centralDamageId || getCentralLoanerDamageId(booking.loanerId, entry.id),
      loaner_id: booking.loanerId,
      booking_id: booking.id,
      status,
      photo_url: entry.url || "",
      description: entry.description || "",
      reported_by:
        entry.reportedBy ||
        booking.returnEmployeeName ||
        booking.handoverEmployeeName ||
        profile?.email ||
        session?.user?.email ||
        "",
      reported_at:
        entry.reportedAt ||
        booking.checkedInAt ||
        booking.createdAt ||
        new Date().toISOString(),
      ...deletedPatch,
    };
    const { error } = await supabase.from("loaner_damages").upsert(row);
    if (error) throw error;
    setLoanerDamages((prev) => {
      const mapped = mapSupabaseLoanerDamage(row);
      return prev.some((item) => item.id === mapped.id)
        ? prev.map((item) => (item.id === mapped.id ? mapped : item))
        : [mapped, ...prev];
    });
  };

  const persistCentralDamageEntries = async (booking) => {
    await Promise.all(
      (booking.damageEntries || []).map((entry) => {
        const centralDamageId =
          entry.centralDamageId ||
          getCentralLoanerDamageId(booking.loanerId, entry.id);
        const currentLifecycleEntry = loanerDamages.find(
          (item) => item.id === centralDamageId
        );
        const status = entry.deletedAt
          ? "deleted"
          : currentLifecycleEntry?.status &&
              currentLifecycleEntry.status !== "active"
            ? currentLifecycleEntry.status
            : "active";
        const deletedPatch = entry.deletedAt
          ? {
              deleted_by: entry.deletedBy || "",
              deleted_at: entry.deletedAt,
              delete_reason: entry.deleteReason || "",
            }
          : status !== "active" && currentLifecycleEntry
            ? {
                deleted_by: currentLifecycleEntry.deletedBy || "",
                deleted_at: currentLifecycleEntry.deletedAt || null,
                delete_reason: currentLifecycleEntry.deleteReason || "",
              }
            : {};
        return persistCentralLoanerDamage({
          booking,
          entry: { ...entry, centralDamageId },
          status,
          deletedPatch,
        });
      })
    );
  };

  const uploadLoanerMediaMap = async (bookingId, prefix, media = {}) => {
    const entries = await Promise.all(
      Object.entries(media || {}).map(async ([key, value]) => [
        key,
        await uploadLoanerMediaValue(bookingId, `${prefix}_${key}`, value),
      ])
    );
    return Object.fromEntries(entries);
  };

  const prepareBookingMediaForSave = async (booking) => ({
    ...booking,
    conditionPhotos: await uploadLoanerMediaMap(booking.id, "condition", booking.conditionPhotos),
    driverLicenseFront: await uploadLoanerMediaValue(
      booking.id,
      "driver_license_front",
      booking.driverLicenseFront
    ),
    driverLicenseBack: await uploadLoanerMediaValue(
      booking.id,
      "driver_license_back",
      booking.driverLicenseBack
    ),
    damageEntries: await Promise.all(
      (booking.damageEntries || []).map(async (entry, index) => ({
        ...entry,
        url: await uploadLoanerMediaValue(booking.id, `damage_${index}`, entry.url),
      }))
    ),
    returnPhotos: await Promise.all(
      (booking.returnPhotos || []).map(async (entry, index) => ({
        ...entry,
        url: await uploadLoanerMediaValue(booking.id, `return_${entry.key || index}`, entry.url),
      }))
    ),
    signature: await uploadLoanerMediaValue(booking.id, "signature", booking.signature),
  });

  const persistBooking = async (booking) => {
    setMediaSyncStatus("Leihwagenvorgang wird gespeichert ...");
    const bookingForSave = await prepareBookingMediaForSave(booking);
    const row = bookingToDbRow(bookingForSave);
    let { error } = await supabase.from("loaner_bookings").upsert(row);

    if (
      error &&
      String(error.message || "").toLowerCase().includes("column") &&
      /(handover_employee_name|return_employee_name|card_uuid|confirmation_events)/i.test(error.message || "")
    ) {
      const fallbackRow = { ...row };
      delete fallbackRow.handover_employee_name;
      delete fallbackRow.return_employee_name;
      delete fallbackRow.card_uuid;
      delete fallbackRow.confirmation_events;
      const retry = await supabase.from("loaner_bookings").upsert(fallbackRow);
      error = retry.error;
    }

    if (
      error &&
      String(error.message || "").toLowerCase().includes("invalid input") &&
      /(card_id|bigint|uuid)/i.test(error.message || "")
    ) {
      const retryRow = { ...row, card_id: null };
      const retry = await supabase.from("loaner_bookings").upsert(retryRow);
      error = retry.error;
    }

    if (error) {
      if (isTransientSaveError(error)) {
        const queued = queueOfflineBooking(bookingForSave);
        if (!queued) {
          throw new Error(
            "Verbindung instabil und Offline-Speicher voll. Fotos wurden nicht gespeichert. Bitte Verbindung prüfen und erneut abschließen."
          );
        }
        setDbStatus(
          "Leihwagenvorgang wurde lokal zwischengespeichert und wird automatisch synchronisiert, sobald die Verbindung stabil ist."
        );
        setMediaSyncStatus("Offline gespeichert. Synchronisiert automatisch bei stabiler Verbindung.");
      } else {
        throw error;
      }
    }

    const nextBooking = {
      ...bookingForSave,
      driverLicenseFrontPresent: Boolean(bookingForSave.driverLicenseFront),
      driverLicenseBackPresent: Boolean(bookingForSave.driverLicenseBack),
      licensePresenceLoaded: true,
      mediaLoaded: true,
    };

    setBookings((prev) => {
      const exists = prev.some((item) => item.id === nextBooking.id);
      return exists
        ? prev.map((item) => (item.id === nextBooking.id ? nextBooking : item))
        : [nextBooking, ...prev];
    });
    setMediaSyncStatus("Leihwagenvorgang synchronisiert.");
    return nextBooking;
  };

  const hydrateBookingMedia = async (bookingId) => {
    const current = bookings.find((booking) => booking.id === bookingId);
    if (!current || current.mediaLoaded) return current;

    const { data, error } = await supabase
      .from("loaner_bookings")
      .select("*")
      .eq("id", bookingId)
      .maybeSingle();

    if (error) {
      setDbStatus(`Medien konnten nicht geladen werden (${error.message})`);
      return current;
    }

    if (!data) return current;

    const hydrated = mapSupabaseBooking(data);
    setBookings((prev) =>
      prev.map((booking) => (booking.id === bookingId ? hydrated : booking))
    );
    return hydrated;
  };

  const updateCard = (cardId, updater, options = {}) => {
    const current = cards.find((card) => card.id === cardId);
    if (!current) return;
    const previous =
      typeof structuredClone === "function"
        ? structuredClone(current)
        : JSON.parse(JSON.stringify(current));
    const updated = updater(current);
    if (options.undoLabel) {
      offerUndo(options.undoLabel, previous, updated);
    }
    persistCard(updated);
  };

  const clearIntakeFieldError = (field) => {
    setIntakeFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const buildIntakeCardFromDraft = ({ forceLoaner = false } = {}) => {
    const title = intakeDraft.title.trim();
    const customerFirstName = intakeDraft.customerFirstName.trim();
    const customerLastName = intakeDraft.customerLastName.trim();
    const customerName = `${customerFirstName} ${customerLastName}`.trim();
    const licensePlate = intakeDraft.licensePlate.trim();
    const missingFields = getRequiredIntakeFields(intakeDraft);

    if (missingFields.length > 0) {
      return {
        error: `Bitte folgende Pflichtfelder ausfüllen: ${missingFields.join(", ")}.`,
      };
    }

    const orderNumber = intakeDraft.orderNumber.trim();
    const caseType = intakeDraft.caseType || "service";
    const orderType = intakeDraft.orderType || "external";
    const internalOrder = orderType === "internal";
    const selectedTechnician = getEmployeeById(technicians, intakeDraft.assignedTech);
    const hasTechnicianAssignment = Boolean(selectedTechnician);
    const usesDefaultChecklist =
      intakeDraft.subtasksText.trim() === getDefaultIntakeSubtasks().trim();
    const subtasksSource = intakeDraft.subtasksText
      .split("\n")
      .map((entry) => entry.trim())
      .filter(Boolean);
    const initialChecklist =
      subtasksSource.length > 0 && !usesDefaultChecklist
        ? parseSubtasks(subtasksSource)
        : getConfiguredChecklistForDepartment(caseType, DEFAULT_DEPARTMENT);

    const needsLoaner = forceLoaner || caseType === "loaner" || intakeDraft.needsLoaner;
    const targetRole = hasTechnicianAssignment ? "Werkstatt" : DEFAULT_DEPARTMENT;
    const cardChecklist = hasTechnicianAssignment
      ? getConfiguredChecklistForDepartment(caseType, "Werkstatt")
      : initialChecklist;
    const assignmentApproval = hasTechnicianAssignment
      ? {
          status: "pending_workshop_lead",
          targetTechId: selectedTechnician.id,
          targetTechName: selectedTechnician.name,
          requestedBy: profile?.full_name || session?.user?.email || CREATOR,
          requestedAt: new Date().toISOString(),
          approvedBy: "",
          approvedAt: null,
        }
      : null;

    const card = {
      id: makeCardId(),
      orderNumber,
      customerName,
      customerFirstName,
      customerLastName,
      customerGender: intakeDraft.customerGender,
      customerEmail: intakeDraft.customerEmail.trim(),
      licensePlate,
      vehicleData: {
        brand: intakeDraft.vehicleBrand.trim(),
        model: intakeDraft.vehicleModel.trim(),
        firstRegistrationDate: intakeDraft.firstRegistrationDate,
        vin: intakeDraft.vin.trim(),
        engineType: intakeDraft.engineType,
        transmissionType: intakeDraft.transmissionType,
        mileage: intakeDraft.mileage,
        nationalCode: intakeDraft.nationalCode.trim(),
        curbWeight: intakeDraft.curbWeight,
        grossWeight: intakeDraft.grossWeight,
        estimatedHours: getEstimatedHours(intakeDraft),
      },
      loanerFlow: {
        orderType,
        intakeSignature: "",
        signatureUnavailable: internalOrder,
        signatureMode: internalOrder ? "not_required" : "email_confirmation",
        intakeAcceptedAt: internalOrder ? new Date().toISOString() : null,
        orderConfirmationToken: internalOrder ? "" : makeToken(),
        orderConfirmationStatus: internalOrder ? "not_required" : "pending_email",
        orderConfirmationCreatedAt: internalOrder ? null : new Date().toISOString(),
        orderConfirmationSentAt: null,
        orderConfirmationViewedAt: null,
        orderConfirmationAcceptedAt: null,
        assignmentApproval,
      },
      title,
      role: targetRole,
      caseType,
      prio: intakeDraft.prio,
      due: intakeDraft.due,
      needsSpenglerei: false,
      needsLoaner,
      assignedTech: intakeDraft.assignedTech || null,
      blockerType: hasTechnicianAssignment ? "approval" : "",
      nextAction:
        cardChecklist[0]?.text || "Kundenauftrag prüfen und weiter disponieren",
      nextActionDue: intakeDraft.due,
      responsibleLabel: profile?.full_name || session?.user?.email || "",
      escalationLevel: 0,
      branch: intakeDraft.branch || profile?.branch || "",
      nextDepartment: getNextDepartmentForCard({ caseType, role: targetRole }),
      sourceLabel: intakeDraft.sourceLabel || "manuell",
      externalSystem: intakeDraft.externalSystem || "",
      externalReference: intakeDraft.externalReference.trim(),
      managementNotes: [
        intakeDraft.sourceLabel ? `Quelle: ${intakeDraft.sourceLabel}` : "",
        intakeDraft.externalReference.trim()
          ? `Externe Referenz: ${intakeDraft.externalReference.trim()}`
          : "",
        intakeDraft.notes.trim(),
      ]
        .filter(Boolean)
        .join(" | "),
      invoice: null,
      parts: [],
      questions: hasTechnicianAssignment
        ? [
            {
              id: `question_${Date.now()}`,
              text: `Abgabe an ${selectedTechnician.name} angefordert.`,
              author: profile?.full_name || session?.user?.email || CREATOR,
              createdAt: new Date().toISOString(),
              answered: false,
              answer: "",
              answeredAt: null,
            },
          ]
        : [],
      subs: cardChecklist,
      watchers: [],
      workStatus: hasTechnicianAssignment ? WORK_STATUS.BLOCKED : WORK_STATUS.NEW,
      history: [
        {
          ts: new Date().toISOString(),
          by: CREATOR,
          action: intakeDraft.notes.trim()
            ? `Manueller Auftrag angelegt: ${intakeDraft.notes.trim()}`
            : "Manueller Auftrag angelegt",
        },
        ...(internalOrder
          ? [
              {
                ts: new Date().toISOString(),
                by: CREATOR,
                action: "Interner Auftrag: Kundenbestätigung nicht erforderlich",
              },
            ]
          : []),
        ...(hasTechnicianAssignment
          ? [
              {
                ts: new Date().toISOString(),
                by: profile?.full_name || session?.user?.email || CREATOR,
                action: `Technikerfreigabe angefordert: ${selectedTechnician.name}`,
              },
            ]
          : []),
      ],
      media: [],
    };

    return { card, orderNumber };
  };

  const saveIntakeCard = async ({ forceLoaner = false, openLoanerWizard = false } = {}) => {
    const missingFieldKeys = getRequiredIntakeFieldKeys(intakeDraft);
    if (missingFieldKeys.length > 0) {
      const nextErrors = Object.fromEntries(missingFieldKeys.map((field) => [field, true]));
      setIntakeFieldErrors(nextErrors);
      setIntakeTab(INTAKE_FIELD_TABS[missingFieldKeys[0]] || "core");
    } else {
      setIntakeFieldErrors({});
    }
    const prepared = buildIntakeCardFromDraft({ forceLoaner });
    if (prepared.error) {
      setIntakeDraft((prev) => ({
        ...prev,
        error: prepared.error,
        success: "",
      }));
      return;
    }

    const { card, orderNumber } = prepared;

    setIntakeDraft((prev) => ({ ...prev, saving: true, error: "", success: "" }));
    const savedCard = await persistCard(card);
    if (!savedCard) {
      setIntakeDraft((prev) => ({
        ...prev,
        saving: false,
        error: `Auftrag konnte nicht gespeichert werden${card.lastSaveError ? `: ${card.lastSaveError}` : "."}`,
      }));
      return;
    }

    let confirmationNotice = "";
    if (
      !isInternalOrder(savedCard) &&
      !savedCard.needsLoaner &&
      savedCard.customerEmail &&
      savedCard.loanerFlow?.orderConfirmationToken
    ) {
      try {
        const mailPayload = await sendOrderConfirmationEmail(savedCard);
        confirmationNotice =
          mailPayload.sent === false
            ? ` Bestätigungslink wurde erzeugt, aber nicht per E-Mail versendet: ${mailPayload.reviewUrl}`
            : ` Bestätigung wurde an ${savedCard.customerEmail} gesendet.`;
      } catch (error) {
        confirmationNotice = ` Auftrag wurde gespeichert, die Bestätigungs-E-Mail konnte aber nicht versendet werden (${error.message}).`;
      }
    }

    setIntakeDraft({
      ...getDefaultIntakeDraft(),
      branch: profile?.branch || "",
      assignedTech: "",
      success: openLoanerWizard
        ? `Auftrag ${savedCard.orderNumber || orderNumber} wurde angelegt. Leihwagenvergabe startet jetzt.`
        : forceLoaner
          ? `Leihwagenbedarf für Auftrag ${savedCard.orderNumber || orderNumber} wurde angemeldet.`
          : `Auftrag ${savedCard.orderNumber || orderNumber} wurde angelegt.${isInternalOrder(savedCard) ? " Interner Auftrag: keine Kundenbestätigung erforderlich." : confirmationNotice}${
              savedCard.loanerFlow?.assignmentApproval?.status === "pending_workshop_lead"
                ? " Techniker-Zuweisung wartet auf Freigabe der Werkstättenleitung."
                : ""
            }`,
    });
    localStorage.removeItem(INTAKE_DRAFT_STORAGE_KEY);
    setIntakeDraftSavedAt(null);
    if (savedCard.loanerFlow?.assignmentApproval?.status === "pending_workshop_lead") {
      setIntakeWorkspaceTab("approvals");
      setTaskDetailCardId(savedCard.id);
    }
    setUi((prev) => ({ ...prev, activeView: "Annahme" }));
    if (openLoanerWizard) {
      startLoanerWizard(savedCard);
    }
  };

  const createIntakeCard = async () => {
    await saveIntakeCard();
  };

  const createLoanerDemandFromIntake = async () => {
    await saveIntakeCard({ forceLoaner: true });
  };

  const createAndAssignLoanerFromIntake = async () => {
    await saveIntakeCard({ forceLoaner: true, openLoanerWizard: true });
  };

  const toggleSub = (cardId, subId) => {
    let shouldOfferHandover = false;
    let nextCardForHandover = null;
    updateCard(cardId, (card) => {
      const nextSubs = (card.subs || []).map((sub) =>
        sub.id === subId ? { ...sub, done: !sub.done } : sub
      );
      const changedToDone = nextSubs.find((sub) => sub.id === subId)?.done;
      shouldOfferHandover = Boolean(changedToDone && nextSubs.length > 0 && !nextSubs.some(isBlockingSubtask));
      nextCardForHandover = {
        ...card,
        subs: nextSubs,
        workStatus:
          card.workStatus === WORK_STATUS.NEW && changedToDone ? WORK_STATUS.IN_WORK : card.workStatus,
        nextAction: nextSubs.find(isBlockingSubtask)?.text || card.nextAction,
      };
      return nextCardForHandover;
    });

    if (shouldOfferHandover && nextCardForHandover) {
      window.setTimeout(() => {
        const confirmed = window.confirm(
          "Alle To-dos dieser Abteilung sind erledigt. Soll das Fahrzeug jetzt an die nächste Abteilung übergeben werden?"
        );
        if (confirmed) doHandover(nextCardForHandover);
      }, 120);
    }
  };

  const addDepartmentTodoToCard = (cardId, text) => {
    const trimmed = String(text || "").trim();
    if (!trimmed) return;
    updateCard(cardId, (card) => ({
      ...card,
      subs: [
        ...(card.subs || []),
        {
          id: `sub_${Date.now()}`,
          text: trimmed,
          done: false,
        },
      ],
      history: [
        ...(card.history || []),
        {
          ts: new Date().toISOString(),
          by: profile?.full_name || session?.user?.email || CREATOR,
          action: `To-do hinzugefügt: ${trimmed}`,
        },
      ],
    }));
  };

  const addWorkshopQuickAction = (card, text, { requestApproval = false } = {}) => {
    if (!card || !text) return;
    if (requestApproval) {
      requestDepartmentApproval(card, `Zusatzarbeit aus der Werkstatt angefordert: ${text}`, {
        requestType: "change_request",
        requiresApproval: true,
      });
      return;
    }
    addDepartmentTodoToCard(card.id, text);
  };

  const startWorkshopCard = (card) => {
    if (!card) return;
    const tech = profileTechnicianId ? getEmployeeById(technicians, profileTechnicianId) : null;
    updateCard(card.id, (current) => ({
      ...current,
      assignedTech: current.assignedTech || tech?.id || current.assignedTech,
      workStatus: current.workStatus === WORK_STATUS.NEW ? WORK_STATUS.IN_WORK : current.workStatus,
      nextAction: "In Bearbeitung",
      history: [
        ...(current.history || []),
        {
          ts: new Date().toISOString(),
          by: profile?.full_name || session?.user?.email || CREATOR,
          action: current.assignedTech
            ? "Werkstattauftrag gestartet"
            : `Werkstattauftrag übernommen${tech?.name ? ` von ${tech.name}` : ""}`,
        },
      ],
    }));
  };

  const openWorkshopBlocker = (card, reason = "parts") => {
    if (!card) return;
    setWorkshopBlockerModal({
      open: true,
      cardId: card.id,
      reason,
      note: "",
    });
  };

  const submitWorkshopBlocker = () => {
    const card = cards.find((item) => item.id === workshopBlockerModal.cardId);
    if (!card) return;
    const reasonLabel = getBlockerLabel(workshopBlockerModal.reason);
    const note = workshopBlockerModal.note.trim();
    updateCard(card.id, (current) => ({
      ...current,
      blockerType: workshopBlockerModal.reason,
      workStatus: WORK_STATUS.BLOCKED,
      nextAction: note || reasonLabel,
      questions: [
        ...(current.questions || []),
        {
          id: `q_${Date.now()}`,
          text: note ? `${reasonLabel}: ${note}` : reasonLabel,
          author: profile?.full_name || session?.user?.email || CREATOR,
          createdAt: new Date().toISOString(),
          answered: false,
          answer: "",
        },
      ],
      history: [
        ...(current.history || []),
        {
          ts: new Date().toISOString(),
          by: profile?.full_name || session?.user?.email || CREATOR,
          action: `Ich kann nicht weiter: ${reasonLabel}${note ? ` (${note})` : ""}`,
        },
      ],
    }));
    setDbStatus("Blocker wurde gespeichert und im Auftrag dokumentiert.");
    setWorkshopBlockerModal({ open: false, cardId: "", reason: "parts", note: "" });
  };

  const openWorkshopHandover = (card) => {
    if (!card) return;
    setWorkshopHandoverModal({
      open: true,
      cardId: card.id,
      target: card.nextDepartment || "Fahrzeugaufbereitung",
      note: "",
      acknowledgeWarnings: false,
    });
  };

  const submitWorkshopHandover = () => {
    const card = cards.find((item) => item.id === workshopHandoverModal.cardId);
    if (!card) return;
    const issues = getHandoverIssues(card);
    if (issues.length && !workshopHandoverModal.acknowledgeWarnings) return;
    const target = workshopHandoverModal.target || card.nextDepartment || "Fahrzeugaufbereitung";
    const targetDepartment = target === "Fertig" ? null : target;
    const note = workshopHandoverModal.note.trim();
    setWorkshopHandoverModal({
      open: false,
      cardId: "",
      target: "",
      note: "",
      acknowledgeWarnings: false,
    });
    window.setTimeout(
      () =>
        doHandover(
          { ...card, nextDepartment: targetDepartment },
          { confirmed: true, allowWarnings: true, targetDepartment, note }
        ),
      80
    );
  };

  const openWorkshopPhoto = (card, category = "Arbeit dokumentiert", subId = "") => {
    if (!card) return;
    setWorkshopPhotoModal({
      open: true,
      cardId: card.id,
      subId,
      category,
      note: "",
      fileName: "",
      saving: false,
    });
  };

  const handleWorkshopPhotoFile = async (files) => {
    const file = files?.[0];
    if (!file || !workshopPhotoModal.cardId) return;
    setWorkshopPhotoModal((prev) => ({ ...prev, saving: true, fileName: file.name }));
    const dataUrl = await fileToDataUrl(file);
    const category = workshopPhotoModal.category;
    const note = workshopPhotoModal.note.trim();
    const sub = workshopPhotoModal.subId
      ? cards
          .find((item) => item.id === workshopPhotoModal.cardId)
          ?.subs?.find((item) => item.id === workshopPhotoModal.subId)
      : null;
    updateCard(workshopPhotoModal.cardId, (current) => ({
      ...current,
      media: [
        ...(current.media || []),
        {
          id: `media_${Date.now()}`,
          url: dataUrl,
          label: category,
          group: "Werkstatt",
          note: sub ? `${sub.text}${note ? `: ${note}` : ""}` : note,
          subId: workshopPhotoModal.subId || "",
          createdAt: new Date().toISOString(),
          by: profile?.full_name || session?.user?.email || CREATOR,
        },
      ],
      history: [
        ...(current.history || []),
        {
          ts: new Date().toISOString(),
          by: profile?.full_name || session?.user?.email || CREATOR,
          action: `Foto hinzugefügt: ${category}${sub ? ` zu To-do "${sub.text}"` : ""}${note ? ` (${note})` : ""}`,
        },
      ],
    }));
    setWorkshopPhotoModal({
      open: false,
      cardId: "",
      subId: "",
      category: "Arbeit dokumentiert",
      note: "",
      fileName: "",
      saving: false,
    });
  };

  const deleteDepartmentTodoFromCard = (cardId, subId) => {
    const card = cards.find((item) => item.id === cardId);
    const sub = (card?.subs || []).find((item) => item.id === subId);
    if (!sub) return;
    const isMandatory = isBlockingSubtask(sub);
    const confirmed = window.confirm(
      isMandatory
        ? `Achtung: Dieses Pflicht-To-do ist Teil des Standardprozesses.\n\n"${sub.text}"\n\nWirklich löschen? Diese Änderung wird im Verlauf dokumentiert.`
        : `To-do wirklich löschen?\n\n${sub.text}`
    );
    if (!confirmed) return;

    updateCard(cardId, (current) => ({
      ...current,
      subs: (current.subs || []).filter((item) => item.id !== subId),
      history: [
        ...(current.history || []),
        {
          ts: new Date().toISOString(),
          by: profile?.full_name || session?.user?.email || CREATOR,
          action: `To-do gelöscht: ${sub.text}`,
        },
      ],
    }), { undoLabel: `To-do gelöscht: ${sub.text}` });
  };

  const assignTechnician = (cardId, techId) => {
    const targetTech = getEmployeeById(technicians, techId);
    const actor = profile?.full_name || session?.user?.email || CREATOR;
    const isWorkshopLeadAction =
      isWorkshopLead && ["Werkstättenleitung", "Werkstattplanung"].includes(ui.activeView);

    updateCard(cardId, (card) => {
      if (!techId) {
        return {
          ...card,
          assignedTech: null,
          loanerFlow: {
            ...(card.loanerFlow || {}),
            assignmentApproval: null,
          },
          history: [
            ...(card.history || []),
            {
              ts: new Date().toISOString(),
              by: actor,
              action: "Techniker-Zuweisung entfernt",
            },
          ],
        };
      }

      if (isWorkshopLeadAction) {
        return {
          ...card,
          role: "Werkstatt",
          assignedTech: techId,
          blockerType: card.blockerType === "approval" ? "" : card.blockerType,
          workStatus: card.workStatus === WORK_STATUS.BLOCKED ? WORK_STATUS.IN_WORK : card.workStatus,
          loanerFlow: {
            ...(card.loanerFlow || {}),
            assignmentApproval: {
              status: "approved",
              targetTechId: techId,
              targetTechName: targetTech?.name || techId,
              requestedBy: actor,
              requestedAt: new Date().toISOString(),
              approvedBy: actor,
              approvedAt: new Date().toISOString(),
            },
          },
          history: [
            ...(card.history || []),
            {
              ts: new Date().toISOString(),
              by: actor,
              action: `Techniker direkt zugewiesen: ${targetTech?.name || techId}`,
            },
          ],
        };
      }

      return {
        ...card,
        role: "Werkstatt",
        assignedTech: techId,
        blockerType: "approval",
        workStatus: WORK_STATUS.BLOCKED,
        loanerFlow: {
          ...(card.loanerFlow || {}),
          assignmentApproval: {
            status: "pending_workshop_lead",
            targetTechId: techId,
            targetTechName: targetTech?.name || techId,
            requestedBy: actor,
            requestedAt: new Date().toISOString(),
            approvedBy: "",
            approvedAt: null,
          },
        },
        questions: [
          ...(card.questions || []),
          {
            id: `question_${Date.now()}`,
            text: `Abgabe an ${targetTech?.name || techId} angefordert.`,
            author: actor,
            createdAt: new Date().toISOString(),
            answered: false,
            answer: "",
            answeredAt: null,
          },
        ],
        history: [
          ...(card.history || []),
          {
            ts: new Date().toISOString(),
            by: actor,
            action: `Techniker-Zuweisung zur Freigabe angefordert: ${targetTech?.name || techId}`,
          },
        ],
      };
    }, { undoLabel: targetTech ? `Techniker geändert auf ${targetTech.name}` : "Techniker-Zuweisung entfernt" });
  };

  const moveWorkshopCardSchedule = (cardId, techId, dueDate) => {
    const targetTech = getEmployeeById(technicians, techId);
    const actor = profile?.full_name || session?.user?.email || CREATOR;
    updateCard(cardId, (card) => ({
      ...card,
      role: "Werkstatt",
      assignedTech: techId || null,
      due: dueDate || card.due,
      nextActionDue: dueDate || card.nextActionDue,
      blockerType:
        card.loanerFlow?.assignmentApproval?.status === "pending_workshop_lead"
          ? ""
          : card.blockerType,
      workStatus:
        card.workStatus === WORK_STATUS.BLOCKED &&
        card.loanerFlow?.assignmentApproval?.status === "pending_workshop_lead"
          ? WORK_STATUS.IN_WORK
          : card.workStatus,
      loanerFlow: {
        ...(card.loanerFlow || {}),
        assignmentApproval: techId
          ? {
              status: "approved",
              targetTechId: techId,
              targetTechName: targetTech?.name || techId,
              requestedBy: actor,
              requestedAt: new Date().toISOString(),
              approvedBy: actor,
              approvedAt: new Date().toISOString(),
            }
          : null,
      },
      history: [
        ...(card.history || []),
        {
          ts: new Date().toISOString(),
          by: actor,
          action: `Werkstattplanung geändert: Techniker ${targetTech?.name || "nicht zugewiesen"}, Fälligkeit ${fmtDate(card.due)} -> ${fmtDate(dueDate)}`,
        },
      ],
    }), { undoLabel: "Werkstattplanung verschoben" });
  };

  const openPlanningMove = (card) => {
    setPlanningMove({
      open: true,
      cardId: card.id,
      techId: card.assignedTech || "",
      due: card.due || addDays(0),
      estimatedHours: String(getEstimatedHours(card)),
    });
  };

  const closePlanningMove = () => {
    setPlanningMove({
      open: false,
      cardId: "",
      techId: "",
      due: "",
      estimatedHours: "",
    });
  };

  const applyPlanningMove = () => {
    const card = cards.find((item) => item.id === planningMove.cardId);
    if (!card) {
      closePlanningMove();
      return;
    }
    const targetTech = getEmployeeById(technicians, planningMove.techId);
    updateCard(card.id, (current) => ({
      ...current,
      role: "Werkstatt",
      assignedTech: planningMove.techId || null,
      due: planningMove.due || current.due,
      nextActionDue: planningMove.due || current.nextActionDue,
      vehicleData: {
        ...(current.vehicleData || {}),
        estimatedHours: planningMove.estimatedHours || getEstimatedHours(current),
      },
      loanerFlow: {
        ...(current.loanerFlow || {}),
        assignmentApproval: planningMove.techId
          ? {
              status: "approved",
              targetTechId: planningMove.techId,
              targetTechName: targetTech?.name || planningMove.techId,
              requestedBy: profile?.full_name || session?.user?.email || CREATOR,
              requestedAt: new Date().toISOString(),
              approvedBy: profile?.full_name || session?.user?.email || CREATOR,
              approvedAt: new Date().toISOString(),
            }
          : null,
      },
      history: [
        ...(current.history || []),
        {
          ts: new Date().toISOString(),
          by: profile?.full_name || session?.user?.email || CREATOR,
          action: `Verschieben-Popup angewendet: ${getEmployeeNameById(technicians, planningMove.techId) || "nicht zugewiesen"}, ${fmtDate(planningMove.due)}, ${formatHours(planningMove.estimatedHours)}`,
        },
      ],
    }), { undoLabel: "Auftrag per Verschieben-Popup geändert" });
    closePlanningMove();
  };

  const togglePartInstalled = (cardId, partId) => {
    updateCard(cardId, (card) => ({
      ...card,
      parts: (card.parts || []).map((part) =>
        part.id === partId ? { ...part, installed: !part.installed } : part
      ),
    }));
  };

  const togglePartDelivered = (cardId, partId) => {
    updateCard(cardId, (card) => ({
      ...card,
      parts: (card.parts || []).map((part) =>
        part.id === partId ? { ...part, delivered: !part.delivered } : part
      ),
    }));
  };

  const addPartToCard = (cardId) => {
    const partName = (partDrafts[cardId] || "").trim();
    if (!partName) return;

    updateCard(cardId, (card) => ({
      ...card,
      parts: [
        ...(card.parts || []),
        {
          id: "part_" + Date.now(),
          name: partName,
          ordered: true,
          delivered: false,
          installed: false,
        },
      ],
    }));

    setPartDrafts((prev) => ({ ...prev, [cardId]: "" }));
  };

  const toggleInvoicePaid = (cardId) => {
    updateCard(cardId, (card) => ({
      ...card,
      invoice: {
        ...(card.invoice || { total: 0, paid: false }),
        paid: !(card.invoice?.paid || false),
      },
    }));
  };

  const addTaskQuestion = (cardId, questionText) => {
    const trimmed = String(questionText || "").trim();
    if (!trimmed) return;
    updateCard(cardId, (card) => ({
      ...card,
      questions: [
        ...(card.questions || []),
        {
          id: `question_${Date.now()}`,
          text: trimmed,
          author: profile?.full_name || session?.user?.email || CREATOR,
          createdAt: new Date().toISOString(),
          answered: false,
          answer: "",
          answeredAt: null,
        },
      ],
      history: [
        ...(card.history || []),
        {
          ts: new Date().toISOString(),
          by: profile?.full_name || session?.user?.email || CREATOR,
          action: `Rückfrage gestellt: ${trimmed}`,
        },
      ],
    }));
  };

  const requestDepartmentApproval = (card, text = "Freigabe durch Annahme erforderlich.", meta = {}) => {
    const actor = profile?.full_name || session?.user?.email || CREATOR;
    updateCard(card.id, (current) => ({
      ...current,
      blockerType: "approval",
      workStatus: WORK_STATUS.BLOCKED,
      questions: [
        ...(current.questions || []),
        {
          id: `question_${Date.now()}`,
          text,
          author: actor,
          createdAt: new Date().toISOString(),
          requestType: meta.requestType || "approval",
          targetDepartment: meta.targetDepartment || "",
          targetDue: meta.targetDue || "",
          requiresApproval: meta.requiresApproval !== false,
          answered: false,
          answer: "",
          answeredAt: null,
        },
      ],
      history: [
        ...(current.history || []),
        {
          ts: new Date().toISOString(),
          by: actor,
          action: `Freigabe angefordert: ${text}`,
        },
      ],
    }));
    setDbStatus("Freigabeanforderung wurde gespeichert und der Auftrag blockiert.");
  };

  const submitIntakeDepartmentRequest = (card) => {
    const type = partDrafts[`intakeRequestType_${card.id}`] || "change_request";
    const detail = String(partDrafts[`intakeRequestText_${card.id}`] || "").trim();
    const targetDepartment = partDrafts[`intakeRequestTarget_${card.id}`] || "";
    const targetDue = partDrafts[`intakeRequestDue_${card.id}`] || "";
    const label = INTAKE_REQUEST_TYPES.find((entry) => entry.value === type)?.label || "Anforderung";
    if (!detail) {
      window.alert("Bitte kurz beschreiben, was angefordert oder gefragt werden soll.");
      return;
    }

    if (type === "question") {
      addTaskQuestion(card.id, `Rückfrage Annahme: ${detail}`);
      setPartDrafts((prev) => ({
        ...prev,
        [`intakeRequestText_${card.id}`]: "",
      }));
      setDbStatus("Rückfrage der Annahme wurde beim Auftrag gespeichert.");
      return;
    }

    if (type === "handover_request" && !targetDepartment) {
      window.alert("Bitte die Zielabteilung für die Weitergabe auswählen.");
      return;
    }

    const targetText = targetDepartment ? ` Ziel: ${targetDepartment}.` : "";
    const dueText = targetDue ? ` Gewünschter Termin: ${fmtDate(targetDue)}.` : "";
    requestDepartmentApproval(card, `${label} durch Annahme: ${detail}.${targetText}${dueText}`, {
      requestType: type,
      targetDepartment: type === "handover_request" ? targetDepartment : "",
      targetDue,
      requiresApproval: true,
    });
    setPartDrafts((prev) => ({
      ...prev,
      [`intakeRequestText_${card.id}`]: "",
      [`intakeRequestTarget_${card.id}`]: "",
      [`intakeRequestDue_${card.id}`]: "",
    }));
  };

  const answerTaskQuestion = (cardId, questionId, answerText) => {
    const trimmed = String(answerText || "").trim();
    if (!trimmed) return;
    updateCard(cardId, (card) => ({
      ...card,
      questions: (card.questions || []).map((question) =>
        question.id === questionId
          ? {
              ...question,
              answered: true,
              answer: trimmed,
              answeredAt: new Date().toISOString(),
            }
          : question
      ),
      history: [
        ...(card.history || []),
        {
          ts: new Date().toISOString(),
          by: profile?.full_name || session?.user?.email || CREATOR,
          action: `Rückfrage beantwortet: ${trimmed}`,
        },
      ],
    }));
  };

  const resolveApprovalRequest = (card, approved, commentText = "") => {
    const actor = profile?.full_name || session?.user?.email || CREATOR;
    const comment = String(commentText || "").trim();
    const commentSuffix = comment ? ` Kommentar: ${comment}` : "";
    const latestApprovalQuestion = (card.questions || [])
      .slice()
      .reverse()
      .find((question) => !question.answered && question.requiresApproval);
    const targetDepartment = approved ? getApprovalRequestTarget(card) : "";
    const assignmentRequest = card.loanerFlow?.assignmentApproval || null;
    const targetEmployee = approved
      ? getEmployeeById(technicians, assignmentRequest?.targetTechId) ||
        getApprovalRequestEmployee(card, technicians)
      : null;
    if (approved && targetDepartment) {
      const nextChecklist = getConfiguredChecklistForDepartment(card.caseType || "other", targetDepartment);
      persistCard({
        ...card,
        role: targetDepartment,
        nextDepartment: getNextDepartmentForCard({ ...card, role: targetDepartment }),
        workStatus: WORK_STATUS.NEW,
        blockerType: "",
        subs: nextChecklist,
        nextAction: nextChecklist[0]?.text || `Bearbeitung in ${targetDepartment} starten`,
        questions: (card.questions || []).map((question) =>
          !question.answered &&
          (question.requiresApproval || String(question.text || "").toLowerCase().includes("freigabe"))
            ? {
                ...question,
                answered: true,
                answer: `Freigegeben durch ${actor}${commentSuffix}`,
                answeredAt: new Date().toISOString(),
              }
            : question
        ),
        history: [
          ...(card.history || []),
          {
            ts: new Date().toISOString(),
            by: actor,
            action: `Freigabe erteilt und an ${targetDepartment} weitergeleitet${commentSuffix}`,
          },
        ],
      });
      setDbStatus(`Freigabe erteilt. Auftrag wurde an ${targetDepartment} weitergeleitet.`);
      return;
    }

    if (approved && targetEmployee) {
      updateCard(card.id, (current) => ({
        ...current,
        role: "Werkstatt",
        assignedTech: targetEmployee.id,
        responsibleLabel: targetEmployee.name,
        blockerType: "",
        workStatus: WORK_STATUS.IN_WORK,
        loanerFlow: {
          ...(current.loanerFlow || {}),
          assignmentApproval: {
            ...((current.loanerFlow || {}).assignmentApproval || {}),
            status: "approved",
            targetTechId: targetEmployee.id,
            targetTechName: targetEmployee.name,
            approvedBy: actor,
            approvedAt: new Date().toISOString(),
          },
        },
        questions: (current.questions || []).map((question) =>
          !question.answered && String(question.text || "").includes(targetEmployee.name)
            ? {
                ...question,
                answered: true,
                answer: `Freigegeben durch ${actor}${commentSuffix}`,
                answeredAt: new Date().toISOString(),
              }
            : question
        ),
        history: [
          ...(current.history || []),
          {
            ts: new Date().toISOString(),
            by: actor,
            action: `Freigabe erteilt und an ${targetEmployee.name} zugewiesen${commentSuffix}`,
          },
        ],
      }));
      setDbStatus(`Freigabe erteilt. Auftrag wurde ${targetEmployee.name} zugewiesen.`);
      return;
    }

    updateCard(card.id, (current) => ({
      ...current,
      blockerType: approved ? "" : "approval",
      workStatus: approved ? WORK_STATUS.IN_WORK : WORK_STATUS.BLOCKED,
      prio:
        approved && latestApprovalQuestion?.requestType === "escalation_request"
          ? "high"
          : current.prio,
      nextAction:
        approved && latestApprovalQuestion?.requestType === "change_request"
          ? latestApprovalQuestion.text
          : current.nextAction,
      nextActionDue:
        approved && latestApprovalQuestion?.targetDue
          ? latestApprovalQuestion.targetDue
          : current.nextActionDue,
      loanerFlow: {
        ...(current.loanerFlow || {}),
        assignmentApproval:
          current.loanerFlow?.assignmentApproval?.status === "pending_workshop_lead"
            ? {
                ...(current.loanerFlow.assignmentApproval || {}),
                status: approved ? "approved" : "rejected",
                approvedBy: approved ? actor : "",
                approvedAt: approved ? new Date().toISOString() : null,
                rejectedBy: approved ? "" : actor,
                rejectedAt: approved ? null : new Date().toISOString(),
              }
            : current.loanerFlow?.assignmentApproval || null,
      },
      questions: (current.questions || []).map((question) =>
        !question.answered &&
        (question.requiresApproval || String(question.text || "").toLowerCase().includes("freigabe"))
          ? {
              ...question,
              answered: true,
              answer: approved ? `Freigegeben durch ${actor}${commentSuffix}` : `Abgelehnt durch ${actor}${commentSuffix}`,
              answeredAt: new Date().toISOString(),
            }
          : question
      ),
      history: [
        ...(current.history || []),
        {
          ts: new Date().toISOString(),
          by: actor,
          action: approved ? `Freigabe erteilt${commentSuffix}` : `Freigabe abgelehnt${commentSuffix}`,
        },
      ],
    }));
    setDbStatus(approved ? "Freigabe erteilt." : "Freigabe abgelehnt und Auftrag bleibt blockiert.");
  };

  const doHandover = (card, options = {}) => {
    if (!canEditDepartmentCard(card)) {
      window.alert("Du kannst nur Aufträge deiner eigenen Abteilung weitergeben.");
      return;
    }
    if (!isAdmin && card.blockerType) {
      window.alert(
        "Dieser Auftrag ist blockiert oder wartet auf Freigabe. Die Übergabe muss durch Admin / Annahme freigegeben werden."
      );
      return;
    }
    const handoverIssues = getHandoverIssues(card);
    if (!isAdmin && handoverIssues.length > 0 && !options.allowWarnings) {
      window.alert(
        `Dieser Auftrag kann noch nicht weitergegeben werden:\n\n- ${handoverIssues.join("\n- ")}\n\nBitte erledigen oder eine Freigabe / einen Blocker setzen.`
      );
      return;
    }
    if (isAdmin && handoverIssues.length > 0 && !options.allowWarnings) {
      const override = window.confirm(
        `Abschluss-Check meldet offene Punkte:\n\n- ${handoverIssues.join("\n- ")}\n\nTrotzdem weitergeben?`
      );
      if (!override) return;
    }
    const nextDepartment = Object.prototype.hasOwnProperty.call(options, "targetDepartment")
      ? options.targetDepartment
      : getNextDepartmentForCard(card);
    if (!options.confirmed) {
      const isReady = window.confirm(
        nextDepartment
          ? `Ist dieser Task bereit für ${nextDepartment}?`
          : "Ist dieser Task abgeschlossen?"
      );
      if (!isReady) return;
    }

    const actor = profile?.full_name || session?.user?.email || CREATOR;
    const nextChecklist = nextDepartment
      ? getConfiguredChecklistForDepartment(card.caseType || "other", nextDepartment)
      : [];
    const movedCard = nextDepartment
      ? {
          ...card,
          role: nextDepartment,
          nextDepartment: getNextDepartmentForCard({ ...card, role: nextDepartment }),
          workStatus: WORK_STATUS.NEW,
          blockerType: "",
          subs: nextChecklist,
          nextAction: nextChecklist[0]?.text || `Bearbeitung in ${nextDepartment} starten`,
          history: [
            ...(card.history || []),
            ...(options.note
              ? [
                  {
                    ts: new Date().toISOString(),
                    by: actor,
                    action: `Notiz zur Fertigmeldung: ${options.note}`,
                  },
                ]
              : []),
            {
              ts: new Date().toISOString(),
              by: actor,
              action: `Bereit für ${nextDepartment} bestätigt`,
            },
            {
              ts: new Date().toISOString(),
              by: actor,
              action: `Übergabe an ${nextDepartment}`,
            },
          ],
        }
      : {
          ...card,
          workStatus: WORK_STATUS.DONE,
          nextDepartment: null,
          history: [
            ...(card.history || []),
            ...(options.note
              ? [
                  {
                    ts: new Date().toISOString(),
                    by: actor,
                    action: `Notiz zur Fertigmeldung: ${options.note}`,
                  },
                ]
              : []),
            {
              ts: new Date().toISOString(),
              by: actor,
              action: "Task als abgeschlossen bestätigt",
            },
          ],
        };

    persistCard(movedCard);
  };

  const forwardCardToDepartment = (card, targetDepartment) => {
    if (!targetDepartment || targetDepartment === card.role) return;
    const actor = profile?.full_name || session?.user?.email || CREATOR;
    if (!isAdmin) {
      updateCard(card.id, (current) => ({
        ...current,
        blockerType: "approval",
        workStatus: WORK_STATUS.BLOCKED,
        questions: [
          ...(current.questions || []),
          {
            id: `question_${Date.now()}`,
            text: `Manuelle Übergabe an ${targetDepartment} angefordert.`,
            author: actor,
            createdAt: new Date().toISOString(),
            answered: false,
            answer: "",
            answeredAt: null,
          },
        ],
        history: [
          ...(current.history || []),
          {
            ts: new Date().toISOString(),
            by: actor,
            action: `Admin-Freigabe für manuelle Übergabe an ${targetDepartment} angefordert`,
          },
        ],
      }));
      setDbStatus(`Manuelle Übergabe an ${targetDepartment} wurde zur Admin-Freigabe angefordert.`);
      return;
    }
    const nextChecklist = getConfiguredChecklistForDepartment(card.caseType || "other", targetDepartment);
    const movedCard = {
      ...card,
      role: targetDepartment,
      nextDepartment: getNextDepartmentForCard({ ...card, role: targetDepartment }),
      workStatus: WORK_STATUS.NEW,
      blockerType: "",
      subs: nextChecklist,
      nextAction: nextChecklist[0]?.text || `Bearbeitung in ${targetDepartment} starten`,
      history: [
        ...(card.history || []),
        {
          ts: new Date().toISOString(),
          by: actor,
          action: `Manuell weitergeleitet an ${targetDepartment}`,
        },
      ],
    };

    persistCard(movedCard);
  };

  const toggleLoanerRequirement = (card) => {
    updateCard(card.id, (current) => ({
      ...current,
      needsLoaner: !current.needsLoaner,
    }));
  };

  const startLoanerWizard = (card, initialStep = 0) => {
    let nextWizard = getDefaultWizardState(card);
    let nextStep = initialStep;
    const lock = card?.loanerFlow?.loanerWizardLock;
    const lockedByOther =
      lock?.email &&
      session?.user?.email &&
      lock.email !== session.user.email &&
      isRecentActivity(lock.at, 10);
    if (lockedByOther) {
      const takeover = window.confirm(
        `Dieser Leihwagenvorgang wird gerade von ${lock.by || lock.email} bearbeitet (zuletzt ${fmtDateTime(lock.at)}).\n\nTrotzdem auf diesem Gerät übernehmen?`
      );
      if (!takeover) return;
    }
    const sharedDraft = card?.loanerFlow?.loanerWizardDraft;
    if (canRestoreWizardDraft(sharedDraft)) {
      nextWizard = {
        ...nextWizard,
        ...sharedDraft,
        cardId: card.id,
        orderNumber: sharedDraft.orderNumber || card.orderNumber || "",
        isSubmitting: false,
        submissionError: "",
        submissionSuccess: sharedDraft.savedAt
          ? `Gemeinsamer Entwurf vom ${fmtDateTime(sharedDraft.savedAt)} geladen. Fotos und Unterschrift werden aus Datenschutzgründen erst auf diesem Gerät aufgenommen.`
          : "Gemeinsamer Leihwagen-Entwurf geladen.",
      };
      nextStep = Number(sharedDraft.wizardStep ?? initialStep);
    }
    try {
      const saved = JSON.parse(localStorage.getItem(WIZARD_DRAFT_STORAGE_KEY) || "null");
      if (saved?.savedAt && !isStoredDraftFresh(saved.savedAt)) {
        localStorage.removeItem(WIZARD_DRAFT_STORAGE_KEY);
        throw new Error("Alter Entwurf verworfen");
      }
      if (
        canRestoreWizardDraft(saved?.wizard) &&
        (!card || !saved.wizard.cardId || saved.wizard.cardId === card.id)
      ) {
        const restore = window.confirm(
          `Es gibt einen lokal gespeicherten Leihwagen-Entwurf${saved.savedAt ? ` vom ${fmtDateTime(saved.savedAt)}` : ""}. Fortsetzen?`
        );
        if (restore) {
          nextWizard = {
            ...nextWizard,
            ...saved.wizard,
            isSubmitting: false,
            submissionError: "",
            submissionSuccess: "Entwurf wurde automatisch wiederhergestellt.",
          };
          nextStep = Number(saved.wizardStep || initialStep);
        }
      }
    } catch {
      // Ungültige lokale Entwürfe werden ignoriert.
    }
    if (card?.loanerId) {
      nextWizard.loanerId = card.loanerId;
    }
    setWizard(nextWizard);
    setWizardStep(Math.max(0, Math.min(WIZARD_STEPS.length - 1, nextStep)));
    setWizardUsageFilter("customer_loaner");
    setAvailabilityInspector((prev) => ({ ...prev, open: false, error: "" }));
    setWizardOpen(true);
  };

  const saveLoanerWizardForDeviceHandover = async () => {
    const selectedLoaner = loaners.find((loaner) => loaner.id === wizard.loanerId);
    const existingCard = wizard.cardId
      ? cards.find((card) => card.id === wizard.cardId)
      : null;
    const internalOrder = isInternalOrder(existingCard);
    const missingMasterData = [
      !wizard.loanerId ? "Leihwagen" : "",
      !wizard.customerFirstName ? "Vorname" : "",
      !wizard.customerLastName ? "Nachname" : "",
      !wizard.customerGender ? "Geschlecht" : "",
      !internalOrder && !wizard.customerEmail ? "E-Mail" : "",
      !wizard.plannedPickupDate ? "Verleihbeginn" : "",
      !wizard.expectedReturnDate ? "Rückgabedatum" : "",
    ].filter(Boolean);

    if (missingMasterData.length > 0) {
      setWizard((prev) => ({
        ...prev,
        submissionError: `Für den Gerätewechsel bitte zuerst vervollständigen: ${missingMasterData.join(", ")}.`,
      }));
      setWizardStep(wizard.loanerId ? 2 : 1);
      return;
    }

    if (wizard.expectedReturnDate < wizard.plannedPickupDate) {
      setWizard((prev) => ({
        ...prev,
        submissionError: "Die Rückgabe muss am oder nach dem Verleihbeginn liegen.",
      }));
      setWizardStep(2);
      return;
    }

    if (!selectedLoaner) {
      setWizard((prev) => ({
        ...prev,
        submissionError: "Der ausgewählte Leihwagen wurde nicht gefunden.",
      }));
      setWizardStep(1);
      return;
    }

    const cardId = existingCard?.id || makeCardId();
    const customerName = `${wizard.customerFirstName} ${wizard.customerLastName}`.trim();
    const actor = profile?.full_name || session?.user?.email || CREATOR;
    const savedAt = new Date().toISOString();
    const resumeStep = Math.max(wizardStep, 3);
    const sharedDraft = buildSharedWizardDraft(
      {
        ...wizard,
        cardId,
        orderNumber: wizard.orderNumber || existingCard?.orderNumber || "",
      },
      resumeStep
    );
    const isStandaloneProcess =
      existingCard?.loanerFlow?.standaloneLoanerProcess || !existingCard;
    const handoverCard = {
      ...(existingCard || {}),
      id: cardId,
      orderNumber: existingCard?.orderNumber || wizard.orderNumber || "",
      customerName,
      customerFirstName: wizard.customerFirstName,
      customerLastName: wizard.customerLastName,
      customerGender: wizard.customerGender,
      customerEmail: wizard.customerEmail,
      licensePlate: existingCard?.licensePlate || "",
      vehicleData: existingCard?.vehicleData || {},
      title:
        existingCard?.title ||
        `Leihwagen - ${customerName || selectedLoaner.plate || "Entwurf"}`,
      role: existingCard?.role || "Leihwagen",
      caseType: existingCard?.caseType || "loaner",
      prio: existingCard?.prio || "normal",
      due: existingCard?.due || wizard.plannedPickupDate || "",
      workStatus: existingCard?.workStatus || WORK_STATUS.NEW,
      needsLoaner: true,
      needsSpenglerei: Boolean(existingCard?.needsSpenglerei),
      assignedTech: existingCard?.assignedTech || null,
      blockerType: existingCard?.blockerType || "",
      nextAction:
        existingCard?.nextAction || "Leihwagen-Dokumentation am Tablet fortsetzen",
      nextActionDue: existingCard?.nextActionDue || wizard.plannedPickupDate || "",
      responsibleLabel: existingCard?.responsibleLabel || actor,
      escalationLevel: Number(existingCard?.escalationLevel || 0),
      branch: existingCard?.branch || profile?.branch || selectedLoaner.homeBranch || "",
      managementNotes: existingCard?.managementNotes || "",
      nextDepartment: existingCard?.nextDepartment || "",
      sourceLabel: existingCard?.sourceLabel || "Direkte Leihwagenvergabe",
      externalSystem: existingCard?.externalSystem || "",
      externalReference: existingCard?.externalReference || "",
      questions: existingCard?.questions || [],
      loanerFlow: {
        ...(existingCard?.loanerFlow || {}),
        orderType: existingCard?.loanerFlow?.orderType || "external",
        standaloneLoanerProcess: isStandaloneProcess,
        plannedLoanerId: selectedLoaner.id,
        plannedLoanerPlate: selectedLoaner.plate,
        loanerWizardDraft: {
          ...sharedDraft,
          savedAt,
          savedBy: actor,
        },
        loanerWizardDraftStatus: "ready_for_device_handover",
        loanerWizardLock: null,
      },
      parts: existingCard?.parts || [],
      media: existingCard?.media || [],
      subs: existingCard?.subs || [],
      invoice: existingCard?.invoice || null,
      history: [
        ...(existingCard?.history || []),
        {
          ts: savedAt,
          by: actor,
          action: existingCard
            ? "Leihwagen-Entwurf für Gerätewechsel gespeichert"
            : "Direkter Leihwagenvorgang für Fortsetzung am Tablet angelegt",
        },
      ],
    };

    setWizard((prev) => ({
      ...prev,
      isSubmitting: true,
      submissionError: "",
    }));

    try {
      const savedCard = await persistCard(handoverCard);
      if (!savedCard) {
        throw new Error(handoverCard.lastSaveError || "Zentrale Speicherung fehlgeschlagen.");
      }

      localStorage.removeItem(WIZARD_DRAFT_STORAGE_KEY);
      setLoanerBoardTab("overview");
      setDbStatus(
        `Leihwagen-Entwurf ${savedCard.orderNumber || ""} wurde zentral gespeichert und kann am Tablet fortgesetzt werden.`
      );
      closeWizard({ discardDraft: true });
    } catch (error) {
      setWizard((prev) => ({
        ...prev,
        isSubmitting: false,
        submissionError: getFriendlyProcessError(
          error,
          "Der Entwurf konnte nicht für den Gerätewechsel gespeichert werden."
        ),
      }));
    }
  };

  const closeWizard = ({ confirm = false, discardDraft = false } = {}) => {
    if (confirm && wizard.isSubmitting) return;
    let shouldDiscardDraft = discardDraft;
    if (confirm) {
      if (wizard.cardId) {
        const closeSharedDraft = window.confirm(
          "Bearbeitung auf diesem Gerät schließen?\n\nDer zentral gespeicherte Entwurf bleibt erhalten und kann später auf diesem oder einem anderen Gerät fortgesetzt werden."
        );
        if (!closeSharedDraft) return;
        shouldDiscardDraft = true;
      } else {
        const ok = window.confirm(
          "Leihwagen-Anlage schließen?\n\nOK = schließen und lokalen Entwurf löschen\nAbbrechen = nur Fenster schließen, Entwurf bleibt erhalten"
        );
        shouldDiscardDraft = ok;
      }
    }
    setWizardOpen(false);
    if (shouldDiscardDraft) {
      localStorage.removeItem(WIZARD_DRAFT_STORAGE_KEY);
    }
    setWizard(getDefaultWizardState());
    setWizardStep(0);
    setWizardUsageFilter("customer_loaner");
    setAvailabilityInspector((prev) => ({ ...prev, open: false, error: "" }));
  };

  const toggleCardExpanded = (cardId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [cardId]: !prev[cardId],
    }));
    setDetailTabs((prev) => ({
      ...prev,
      [cardId]: ["work", "info"].includes(prev[cardId]) ? prev[cardId] : "work",
    }));
  };

  const openTaskDetail = (cardId) => {
    setTaskDetailCardId(cardId);
    setExpandedCards((prev) => ({ ...prev, [cardId]: true }));
    setDetailTabs((prev) => ({ ...prev, [cardId]: ["work", "info"].includes(prev[cardId]) ? prev[cardId] : "work" }));
  };

  const closeTaskDetail = () => {
    setTaskDetailCardId(null);
  };

  const openPhotoCaptureSequence = ({ title, items, currentField, onSave }) => {
    const currentIndex = items.findIndex((item) => item.field === currentField);
    if (currentIndex < 0) return;
    const currentItem = items[currentIndex];
    const sequenceItems = currentItem.value
      ? [currentItem]
      : items.slice(currentIndex).filter((item) => !item.value);
    if (sequenceItems.length === 0) return;

    photoCaptureSaveRef.current = onSave;
    setPhotoCaptureSequence({
      open: true,
      title,
      items: sequenceItems,
      index: 0,
    });
  };

  const closePhotoCaptureSequence = () => {
    photoCaptureSaveRef.current = null;
    setPhotoCaptureSequence({
      open: false,
      title: "",
      items: [],
      index: 0,
    });
  };

  const savePhotoSequenceCapture = async (item, dataUrl) => {
    await photoCaptureSaveRef.current?.(item, dataUrl);
    const nextIndex = photoCaptureSequence.index + 1;
    if (nextIndex >= photoCaptureSequence.items.length) {
      closePhotoCaptureSequence();
      return;
    }
    setPhotoCaptureSequence((prev) => ({ ...prev, index: nextIndex }));
  };

  const handleWizardPhotoUpload = async (field, files, multiple = false) => {
    if (!files || files.length === 0) return;

    if (multiple) {
      const nextDamagePhotos = await Promise.all(
        Array.from(files).map(async (file) => ({
          id: "dmg_" + Date.now() + "_" + file.name,
          url: await fileToDataUrl(file),
          name: file.name,
        }))
      );
      setWizard((prev) => ({
        ...prev,
        damagePhotos: [...prev.damagePhotos, ...nextDamagePhotos],
      }));
      return;
    }

    const file = files[0];
    const dataUrl = await fileToDataUrl(file);
    setWizard((prev) => ({ ...prev, [field]: dataUrl }));
  };

  const addDamageEntry = async (files) => {
    if (!files || files.length === 0) return;

    const entries = await Promise.all(
      Array.from(files).map(async (file) => ({
        id: `damage_${Date.now()}_${file.name}`,
        url: await fileToDataUrl(file),
        name: file.name,
        description: "",
      }))
    );

    setWizard((prev) => ({
      ...prev,
      damageEntries: [...prev.damageEntries, ...entries],
    }));
  };

  const updateDamageEntry = (entryId, patch) => {
    setWizard((prev) => ({
      ...prev,
      damageEntries: prev.damageEntries.map((entry) =>
        entry.id === entryId ? { ...entry, ...patch } : entry
      ),
    }));
  };

  const removeDamageEntry = (entryId) => {
    setWizard((prev) => ({
      ...prev,
      damageEntries: prev.damageEntries.filter((entry) => entry.id !== entryId),
    }));
  };

  const updateReturnDamageEntry = (entryId, patch) => {
    setReturnState((prev) => ({
      ...prev,
      returnDamageEntries: ensureTrailingReturnDamageEntry(
        (prev.returnDamageEntries || []).map((entry) =>
          entry.id === entryId ? { ...entry, ...patch } : entry
        )
      ),
      error: "",
    }));
  };

  const handleReturnDamagePhotoUpload = async (entryId, files) => {
    if (!files || files.length === 0) return;
    const newPhotos = await Promise.all(
      Array.from(files).map(async (file) => ({
        id: `return_damage_photo_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        url: await fileToDataUrl(file),
        name: file.name,
      }))
    );
    setReturnState((prev) => ({
      ...prev,
      returnDamageEntries: ensureTrailingReturnDamageEntry(
        (prev.returnDamageEntries || []).map((entry) =>
          entry.id === entryId
            ? {
                ...entry,
                photos: [...(entry.photos || []), ...newPhotos],
              }
            : entry
        )
      ),
      error: "",
    }));
  };

  const removeReturnDamagePhoto = (entryId, photoId) => {
    setReturnState((prev) => ({
      ...prev,
      returnDamageEntries: ensureTrailingReturnDamageEntry(
        (prev.returnDamageEntries || []).map((entry) =>
          entry.id === entryId
            ? {
                ...entry,
                photos: (entry.photos || []).filter((photo) => photo.id !== photoId),
              }
            : entry
        )
      ),
      error: "",
    }));
  };

  const handleReturnComparisonUpload = async (slot, files) => {
    if (!files || files.length === 0) return;
    const photoUrl = await fileToDataUrl(files[0]);
    setReturnState((prev) => ({
      ...prev,
      comparisonPhotos: {
        ...prev.comparisonPhotos,
        [slot]: photoUrl,
      },
    }));
  };

  const sendConfirmationEmail = async (booking) => {
    const response = await fetch("/api/send-loaner-confirmation", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        bookingId: booking.id,
        customerName: booking.customerName,
        customerEmail: booking.customerEmail,
        loanerPlate: booking.loanerPlate,
        orderNumber: booking.orderNumber,
        reviewUrl: `${window.location.origin}?confirmation=${booking.confirmationToken}`,
      }),
    });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || "E-Mail konnte nicht versendet werden.");
      }

    return payload;
  };

  const sendOrderConfirmationEmail = async (card) => {
    const token = card.loanerFlow?.orderConfirmationToken;
    const response = await fetch("/api/send-order-confirmation", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        cardId: card.id,
        reviewUrl: token ? `${window.location.origin}?orderConfirmation=${token}` : "",
      }),
    });

    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload.error || "Auftragsbestätigung konnte nicht versendet werden.");
    }

    return payload;
  };

  const finalizeLoanerBooking = async () => {
    const requiredPhotos = PHOTO_LABELS.every((entry) => wizard[`${entry.key}Photo`]);
    const hasStructuredDamageGap = wizard.damageEntries.some(
      (entry) => !entry.url || !String(entry.description || "").trim()
    );
    const linkedWizardCard = wizard.cardId
      ? cards.find((card) => card.id === wizard.cardId)
      : cards.find((card) => card.orderNumber && card.orderNumber === wizard.orderNumber);
    const wizardInternalOrder = isInternalOrder(linkedWizardCard);
    const failWizardStep = (message, step) => {
      setWizard((prev) => ({ ...prev, submissionError: message }));
      setWizardStep(step);
    };
    if (!wizard.handoverEmployeeId) {
      failWizardStep("Bitte zuerst auswählen, wer die Übergabe durchführt.", 0);
      return;
    }

    if (!wizard.loanerId) {
      failWizardStep("Bitte einen Leihwagen auswählen.", 1);
      return;
    }

    if (!wizard.customerFirstName || !wizard.customerLastName || !wizard.customerGender) {
      failWizardStep("Vorname, Nachname und Geschlecht des Kunden sind Pflicht.", 2);
      return;
    }

    if (!wizardInternalOrder && !wizard.customerEmail) {
      failWizardStep("Bitte eine Kunden-E-Mail erfassen.", 2);
      return;
    }

    if (!wizard.plannedPickupDate) {
      failWizardStep("Bitte einen Verleihbeginn setzen.", 2);
      return;
    }

    if (!wizard.expectedReturnDate) {
      failWizardStep("Bitte ein voraussichtliches Rückgabedatum setzen.", 2);
      return;
    }

    if (wizard.expectedReturnDate < wizard.plannedPickupDate) {
      failWizardStep("Die Rückgabe muss am oder nach dem Verleihbeginn liegen.", 2);
      return;
    }

    if (!wizard.licenseFrontPhoto || !wizard.licenseBackPhoto) {
      failWizardStep("Bitte Vorder- und Rückseite des Führerscheins dokumentieren.", 3);
      return;
    }

    if (!requiredPhotos) {
      failWizardStep("Bitte die Pflichtfotos vorne, Fahrerseite, hinten und Beifahrerseite aufnehmen.", 5);
      return;
    }

    if (hasStructuredDamageGap) {
      failWizardStep("Jeder dokumentierte Schaden benötigt Foto und Beschreibung.", 6);
      return;
    }

    if (!wizard.noOtherDamageConfirmed) {
      failWizardStep("Bitte bestätigen, dass keine weiteren Schäden vorhanden sind.", 6);
      return;
    }

    if (!wizard.termsAccepted) {
      failWizardStep("Bitte die Bedingungen für den Leihwagen bestätigen.", 4);
      return;
    }

    if (!wizard.kmStart || !wizard.fuelStart || (!wizardInternalOrder && !wizard.signature)) {
      failWizardStep(
        wizardInternalOrder
          ? "KM-Stand und Tankstand sind Pflicht."
          : "KM-Stand, Tankstand und Unterschrift sind Pflicht.",
        7
      );
      return;
    }

    if (!wizard.dashboardPhotoPrimary && !wizard.dashboardPhotoSecondary) {
      failWizardStep("Bitte mindestens ein Foto von Kilometerstand und Tankstand aufnehmen.", 7);
      return;
    }

    const selectedLoaner = loaners.find((loaner) => loaner.id === wizard.loanerId);
    if (!selectedLoaner) {
      failWizardStep("Leihwagen nicht gefunden.", 1);
      return;
    }

    if (isLoanerUnavailable(selectedLoaner, availabilityMap)) {
      failWizardStep("Dieses Fahrzeug ist aktuell nicht verfügbar oder dauerhaft verliehen. Bitte ein verfügbares Fahrzeug auswählen.", 1);
      return;
    }

    const bookingConflicts = getLoanerConflictsForRange(
      selectedLoaner.id,
      wizard.plannedPickupDate,
      wizard.expectedReturnDate
    );
    if (bookingConflicts.length > 0) {
      failWizardStep("Dieses Fahrzeug ist im gewählten Zeitraum bereits reserviert oder verliehen. Bitte anderen Zeitraum oder anderes Fahrzeug wählen.", 1);
      return;
    }

    if (
      profile?.branch &&
      selectedLoaner.homeBranch &&
      profile.branch !== selectedLoaner.homeBranch &&
      !wizard.branchWarningAccepted
    ) {
      failWizardStep("Das Fahrzeug ist einer anderen Filiale zugeordnet. Bitte Warnung bestätigen, falls das Fahrzeug bereits am Standort vorhanden ist.", 1);
      return;
    }

    setWizard((prev) => ({
      ...prev,
      isSubmitting: true,
      submissionError: "",
      submissionSuccess: "",
    }));

    const booking = {
      id: "booking_" + Date.now(),
      loanerId: selectedLoaner.id,
      loanerPlate: selectedLoaner.plate,
      loanerBrand: selectedLoaner.brand,
      loanerModel: selectedLoaner.model,
      loanerHomeBranch: selectedLoaner.homeBranch || "",
      loanerUsageType: selectedLoaner.usageType || "customer_loaner",
      cardId: wizard.cardId,
      orderNumber: wizard.orderNumber,
      customerName: `${wizard.customerFirstName} ${wizard.customerLastName}`.trim(),
      customerFirstName: wizard.customerFirstName,
      customerLastName: wizard.customerLastName,
      customerGender: wizard.customerGender,
      customerEmail: wizard.customerEmail,
      plannedPickupDate: wizard.plannedPickupDate,
      expectedReturnDate: wizard.expectedReturnDate,
      status: wizardInternalOrder ? "ready_for_checkout" : "awaiting_customer_confirmation",
      driverLicenseFront: wizard.licenseFrontPhoto,
      driverLicenseBack: wizard.licenseBackPhoto,
      insuranceOption: wizard.insuranceOption,
      branchWarningAcknowledged:
        profile?.branch &&
        selectedLoaner.homeBranch &&
        profile.branch !== selectedLoaner.homeBranch
          ? wizard.branchWarningAccepted
          : false,
      termsAccepted: wizard.termsAccepted,
          conditionPhotos: {
            front: wizard.frontPhoto,
            left: wizard.leftPhoto,
            rear: wizard.rearPhoto,
            right: wizard.rightPhoto,
            dashboardPrimary: wizard.dashboardPhotoPrimary,
            dashboardSecondary: wizard.dashboardPhotoSecondary,
          },
      damageEntries: wizard.damageEntries,
      noOtherDamageConfirmed: true,
      kmStart: wizard.kmStart,
      fuelStart: wizard.fuelStart,
      returnDamageNotes: wizard.handoverEmployeeName
        ? `Übergabe durchgeführt durch ${wizard.handoverEmployeeName}.`
        : "",
      signature: wizard.signature,
      confirmationToken: wizardInternalOrder ? "" : makeToken(),
      confirmationStatus: wizardInternalOrder ? "accepted" : "pending",
      confirmationAcceptedAt: wizardInternalOrder ? new Date().toISOString() : null,
      createdAt: new Date().toISOString(),
      handoverEmployeeName: wizard.handoverEmployeeName,
      createdBy: session.user.id,
    };

    const completeWithSavedBooking = (message, savedBooking = booking) => {
      setDbStatus(message);
      setWizard((prev) => ({
        ...prev,
        isSubmitting: false,
        submissionSuccess: message,
      }));
      setLoanerCompletion({
        booking: savedBooking,
        message,
        reviewUrl: getLoanerConfirmationUrl(savedBooking),
      });
      closeWizard({ discardDraft: true });
    };

    try {
      await persistBooking(booking);
      persistCentralDamageEntries(booking).catch(() => undefined);

      const startsToday = wizard.plannedPickupDate <= toDateOnlyString(new Date());
      if (startsToday) {
        const updatedLoaner = {
          ...selectedLoaner,
          status: "reserved",
        };
        try {
          await persistLoaner(updatedLoaner);
        } catch (error) {
          setDbStatus(
            `Leihwagen ${selectedLoaner.plate} wurde reserviert. Fahrzeugstatus konnte nicht sofort aktualisiert werden (${error.message}).`
          );
        }
      }

      const linkedCard = linkedWizardCard;
      let linkedCardForSave = linkedCard || null;
      if (linkedCard) {
        try {
          const updatedCard = {
            ...linkedCard,
            needsLoaner: false,
            loanerId: selectedLoaner.id,
            loanerPlate: selectedLoaner.plate,
            customerName: `${wizard.customerFirstName} ${wizard.customerLastName}`.trim(),
            customerFirstName: wizard.customerFirstName,
            customerLastName: wizard.customerLastName,
            customerGender: wizard.customerGender,
            customerEmail: wizard.customerEmail,
            workStatus: linkedCard.loanerFlow?.standaloneLoanerProcess
              ? WORK_STATUS.DONE
              : linkedCard.workStatus,
            nextAction: linkedCard.loanerFlow?.standaloneLoanerProcess
              ? "Leihwagenvorgang in der Historie verfolgen"
              : linkedCard.nextAction,
            loanerFlow: {
              ...(linkedCard.loanerFlow || {}),
              orderConfirmationStatus: wizardInternalOrder ? "not_required" : "pending_loaner_confirmation",
              loanerBookingId: booking.id,
              loanerPlate: selectedLoaner.plate,
              loanerConfirmationSentAt: null,
              loanerConfirmationAcceptedAt: wizardInternalOrder ? new Date().toISOString() : null,
              loanerWizardDraft: null,
              loanerWizardDraftStatus: "completed",
              loanerWizardLock: null,
            },
            history: [
              ...(linkedCard.history || []),
              {
                ts: new Date().toISOString(),
                by: CREATOR,
                action: `Leihwagenbedarf erledigt und Bestätigung vorbereitet -> ${selectedLoaner.plate}`,
              },
            ],
          };
          linkedCardForSave = updatedCard;
          await persistCard(updatedCard);
        } catch (error) {
          setDbStatus(
            `Leihwagen ${selectedLoaner.plate} wurde reserviert. Auftrag konnte nicht sofort ergänzt werden (${error.message}).`
          );
        }
      }

      let successMessage = `Leihwagen ${selectedLoaner.plate} wurde reserviert.`;

      if (wizardInternalOrder) {
        successMessage = `Leihwagen ${selectedLoaner.plate} wurde für den internen Auftrag reserviert. Keine Kundenbestätigung erforderlich.`;
      } else {
      try {
        const mailPayload = await sendConfirmationEmail(booking);
        const sentAt = mailPayload.sent ? mailPayload.sentAt || new Date().toISOString() : null;

        if (sentAt) {
          try {
            await persistBooking({
              ...booking,
              confirmationSentAt: sentAt,
            });
            if (linkedCardForSave) {
              await persistCard({
                ...linkedCardForSave,
                needsLoaner: false,
                loanerId: selectedLoaner.id,
                loanerPlate: selectedLoaner.plate,
                loanerFlow: {
                  ...(linkedCardForSave.loanerFlow || {}),
                  orderConfirmationStatus: "pending_loaner_confirmation",
                  loanerBookingId: booking.id,
                  loanerPlate: selectedLoaner.plate,
                  loanerConfirmationSentAt: sentAt,
                  loanerConfirmationAcceptedAt: null,
                  loanerWizardDraft: null,
                  loanerWizardDraftStatus: "completed",
                  loanerWizardLock: null,
                },
              });
            }
          } catch (error) {
            setDbStatus(
              `Bestätigung wurde versendet, der Versandzeitpunkt konnte aber nicht gespeichert werden (${error.message}).`
            );
          }
        }

        successMessage =
          mailPayload.sent === false
            ? `Leihwagen ${selectedLoaner.plate} reserviert. Bestätigungslink wurde erzeugt, aber nicht per E-Mail versendet: ${mailPayload.reviewUrl}`
            : `Leihwagen ${selectedLoaner.plate} reserviert. Bestätigung wurde an ${wizard.customerEmail} gesendet (${fmtDateTime(sentAt)}).`;
      } catch (error) {
        successMessage = `Leihwagen ${selectedLoaner.plate} wurde reserviert. Die E-Mail konnte nicht automatisch versendet werden (${error.message}). Bitte den Vorgang in der Historie öffnen und E-Mail erneut senden.`;
      }
      }

      completeWithSavedBooking(successMessage, booking);
    } catch (error) {
      if (isStatementTimeoutError(error)) {
        try {
          const { data } = await supabase
            .from("loaner_bookings")
            .select("*")
            .eq("id", booking.id)
            .maybeSingle();

          if (data) {
            const recoveredBooking = mapSupabaseBooking(data);
            setBookings((prev) => {
              const exists = prev.some((item) => item.id === recoveredBooking.id);
              return exists
                ? prev.map((item) => (item.id === recoveredBooking.id ? recoveredBooking : item))
                : [recoveredBooking, ...prev];
            });
            completeWithSavedBooking(
              `Leihwagen ${selectedLoaner.plate} wurde gespeichert. Die Datenbank hat beim Abschluss zu spät geantwortet, der Vorgang ist aber in der Historie vorhanden.`,
              recoveredBooking
            );
            return;
          }
        } catch (recoveryError) {
          setDbStatus(
            `Datenbank-Timeout beim Abschluss. Bitte Historie prüfen, bevor der Vorgang erneut angelegt wird (${recoveryError.message}).`
          );
        }
      }

      setWizard((prev) => ({
        ...prev,
        isSubmitting: false,
        submissionError: getFriendlyProcessError(
          error,
          "Der Leihwagen-Vorgang konnte nicht abgeschlossen werden. Bitte Historie prüfen und den Schritt erneut versuchen."
        ),
      }));
    }
  };

  const resendConfirmationEmail = async (booking) => {
    const linkedCard = booking.cardId
      ? cards.find((card) => card.id === booking.cardId)
      : cards.find((card) => card.orderNumber && card.orderNumber === booking.orderNumber);
    if (isInternalOrder(linkedCard)) {
      setDbStatus("Interner Auftrag: keine Kundenbestätigung per E-Mail erforderlich.");
      return;
    }
    const cooldownUntil = resendCooldowns[booking.id] || 0;
    if (cooldownUntil > Date.now()) {
      setDbStatus(
        `E-Mail wurde bereits versendet. Ein erneuter Versand ist in ${formatResendWait(cooldownUntil)} möglich.`
      );
      return;
    }

    setResendSending((prev) => ({ ...prev, [booking.id]: true }));
    setDbStatus(`E-Mail wird an ${booking.customerEmail} versendet ...`);

    try {
      const payload = await sendConfirmationEmail(booking);
      if (!payload.sent) {
        setDbStatus(
          `Bestätigungslink wurde erzeugt, der E-Mail-Versand ist jedoch nicht aktiv: ${payload.reviewUrl}`
        );
        return;
      }

      const sentAt = payload.sentAt || new Date().toISOString();
      const nextCooldownUntil = Date.now() + RESEND_COOLDOWN_MS;
      setResendClock(Date.now());
      setResendCooldowns((prev) => ({
        ...prev,
        [booking.id]: nextCooldownUntil,
      }));
      setDbStatus(
        `E-Mail wurde an ${booking.customerEmail} versendet. Keine weitere Aktion erforderlich. Ein erneuter Versand ist in ${formatResendWait(nextCooldownUntil)} möglich.`
      );

      try {
        await persistBooking({
          ...booking,
          confirmationSentAt: sentAt,
        });
      } catch (persistError) {
        setDbStatus(
          `E-Mail wurde an ${booking.customerEmail} versendet. Der Versandzeitpunkt konnte nicht gespeichert werden (${persistError.message}).`
        );
      }
    } catch (error) {
      if (/gerade erst|bereits versendet|too many|429/i.test(String(error.message || ""))) {
        const nextCooldownUntil = Date.now() + RESEND_COOLDOWN_MS;
        setResendClock(Date.now());
        setResendCooldowns((prev) => ({
          ...prev,
          [booking.id]: nextCooldownUntil,
        }));
        setDbStatus(
          `E-Mail wurde bereits versendet. Ein erneuter Versand ist in ${formatResendWait(nextCooldownUntil)} möglich.`
        );
      } else {
        setDbStatus(`E-Mail konnte nicht versendet werden: ${error.message}`);
      }
    } finally {
      setResendSending((prev) => ({ ...prev, [booking.id]: false }));
    }
  };

  const openLoanerCorrectionModal = async (booking) => {
    const hydrated = await hydrateBookingMedia(booking.id);
    const source = hydrated || booking;
    setLoanerCorrection({
      bookingId: source.id,
      customerFirstName: source.customerFirstName || String(source.customerName || "").split(" ")[0] || "",
      customerLastName:
        source.customerLastName ||
        String(source.customerName || "")
          .split(" ")
          .slice(1)
          .join(" "),
      customerGender: source.customerGender || "",
      customerEmail: source.customerEmail || "",
      plannedPickupDate: source.plannedPickupDate || "",
      expectedReturnDate: source.expectedReturnDate || "",
      kmStart: source.kmStart || "",
      fuelStart: source.fuelStart || "",
      orderNumber: source.orderNumber || "",
      reason: "",
      error: "",
      saving: false,
      emailChanged: false,
    });
  };

  const saveLoanerCorrection = async () => {
    if (!loanerCorrection?.bookingId) return;
    const booking = bookings.find((item) => item.id === loanerCorrection.bookingId);
    if (!booking) {
      setLoanerCorrection((prev) => ({ ...prev, error: "Leihwagen-Vorgang wurde nicht gefunden." }));
      return;
    }
    const reason = loanerCorrection.reason.trim();
    if (!reason) {
      setLoanerCorrection((prev) => ({ ...prev, error: "Bitte einen Grund für die Korrektur angeben." }));
      return;
    }
    if (loanerCorrection.customerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loanerCorrection.customerEmail)) {
      setLoanerCorrection((prev) => ({ ...prev, error: "Bitte eine gültige E-Mail-Adresse eingeben." }));
      return;
    }

    setLoanerCorrection((prev) => ({ ...prev, saving: true, error: "" }));
    const now = new Date().toISOString();
    const nextName = `${loanerCorrection.customerFirstName} ${loanerCorrection.customerLastName}`.trim();
    const emailChanged = String(booking.customerEmail || "") !== String(loanerCorrection.customerEmail || "");
    const changes = [
      ["Vorname", booking.customerFirstName, loanerCorrection.customerFirstName],
      ["Nachname", booking.customerLastName, loanerCorrection.customerLastName],
      ["Geschlecht", booking.customerGender, loanerCorrection.customerGender],
      ["E-Mail", booking.customerEmail, loanerCorrection.customerEmail],
      ["Verleihbeginn", booking.plannedPickupDate, loanerCorrection.plannedPickupDate],
      ["Rückgabe", booking.expectedReturnDate, loanerCorrection.expectedReturnDate],
      ["KM Übergabe", booking.kmStart, loanerCorrection.kmStart],
      ["Tankstand Übergabe", booking.fuelStart, loanerCorrection.fuelStart],
      ["Auftrag", booking.orderNumber, loanerCorrection.orderNumber],
    ]
      .filter(([, before, after]) => String(before || "") !== String(after || ""))
      .map(([field, before, after]) => ({ field, before: before || "-", after: after || "-" }));
    const legalRelevantFields = [
      emailChanged ? "E-Mail" : "",
      booking.plannedPickupDate !== loanerCorrection.plannedPickupDate ? "Verleihbeginn" : "",
      booking.expectedReturnDate !== loanerCorrection.expectedReturnDate ? "Rückgabe" : "",
      String(booking.kmStart || "") !== String(loanerCorrection.kmStart || "") ? "KM Übergabe" : "",
      booking.fuelStart !== loanerCorrection.fuelStart ? "Tankstand Übergabe" : "",
    ].filter(Boolean);
    const needsNewCustomerApproval =
      legalRelevantFields.length > 0 &&
      ["accepted", "ready_for_checkout", "checked_out", "returned"].includes(
        booking.confirmationStatus || booking.status
      );
    const shouldMoveBackToAwaiting =
      needsNewCustomerApproval &&
      ["awaiting_customer_confirmation", "ready_for_checkout"].includes(booking.status);
    const correctionEvent = {
      type: "loaner_correction",
      at: now,
      by: profile?.email || session?.user?.email || "Benutzer",
      reason,
      changedFields: changes.map((change) => change.field),
      changes,
    };
    const resetEvent = needsNewCustomerApproval
      ? {
          type: "confirmation_reset",
          at: now,
          by: profile?.email || session?.user?.email || "Benutzer",
          reason: `Neue Kundenfreigabe erforderlich nach Änderung: ${legalRelevantFields.join(", ")}`,
          changedFields: legalRelevantFields,
          changes: changes.filter((change) => legalRelevantFields.includes(change.field)),
        }
      : null;
    const updatedBooking = {
      ...booking,
      customerFirstName: loanerCorrection.customerFirstName,
      customerLastName: loanerCorrection.customerLastName,
      customerName: nextName || booking.customerName,
      customerGender: loanerCorrection.customerGender,
      customerEmail: loanerCorrection.customerEmail,
      plannedPickupDate: loanerCorrection.plannedPickupDate,
      expectedReturnDate: loanerCorrection.expectedReturnDate,
      kmStart: loanerCorrection.kmStart,
      fuelStart: loanerCorrection.fuelStart,
      orderNumber: loanerCorrection.orderNumber,
      status: shouldMoveBackToAwaiting ? "awaiting_customer_confirmation" : booking.status,
      confirmationStatus: needsNewCustomerApproval ? "pending" : booking.confirmationStatus,
      confirmationAcceptedAt: needsNewCustomerApproval ? null : booking.confirmationAcceptedAt,
      confirmationViewedAt: needsNewCustomerApproval ? null : booking.confirmationViewedAt,
      confirmationSentAt:
        (emailChanged || needsNewCustomerApproval) && booking.status === "awaiting_customer_confirmation"
          ? null
          : needsNewCustomerApproval
            ? null
            : booking.confirmationSentAt,
      confirmationToken: booking.confirmationToken || makeToken(),
      confirmationEvents: [...(booking.confirmationEvents || []), correctionEvent, resetEvent].filter(Boolean),
      returnDamageNotes: [
        booking.returnDamageNotes,
        `Korrektur am ${fmtDateTime(now)} durch ${correctionEvent.by}: ${reason}`,
      ]
        .filter(Boolean)
        .join(" | "),
    };

    try {
      await persistBooking(updatedBooking);
      if (booking.cardId) {
        const linkedCard = cards.find((card) => isBookingLinkedToCard(booking, card));
        if (linkedCard) {
          await persistCard({
            ...linkedCard,
            customerFirstName: updatedBooking.customerFirstName,
            customerLastName: updatedBooking.customerLastName,
            customerName: updatedBooking.customerName,
            customerGender: updatedBooking.customerGender,
            customerEmail: updatedBooking.customerEmail,
            orderNumber: updatedBooking.orderNumber || linkedCard.orderNumber,
            history: [
              ...(linkedCard.history || []),
              {
                ts: now,
                by: profile?.email || session?.user?.email || CREATOR,
                action: `Leihwagen-Daten korrigiert: ${correctionEvent.changedFields.join(", ") || "Stammdaten"}`,
              },
            ],
          });
        }
      }
      setDbStatus(
        needsNewCustomerApproval
          ? "Leihwagen-Daten korrigiert. Rechtlich relevante Daten wurden geändert; bitte Kundenbestätigung erneut senden."
          : emailChanged
            ? "Leihwagen-Daten korrigiert. E-Mail wurde geändert; bitte Bestätigung erneut senden."
          : "Leihwagen-Daten korrigiert."
      );
      setLoanerCorrection(null);
    } catch (error) {
      setLoanerCorrection((prev) => ({ ...prev, saving: false, error: error.message }));
    }
  };

  const retryLoanerMediaSync = async (booking) => {
    if (!bookingHasInlineMedia(booking)) {
      setDbStatus("Für diesen Vorgang sind keine lokal wartenden Medien vorhanden.");
      return;
    }
    setMediaSyncStatus("Medien werden erneut synchronisiert ...");
    try {
      await persistBooking(booking);
      await hydrateBookingMedia(booking.id);
      setDbStatus("Medien-Synchronisierung wurde erneut angestoßen.");
    } catch (error) {
      setDbStatus(`Medien konnten nicht erneut synchronisiert werden (${error.message}).`);
    }
  };

  const softDeleteBookingDamageEntry = async (booking, damageEntry) => {
    const reason = window.prompt("Optionaler Löschgrund für diesen Schaden:", "");
    const now = new Date().toISOString();
    const actor = profile?.full_name || profile?.email || session?.user?.email || "Benutzer";
    const updatedBooking = {
      ...booking,
      damageEntries: (booking.damageEntries || []).map((entry) =>
        entry.id === damageEntry.id
          ? {
              ...entry,
              deletedAt: now,
              deletedBy: actor,
              deleteReason: reason || "",
            }
          : entry
      ),
      confirmationEvents: [
        ...(booking.confirmationEvents || []),
        {
          type: "damage_deleted",
          at: now,
          by: actor,
          reason: reason || "Schadenseintrag entfernt",
          damageId: damageEntry.id || "",
          damageDescription: damageEntry.description || "",
        },
      ],
    };
    try {
      await persistBooking(updatedBooking);
      persistCentralDamageEntries(updatedBooking).catch(() => undefined);
      setDbStatus("Schadenseintrag wurde als gelöscht markiert und bleibt im Verlauf nachvollziehbar.");
    } catch (error) {
      setDbStatus(`Schaden konnte nicht gelöscht werden: ${error.message}`);
    }
  };

  const updateActiveDamageLifecycle = async (damageEntry, status) => {
    const isRepair = status === "repaired";
    const actionLabel = isRepair ? "als behoben markieren" : "löschen";
    if (
      !window.confirm(
        `Schaden „${damageEntry.description || "Ohne Beschreibung"}“ wirklich ${actionLabel}?`
      )
    ) {
      return;
    }

    const actor =
      profile?.full_name ||
      profile?.email ||
      session?.user?.email ||
      "Benutzer";
    const now = new Date().toISOString();
    const sourceBooking =
      bookings.find((item) => item.id === damageEntry.sourceBookingId) || {
        id: damageEntry.sourceBookingId || "",
        loanerId: damageEntry.loanerId,
        createdAt: damageEntry.sourceDate || now,
      };
    const isCentralEntry = loanerDamages.some(
      (entry) => entry.id === damageEntry.id
    );
    const entryForSave = {
      ...damageEntry,
      centralDamageId:
        damageEntry.centralDamageId ||
        (isCentralEntry
          ? damageEntry.id
          : getCentralLoanerDamageId(sourceBooking.loanerId, damageEntry.id)),
    };

    try {
      await persistCentralLoanerDamage({
        booking: sourceBooking,
        entry: entryForSave,
        status,
        deletedPatch: {
          deleted_by: actor,
          deleted_at: now,
          delete_reason: isRepair
            ? "Schaden als behoben markiert"
            : "Schaden gelöscht",
        },
      });
      setDbStatus(
        isRepair
          ? "Schaden wurde als behoben markiert und aus den offenen Schäden entfernt."
          : "Schaden wurde gelöscht und aus den offenen Schäden entfernt."
      );
    } catch (error) {
      setDbStatus(
        `Schadensstatus konnte nicht gespeichert werden: ${error.message}`
      );
    }
  };

  const cancelLoanerBooking = async (booking) => {
    const cancellableStatuses = ["awaiting_customer_confirmation", "ready_for_checkout"];
    if (!cancellableStatuses.includes(booking.status)) {
      setDbStatus("Dieser Leihwagen-Vorgang kann nicht mehr abgebrochen werden.");
      return;
    }

    const confirmed = window.confirm(
      `Leihwagen-Vorgang ${booking.loanerPlate || ""} wirklich abbrechen? Das Fahrzeug wird wieder freigegeben.`
    );
    if (!confirmed) return;

    const loaner = loaners.find((item) => item.id === booking.loanerId);
    const cancelledBooking = {
      ...booking,
      status: "cancelled",
      returnDamageNotes: [
        booking.returnDamageNotes,
        `Vorgang am ${fmtDateTime(new Date().toISOString())} durch ${profile?.email || "Benutzer"} abgebrochen.`,
      ]
        .filter(Boolean)
        .join(" | "),
    };

    try {
      await persistBooking(cancelledBooking);

      const hasOtherActiveBooking = bookings.some(
        (entry) =>
          entry.id !== booking.id &&
          entry.loanerId === booking.loanerId &&
          isActiveBooking(entry)
      );

      if (loaner && !hasOtherActiveBooking && !isPermanentlyCheckedOutLoaner(loaner)) {
        await persistLoaner({
          ...loaner,
          status: "available",
        });
      }

      if (booking.cardId) {
        updateCard(booking.cardId, (card) => ({
          ...card,
          loanerId: "",
          loanerPlate: "",
          history: [
            ...(card.history || []),
            {
              ts: new Date().toISOString(),
              by: CREATOR,
              action: `Leihwagen-Vorgang abgebrochen -> ${booking.loanerPlate || "-"}`,
            },
          ],
        }));
      }

      setDbStatus(`Leihwagen-Vorgang ${booking.loanerPlate || ""} wurde abgebrochen.`);
    } catch (error) {
      setDbStatus(`Leihwagen-Vorgang konnte nicht abgebrochen werden: ${error.message}`);
    }
  };

  const markLoanerHandedOver = async (booking) => {
    const loaner = loaners.find((item) => item.id === booking.loanerId);
    if (!loaner) return;

    try {
      const updatedBooking = {
        ...booking,
        status: "checked_out",
        checkedOutAt: new Date().toISOString(),
        returnDamageNotes: [
          booking.returnDamageNotes,
          `Fahrzeug am ${fmtDateTime(new Date().toISOString())} zur Ausgabe markiert.`,
        ]
          .filter(Boolean)
          .join(" | "),
      };
      await persistBooking(updatedBooking);
      await persistLoaner({
        ...loaner,
        status: "out",
      });
    } catch (error) {
      setDbStatus(error.message);
    }
  };

  const openReturnPanel = async (booking) => {
    const hydratedBooking = await hydrateBookingMedia(booking.id);
    const nextBooking = hydratedBooking || booking;
    let nextReturnState = {
      ...getDefaultReturnState(nextBooking),
      kmEnd: nextBooking.kmEnd || "",
      fuelEnd: nextBooking.fuelEnd || "Voll",
    };
    let nextTab = "employee";
    try {
      const saved = JSON.parse(localStorage.getItem(RETURN_DRAFT_STORAGE_KEY) || "null");
      if (saved?.savedAt && !isStoredDraftFresh(saved.savedAt)) {
        localStorage.removeItem(RETURN_DRAFT_STORAGE_KEY);
        throw new Error("Alter Rücknahme-Entwurf verworfen");
      }
      if (canRestoreReturnDraft(saved?.returnState) && saved.returnState.bookingId === nextBooking.id) {
        const restore = window.confirm(
          `Es gibt einen lokal gespeicherten Rücknahme-Entwurf${saved.savedAt ? ` vom ${fmtDateTime(saved.savedAt)}` : ""}. Fortsetzen?`
        );
        if (restore) {
          const savedDamageEntries = getRelevantReturnDamageEntries(
            saved.returnState.returnDamageEntries
          );
          const legacyDamageNotes = String(
            saved.returnState.returnDamageNotes || ""
          ).trim();
          const shouldMigrateLegacyDamageNotes =
            savedDamageEntries.length === 0 && Boolean(legacyDamageNotes);
          nextReturnState = {
            ...nextReturnState,
            ...saved.returnState,
            returnDamageEntries: ensureTrailingReturnDamageEntry(
              shouldMigrateLegacyDamageNotes
                ? [
                    {
                      ...createEmptyReturnDamageEntry(),
                      description: legacyDamageNotes,
                    },
                  ]
                : saved.returnState.returnDamageEntries
            ),
            returnDamageNotes: shouldMigrateLegacyDamageNotes
              ? ""
              : saved.returnState.returnDamageNotes || "",
            isSubmitting: false,
            error: "Rücknahme-Entwurf wurde automatisch wiederhergestellt.",
          };
          nextTab = saved.activeTab || "employee";
          setReturnDraftSavedAt(saved.savedAt || null);
        }
      }
    } catch {
      // Ungültige lokale Rücknahme-Entwürfe werden ignoriert.
    }
    setReturnState(nextReturnState);
    setReturnTabs((prev) => ({ ...prev, [nextBooking.id]: nextTab }));
  };

  const closeReturnPanel = ({ discardDraft = false } = {}) => {
    if (discardDraft) {
      localStorage.removeItem(RETURN_DRAFT_STORAGE_KEY);
      setReturnDraftSavedAt(null);
    }
    setReturnState(getDefaultReturnState());
  };

  const validateReturnDamageDocumentation = (bookingId = returnState.bookingId) => {
    const incompleteEntries = getIncompleteReturnDamageEntries(
      returnState.returnDamageEntries
    );
    if (incompleteEntries.length === 0) return true;

    setReturnState((prev) => ({
      ...prev,
      error:
        "Zu jedem dokumentierten Schaden sind eine Beschreibung und mindestens ein zusätzliches Foto erforderlich.",
    }));
    if (bookingId) {
      setReturnTabs((prev) => ({ ...prev, [bookingId]: "condition" }));
    }
    return false;
  };

  const validateReturnFuelReceiptPhoto = (
    bookingId = returnState.bookingId
  ) => {
    if (returnState.fuelReceiptPhoto) return true;

    setReturnState((prev) => ({
      ...prev,
      error:
        "Bitte hinterlegen Sie ein Foto der Tankrechnung, bevor Sie fortfahren.",
    }));
    if (bookingId) {
      setReturnTabs((prev) => ({ ...prev, [bookingId]: "condition" }));
    }
    return false;
  };

  const navigateReturnStep = (bookingId, nextTab) => {
    const stepOrder = ["employee", "compare", "condition", "finish"];
    const currentTab = returnTabs[bookingId] || "compare";
    const movesForward =
      stepOrder.indexOf(nextTab) > stepOrder.indexOf(currentTab);
    if (movesForward && !validateReturnDamageDocumentation(bookingId)) return;
    if (
      movesForward &&
      nextTab === "finish" &&
      !validateReturnFuelReceiptPhoto(bookingId)
    ) {
      return;
    }
    setReturnTabs((prev) => ({ ...prev, [bookingId]: nextTab }));
  };

  const attemptCloseReturnPanel = (bookingId) => {
    if (!validateReturnDamageDocumentation(bookingId)) return;
    const discard = window.confirm(
      "Rücknahme schließen?\n\nOK = schließen und lokalen Entwurf löschen\nAbbrechen = nur Fenster schließen, Entwurf bleibt erhalten"
    );
    closeReturnPanel({ discardDraft: discard });
  };

  const completeReturn = async ({ forceOverwrite = false } = {}) => {
    const booking = bookings.find((item) => item.id === returnState.bookingId);
    const loaner = loaners.find((item) => item.id === booking?.loanerId);
    if (!booking || !loaner) return;

    if (!validateReturnDamageDocumentation(booking.id)) return;
    if (!validateReturnFuelReceiptPhoto(booking.id)) return;

    if (!returnState.returnEmployeeId) {
      setReturnState((prev) => ({
        ...prev,
        error: "Bitte zuerst auswählen, wer die Rücknahme durchführt.",
      }));
      setReturnTabs((prev) => ({ ...prev, [booking.id]: "employee" }));
      return;
    }

    if (!returnState.kmEnd || !returnState.fuelEnd) {
      setReturnState((prev) => ({
        ...prev,
        error: "Bitte KM-Stand und Tankstand der Rückgabe erfassen.",
      }));
      return;
    }

    const kmStartValue = Number(booking.kmStart || 0);
    const kmEndValue = Number(returnState.kmEnd || 0);
    const correctedKmStartValue = Number(returnState.correctedKmStart || 0);
    const hasKmMismatch =
      kmStartValue > 0 && kmEndValue > 0 && kmEndValue < kmStartValue;
    const hasValidKmStartCorrection =
      hasKmMismatch &&
      correctedKmStartValue > 0 &&
      correctedKmStartValue <= kmEndValue;

    if (hasKmMismatch && !hasValidKmStartCorrection) {
      setReturnState((prev) => ({
        ...prev,
        error:
          "Der KM-Stand der Rückgabe ist niedriger als bei der Übergabe. Bitte entweder den Rückgabe-KM korrigieren oder den ursprünglichen Übergabe-KM unten korrigieren.",
      }));
      setReturnTabs((prev) => ({ ...prev, [booking.id]: "condition" }));
      return;
    }

    const allReturnComparisonPhotosPresent = PHOTO_LABELS.every(
      (entry) => Boolean(returnState.comparisonPhotos?.[entry.key])
    );

    if (!allReturnComparisonPhotosPresent) {
      setReturnState((prev) => ({
        ...prev,
        error: "Bitte für vorne, Fahrerseite, hinten und Beifahrerseite je ein neues Rückgabe-Foto aufnehmen.",
      }));
      return;
    }

    const documentedReturnDamages = getRelevantReturnDamageEntries(
      returnState.returnDamageEntries
    ).map((entry) => ({
      ...entry,
      description: String(entry.description || "").trim(),
      photos: Array.isArray(entry.photos) ? entry.photos : [],
    }));

    setReturnState((prev) => ({ ...prev, isSubmitting: true, error: "" }));

    try {
      const { data: latestBooking, error: latestError } = await supabase
        .from("loaner_bookings")
        .select("id,status,checked_in_at,return_damage_notes,return_employee_name")
        .eq("id", booking.id)
        .maybeSingle();

      if (latestError) throw latestError;

      const alreadyReturned =
        latestBooking?.checked_in_at ||
        latestBooking?.status === "returned";

      if (!forceOverwrite && alreadyReturned && latestBooking.checked_in_at !== booking.checkedInAt) {
        const returnEmployee =
          latestBooking.return_employee_name ||
          extractEmployeeFromNote(
            latestBooking.return_damage_notes,
            "Rücknahme durchgeführt durch"
          ) || "einem Mitarbeiter";
        const returnTime = latestBooking.checked_in_at
          ? fmtDateTime(latestBooking.checked_in_at)
          : "einem unbekannten Zeitpunkt";
        setReturnConflict({
          bookingId: booking.id,
          employee: returnEmployee,
          time: returnTime,
        });
        setReturnState((prev) => ({ ...prev, isSubmitting: false }));
        return;
      }

      const orderedReturnPhotos = PHOTO_LABELS.map((entry) => ({
        id: `return_${entry.key}_${Date.now()}`,
        url: returnState.comparisonPhotos[entry.key],
        name: `Rückgabe ${entry.label}`,
        key: entry.key,
        type: "return_comparison",
      }));
      const returnDamagePhotos = documentedReturnDamages.flatMap((damage, damageIndex) =>
        damage.photos.map((photo, photoIndex) => ({
          id: photo.id || `return_damage_${damageIndex}_${photoIndex}_${Date.now()}`,
          url: photo.url,
          name:
            photo.name ||
            `Rückgabeschaden ${damageIndex + 1} - Foto ${photoIndex + 1}`,
          key: `returnDamage_${damage.id}_${photoIndex}`,
          type: "return_damage",
          damageId: damage.id,
          description: damage.description,
        }))
      );
      const returnDamageReportedAt = new Date().toISOString();
      const returnLifecycleDamageEntries = documentedReturnDamages.map((damage) => ({
        id: damage.id,
        url: damage.photos[0]?.url || "",
        description: damage.description,
        reportedBy: returnState.returnEmployeeName,
        reportedAt: returnDamageReportedAt,
      }));
      const returnProtocol = [
        `Rücknahmeprotokoll ${fmtDateTime(new Date().toISOString())}`,
        `Mitarbeiter: ${returnState.returnEmployeeName || "-"}`,
        `KM Übergabe: ${hasValidKmStartCorrection ? returnState.correctedKmStart : booking.kmStart || "-"}`,
        `KM Rückgabe: ${returnState.kmEnd}`,
        `Tank Übergabe: ${booking.fuelStart || "-"}`,
        `Tank Rückgabe: ${returnState.fuelEnd}`,
        "Tankrechnung: erhalten und fotografiert",
        `Rückgabe-Fotos: ${orderedReturnPhotos.filter((entry) => entry.url).length}/4`,
        `Neue Schäden: ${documentedReturnDamages.length}`,
        `Zusätzliche Schadenfotos: ${returnDamagePhotos.length}`,
      ].join(" | ");
      const updatedBooking = {
        ...booking,
        status: "returned",
        actualReturnDate: new Date().toISOString(),
        checkedInAt: new Date().toISOString(),
        kmStart: hasValidKmStartCorrection ? returnState.correctedKmStart : booking.kmStart,
        kmEnd: returnState.kmEnd,
        fuelEnd: returnState.fuelEnd,
        returnPhotos: returnState.fuelReceiptPhoto
          ? [
              ...orderedReturnPhotos,
              ...returnDamagePhotos,
              {
                id: `return_fuel_receipt_${Date.now()}`,
                url: returnState.fuelReceiptPhoto,
                name: "Tankrechnung Rückgabe",
                key: "fuelReceipt",
                type: "fuel_receipt",
              },
            ]
          : [...orderedReturnPhotos, ...returnDamagePhotos],
        returnEmployeeName: returnState.returnEmployeeName,
        returnDamageNotes: [
          hasValidKmStartCorrection
            ? `KM-Stand bei Übergabe wurde bei der Rücknahme von ${booking.kmStart || "-"} auf ${returnState.correctedKmStart} korrigiert.`
            : "",
          returnState.returnEmployeeName
            ? `Rücknahme durchgeführt durch ${returnState.returnEmployeeName}.`
            : "",
          returnProtocol,
          returnState.returnDamageNotes,
          ...documentedReturnDamages.map(
            (damage, index) =>
              `Neuer Schaden ${index + 1}: ${damage.description} (${damage.photos.length} Foto${damage.photos.length === 1 ? "" : "s"})`
          ),
        ]
          .filter(Boolean)
          .join(" | "),
      };
      const savedBooking = await persistBooking(updatedBooking);
      try {
        const persistedReturnDamages = getReturnDamageGroups(
          savedBooking || updatedBooking
        ).map((entry) => ({
          id: entry.id,
          url: entry.photos[0]?.url || "",
          description: entry.description || "",
          reportedBy: returnState.returnEmployeeName,
          reportedAt: returnDamageReportedAt,
        }));
        await persistCentralDamageEntries({
          ...(savedBooking || updatedBooking),
          damageEntries: [
            ...((savedBooking || updatedBooking).damageEntries || []),
            ...(persistedReturnDamages.length > 0
              ? persistedReturnDamages
              : returnLifecycleDamageEntries),
          ],
        });
      } catch (damageError) {
        setDbStatus(
          `Rücknahme wurde gespeichert. Die zentrale Schadensliste konnte nicht sofort synchronisiert werden (${damageError.message}).`
        );
      }
      const updatedLoaner = {
          ...loaner,
          status: "available",
        };
      const loanerSaved = await persistLoaner(updatedLoaner);
      if (!loanerSaved) {
        queueOfflineReturn({ booking: updatedBooking, loaner: updatedLoaner });
        setDbStatus(
          `Rücknahme ${booking.loanerPlate} wurde lokal gespeichert und wird automatisch synchronisiert, sobald die Verbindung stabil ist.`
        );
      } else {
        setDbStatus(`Rücknahme ${booking.loanerPlate} abgeschlossen. Fahrzeug ist wieder verfügbar.`);
      }
        closeReturnPanel({ discardDraft: true });
      } catch (error) {
      setReturnState((prev) => ({
        ...prev,
        isSubmitting: false,
        error: error.message,
      }));
    }
  };

  const saveLoanerDraft = async () => {
    if (!loanerDraft.plate) {
      setLoanerFormMessage("Bitte mindestens ein Kennzeichen eintragen.");
      return;
    }

    const loaner = {
      ...loanerDraft,
      id: loanerDraft.id || "loaner_" + Date.now(),
    };
    const ok = await persistLoaner(loaner);
    if (!ok) return;

    setLoanerDraft(getDefaultLoanerDraft());
    setLoanerEditorOpen(false);
    setLoanerFormMessage("Leihwagen gespeichert.");
  };

  const downloadCsv = (name, rows) => {
    const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const csv = "\uFEFF" + rows.map((row) => row.map(csvCell).join(";")).join("\n");
    const link = document.createElement("a");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    link.href = url;
    link.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadJson = (name, data) => {
    const link = document.createElement("a");
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(data, null, 2)], { type: "application/json;charset=utf-8" })
    );
    link.href = url;
    link.download = `${name}-${new Date().toISOString().slice(0, 10)}.json`;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportLoanerManagementReport = () => {
    downloadJson("leihwagen-management-watchdog", {
      version: APP_BUILD_LABEL,
      exportedAt: new Date().toISOString(),
      filters: managementLoanerFilters,
      kpis: loanerManagement.kpis,
      processHealth: loanerManagement.processHealth,
      periodComparison: loanerManagement.periodComparison,
      statusDistribution: loanerManagement.statusDistribution,
      recurring: loanerManagement.recurring,
      topCauses: loanerManagement.topCauses,
      managementAlerts: loanerManagement.managementAlerts.map((alert) => ({
        severity: alert.severity,
        title: alert.title,
        target: alert.target,
        cause: alert.cause,
        recommendation: alert.recommendation,
        urgency: alert.urgency,
      })),
      criticalCases: loanerManagement.criticalCases.map(({ booking, linkedCard, assessment, branch, responsible }) => ({
        bookingId: booking.id,
        orderNumber: booking.orderNumber,
        customerName: booking.customerName,
        loaner: `${booking.loanerBrand || ""} ${booking.loanerModel || ""} ${booking.loanerPlate || ""}`.trim(),
        branch: branch ? getBranchLabel(branch) : "",
        responsible,
        status: getBookingStatusLabel(booking.status),
        risk: assessment.label,
        score: assessment.score,
        estimatedCost: assessment.cost.total,
        durationDays: assessment.cost.durationDays,
        linkedOrderId: linkedCard?.id || "",
        warnings: assessment.warnings,
      })),
      allCases: loanerManagement.items.map(({ booking, assessment, branch, responsible }) => ({
        bookingId: booking.id,
        orderNumber: booking.orderNumber,
        customerName: booking.customerName,
        customerEmail: booking.customerEmail,
        loanerPlate: booking.loanerPlate,
        branch: branch ? getBranchLabel(branch) : "",
        responsible,
        status: getBookingStatusLabel(booking.status),
        risk: assessment.label,
        score: assessment.score,
        estimatedCost: assessment.cost.total,
        durationDays: assessment.cost.durationDays,
        warningCount: assessment.warnings.length,
      })),
    });
    setDbStatus("Leihwagen-Watchdog-Bericht wurde exportiert.");
  };

  const openLoanerManagementCase = (entry) => {
    if (entry?.linkedCard?.id) {
      openTaskDetail(entry.linkedCard.id);
      return;
    }
    setLoanerBoardTab("history");
    setLoanerHistorySearch(entry?.booking?.loanerPlate || entry?.booking?.orderNumber || "");
    setUi((prev) => ({ ...prev, activeView: "Leihwagen" }));
  };

  const exportBookingDocumentationPackage = async (booking) => {
    const hydrated = await hydrateBookingMedia(booking.id);
    const nextBooking = hydrated || booking;
    const linkedCard = cards.find((card) => isBookingLinkedToCard(nextBooking, card));
    const auditEvents = (systemStatus?.recentAudit || []).filter((entry) => {
      const haystack = [
        entry.entity_id,
        entry.details?.orderNumber,
        entry.details?.bookingId,
        entry.details?.customerEmail,
        nextBooking.id,
        nextBooking.orderNumber,
        nextBooking.customerEmail,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return (
        haystack.includes(String(nextBooking.id || "").toLowerCase()) ||
        haystack.includes(String(nextBooking.orderNumber || "").toLowerCase()) ||
        haystack.includes(String(nextBooking.customerEmail || "").toLowerCase())
      );
    });
    buildBookingPdf(nextBooking);
    downloadJson(
      `dokumentationspaket-${nextBooking.loanerPlate || nextBooking.orderNumber || nextBooking.id}`,
      buildBookingPackage({ booking: nextBooking, card: linkedCard, auditEvents, includeSensitiveMedia: isAdmin })
    );
    setDbStatus("Dokumentationspaket wurde als PDF und JSON exportiert.");
  };

  const exportBoardCsv = (scope = "all") => {
    const section = (title, header, rows) => [
      [title],
      header,
      ...rows,
      [],
    ];
    const cardRows = cards.map((card) => [
      card.id,
      card.orderNumber,
      card.customerName,
      card.customerEmail,
      card.licensePlate,
      card.branch ? getBranchLabel(card.branch) : "",
      card.role,
      card.workStatus,
      card.prio,
      card.due,
      calcProgress(card.subs || []),
      card.needsLoaner ? "ja" : "nein",
      card.caseType ? getCaseTypeLabel(card.caseType) : "",
      card.nextAction,
      card.sourceLabel,
      card.externalReference,
    ]);
    const loanerRows = loaners.map((loaner) => [
      loaner.id,
      loaner.plate,
      loaner.brand,
      loaner.model,
      loaner.vin,
      getLoanerUsageLabel(loaner.usageType),
      loaner.homeBranch ? getBranchLabel(loaner.homeBranch) : "",
      loaner.status,
      loaner.permanentlyCheckedOut ? "dauerhaft verliehen" : "",
      loaner.isActive ? "aktiv" : "inaktiv",
      loaner.registrationStatus,
      loaner.tireType,
      loaner.hasDepot ? "ja" : "nein",
      loaner.vignetteDate,
      loaner.hasParkingPermit ? "ja" : "nein",
      loaner.engineType,
      loaner.notes,
    ]);
    const bookingRows = bookings.map((booking) => [
      booking.id,
      booking.orderNumber,
      booking.customerName,
      booking.customerEmail,
      booking.loanerPlate,
      booking.loanerBrand,
      booking.loanerModel,
      booking.loanerHomeBranch ? getBranchLabel(booking.loanerHomeBranch) : "",
      getLoanerUsageLabel(booking.loanerUsageType),
      booking.status,
      booking.plannedPickupDate,
      booking.expectedReturnDate,
      booking.actualReturnDate,
      booking.kmStart,
      booking.kmEnd,
      booking.fuelStart,
      booking.fuelEnd,
      getInsuranceLabel(booking.insuranceOption),
      booking.confirmationStatus,
      booking.confirmationSentAt,
      booking.confirmationViewedAt,
      booking.confirmationAcceptedAt,
      booking.checkedOutAt,
      getBookingHandoverEmployee(booking),
      booking.checkedInAt,
      getBookingReturnEmployee(booking),
      getBookingPhotoChecklist(booking)
        .map((entry) => `${entry.label}: ${entry.value}`)
        .join(" | "),
      booking.returnDamageNotes,
    ]);
    const bluePlateRows = bluePlates.map((plate) => [
      plate.id,
      plate.plate,
      plate.isActive ? "aktiv" : "inaktiv",
      plate.notes,
    ]);
    const cardSection = section(
        "AUFTRAEGE",
        [
          "id",
          "auftragsnummer",
          "kunde",
          "email",
          "kennzeichen",
          "filiale",
          "abteilung",
          "status",
          "prioritaet",
          "faellig",
          "fortschritt",
          "leihwagenbedarf",
          "falltyp",
          "naechster_schritt",
          "quelle",
          "externe_referenz",
        ],
        cardRows
      );
    const loanerSection = section(
        "LEIHWAGEN",
        [
          "id",
          "kennzeichen",
          "marke",
          "modell",
          "fahrgestellnummer",
          "nutzung",
          "filiale",
          "verfuegbarkeit",
          "dauerhaft_verliehen",
          "aktiv",
          "anmeldestatus",
          "bereifung",
          "depot",
          "vignette",
          "parkpickerl",
          "motorart",
          "notizen",
        ],
        loanerRows
      );
    const bookingSection = section(
        "LEIHVORGAENGE",
        [
          "id",
          "auftragsnummer",
          "kunde",
          "email",
          "kennzeichen",
          "marke",
          "modell",
          "filiale",
          "nutzung",
          "status",
          "von",
          "bis",
          "rueckgabe_am",
          "km_uebergabe",
          "km_rueckgabe",
          "tank_uebergabe",
          "tank_rueckgabe",
          "tarif",
          "kundenbestaetigung",
          "email_gesendet_am",
          "bestaetigung_angesehen_am",
          "bestaetigt_am",
          "uebergeben_am",
          "uebergabe_mitarbeiter",
          "zurueckgenommen_am",
          "ruecknahme_mitarbeiter",
          "foto_status",
          "ruecknahme_notizen",
        ],
        bookingRows
      );
    const bluePlateSection = section("BLAUE_KENNZEICHEN", ["id", "kennzeichen", "aktiv", "notizen"], bluePlateRows);

    const scopes = {
      cards: ["autohaus-auftraege", cardSection],
      loaners: ["autohaus-fahrzeugbestand", loanerSection],
      bookings: ["autohaus-leihwagenhistorie", bookingSection],
      bluePlates: ["autohaus-blaue-kennzeichen", bluePlateSection],
      all: ["autohaus-tool-gesamtexport", [...cardSection, ...loanerSection, ...bookingSection, ...bluePlateSection]],
    };
    const [fileName, rows] = scopes[scope] || scopes.all;
    downloadCsv(fileName, rows);
  };

  const renderIntakePanel = () => {
    if (ui.activeView !== "Annahme") return null;

    const intakeFlow = getDepartmentFlow(intakeDraft.caseType);
    const nextDepartment = intakeFlow[1] || "Werkstatt";
    const selectedMode = "task";
    const isLoanerMode = intakeDraft.caseType === "loaner";
    const isInternalIntakeOrder = intakeDraft.orderType === "internal";
    const setIntakeMode = (mode) =>
      setIntakeDraft((prev) => ({
        ...prev,
        mode,
        needsLoaner: mode === "task" ? prev.needsLoaner : true,
      }));
    const intakeTabs = [
      { id: "core", label: "1. Auftrag" },
      { id: "customer", label: "2. Kunde" },
      { id: "details", label: "3. Details" },
    ];
    const fieldErrorClass = (field) => (intakeFieldErrors[field] ? "fieldInvalid" : "");
    const updateIntakeField = (field, value) => {
      clearIntakeFieldError(field);
      setIntakeDraft((prev) => ({ ...prev, [field]: value }));
    };
    const modeCards = [
      {
        id: "task",
        tag: "Standard",
        title: "Neuen Werkstattauftrag anlegen",
        text: "Service, Schaden, Reparatur, Leihwagenbedarf oder Sonstiges sauber für die passende Abteilung anlegen.",
      },
    ];
    const recommendedIntakeTechnicians = getEmployeesForBranchAndTypes(
      getRecommendedTechnicians(intakeDraft),
      intakeDraft.branch || profile?.branch,
      ["Techniker"]
    );

    return (
      <div className="card fullSpan intakeWorkspaceCard">
        <div className="intakeHero">
          <div className="intakeHeaderRow">
            <span className="sectionTag">Annahme</span>
            <InfoHint text="Hier legst du neue Fälle an, markierst Leihwagenbedarf oder startest direkt eine Verleihung." />
          </div>

          <div className="intakeModeGrid">
            {modeCards.map((mode) => (
              <button
                type="button"
                key={mode.id}
                className={`intakeModeCard ${selectedMode === mode.id ? "active" : ""}`}
                onClick={() => setIntakeMode(mode.id)}
              >
                <div className="modeCardTopRow">
                  <span className="sectionTag">{mode.tag}</span>
                  <InfoHint text={mode.text} />
                </div>
                <span className="modeCardTitle">{mode.title}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="intakeSummaryStrip compact">
          <div className="summaryBadge">
            <span>Falltyp</span>
            <strong>{getCaseTypeLabel(intakeDraft.caseType)}</strong>
          </div>
          <div className="summaryBadge">
            <span>Auftragstyp</span>
            <strong>{getOrderTypeLabel(intakeDraft.orderType)}</strong>
          </div>
          <div className="summaryBadge">
            <span>Filiale</span>
            <strong>{getBranchLabel(intakeDraft.branch) || "Bitte wählen"}</strong>
          </div>
          <div className="summaryBadge">
            <span>Nächste Abteilung</span>
            <strong>{nextDepartment}</strong>
          </div>
          <div className="summaryBadge subtle">
            <span>Leihwagen</span>
            <strong>{isLoanerMode || intakeDraft.needsLoaner ? "aktiv" : "optional"}</strong>
          </div>
        </div>

        <div className="intakeStageGrid">
          <section className="intakeMainColumn">
            <div className="intakeSection">
              <div className="intakeHeaderRow compact">
                <span className="sectionTag">Pflichtangaben</span>
                <InfoHint text="Nur die Pflichtangaben ausfüllen. Alles Weitere kommt erst danach." />
              </div>

              <div className="detailTabBar intakeTabBar">
                {intakeTabs.map((tab) => (
                  <button
                    type="button"
                    key={tab.id}
                    className={`detailTabButton ${intakeTab === tab.id ? "active" : ""}`}
                    onClick={() => setIntakeTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {intakeTab === "core" && (
                <div className="formGrid intakeEssentialGrid">
                  <label>
                    <span className="fieldHeader"><span>Auftragstyp</span><InfoHint text="Extern benötigt Kundenbestätigung per E-Mail. Intern läuft ohne Bestätigungslink, E-Mail und Unterschrift." /></span>
                    <select
                      className={fieldErrorClass("orderType")}
                      value={intakeDraft.orderType}
                      onChange={(event) =>
                        updateIntakeField("orderType", event.target.value)
                      }
                    >
                      {ORDER_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span className="fieldHeader"><span>Falltyp</span><InfoHint text="Wähle, ob es sich um Service, Schaden, Reparatur oder Sonstiges handelt." /></span>
                    <select
                      value={intakeDraft.caseType}
                      onChange={(event) =>
                        setIntakeDraft((prev) => ({ ...prev, caseType: event.target.value }))
                      }
                    >
                      {CASE_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span className="fieldHeader"><span>Filiale</span><InfoHint text="Standort des Falls auswählen: 21 oder 22." /></span>
                    <select
                      className={fieldErrorClass("branch")}
                      value={intakeDraft.branch}
                      onChange={(event) =>
                        updateIntakeField("branch", event.target.value)
                      }
                    >
                      <option value="">Filiale wählen</option>
                      {BRANCH_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span className="fieldHeader"><span>Kennzeichen</span><InfoHint text="Kennzeichen des Fahrzeugs eintragen, falls bereits bekannt." /></span>
                    <input
                      className={fieldErrorClass("licensePlate")}
                      placeholder="Kennzeichen"
                      value={intakeDraft.licensePlate}
                      onChange={(event) =>
                        updateIntakeField("licensePlate", event.target.value)
                      }
                    />
                  </label>

                  <label>
                    <span className="fieldHeader"><span>Fälligkeit</span><InfoHint text="Bis wann der nächste Bearbeitungsschritt idealerweise erledigt sein soll." /></span>
                    <input
                      type="date"
                      value={intakeDraft.due}
                      onChange={(event) =>
                        setIntakeDraft((prev) => ({ ...prev, due: event.target.value }))
                      }
                    />
                  </label>

                  <label>
                    <span className="fieldHeader"><span>Geplante Dauer</span><InfoHint text="Vorgeschlagene Arbeitszeit für die Werkstattplanung. Leer lassen nutzt den Standardwert des Falltyps." /></span>
                    <input
                      type="number"
                      min="0.25"
                      step="0.25"
                      placeholder={`${formatHours(CASE_TYPE_ESTIMATED_HOURS[intakeDraft.caseType] || CASE_TYPE_ESTIMATED_HOURS.other)} vorgeschlagen`}
                      value={intakeDraft.estimatedHours}
                      onChange={(event) =>
                        setIntakeDraft((prev) => ({ ...prev, estimatedHours: event.target.value }))
                      }
                    />
                  </label>

                  <label>
                    <span className="fieldHeader"><span>Werkstatt-Zuweisung</span><InfoHint text="Optional: Den Auftrag direkt einem Techniker oder Mitarbeiter zuweisen." /></span>
                    <select
                      value={intakeDraft.assignedTech}
                      onChange={(event) =>
                        setIntakeDraft((prev) => ({ ...prev, assignedTech: event.target.value }))
                      }
                    >
                      <option value="">Noch nicht zuweisen</option>
                      {recommendedIntakeTechnicians.map((tech, index) => (
                        <option key={tech.id} value={tech.id}>
                          {index === 0 ? "Empfohlen: " : ""}{tech.name} · {tech.recommendation?.reasons?.join(", ") || "verfügbar"}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="fullField">
                    <span className="fieldHeader"><span>Titel / Anliegen</span><InfoHint text="Kurz und klar beschreiben, worum es geht." /></span>
                    <input
                      className={fieldErrorClass("title")}
                      value={intakeDraft.title}
                      onChange={(event) =>
                        updateIntakeField("title", event.target.value)
                      }
                      placeholder="z. B. Hyundai Kona – Service und Annahme"
                    />
                  </label>

                  <div className="fullField intakeSubsectionTitle">
                    <span className="sectionTag">Fahrzeugdaten</span>
                  </div>

                  <label>
                    <span className="fieldHeader"><span>Fahrzeugmarke</span><InfoHint text="Hersteller des Kundenfahrzeugs." /></span>
                    <input
                      className={fieldErrorClass("vehicleBrand")}
                      list="vehicle-brand-suggestions"
                      placeholder="Fahrzeugmarke"
                      value={intakeDraft.vehicleBrand}
                      onChange={(event) =>
                        updateIntakeField("vehicleBrand", event.target.value)
                      }
                    />
                    <datalist id="vehicle-brand-suggestions">
                      {VEHICLE_BRAND_SUGGESTIONS.map((brand) => (
                        <option key={brand} value={brand} />
                      ))}
                    </datalist>
                  </label>

                  <label>
                    <span className="fieldHeader"><span>Fahrzeugmodell</span><InfoHint text="Modellbezeichnung des Kundenfahrzeugs." /></span>
                    <input
                      className={fieldErrorClass("vehicleModel")}
                      placeholder="Fahrzeugmodell"
                      value={intakeDraft.vehicleModel}
                      onChange={(event) =>
                        updateIntakeField("vehicleModel", event.target.value)
                      }
                    />
                  </label>

                  <label>
                    <span className="fieldHeader"><span>Erstzulassung</span><InfoHint text="Vollständiges Datum der Erstzulassung im Kurzformat." /></span>
                    <input
                      className={fieldErrorClass("firstRegistrationDate")}
                      type="date"
                      value={intakeDraft.firstRegistrationDate}
                      onChange={(event) =>
                        updateIntakeField("firstRegistrationDate", event.target.value)
                      }
                    />
                  </label>

                  <label>
                    <span className="fieldHeader"><span>Fahrgestellnummer</span><InfoHint text="VIN / Fahrgestellnummer des Kundenfahrzeugs." /></span>
                    <input
                      className={fieldErrorClass("vin")}
                      placeholder="Fahrgestellnummer"
                      value={intakeDraft.vin}
                      onChange={(event) =>
                        updateIntakeField("vin", event.target.value)
                      }
                    />
                  </label>

                  <label>
                    <span className="fieldHeader"><span>Motorart</span><InfoHint text="Antriebsenergie des Kundenfahrzeugs." /></span>
                    <select
                      value={intakeDraft.engineType}
                      onChange={(event) =>
                        setIntakeDraft((prev) => ({ ...prev, engineType: event.target.value }))
                      }
                    >
                      <option value="">Motorart wählen</option>
                      {ENGINE_TYPE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span className="fieldHeader"><span>Antriebsart</span><InfoHint text="Getriebeart des Kundenfahrzeugs." /></span>
                    <select
                      value={intakeDraft.transmissionType}
                      onChange={(event) =>
                        setIntakeDraft((prev) => ({ ...prev, transmissionType: event.target.value }))
                      }
                    >
                      <option value="">Antriebsart wählen</option>
                      {TRANSMISSION_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span className="fieldHeader"><span>Kilometerstand</span><InfoHint text="Aktueller Kilometerstand bei Annahme." /></span>
                    <input
                      className={fieldErrorClass("mileage")}
                      type="number"
                      placeholder="Kilometerstand"
                      value={intakeDraft.mileage}
                      onChange={(event) =>
                        updateIntakeField("mileage", event.target.value)
                      }
                    />
                  </label>

                  <label>
                    <span className="fieldHeader"><span>Nationaler Code</span><InfoHint text="Optionaler Primary / Secondary Key zur späteren Datenanreicherung." /></span>
                    <input
                      placeholder="Nationaler Code"
                      value={intakeDraft.nationalCode}
                      onChange={(event) =>
                        setIntakeDraft((prev) => ({ ...prev, nationalCode: event.target.value }))
                      }
                    />
                  </label>

                  <label>
                    <span className="fieldHeader"><span>Eigengewicht</span><InfoHint text="Eigengewicht laut Fahrzeugdaten." /></span>
                    <input
                      type="number"
                      placeholder="kg"
                      value={intakeDraft.curbWeight}
                      onChange={(event) =>
                        setIntakeDraft((prev) => ({ ...prev, curbWeight: event.target.value }))
                      }
                    />
                  </label>

                  <label>
                    <span className="fieldHeader"><span>Höchstzulässiges Gesamtgewicht</span><InfoHint text="Höchstzulässiges Gesamtgewicht laut Fahrzeugdaten." /></span>
                    <input
                      type="number"
                      placeholder="kg"
                      value={intakeDraft.grossWeight}
                      onChange={(event) =>
                        setIntakeDraft((prev) => ({ ...prev, grossWeight: event.target.value }))
                      }
                    />
                  </label>
                </div>
              )}

              {intakeTab === "customer" && (
                <div className="formGrid intakeEssentialGrid">
                  <label>
                    <span className="fieldHeader"><span>Vorname</span><InfoHint text="Vorname des Kunden." /></span>
                    <input
                      className={fieldErrorClass("customerFirstName")}
                      placeholder="Vorname"
                      value={intakeDraft.customerFirstName}
                      onChange={(event) =>
                        updateIntakeField("customerFirstName", event.target.value)
                      }
                    />
                  </label>

                  <label>
                    <span className="fieldHeader"><span>Nachname</span><InfoHint text="Nachname des Kunden." /></span>
                    <input
                      className={fieldErrorClass("customerLastName")}
                      placeholder="Nachname"
                      value={intakeDraft.customerLastName}
                      onChange={(event) =>
                        updateIntakeField("customerLastName", event.target.value)
                      }
                    />
                  </label>

                  <label>
                    <span className="fieldHeader"><span>Geschlecht</span><InfoHint text="Geschlecht des Kunden auswählen." /></span>
                    <select
                      value={intakeDraft.customerGender}
                      onChange={(event) =>
                        setIntakeDraft((prev) => ({ ...prev, customerGender: event.target.value }))
                      }
                    >
                      <option value="">Geschlecht wählen</option>
                      {GENDER_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span className="fieldHeader"><span>Kunden-E-Mail</span><InfoHint text={isInternalIntakeOrder ? "Bei internen Aufträgen optional, weil keine Kundenbestätigung versendet wird." : "E-Mail-Adresse für Dokumentation und Kundenbestätigung."} /></span>
                    <input
                      className={fieldErrorClass("customerEmail")}
                      type="email"
                      placeholder={isInternalIntakeOrder ? "optional" : "Kunden-E-Mail"}
                      value={intakeDraft.customerEmail}
                      onChange={(event) =>
                        updateIntakeField("customerEmail", event.target.value)
                      }
                    />
                  </label>
                </div>
              )}

              {intakeTab === "details" && (
                <>
                  <div className="intakeSection">
                    <div className="intakeHeaderRow compact">
                      <span className="sectionTag">Annahme-Notiz</span>
                      <InfoHint text="Nur interne Hinweise, die für die weitere Bearbeitung wichtig sind." />
                    </div>
                    <label>
                      <span className="fieldHeader"><span>Interne Notiz</span><InfoHint text="Kurze interne Zusatzinfo für die nächste Abteilung." /></span>
                      <textarea
                        rows={3}
                        placeholder="Interne Notiz"
                        value={intakeDraft.notes}
                        onChange={(event) =>
                          setIntakeDraft((prev) => ({ ...prev, notes: event.target.value }))
                        }
                      />
                    </label>

                    {!isLoanerMode && (
                      <label className="checkRow intakeCheckRow">
                        <input
                          type="checkbox"
                          checked={intakeDraft.needsLoaner}
                          onChange={(event) =>
                            setIntakeDraft((prev) => ({ ...prev, needsLoaner: event.target.checked }))
                          }
                        />
                        Leihwagen für diesen Fall vormerken
                      </label>
                    )}
                  </div>

                  <div className="intakeSection confirmationInfoBox">
                    <div className="intakeHeaderRow compact">
                      <span className="sectionTag">{isInternalIntakeOrder ? "Interner Auftrag" : "Kundenbestätigung"}</span>
                      <InfoHint text={isInternalIntakeOrder ? "Interne Aufträge benötigen keine Kunden-E-Mail, keinen Bestätigungslink und keine Unterschrift." : "Der Auftrag wird nach dem Speichern per E-Mail an den Kunden gesendet. Der Kunde unterschreibt und bestätigt selbst über den sicheren Link."} />
                    </div>
                    <p className="mutedText">
                      {isInternalIntakeOrder
                        ? "Dieser Auftrag wird intern freigegeben. Es wird keine E-Mail versendet und kein Bestätigungslink erzeugt."
                        : "Nach dem Speichern erhält der Kunde einen Bestätigungslink. Die Unterschrift wird erst dort vom Kunden erfasst, nicht am PC der Annahme."}
                    </p>
                  </div>

                  {isAdmin && (
                    <details className="intakeAdvancedPanel">
                      <summary>Weitere Angaben</summary>
                      <div className="formGrid">
                        <label>
                          <span className="fieldHeader"><span>Quelle</span><InfoHint text="Woher der Fall kommt, zum Beispiel manuell oder aus einem anderen System." /></span>
                          <input
                            value={intakeDraft.sourceLabel}
                            onChange={(event) =>
                              setIntakeDraft((prev) => ({ ...prev, sourceLabel: event.target.value }))
                            }
                            placeholder="z. B. manuell"
                          />
                        </label>

                        <label>
                          <span className="fieldHeader"><span>Externes System</span><InfoHint text="Optional: Name eines anderen Systems wie MotionData." /></span>
                          <input
                            value={intakeDraft.externalSystem}
                            onChange={(event) =>
                              setIntakeDraft((prev) => ({ ...prev, externalSystem: event.target.value }))
                            }
                            placeholder="z. B. MotionData"
                          />
                        </label>

                        <label>
                          <span className="fieldHeader"><span>Externe Referenz</span><InfoHint text="Optional: Vorgangsnummer oder Referenz aus einem anderen System." /></span>
                          <input
                            value={intakeDraft.externalReference}
                            onChange={(event) =>
                              setIntakeDraft((prev) => ({ ...prev, externalReference: event.target.value }))
                            }
                            placeholder="optional"
                          />
                        </label>

                        <label>
                          <span className="fieldHeader"><span>Auftragsnummer</span><InfoHint text="Optional. Wenn leer, wird eine Nummer automatisch vergeben." /></span>
                          <input
                            value={intakeDraft.orderNumber}
                            onChange={(event) =>
                              setIntakeDraft((prev) => ({ ...prev, orderNumber: event.target.value }))
                            }
                            placeholder="optional, sonst automatisch"
                          />
                        </label>

                        <label>
                          <span className="fieldHeader"><span>Priorität</span><InfoHint text="Wie dringend der Fall intern behandelt werden soll." /></span>
                          <select
                            value={intakeDraft.prio}
                            onChange={(event) =>
                              setIntakeDraft((prev) => ({ ...prev, prio: event.target.value }))
                            }
                          >
                            <option value="urgent">Dringend</option>
                            <option value="high">Hoch</option>
                            <option value="normal">Normal</option>
                            <option value="low">Niedrig</option>
                          </select>
                        </label>
                      </div>

                      <label>
                        <span className="fieldHeader"><span>Start-Checkliste</span><InfoHint text="Die ersten internen To-dos für die Abteilung." /></span>
                        <textarea
                          rows={5}
                          placeholder="Start-Checkliste"
                          value={intakeDraft.subtasksText}
                          onChange={(event) =>
                            setIntakeDraft((prev) => ({ ...prev, subtasksText: event.target.value }))
                          }
                        />
                      </label>
                    </details>
                  )}
                </>
              )}
            </div>
          </section>

          <aside className="intakeActionColumn">
            <div className="loanerDemandPanel intakeActionPanel">
              <div className="cardHeader">
                <div className="sectionHeadline compactHeadline">
                  <div>
                    <span className="sectionTag">Nächster Schritt</span>
                    <h4>Werkstattauftrag anlegen</h4>
                  </div>
                  <p className="mutedText">
                    {isLoanerMode
                      ? "Der Auftrag wird gespeichert und erscheint als offener Leihwagenbedarf im Leihwagen-Modul."
                      : "Der Auftrag wird jetzt angelegt und für die nächste Abteilung vorbereitet."}
                  </p>
                </div>
              </div>

              <div className="intakeActionSummary">
                <div className="summaryBadge">
                  <span>Route</span>
                  <strong>{intakeFlow.join(" -> ")}</strong>
                </div>
                <div className="summaryBadge">
                  <span>Filiale</span>
                  <strong>{getBranchLabel(intakeDraft.branch) || "Bitte wählen"}</strong>
                </div>
              </div>

              <div className="stackActions">
                <button
                  type="button"
                  className="btn primary bigActionBtn"
                  onClick={createIntakeCard}
                  disabled={intakeDraft.saving}
                >
                  {intakeDraft.saving ? "Speichere ..." : "Auftrag anlegen"}
                </button>
              </div>
            </div>
          </aside>
        </div>

        {isLoanerMode && (
          <div className="loanerDemandPanel intakeHelperStrip">
            <div className="sectionHeadline compactHeadline">
              <div>
                <span className="sectionTag">Leihwagen Fokus</span>
                <h4>Bedarf wird im Leihwagen-Modul vorbereitet</h4>
              </div>
              <p className="mutedText">
                Nach dem Speichern erscheint der Auftrag als offener Leihwagenbedarf.
                Die Verleihdokumentation wird anschließend im Modul Leihwagen gestartet.
              </p>
            </div>
            <div className="summaryBadge subtle">
              <span>Empfehlung</span>
              <strong>Immer nur ein Kunde, ein Fahrzeug, ein klarer nächster Schritt</strong>
            </div>
          </div>
        )}

        {intakeDraft.error && <div className="authError">{intakeDraft.error}</div>}
        {intakeDraft.success && <div className="customerSuccess">{intakeDraft.success}</div>}
        {intakeDraftSavedAt && !intakeDraft.success && (
          <div className="draftSaveHint">
            <span>Entwurf lokal gesichert: {fmtDateTime(intakeDraftSavedAt)}</span>
            <button
              type="button"
              className="btn"
              onClick={() => {
                localStorage.removeItem(INTAKE_DRAFT_STORAGE_KEY);
                setIntakeDraft({ ...getDefaultIntakeDraft(), branch: profile?.branch || "" });
                setIntakeDraftSavedAt(null);
              }}
            >
              Entwurf verwerfen
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderDashboard = () => (
    <>
      <div className="executiveBar">
        {executiveSignals.map((signal, index) => (
          <div key={index} className={`execItem ${signal.type}`}>
            <strong>{signal.text}</strong>
          </div>
        ))}
      </div>

      <div className="topDashboard">
          <div className="dashBlock">
            <div className="dashTitle">Techniker Auslastung</div>
            <div className="dashRow">
              {configuredTechnicians.filter((technician) => technician.type === "Techniker").map((technician) => (
                <div key={technician.id} className="dashItem">
                  <strong>{technician.name}</strong>
                  <span>{technicianWorkload[technician.id] || 0} Aufträge · {formatHours(getTechnicianActiveHours(technician.id))}/{formatHours(technician.capacityHours || DEFAULT_TECH_CAPACITY_HOURS)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="dashBlock">
            <div className="dashTitle">Abteilungen</div>
            <div className="dashRow">
              {Object.entries(departmentCapacity).map(([role, count]) => (
                <div key={role} className="dashItem">
                  <strong>{role}</strong>
                  <span>{count} aktiv</span>
                </div>
              ))}
            </div>
          </div>

          <div className="dashBlock">
            <div className="dashTitle">Leihwagen</div>
            <div className="dashRow">
              <div className="dashItem">
                <strong>Verfügbar</strong>
                <span>
                  {
                    loaners.filter(
                      (loaner) =>
                        loaner.isActive &&
                        !isLoanerUnavailable(loaner, availabilityMap)
                    ).length
                  }
                </span>
              </div>
              <div className="dashItem">
                <strong>Reserviert</strong>
                <span>{bookings.filter((booking) => booking.status === "awaiting_customer_confirmation").length}</span>
              </div>
              <div className="dashItem">
                <strong>Unterwegs</strong>
                <span>{bookings.filter((booking) => booking.status === "checked_out").length}</span>
              </div>
              <button type="button" className="btn" onClick={() => exportBoardCsv("all")}>
                CSV Export
              </button>
            </div>
          </div>
      </div>
    </>
  );

  const renderApprovalRow = (card) => {
    const targetDepartment = getApprovalRequestTarget(card);
    const assignmentRequest = card.loanerFlow?.assignmentApproval || null;
    const targetEmployee =
      getEmployeeById(configuredTechnicians, assignmentRequest?.targetTechId) ||
      getApprovalRequestEmployee(card, configuredTechnicians);
    const latestQuestion = (card.questions || [])
      .slice()
      .reverse()
      .find((question) => !question.answered && question.requiresApproval) ||
      (card.questions || [])
        .slice()
        .reverse()
        .find((question) => !question.answered) ||
      {};
    const requestTypeLabel =
      INTAKE_REQUEST_TYPES.find((entry) => entry.value === latestQuestion.requestType)?.label ||
      getApprovalRequestType(card);
    const requestedAt = assignmentRequest?.requestedAt || latestQuestion.createdAt || card.history?.[card.history.length - 1]?.ts;
    const requestedBy = assignmentRequest?.requestedBy || latestQuestion.author || card.responsibleLabel || "-";
    return (
      <div key={card.id} className="approvalRow">
        <div>
          <strong>{card.title || card.orderNumber}</strong>
          <span>{card.orderNumber || "-"} · {card.customerName || "-"} · {card.licensePlate || "-"}</span>
        </div>
        <div>
          <span>Anforderung von {requestedBy}</span>
          <strong>{latestQuestion.text || getBlockerLabel(card.blockerType)}</strong>
          <span>{requestTypeLabel}</span>
        </div>
        <div>
          <span>Status</span>
          <strong>{getOperationalStatusLabel(card)} · {requestedAt ? fmtDateTime(requestedAt) : "Zeit offen"}</strong>
        </div>
        <div>
          <span>Ziel</span>
          <strong>
            {targetEmployee?.name || targetDepartment || card.nextDepartment || "Freigabe"}
            {targetEmployee?.specialties?.length ? ` · ${targetEmployee.specialties.slice(0, 2).join(", ")}` : ""}
          </strong>
        </div>
        <label className="approvalCommentField">
          Kommentar
          <input
            type="text"
            placeholder="optional, aber hilfreich"
            value={approvalComments[card.id] || ""}
            onChange={(event) =>
              setApprovalComments((prev) => ({ ...prev, [card.id]: event.target.value }))
            }
          />
        </label>
        <div className="actions">
          <button type="button" className="btn" onClick={() => openTaskDetail(card.id)}>
            Details
          </button>
          <button type="button" className="btn" onClick={() => openPlanningMove(card)}>
            Umplanen
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => {
              if (!String(approvalComments[card.id] || "").trim()) {
                window.alert("Bitte bei einer Ablehnung kurz begründen, was als Nächstes zu tun ist.");
                return;
              }
              resolveApprovalRequest(card, false, approvalComments[card.id]);
              setApprovalComments((prev) => ({ ...prev, [card.id]: "" }));
            }}
          >
            Ablehnen
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => {
              resolveApprovalRequest(card, true, approvalComments[card.id]);
              setApprovalComments((prev) => ({ ...prev, [card.id]: "" }));
            }}
          >
            Freigeben
          </button>
        </div>
      </div>
    );
  };

  const renderApprovalCenter = ({ compact = false } = {}) => (
    <div className={`card fullSpan approvalCenterCard ${compact ? "compact" : ""}`}>
      <div className="cardHeader">
        <div>
          <h3>Freigabe-Center</h3>
          <p className="mutedText">
            Freigaben sind nach Typ getrennt, damit Management und Werkstättenleitung sofort den richtigen Kontext sehen.
          </p>
        </div>
        <span className="summaryBadge">
          <span>Offen</span>
          <strong>{approvalRequests.length}</strong>
        </span>
        <span className="summaryBadge subtle">
          <span>Planungsdaten</span>
          <strong>{technicianSettingsStatus}</strong>
        </span>
      </div>
      <div className="approvalGroupGrid">
        {Object.entries(approvalGroups).map(([type, items]) => (
          <section key={type} className="approvalGroup">
            <div className="approvalGroupHead">
              <strong>{type}</strong>
              <span>{items.length}</span>
            </div>
            <div className="approvalList">
              {items.map((card) => renderApprovalRow(card))}
            </div>
          </section>
        ))}
        {approvalRequests.length === 0 && (
          <div className="mutedText">Aktuell sind keine Freigaben offen.</div>
        )}
      </div>
    </div>
  );

  const renderManagementPanel = () => (
    <main className="board">
      <div className="card fullSpan managementHero">
        <h3>Heute Morgen</h3>
        <p className="mutedText">
          Schneller Tagesstart: Was muss raus, was hängt, wer ist voll und welche Entscheidung fehlt?
        </p>
        <div className="kpiGrid">
          {managementKpis.map((item) => (
            <div key={item.label} className="kpiTile">
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </div>
          ))}
        </div>
      </div>

      <div className="card fullSpan morningBriefingCard">
        <div className="morningBriefingGrid">
          <section>
            <div className="briefingHead">
              <strong>Heute raus</strong>
              <span>{morningBriefing.dueToday.length}</span>
            </div>
            {morningBriefing.dueToday.slice(0, 6).map((card) => (
              <button type="button" key={`due_${card.id}`} className="briefingRow" onClick={() => openTaskDetail(card.id)}>
                <span>{card.orderNumber || card.title}</span>
                <b>{card.licensePlate || card.role}</b>
                <small>{getOperationalStatusLabel(card)}</small>
              </button>
            ))}
            {morningBriefing.dueToday.length === 0 && <div className="helpEmpty">Heute ist nichts kritisch fällig.</div>}
          </section>
          <section>
            <div className="briefingHead">
              <strong>Hängt fest</strong>
              <span>{morningBriefing.stuckCards.length}</span>
            </div>
            {morningBriefing.stuckCards.slice(0, 6).map((card) => (
              <button type="button" key={`stuck_${card.id}`} className="briefingRow warn" onClick={() => openTaskDetail(card.id)}>
                <span>{card.orderNumber || card.title}</span>
                <b>{getBlockerLabel(card.blockerType)}</b>
                <small>{card.stuckHours ? `${card.stuckHours} h` : "Zeit offen"}</small>
              </button>
            ))}
            {morningBriefing.stuckCards.length === 0 && <div className="helpEmpty">Keine hängenden Aufträge.</div>}
          </section>
          <section>
            <div className="briefingHead">
              <strong>Techniker-Auslastung</strong>
              <span>{morningBriefing.techLoad.filter((tech) => tech.overloaded).length} voll</span>
            </div>
            {morningBriefing.techLoad.slice(0, 6).map((tech) => (
              <button type="button" key={`load_${tech.id}`} className={`briefingRow ${tech.overloaded ? "danger" : ""}`} onClick={() => setUi((prev) => ({ ...prev, activeView: "Werkstattplanung" }))}>
                <span>{tech.name}</span>
                <b>{tech.assignedCount} Aufträge</b>
                <small>{formatHours(tech.hours)}</small>
              </button>
            ))}
          </section>
          <section>
            <div className="briefingHead">
              <strong>Entscheidungen</strong>
              <span>{morningBriefing.approvalCards.length}</span>
            </div>
            <button type="button" className="briefingRow" onClick={() => setUi((prev) => ({ ...prev, activeView: "Werkstattplanung" }))}>
              <span>Freigaben offen</span>
              <b>{morningBriefing.approvalCards.length}</b>
              <small>prüfen</small>
            </button>
            <button type="button" className="briefingRow warn" onClick={() => setUi((prev) => ({ ...prev, activeView: "Werkstattplanung" }))}>
              <span>Teile fehlen</span>
              <b>{morningBriefing.missingParts.length}</b>
              <small>nachfassen</small>
            </button>
            <button type="button" className="briefingRow ok" onClick={() => setUi((prev) => ({ ...prev, activeView: "Übergabe" }))}>
              <span>Kann weiter</span>
              <b>{morningBriefing.readyVehicles.length}</b>
              <small>übergeben</small>
            </button>
          </section>
        </div>
      </div>

      <div className="card fullSpan loanerWatchdogCard">
        <div className="watchdogHeader">
          <div>
            <p className="eyebrow">Digital Watchdog</p>
            <h3>Leihwagen-Management</h3>
            <p className="mutedText">
              Überwacht Rückgaben, Dokumentation, Freigaben, Kostenindikatoren und auffällige Wiederholungen im Leihwagenprozess.
            </p>
          </div>
          <div className="watchdogHeaderActions">
            <button type="button" className="btn" onClick={exportLoanerManagementReport}>
              Bericht exportieren
            </button>
            <button
              type="button"
              className="btn primary"
              onClick={() => setUi((prev) => ({ ...prev, activeView: "Leihwagen" }))}
            >
              Zum Leihwagenmodul
            </button>
          </div>
        </div>

        <div className="managementFilterBar">
          <label>
            Zeitraum
            <select
              value={managementLoanerFilters.period}
              onChange={(event) =>
                setManagementLoanerFilters((prev) => ({ ...prev, period: event.target.value }))
              }
            >
              <option value="7">Letzte 7 Tage</option>
              <option value="30">Letzte 30 Tage</option>
              <option value="90">Letzte 90 Tage</option>
              <option value="all">Alle Vorgänge</option>
            </select>
          </label>
          <label>
            Standort
            <select
              value={managementLoanerFilters.branch}
              onChange={(event) =>
                setManagementLoanerFilters((prev) => ({ ...prev, branch: event.target.value }))
              }
            >
              <option value="">Alle Standorte</option>
              {BRANCH_OPTIONS.map((branch) => (
                <option key={branch.value} value={branch.value}>
                  {branch.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select
              value={managementLoanerFilters.status}
              onChange={(event) =>
                setManagementLoanerFilters((prev) => ({ ...prev, status: event.target.value }))
              }
            >
              <option value="">Alle Status</option>
              {Object.entries(BOOKING_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Ampel
            <select
              value={managementLoanerFilters.risk}
              onChange={(event) =>
                setManagementLoanerFilters((prev) => ({ ...prev, risk: event.target.value }))
              }
            >
              <option value="">Alle Ampeln</option>
              <option value="critical">Kritisch</option>
              <option value="warn">Warnung</option>
              <option value="normal">Normal</option>
            </select>
          </label>
          <label>
            Fahrzeug
            <input
              value={managementLoanerFilters.vehicle}
              onChange={(event) =>
                setManagementLoanerFilters((prev) => ({ ...prev, vehicle: event.target.value }))
              }
              placeholder="Kennzeichen, Modell, Auftrag"
            />
          </label>
          <label>
            Verantwortlich
            <input
              value={managementLoanerFilters.responsible}
              onChange={(event) =>
                setManagementLoanerFilters((prev) => ({ ...prev, responsible: event.target.value }))
              }
              placeholder="Mitarbeiter oder Kunde"
            />
          </label>
        </div>

        <div className="managementQuickFilters">
          <button
            type="button"
            className="btn"
            onClick={() =>
              setManagementLoanerFilters((prev) => ({ ...prev, risk: "critical", status: "" }))
            }
          >
            Nur kritische Fälle
          </button>
          <button
            type="button"
            className="btn"
            onClick={() =>
              setManagementLoanerFilters((prev) => ({ ...prev, status: "checked_out", risk: "" }))
            }
          >
            Ausgegebene Fahrzeuge
          </button>
          <button
            type="button"
            className="btn"
            onClick={() =>
              setManagementLoanerFilters((prev) => ({
                ...prev,
                status: "awaiting_customer_confirmation",
                risk: "",
              }))
            }
          >
            Offene Kundenfreigaben
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={() =>
              setManagementLoanerFilters({
                period: "30",
                branch: "",
                status: "",
                vehicle: "",
                responsible: "",
                risk: "",
              })
            }
          >
            Filter zurücksetzen
          </button>
        </div>

        <div className="watchdogKpiGrid">
          {loanerManagement.kpis.map((item) => (
            <div key={item.label} className={`watchdogKpi ${item.tone || "normal"}`}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
              {item.delta && <small>{item.delta}</small>}
            </div>
          ))}
        </div>

        <div className={`processHealthStrip ${loanerManagement.processHealth.score >= 85 ? "ok" : loanerManagement.processHealth.score >= 65 ? "warn" : "danger"}`}>
          <div className="processHealthScore">
            <span>Prozessgesundheit</span>
            <strong>{loanerManagement.processHealth.score}%</strong>
            <small>{loanerManagement.processHealth.label}</small>
          </div>
          <div className="processHealthSignal">
            <span>Verfügbarkeit</span>
            <strong>{loanerManagement.processHealth.availabilityRate}% frei</strong>
            <small>{loanerManagement.processHealth.lowAvailability ? "Engpass beobachten" : "ausreichend"}</small>
          </div>
          <div className="processHealthSignal">
            <span>Vorperiode</span>
            <strong>
              {loanerManagement.periodComparison.critical >= 0 ? "+" : ""}
              {loanerManagement.periodComparison.critical} kritisch
            </strong>
            <small>
              {loanerManagement.periodComparison.costs >= 0 ? "+" : ""}
              {fmtCurrency(loanerManagement.periodComparison.costs)} Kostenindikator
            </small>
          </div>
        </div>

        <section className="watchdogPanel managementTasksPanel">
          <div className="watchdogPanelHead">
            <div>
              <h4>Management-Aufgaben</h4>
              <p className="mutedText">Exception-first: diese Punkte zuerst entscheiden oder nachfassen.</p>
            </div>
            <span className="summaryBadge">
              <span>Offen</span>
              <strong>{loanerManagement.managementAlerts.length}</strong>
            </span>
          </div>
          <div className="managementAlertList">
            {loanerManagement.managementAlerts.map((alert) => (
              <button
                type="button"
                key={alert.id}
                className={`managementAlertRow ${alert.severity}`}
                onClick={() => alert.entry && openLoanerManagementCase(alert.entry)}
              >
                <span className={`riskPill ${alert.severity}`}>{alert.severity === "critical" ? "Kritisch" : "Warnung"}</span>
                <div>
                  <strong>{alert.title}</strong>
                  <small>{alert.target}</small>
                </div>
                <div>
                  <span>{alert.cause}</span>
                  <small>{alert.recommendation}</small>
                </div>
                <b>{alert.urgency}</b>
              </button>
            ))}
            {loanerManagement.managementAlerts.length === 0 && (
              <div className="helpEmpty">Keine Management-Ausnahmen im aktuellen Filter.</div>
            )}
          </div>
        </section>

        <div className="watchdogGrid">
          <section className="watchdogPanel criticalPanel">
            <div className="watchdogPanelHead">
              <div>
                <h4>Kritische Vorgänge</h4>
                <p className="mutedText">Schnellzugriff auf Fälle mit Managementbedarf.</p>
              </div>
              <span className="summaryBadge">
                <span>Treffer</span>
                <strong>{loanerManagement.criticalCases.length}</strong>
              </span>
            </div>
            <div className="watchdogCaseList">
              {loanerManagement.criticalCases.map((entry) => {
                const firstWarning = entry.assessment.warnings[0];
                return (
                  <button
                    type="button"
                    key={entry.booking.id}
                    className={`watchdogCase ${entry.assessment.level}`}
                    onClick={() => openLoanerManagementCase(entry)}
                  >
                    <div className="watchdogCaseTop">
                      <span className={`riskPill ${entry.assessment.level}`}>
                        {entry.assessment.label}
                      </span>
                      <strong>{entry.booking.loanerPlate || entry.booking.orderNumber}</strong>
                      <small>{getBookingStatusLabel(entry.booking.status)}</small>
                    </div>
                    <div className="watchdogCaseMeta">
                      <span>{entry.booking.customerName || "Kunde offen"}</span>
                      <span>{entry.booking.loanerBrand} {entry.booking.loanerModel}</span>
                      <span>{entry.branch ? getBranchLabel(entry.branch) : "Standort offen"}</span>
                    </div>
                    {firstWarning && (
                      <div className="watchdogRecommendation">
                        <b>{firstWarning.label}</b>
                        <span>{firstWarning.recommendation}</span>
                        <small>{firstWarning.urgency}</small>
                      </div>
                    )}
                  </button>
                );
              })}
              {loanerManagement.criticalCases.length === 0 && (
                <div className="helpEmpty">Keine kritischen Leihwagenvorgänge im aktuellen Filter.</div>
              )}
            </div>
          </section>

          <section className="watchdogPanel">
            <div className="watchdogPanelHead">
              <div>
                <h4>Statusverteilung</h4>
                <p className="mutedText">Wo stehen die Vorgänge gerade?</p>
              </div>
            </div>
            <div className="statusDistribution">
              {loanerManagement.statusDistribution.map((entry) => (
                <div key={entry.label} className="statusDistributionRow">
                  <div>
                    <strong>{entry.label}</strong>
                    <span>{entry.count} Vorgänge</span>
                  </div>
                  <div className="statusDistributionTrack" aria-hidden="true">
                    <span style={{ width: `${Math.max(4, entry.percentage)}%` }} />
                  </div>
                  <b>{entry.percentage}%</b>
                </div>
              ))}
              {loanerManagement.statusDistribution.length === 0 && (
                <div className="helpEmpty">Keine Vorgänge für die gewählten Filter.</div>
              )}
            </div>
          </section>
        </div>

        <div className="watchdogGrid secondary">
          <section className="watchdogPanel">
            <div className="watchdogPanelHead">
              <div>
                <h4>Top-Ursachen</h4>
                <p className="mutedText">Warum Vorgänge auffällig werden.</p>
              </div>
            </div>
            <div className="topCauseList">
              {loanerManagement.topCauses.map((cause) => (
                <div key={cause.key} className="topCauseRow">
                  <div>
                    <strong>{cause.label}</strong>
                    <span>{cause.recommendation}</span>
                  </div>
                  <b>{cause.count}</b>
                  <small>{cause.critical} kritisch</small>
                </div>
              ))}
              {loanerManagement.topCauses.length === 0 && (
                <div className="helpEmpty">Keine auffälligen Ursachen im aktuellen Filter.</div>
              )}
            </div>
          </section>

          <section className="watchdogPanel">
            <div className="watchdogPanelHead">
              <div>
                <h4>Wiederkehrende Auffälligkeiten</h4>
                <p className="mutedText">Muster nach Fahrzeug, Standort und Verantwortlichkeit.</p>
              </div>
            </div>
            <div className="repeatIssueGrid">
              <div>
                <strong>Fahrzeuge</strong>
                {loanerManagement.recurring.vehicles.map((entry) => (
                  <span key={entry.key}>{entry.label}: {entry.count}</span>
                ))}
                {loanerManagement.recurring.vehicles.length === 0 && <small>Keine Häufung.</small>}
              </div>
              <div>
                <strong>Standorte</strong>
                {loanerManagement.recurring.branches.map((entry) => (
                  <span key={entry.key}>{entry.label}: {entry.count}</span>
                ))}
                {loanerManagement.recurring.branches.length === 0 && <small>Keine Häufung.</small>}
              </div>
              <div>
                <strong>Verantwortlich</strong>
                {loanerManagement.recurring.employees.map((entry) => (
                  <span key={entry.key}>{entry.label}: {entry.count}</span>
                ))}
                {loanerManagement.recurring.employees.length === 0 && <small>Keine Häufung.</small>}
              </div>
            </div>
          </section>
        </div>

        <div className="watchdogGrid secondary">
          <section className="watchdogPanel">
            <div className="watchdogPanelHead">
              <div>
                <h4>7-Tage-Trend</h4>
                <p className="mutedText">Neue Vorgänge und kritische Signale im Verlauf.</p>
              </div>
            </div>
            <div className="trendBars">
              {loanerManagement.trends.map((entry) => {
                const max = Math.max(...loanerManagement.trends.map((item) => item.total), 1);
                return (
                  <div key={entry.key} className="trendBarColumn">
                    <div className="trendBarTrack">
                      <span
                        className="trendBarTotal"
                        style={{ height: `${Math.max(8, (entry.total / max) * 100)}%` }}
                      />
                      {entry.critical > 0 && <span className="trendBarCritical" />}
                    </div>
                    <strong>{entry.total}</strong>
                    <small>{entry.label}</small>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </div>

      {renderApprovalCenter()}

      <div className="card">
        <h3>Kritische Blocker</h3>
        <div className="taskList">
          {blockedCards.slice(0, 8).map((card) => (
            <div key={card.id} className="taskListItem">
              <strong>{card.title}</strong>
              <span>{card.role}</span>
              <span>{getBlockerLabel(card.blockerType)}</span>
            </div>
          ))}
          {blockedCards.length === 0 && <div className="mutedText">Keine aktiven Blocker.</div>}
        </div>
      </div>

      <div className="card">
        <h3>Nächste Aktionen überfällig</h3>
        <div className="taskList">
          {nextActionCritical.slice(0, 8).map((card) => (
            <div key={card.id} className="taskListItem">
              <strong>{card.nextAction || card.title}</strong>
              <span>{card.responsibleLabel || "nicht zugewiesen"}</span>
              <span>{fmtDate(card.nextActionDue)}</span>
            </div>
          ))}
          {nextActionCritical.length === 0 && (
            <div className="mutedText">Keine überfälligen Folgeaktionen.</div>
          )}
        </div>
      </div>

      <div className="card fullSpan">
        <h3>Abteilungsengpässe</h3>
        <div className="departmentMetricsGrid">
          {departmentMetrics.map((department) => (
            <div key={department.role} className="departmentMetricCard">
              <div className="departmentMetricHead">
                <strong>{department.role}</strong>
                <span>{department.total} aktiv</span>
              </div>
              <div className="departmentMetricStats">
                <span>Blockiert: {department.blocked}</span>
                <span>Überfällig: {department.overdue}</span>
                <span>Heute fällig: {department.dueToday}</span>
                <span>In Arbeit: {department.active}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card fullSpan">
        <h3>Leihwagen & Kundenfreigaben</h3>
        <div className="departmentMetricsGrid">
          <div className="departmentMetricCard">
            <div className="departmentMetricHead">
              <strong>Bereit zur Ausgabe</strong>
              <span>{bookings.filter((booking) => booking.status === "ready_for_checkout").length}</span>
            </div>
            <div className="taskList">
              {bookings
                .filter((booking) => booking.status === "ready_for_checkout")
                .slice(0, 5)
                .map((booking) => (
                  <div key={booking.id} className="taskListItem">
                    <strong>{booking.loanerPlate}</strong>
                    <span>{booking.customerName}</span>
                    <span>{booking.orderNumber || "-"}</span>
                  </div>
                ))}
            </div>
          </div>
          <div className="departmentMetricCard">
            <div className="departmentMetricHead">
              <strong>Wartet auf Kundenbestätigung</strong>
              <span>{bookings.filter((booking) => booking.status === "awaiting_customer_confirmation").length}</span>
            </div>
            <div className="taskList">
              {bookings
                .filter((booking) => booking.status === "awaiting_customer_confirmation")
                .slice(0, 5)
                .map((booking) => (
                  <div key={booking.id} className="taskListItem">
                    <strong>{booking.customerName}</strong>
                    <span>{booking.loanerPlate}</span>
                    <span>{booking.customerEmail}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );

  const renderLeadstandPanel = () => (
    <main className="board">
      {departmentMetrics.map((department) => (
        <div key={department.role} className="card">
          <div className="cardHeader">
            <div>
              <h3>{department.role}</h3>
              <div className="meta">
                <span>{department.total} aktiv</span>
                <span>{department.blocked} Blocker</span>
                <span>{department.overdue} überfällig</span>
              </div>
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => setUi((prev) => ({ ...prev, activeView: department.role }))}
            >
              Zur Abteilung
            </button>
          </div>
          <div className="taskList">
            {department.items.slice(0, 5).map((card) => (
              <div key={card.id} className="taskListItem">
                <strong>{card.title}</strong>
                <span>{card.nextAction || "Nächste Aktion offen"}</span>
                <span>{card.responsibleLabel || "keine Zuweisung"}</span>
              </div>
            ))}
            {department.items.length === 0 && (
              <div className="mutedText">Aktuell keine offenen Aufträge.</div>
            )}
          </div>
        </div>
      ))}
    </main>
  );

  const renderWorkshopPlanningPanel = () => {
    const workshopCards = activeCards
      .filter((card) => card.role === "Werkstatt" || card.assignedTech)
      .sort((left, right) => new Date(left.due || 0) - new Date(right.due || 0));
    const workshopTechnicians = configuredTechnicians.filter((technician) => technician.type === "Techniker");
    const unassignedCards = workshopCards.filter((card) => !card.assignedTech);
    const pendingAssignmentCards = workshopCards.filter(
      (card) => card.loanerFlow?.assignmentApproval?.status === "pending_workshop_lead" || card.blockerType === "approval"
    );
    const planningDays = Array.from({ length: 7 }, (_, index) => addDays(index));
    const getTechnicianDayHours = (techId, day) =>
      workshopCards
        .filter((card) => (card.assignedTech || "") === techId && (card.due || addDays(0)) === day)
        .reduce((sum, card) => sum + getEstimatedHours(card), 0);
    const overloadedSlots = workshopTechnicians.flatMap((technician) =>
      planningDays
        .map((day) => ({
          technician,
          day,
          hours: getTechnicianDayHours(technician.id, day),
          capacity: technician.capacityHours || DEFAULT_TECH_CAPACITY_HOURS,
        }))
        .filter((slot) => slot.hours > slot.capacity)
    );
    const getCardDayIndex = (card) => {
      if (!card.due) return 0;
      const today = new Date(addDays(0));
      const due = new Date(card.due);
      if (Number.isNaN(due.getTime())) return 0;
      const diff = Math.floor((due.setHours(0, 0, 0, 0) - today.setHours(0, 0, 0, 0)) / 86400000);
      return Math.max(0, Math.min(6, diff));
    };

    return (
      <main className="board workshopPlanningBoard">
        <div className="card fullSpan managementHero">
          <h3>Werkstattplanung</h3>
          <p className="mutedText">
            Aufträge nach Techniker, Auslastung und Fälligkeit planen. Zuweisungen der Werkstättenleitung werden direkt wirksam.
          </p>
          <div className="managementKpiGrid">
            <div className="summaryBadge">
              <span>Werkstatt-Aufträge</span>
              <strong>{workshopCards.length}</strong>
            </div>
            <div className="summaryBadge">
              <span>Ohne Techniker</span>
              <strong>{unassignedCards.length}</strong>
            </div>
            <div className="summaryBadge">
              <span>Freigaben</span>
              <strong>{pendingAssignmentCards.length}</strong>
            </div>
            <div className="summaryBadge">
              <span>Techniker</span>
              <strong>{workshopTechnicians.length}</strong>
            </div>
            <div className={`summaryBadge ${overloadedSlots.length ? "warn" : "subtle"}`}>
              <span>Überlastungen</span>
              <strong>{overloadedSlots.length}</strong>
            </div>
          </div>
          <div className="managementHintLine">
            <span>Planungsdaten: {technicianSettingsStatus}</span>
            <span>Vorschläge berücksichtigen Filiale, Spezialisierung und aktuelle Auslastung.</span>
          </div>
        </div>

        {overloadedSlots.length > 0 && (
          <div className="card fullSpan planningWarningCard">
            <div className="cardHeader">
              <div>
                <h3>Kapazität prüfen</h3>
                <p className="mutedText">Diese Tage sind über der geplanten Techniker-Kapazität.</p>
              </div>
            </div>
            <div className="fleetQualityList">
              {overloadedSlots.slice(0, 8).map((slot) => (
                <span key={`${slot.technician.id}_${slot.day}`}>
                  <strong>{slot.technician.name}</strong> {fmtDate(slot.day)}: {formatHours(slot.hours)} / {formatHours(slot.capacity)}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="card fullSpan technicianConfigCard">
          <div className="cardHeader">
            <div>
              <h3>Techniker-Kapazitäten</h3>
              <p className="mutedText">Tageskapazität und Schwerpunkte für die Planung pflegen.</p>
            </div>
          </div>
          <div className="technicianConfigGrid">
            {workshopTechnicians.map((technician) => {
              const setting = getTechnicianSetting(technician.id);
              return (
                <div key={`tech_config_${technician.id}`} className="technicianConfigRow">
                  <div className="technicianConfigIdentity">
                    <strong>{technician.name}</strong>
                    <span>{getBranchLabel(technician.branch)} · {technician.type}</span>
                  </div>
                  <label>
                    Tageskapazität
                    <input
                      type="number"
                      min="1"
                      max="12"
                      step="0.25"
                      value={setting.capacityHours ?? technician.capacityHours ?? DEFAULT_TECH_CAPACITY_HOURS}
                      onChange={(event) => updateTechnicianSetting(technician.id, { capacityHours: event.target.value })}
                    />
                  </label>
                  <label className="wideField">
                    Spezialisierungen
                    <input
                      placeholder="z.B. §57A, Hyundai EV, Bremsen"
                      value={(technician.specialties || []).join(", ")}
                      onChange={(event) =>
                        updateTechnicianSetting(technician.id, {
                          specialties: event.target.value
                            .split(",")
                            .map((entry) => entry.trim())
                            .filter(Boolean),
                        })
                      }
                    />
                  </label>
                </div>
              );
            })}
          </div>
        </div>

        {unassignedCards.length > 0 && (
          <div className="card fullSpan technicianSuggestionCard">
            <div className="cardHeader">
              <div>
                <h3>Automatische Techniker-Vorschläge</h3>
                <p className="mutedText">Schnell zuweisen nach Filiale, Schwerpunkt und freier Kapazität.</p>
              </div>
            </div>
            <div className="technicianSuggestionList">
              {unassignedCards.slice(0, 8).map((card) => {
                const suggestions = getRecommendedTechnicians(card).slice(0, 3);
                return (
                  <div key={`suggest_${card.id}`} className="technicianSuggestionRow">
                    <div>
                      <strong>{card.title || card.orderNumber}</strong>
                      <span>{getCaseTypeLabel(card.caseType)} · {card.licensePlate || "-"} · {getBranchLabel(card.branch)}</span>
                    </div>
                    <div className="technicianSuggestionActions">
                      {suggestions.map((tech, index) => (
                        <button
                          type="button"
                          key={`${card.id}_${tech.id}`}
                          className={`btn ${index === 0 ? "primary" : ""}`}
                          onClick={() => assignTechnician(card.id, tech.id)}
                        >
                          {index === 0 ? "Empfohlen: " : ""}{tech.name}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {pendingAssignmentCards.length > 0 && (
          <div className="card fullSpan approvalCenterCard compact">
            <div className="cardHeader">
              <div>
                <h3>Offene Techniker-Freigaben</h3>
                <p className="mutedText">Von Annahme angeforderte Übergaben prüfen und freigeben.</p>
              </div>
            </div>
            <div className="approvalList">
              {pendingAssignmentCards.map((card) => {
                const targetEmployee = getApprovalRequestEmployee(card, technicians) ||
                  getEmployeeById(technicians, card.loanerFlow?.assignmentApproval?.targetTechId);
                return (
                  <div key={`plan_approval_${card.id}`} className="approvalRow">
                    <div>
                      <strong>{card.title || card.orderNumber}</strong>
                      <span>{card.customerName || "-"} · {card.licensePlate || "-"}</span>
                    </div>
                    <div>
                      <span>Techniker</span>
                      <strong>{targetEmployee?.name || "offen"}</strong>
                    </div>
                    <div className="actions">
                      <button type="button" className="btn" onClick={() => openTaskDetail(card.id)}>
                        Details
                      </button>
                      <button type="button" className="btn" onClick={() => resolveApprovalRequest(card, false)}>
                        Ablehnen
                      </button>
                      <button type="button" className="btn primary" onClick={() => resolveApprovalRequest(card, true)}>
                        Freigeben
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="card fullSpan workshopTimelineCard">
          <div className="cardHeader">
            <div>
              <h3>7-Tage-Planung</h3>
              <p className="mutedText">Balken zeigen die Fälligkeit. Über das Dropdown kann die Werkstättenleitung Tasks sofort verschieben.</p>
            </div>
          </div>
          <div className="timelineHeader">
            <span>Techniker</span>
            {planningDays.map((day) => (
              <strong key={day}>{fmtDate(day)}</strong>
            ))}
          </div>
          <div className="timelineRows">
            {[...workshopTechnicians, { id: "", name: "Nicht zugewiesen", type: "Techniker" }].map((technician) => {
              const techCards = technician.id
                ? workshopCards.filter((card) => card.assignedTech === technician.id)
                : unassignedCards;
              const capacity = technician.capacityHours || DEFAULT_TECH_CAPACITY_HOURS;
              return (
                <div key={`plan_row_${technician.id || "none"}`} className="timelineRow">
                  <div className="timelineTech">
                    <strong>{technician.name}</strong>
                    <span>{techCards.length} Aufträge</span>
                    {technician.id && <span>{formatHours(capacity)} Tageskapazität</span>}
                  </div>
                  <div className="timelineTrack">
                    {planningDays.map((day, dayIndex) => {
                      const dayCards = techCards.filter((card) => getCardDayIndex(card) === dayIndex);
                      const hours = technician.id ? getTechnicianDayHours(technician.id, day) : 0;
                      const overloaded = technician.id && hours > capacity;
                      return (
                        <div
                          key={`${technician.id || "none"}_${day}`}
                          className={`timelineDayCell ${overloaded ? "overloaded" : ""}`}
                          onDragOver={(event) => event.preventDefault()}
                          onDrop={(event) => {
                            event.preventDefault();
                            const cardId = event.dataTransfer.getData("text/plain");
                            if (cardId) moveWorkshopCardSchedule(cardId, technician.id, day);
                          }}
                        >
                          {technician.id && (
                            <span className="timelineCapacityLabel">
                              {formatHours(hours)} / {formatHours(capacity)}
                            </span>
                          )}
                          {dayCards.map((card) => (
                            <button
                              key={`bar_${card.id}`}
                              type="button"
                              draggable
                              className={`timelineBar ${card.blockerType ? "blocked" : ""}`}
                              onDragStart={(event) => event.dataTransfer.setData("text/plain", card.id)}
                              onClick={() => openTaskDetail(card.id)}
                            >
                              <strong>{card.title || card.orderNumber}</strong>
                              <span>{card.licensePlate || "-"} · {formatHours(getEstimatedHours(card))}</span>
                              <small>{getOperationalStatusLabel(card)}</small>
                              <small
                                role="button"
                                tabIndex={0}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openPlanningMove(card);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.stopPropagation();
                                    openPlanningMove(card);
                                  }
                                }}
                              >
                                Verschieben
                              </small>
                            </button>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card fullSpan compactTaskListCard">
          <div className="cardHeader">
            <div>
              <h3>Aufträge verschieben</h3>
              <p className="mutedText">Techniker ändern, ohne in die einzelnen Auftragsdetails wechseln zu müssen.</p>
            </div>
          </div>
          <div className="planningAssignmentList">
            {workshopCards.map((card) => (
              <div key={`plan_assign_${card.id}`} className="planningAssignmentRow">
                <div>
                  <strong>{card.title || card.orderNumber}</strong>
                  <span>{card.customerName || "-"} · {card.licensePlate || "-"} · {getCaseTypeLabel(card.caseType)}</span>
                </div>
                <label>
                  Techniker
                  <select value={card.assignedTech || ""} onChange={(event) => assignTechnician(card.id, event.target.value)}>
                    <option value="">Nicht zugewiesen</option>
                    {getEmployeesForBranchAndTypes(workshopTechnicians, card.branch || profile?.branch, ["Techniker"]).map((tech) => (
                      <option key={tech.id} value={tech.id}>
                        {tech.name}{tech.specialties?.length ? ` (${tech.specialties.slice(0, 2).join(", ")})` : ""}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Dauer
                  <input
                    type="number"
                    min="0.25"
                    step="0.25"
                    value={getEstimatedHours(card)}
                    onChange={(event) =>
                      updateCard(card.id, (current) => ({
                        ...current,
                        vehicleData: {
                          ...(current.vehicleData || {}),
                          estimatedHours: event.target.value,
                        },
                        history: [
                          ...(current.history || []),
                          {
                            ts: new Date().toISOString(),
                            by: profile?.full_name || session?.user?.email || CREATOR,
                            action: `Planungsdauer geändert: ${formatHours(getEstimatedHours(current))} -> ${formatHours(event.target.value)}`,
                          },
                        ],
                      }))
                    }
                  />
                </label>
                <button type="button" className="btn" onClick={() => openTaskDetail(card.id)}>
                  Öffnen
                </button>
                <button type="button" className="btn" onClick={() => openPlanningMove(card)}>
                  Verschieben
                </button>
              </div>
            ))}
            {workshopCards.length === 0 && (
              <div className="mutedText">Aktuell sind keine Werkstatt-Aufträge in Planung.</div>
            )}
          </div>
        </div>
        {planningMove.open && (
          <div className="modalOverlay planningMoveOverlay" onMouseDown={closePlanningMove}>
            <div className="modalCard planningMoveModal" onMouseDown={(event) => event.stopPropagation()}>
              <div className="modalHeader">
                <div>
                  <p className="eyebrow">Werkstattplanung</p>
                  <h3>Auftrag verschieben</h3>
                </div>
                <button type="button" className="btn" onClick={closePlanningMove}>
                  Schließen
                </button>
              </div>
              <div className="formGrid">
                <label>
                  Techniker
                  <select
                    value={planningMove.techId}
                    onChange={(event) =>
                      setPlanningMove((prev) => ({ ...prev, techId: event.target.value }))
                    }
                  >
                    <option value="">Nicht zugewiesen</option>
                    {workshopTechnicians.map((tech) => (
                      <option key={tech.id} value={tech.id}>
                        {tech.name}{tech.specialties?.length ? ` (${tech.specialties.slice(0, 2).join(", ")})` : ""}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Datum
                  <input
                    type="date"
                    value={planningMove.due}
                    onChange={(event) =>
                      setPlanningMove((prev) => ({ ...prev, due: event.target.value }))
                    }
                  />
                </label>
                <label>
                  Dauer
                  <input
                    type="number"
                    min="0.25"
                    step="0.25"
                    value={planningMove.estimatedHours}
                    onChange={(event) =>
                      setPlanningMove((prev) => ({ ...prev, estimatedHours: event.target.value }))
                    }
                  />
                </label>
              </div>
              <div className="actions">
                <button type="button" className="btn" onClick={closePlanningMove}>
                  Abbrechen
                </button>
                <button type="button" className="btn primary" onClick={applyPlanningMove}>
                  Übernehmen
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    );
  };

  const renderKanbanBoard = (roleFilter = null) => {
    const sourceCards = roleFilter
      ? activeCards.filter((card) => card.role === roleFilter)
      : isIntakeStaff
        ? activeCards
        : cardsView;
    const columns = [
      { key: WORK_STATUS.NEW, label: "Neu" },
      { key: WORK_STATUS.IN_WORK, label: "In Arbeit" },
      { key: WORK_STATUS.BLOCKED, label: "Blockiert" },
      { key: WORK_STATUS.DONE, label: "Technisch fertig" },
    ];

    return (
      <main className="kanbanBoard">
        {columns.map((column) => (
          <section key={column.key} className="kanbanColumn">
            <div className="kanbanColumnHead">
              <strong>{column.label}</strong>
              <span>{sourceCards.filter((card) => card.workStatus === column.key).length}</span>
            </div>
            <div className="kanbanColumnBody">
              {sourceCards
                .filter((card) => card.workStatus === column.key)
                .map((card) => renderTaskRow(card))}
              {sourceCards.filter((card) => card.workStatus === column.key).length === 0 && (
                <div className="card empty">Keine Aufträge</div>
              )}
            </div>
          </section>
        ))}
      </main>
    );
  };

  const renderOpenLoanerDemandPanel = () => {
    const openLoanerDemandCards = visibleCards.filter(
      (card) =>
        card.needsLoaner &&
        !bookings.some((booking) => isBookingLinkedToCard(booking, card) && isActiveBooking(booking))
    );
    return (
      <main className="board">
        <div className="card fullSpan loanerDemandOverview">
          <div className="cardHeader">
            <div>
              <h3>Offener Leihwagenbedarf</h3>
              <p className="mutedText">
                Vorgemerkte Aufträge und zentral gespeicherte direkte Leihwagen-Entwürfe.
              </p>
            </div>
            <span className="summaryBadge">
              <span>Offen</span>
              <strong>{openLoanerDemandCards.length}</strong>
            </span>
          </div>
          <div className="loanerDemandList">
            {openLoanerDemandCards.map((card) => (
              <div key={card.id} className="loanerDemandRow">
                <div>
                  <strong>{card.customerName || "Kunde noch offen"}</strong>
                  <span>{card.orderNumber || "ohne Auftragsnummer"}</span>
                </div>
                <div>
                  <span>
                    {card.loanerFlow?.standaloneLoanerProcess
                      ? "Vorgesehener Leihwagen"
                      : "Kundenfahrzeug"}
                  </span>
                  <strong>
                    {card.loanerFlow?.standaloneLoanerProcess
                      ? card.loanerFlow?.plannedLoanerPlate || "-"
                      : card.licensePlate || "-"}
                  </strong>
                </div>
                <div>
                  <span>Filiale</span>
                  <strong>{card.branch ? getBranchLabel(card.branch) : "-"}</strong>
                </div>
                <div>
                  <span>Entwurf</span>
                  <strong>
                    {card.loanerFlow?.loanerWizardDraft?.savedAt
                      ? `gespeichert ${fmtDateTime(card.loanerFlow.loanerWizardDraft.savedAt)}`
                      : "noch nicht gestartet"}
                  </strong>
                </div>
                <div>
                  <span>Bearbeitung</span>
                  <strong>
                    {isRecentActivity(card.loanerFlow?.loanerWizardLock?.at, 10)
                      ? `${card.loanerFlow?.loanerWizardLock?.by || "aktiv"}`
                      : "frei"}
                  </strong>
                </div>
                <div>
                  <span>Offen am Tablet</span>
                  <strong>
                    {card.loanerFlow?.loanerWizardDraft?.hasLocalMedia
                      ? "Fotos/Unterschrift neu prüfen"
                      : "Fotos und Unterschrift"}
                  </strong>
                </div>
                <button type="button" className="btn" onClick={() => openTaskDetail(card.id)}>
                  Auftrag öffnen
                </button>
                <button
                  type="button"
                  className="btn primary"
                  onClick={() => startLoanerWizard(card, card.loanerFlow?.loanerWizardDraft?.wizardStep || 0)}
                >
                  Übergabe fortführen
                </button>
              </div>
            ))}
            {openLoanerDemandCards.length === 0 && (
              <div className="mutedText">Aktuell ist kein offener Leihwagenbedarf vorgemerkt.</div>
            )}
          </div>
        </div>
      </main>
    );
  };

  const renderTechnicianFocusPanel = () => {
    const missionStats = [
      { label: "Werkstatt", value: workshopMission.cards.length },
      { label: "Heute", value: workshopMission.today.length },
      { label: "Überfällig", value: workshopMission.overdue.length, tone: workshopMission.overdue.length ? "warn" : "" },
      { label: "Ohne Techniker", value: workshopMission.unassigned.length },
      { label: "Freigaben", value: workshopMission.approvals.length, tone: workshopMission.approvals.length ? "warn" : "" },
      { label: "Übergabereif", value: workshopMission.readyForHandover.length, tone: "ok" },
    ];
    const focusLanes = [
      {
        title: "Ohne Techniker",
        items: workshopMission.unassigned,
        empty: "Alles zugewiesen.",
      },
      {
        title: "Blocker / Freigaben",
        items: [...workshopMission.blocked, ...workshopMission.approvals].filter(
          (card, index, list) => list.findIndex((item) => item.id === card.id) === index
        ),
        empty: "Keine Blocker.",
      },
      {
        title: "Bereit zur Übergabe",
        items: workshopMission.readyForHandover,
        empty: "Noch nichts übergabereif.",
      },
    ];

    return (
      <div className="card fullSpan technicianFocusPanel">
        <div className="cardHeader">
          <div>
            <h3>Werkstatt-Fokus</h3>
            <p className="mutedText">
              Tagessteuerung für Techniker, Freigaben, Blocker und nächste Übergaben.
            </p>
          </div>
          {(isAdmin || isWorkshopLead) && (
            <button type="button" className="btn" onClick={() => setUi((prev) => ({ ...prev, activeView: "Werkstattplanung" }))}>
              Planung öffnen
            </button>
          )}
        </div>
        <div className="workshopMissionStats">
          {missionStats.map((stat) => (
            <div key={stat.label} className={`missionStat ${stat.tone || ""}`}>
              <span>{stat.label}</span>
              <strong>{stat.value}</strong>
            </div>
          ))}
        </div>
        <div className="technicianFocusGrid">
          <div className="focusHeroTask">
            <span className="sectionTag">Als Nächstes bearbeiten</span>
            {workshopNextCard ? (
              <>
                <strong>
                  {workshopNextCard.orderNumber || workshopNextCard.title || "Auftrag"} |{" "}
                  {[workshopNextCard.vehicleData?.brand, workshopNextCard.vehicleData?.model].filter(Boolean).join(" ") ||
                    "Fahrzeug"}{" "}
                  | {workshopNextCard.licensePlate || "-"}
                </strong>
                <span>
                  {getCaseTypeLabel(workshopNextCard.caseType)} · {fmtDate(workshopNextCard.due) || "ohne Fälligkeit"} ·{" "}
                  {workshopNextCard.assignedTechName || getEmployeeNameById(technicians, workshopNextCard.assignedTech) || "frei"}
                </span>
                <div className="nextWorkActions">
                  <button type="button" className="btn primary" onClick={() => openTaskDetail(workshopNextCard.id)}>
                    Öffnen
                  </button>
                  <button type="button" className="btn" onClick={() => openWorkshopBlocker(workshopNextCard, "internal")}>
                    Kann ich nicht machen
                  </button>
                </div>
              </>
            ) : (
              <p className="mutedText">Aktuell kein direkter nächster Auftrag verfügbar.</p>
            )}
          </div>
          {focusLanes.map((lane) => (
            <div className="focusList" key={lane.title}>
              <strong>{lane.title}</strong>
              {lane.items.slice(0, 5).map((card) => (
                <button type="button" key={`${lane.title}_${card.id}`} onClick={() => openTaskDetail(card.id)}>
                  <span>{card.title || card.orderNumber}</span>
                  <small>
                    {card.assignedTechName || "ohne Techniker"} · {fmtDate(card.due) || "ohne Fälligkeit"}
                  </small>
                </button>
              ))}
              {lane.items.length === 0 && <span className="mutedText">{lane.empty}</span>}
            </div>
          ))}
        </div>
        <div className="workshopMissionHint">
          <strong>Prozessregel</strong>
          <span>
            Annahme legt an und weist zu. Werkstättenleitung bestätigt Zuweisungen oder plant direkt. Techniker
            arbeiten nur ihre eigenen To-dos ab, melden Blocker/Freigaben und übergeben erst nach erledigten
            Pflichtpunkten weiter.
          </span>
        </div>
        {(isAdmin || isWorkshopLead) && (
          <div className="workshopLeadSnapshot">
            <div className="cardHeader">
              <div>
                <h4>Was hängt fest?</h4>
                <p className="mutedText">Schneller Blick für Morgenbesprechung und Tagessteuerung.</p>
              </div>
            </div>
            <div className="leadSnapshotGrid">
              <div>
                <strong>Aufträge pro Mitarbeiter</strong>
                {configuredTechnicians
                  .filter((tech) => tech.type === "Techniker")
                  .slice(0, 8)
                  .map((tech) => {
                    const count = workshopMission.cards.filter(
                      (card) => card.assignedTech === tech.id && card.workStatus !== WORK_STATUS.DONE
                    ).length;
                    return (
                      <button type="button" key={tech.id} onClick={() => setWorkshopSearch(tech.name)}>
                        <span>{tech.name}</span>
                        <b>{count}</b>
                      </button>
                    );
                  })}
              </div>
              <div>
                <strong>Blockiert / Freigabe</strong>
                {[...workshopMission.blocked, ...workshopMission.approvals]
                  .filter((card, index, list) => list.findIndex((item) => item.id === card.id) === index)
                  .slice(0, 6)
                  .map((card) => (
                    <button type="button" key={`lead_blocked_${card.id}`} onClick={() => openTaskDetail(card.id)}>
                      <span>{card.orderNumber || card.title}</span>
                      <b>{getWorkshopCardSignal(card).label}</b>
                    </button>
                  ))}
                {[...workshopMission.blocked, ...workshopMission.approvals].length === 0 && (
                  <span className="mutedText">Keine hängenden Werkstattaufträge.</span>
                )}
              </div>
              <div>
                <strong>Heute raus / Übergabereif</strong>
                {[...workshopMission.today, ...workshopMission.readyForHandover]
                  .filter((card, index, list) => list.findIndex((item) => item.id === card.id) === index)
                  .slice(0, 6)
                  .map((card) => (
                    <button type="button" key={`lead_ready_${card.id}`} onClick={() => openTaskDetail(card.id)}>
                      <span>{card.orderNumber || card.title}</span>
                      <b>{fmtDate(card.due) || getOperationalStatusLabel(card)}</b>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderWorkshopWorklist = () => {
    const today = addDays(0);
    const searchTerm = normalizeLookupText(workshopSearch);
    const matchesWorkshopSearch = (card) => {
      if (!searchTerm) return true;
      return normalizeLookupText(
        [
          card.orderNumber,
          card.title,
          card.customerName,
          card.customerFirstName,
          card.customerLastName,
          card.licensePlate,
          card.vehicleData?.brand,
          card.vehicleData?.model,
        ].filter(Boolean).join(" ")
      ).includes(searchTerm);
    };
    const relevantCards = workshopMission.cards.filter((card) => {
      const isMine = profileTechnicianId && card.assignedTech === profileTechnicianId;
      const isFree = !card.assignedTech;
      const isMyBlocker = isMine && (card.blockerType || card.workStatus === WORK_STATUS.BLOCKED);
      if (showAllWorkshopCards || !profileTechnicianId) return true;
      return isMine || isFree || isMyBlocker;
    });
    const filteredWorkshopCards = relevantCards.filter(matchesWorkshopSearch);
    const assignedToMe = profileTechnicianId
      ? filteredWorkshopCards.filter((card) => card.assignedTech === profileTechnicianId)
      : filteredWorkshopCards;
    const openForPickup = filteredWorkshopCards.filter((card) => !card.assignedTech);
    const listGroups = [
      {
        id: "today",
        title: "Heute zu erledigen",
        helper: "Alles, was heute oder früher fällig ist.",
        items: filteredWorkshopCards.filter(
          (card) => card.due && card.due <= today && card.workStatus !== WORK_STATUS.DONE
        ),
      },
      {
        id: "mine",
        title: "Meine Aufträge",
        helper: profileTechnicianId ? "Deine zugewiesenen Arbeiten." : "Alle aktiven Werkstattarbeiten.",
        items: assignedToMe.filter((card) => card.workStatus !== WORK_STATUS.DONE),
      },
      {
        id: "free",
        title: "Noch frei",
        helper: "Kann von der Werkstatt übernommen werden.",
        items: openForPickup.filter((card) => card.workStatus !== WORK_STATUS.DONE),
      },
      {
        id: "blocked",
        title: "Blockiert",
        helper: "Hier fehlt etwas: Teil, Freigabe, Rückfrage oder Fahrzeug.",
        items: filteredWorkshopCards.filter((card) => card.workStatus === WORK_STATUS.BLOCKED || card.blockerType),
      },
      {
        id: "ready",
        title: "Fertig zur Übergabe",
        helper: "Pflichtpunkte erledigt, nächster Schritt kann gestartet werden.",
        items: filteredWorkshopCards.filter(
          (card) =>
            card.workStatus !== WORK_STATUS.BLOCKED &&
            !card.blockerType &&
            (card.subs || []).length > 0 &&
            !(card.subs || []).some(isBlockingSubtask)
        ),
      },
    ];

    return (
      <main className="workshopWorklist">
        <section className="workshopSearchPanel">
          <div>
            <strong>Auftrag schnell finden</strong>
            <span>Kennzeichen, Auftrag, Kunde oder Fahrzeugmodell eingeben.</span>
          </div>
          <input
            type="search"
            value={workshopSearch}
            onChange={(event) => setWorkshopSearch(event.target.value)}
            placeholder="z. B. W-123AB, AU-..., Tucson"
          />
          <button type="button" className="btn" onClick={() => setShowAllWorkshopCards((prev) => !prev)}>
            {showAllWorkshopCards ? "Nur meine Arbeit" : "Mehr anzeigen"}
          </button>
        </section>
        {listGroups.map((group) => (
          <section key={group.id} className={`workshopListSection ${group.id}`}>
            <div className="workshopListHeader">
              <div>
                <h3>{group.title}</h3>
                <p>{group.helper}</p>
              </div>
              <strong>{group.items.length}</strong>
            </div>
            <div className="workshopListRows">
              {group.items.map((card) => {
                const isOverdue = card.due && card.due < today && card.workStatus !== WORK_STATUS.DONE;
                const progress = calcProgress(card.subs || []);
                const signal = getWorkshopCardSignal(card);
                const vehicleDisplay =
                  [card.vehicleData?.brand, card.vehicleData?.model].filter(Boolean).join(" ") ||
                  card.licensePlate ||
                  "-";
                return (
                  <article key={`${group.id}_${card.id}`} className={`workshopListRow ${isOverdue ? "overdue" : ""} ${card.blockerType ? "blocked" : ""}`}>
                    <button type="button" className="workshopListOpen" onClick={() => openTaskDetail(card.id)}>
                      <span className={`workshopRowState ${signal.tone}`}>{signal.label}</span>
                      <strong>{card.orderNumber || card.title || "Auftrag"}</strong>
                      <span>{vehicleDisplay}</span>
                      <span>{card.licensePlate || "-"}</span>
                      <span>{getCaseTypeLabel(card.caseType)}</span>
                      <span>{fmtDate(card.due) || "ohne Fälligkeit"}</span>
                      <span>{card.assignedTechName || getEmployeeNameById(technicians, card.assignedTech) || "frei"}</span>
                      <span className="workshopProgress">{progress}%</span>
                    </button>
                    <div className="workshopRowActions">
                      <button type="button" className="btn primary" onClick={() => startWorkshopCard(card)} disabled={!canEditDepartmentCard(card)}>
                        {card.assignedTech ? "Arbeit starten" : "Übernehmen"}
                      </button>
                      <button type="button" className="btn" onClick={() => openWorkshopBlocker(card)} disabled={!canEditDepartmentCard(card)}>
                        Ich kann nicht weiter
                      </button>
                      <button type="button" className="btn" onClick={() => openWorkshopHandover(card)} disabled={!canEditDepartmentCard(card)}>
                        Fertig melden
                      </button>
                    </div>
                  </article>
                );
              })}
              {group.items.length === 0 && <div className="workshopEmpty">Keine Aufträge in diesem Bereich.</div>}
            </div>
          </section>
        ))}
      </main>
    );
  };

  const renderDepartmentWorkspace = (department) => (
    <>
      {department === "Annahme" && (
        <>
          <div className="card fullSpan intakeWorkspaceSwitch">
            <div className="detailTabBar">
              {[
                { id: "new", label: "Neue Aufträge" },
                { id: "approvals", label: "Freigaben" },
                { id: "departments", label: "Alle Abteilungen" },
                { id: "loanerDemand", label: "Leihwagenbedarf" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`detailTabButton ${intakeWorkspaceTab === tab.id ? "active" : ""}`}
                  onClick={() => setIntakeWorkspaceTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          {intakeWorkspaceTab === "new" && renderIntakePanel()}
          {intakeWorkspaceTab === "approvals" && renderApprovalCenter({ compact: true })}
          {intakeWorkspaceTab === "departments" && renderKanbanBoard()}
          {intakeWorkspaceTab === "loanerDemand" && renderOpenLoanerDemandPanel()}
        </>
      )}
      {department === "Werkstatt" && renderTechnicianFocusPanel()}
      {department !== "Annahme" && (
        <div className="card fullSpan departmentIntroCard">
        <div className="cardHeader">
          <div>
            <h3>{department} Kanban</h3>
            <p className="mutedText">
              Nur die offenen Fälle dieser Abteilung. Details öffnen, Sub-To-dos abhaken,
              Rückfragen dokumentieren und den Task sauber an die nächste Abteilung übergeben.
            </p>
          </div>
        </div>
        </div>
      )}
      {department === "Werkstatt" ? renderWorkshopWorklist() : renderKanbanBoard(department)}
    </>
  );

  const renderPasswordPanel = () =>
    passwordState.open && (
      <div className="modalOverlay">
        <div className="modalCard">
          <div className="modalHeader">
            <h3>
              {passwordState.required
                ? "Zugang aktivieren"
                : "Passwort ändern"}
            </h3>
            {!passwordState.required && (
              <button
                type="button"
                className="btn"
                onClick={() =>
                  setPasswordState((prev) => ({
                    ...prev,
                    open: false,
                    error: "",
                    success: "",
                  }))
                }
              >
                Schließen
              </button>
            )}
          </div>

          <form className="stackForm" onSubmit={handlePasswordChange}>
            {passwordState.required && (
              <p className="mutedText">
                Bitte vergeben Sie jetzt Ihr persönliches Passwort. Danach ist
                der eingeladene Zugang vollständig aktiviert.
              </p>
            )}
            <label>
              Neues Passwort
              <input
                type="password"
                value={passwordState.nextPassword}
                disabled={passwordState.passwordUpdated}
                onChange={(event) =>
                  setPasswordState((prev) => ({
                    ...prev,
                    nextPassword: event.target.value,
                  }))
                }
              />
            </label>

            <label>
              Neues Passwort wiederholen
              <input
                type="password"
                value={passwordState.confirmPassword}
                disabled={passwordState.passwordUpdated}
                onChange={(event) =>
                  setPasswordState((prev) => ({
                    ...prev,
                    confirmPassword: event.target.value,
                  }))
                }
              />
            </label>

            {passwordState.error && <div className="authError">{passwordState.error}</div>}
            <button type="submit" className="btn primary" disabled={passwordState.saving}>
              {passwordState.saving
                ? "Speichere ..."
                : passwordState.passwordUpdated
                  ? "Aktivierung erneut bestätigen"
                  : "Passwort aktualisieren"}
            </button>
          </form>
        </div>
      </div>
    );

  const renderLoanerCompletionModal = () => {
    if (!loanerCompletion?.booking) return null;
    const booking = loanerCompletion.booking;
    const confirmationUrl = getLoanerConfirmationUrl(booking);
    const qrUrl = getLoanerConfirmationQrUrl(booking);
    const diagnostics = buildMailDiagnosticSteps(booking);
    return (
      <div className="modalOverlay">
        <div className="modalCard completionModal">
          <div className="modalHeader">
            <div>
              <p className="eyebrow">Leihwagen gespeichert</p>
              <h3>{booking.loanerPlate || "Leihwagen"} bereit zur Kundenbestätigung</h3>
            </div>
            <button type="button" className="btn" onClick={() => setLoanerCompletion(null)}>
              Schließen
            </button>
          </div>
          <div className="customerSuccess">{loanerCompletion.message}</div>
          <div className="confirmationStepGrid">
            {diagnostics.map((step) => (
              <div key={step.label} className={`confirmationStep ${step.ok ? "done" : ""}`}>
                <span>{step.label}</span>
                <strong>{step.value}</strong>
              </div>
            ))}
          </div>
          {confirmationUrl && (
            <div className="confirmationFallbackPanel official">
              <div>
                <strong>Kunde bestätigt per QR-Code vor Ort</strong>
                <span>Falls die E-Mail im Spam landet, diesen QR-Code am Tablet scannen lassen.</span>
                <code>{confirmationUrl}</code>
              </div>
              <div className="qrCodeBox large">
                {qrUrl ? <img src={qrUrl} alt="QR-Code zum Bestätigungslink" /> : <span>Link zu lang für QR</span>}
              </div>
            </div>
          )}
          <div className="actions">
            <button
              type="button"
              className="btn"
              disabled={!confirmationUrl}
              onClick={() => {
                navigator.clipboard.writeText(confirmationUrl);
                setDbStatus("Bestätigungslink wurde kopiert.");
              }}
            >
              Link kopieren
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setLoanerCompletion(null);
                setUi((prev) => ({ ...prev, activeView: "Leihwagen" }));
                setLoanerBoardTab("handover");
              }}
            >
              Leihwagen-Modul öffnen
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderImageViewerModal = () => {
    if (!imageViewer.open || !imageViewer.photos.length) return null;
    const activeIndex = Math.min(Math.max(imageViewer.index, 0), imageViewer.photos.length - 1);
    const activePhoto = imageViewer.photos[activeIndex];
    const move = (direction) =>
      setImageViewer((prev) => ({
        ...prev,
        index: (prev.index + direction + prev.photos.length) % prev.photos.length,
      }));
    return (
      <div className="modalOverlay imageViewerOverlay">
        <div className="modalCard imageViewerCard">
          <div className="modalHeader">
            <div>
              <p className="eyebrow">{activePhoto.group}</p>
              <h3>{activePhoto.label}</h3>
              <span className={`mediaStatusPill ${isInlineMedia(activePhoto.url) ? "warn" : "ok"}`}>
                {activePhoto.storage}
              </span>
              {activePhoto.note && <p className="mutedText">{activePhoto.note}</p>}
            </div>
            <button type="button" className="btn" onClick={() => setImageViewer({ open: false, photos: [], index: 0 })}>
              Schließen
            </button>
          </div>
          <div className="imageViewerStage">
            <button type="button" className="imageViewerNav" onClick={() => move(-1)} aria-label="Vorheriges Bild">
              ‹
            </button>
            <img src={activePhoto.url} alt={activePhoto.label} />
            <button type="button" className="imageViewerNav" onClick={() => move(1)} aria-label="Nächstes Bild">
              ›
            </button>
          </div>
          <div className="imageViewerFooter">
            <span>
              Bild {activeIndex + 1} von {imageViewer.photos.length}
            </span>
            <a className="btn" href={activePhoto.url} target="_blank" rel="noreferrer">
              In neuem Tab öffnen
            </a>
          </div>
        </div>
      </div>
    );
  };

  const renderLoanerCorrectionModal = () => {
    if (!loanerCorrection) return null;
    const update = (field, value) =>
      setLoanerCorrection((prev) => ({
        ...prev,
        [field]: value,
        emailChanged:
          field === "customerEmail"
            ? bookings.find((item) => item.id === prev.bookingId)?.customerEmail !== value
            : prev.emailChanged,
      }));
    return (
      <div className="modalOverlay">
        <div className="modalCard correctionModal">
          <div className="modalHeader">
            <div>
              <p className="eyebrow">Korrektur</p>
              <h3>Verleihdaten korrigieren</h3>
              <p className="mutedText">
                Änderungen werden am Vorgang protokolliert. Bei geänderter E-Mail muss die Bestätigung erneut gesendet werden.
              </p>
            </div>
            <button type="button" className="btn" onClick={() => setLoanerCorrection(null)}>
              Schließen
            </button>
          </div>
          {loanerCorrection.error && <div className="authError">{loanerCorrection.error}</div>}
          {loanerCorrection.emailChanged && (
            <div className="warnBox">
              E-Mail wurde geändert. Nach dem Speichern bitte den Bestätigungslink erneut senden.
            </div>
          )}
          <div className="formGrid correctionGrid">
            <label>
              Vorname
              <input value={loanerCorrection.customerFirstName} onChange={(event) => update("customerFirstName", event.target.value)} />
            </label>
            <label>
              Nachname
              <input value={loanerCorrection.customerLastName} onChange={(event) => update("customerLastName", event.target.value)} />
            </label>
            <label>
              Geschlecht
              <select value={loanerCorrection.customerGender} onChange={(event) => update("customerGender", event.target.value)}>
                <option value="">Bitte wählen</option>
                <option value="männlich">männlich</option>
                <option value="weiblich">weiblich</option>
                <option value="divers">divers</option>
              </select>
            </label>
            <label>
              Kunden-E-Mail
              <input type="email" value={loanerCorrection.customerEmail} onChange={(event) => update("customerEmail", event.target.value)} />
            </label>
            <label>
              Verleihbeginn
              <input type="date" value={loanerCorrection.plannedPickupDate} onChange={(event) => update("plannedPickupDate", event.target.value)} />
            </label>
            <label>
              Geplante Rückgabe
              <input type="date" value={loanerCorrection.expectedReturnDate} onChange={(event) => update("expectedReturnDate", event.target.value)} />
            </label>
            <label>
              KM Übergabe
              <input type="number" value={loanerCorrection.kmStart} onChange={(event) => update("kmStart", event.target.value)} />
            </label>
            <label>
              Tankstand Übergabe
              <input value={loanerCorrection.fuelStart} onChange={(event) => update("fuelStart", event.target.value)} />
            </label>
            <label>
              Auftrag
              <input value={loanerCorrection.orderNumber} onChange={(event) => update("orderNumber", event.target.value)} />
            </label>
          </div>
          <label className="fullWidthField">
            Grund der Änderung
            <textarea
              rows={3}
              value={loanerCorrection.reason}
              onChange={(event) => update("reason", event.target.value)}
              placeholder="z.B. E-Mail-Adresse bei Anlage falsch eingegeben"
            />
          </label>
          <div className="actions">
            <button type="button" className="btn" onClick={() => setLoanerCorrection(null)}>
              Abbrechen
            </button>
            <button type="button" className="btn primary" onClick={saveLoanerCorrection} disabled={loanerCorrection.saving}>
              {loanerCorrection.saving ? "Speichert ..." : "Korrektur speichern"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderWizard = () => {
    if (!wizardOpen) return null;

    const selectedWizardLoaner = loaners.find((loaner) => loaner.id === wizard.loanerId);
    const activeVehicleDamages = selectedWizardLoaner
      ? getActiveDamagesForLoaner(selectedWizardLoaner.id)
      : [];
    const lastReturnedKm = selectedWizardLoaner ? getLastReturnedKmForLoaner(selectedWizardLoaner.id) : "";
    const kmLowerThanLast = shouldWarnMileage(wizard.kmStart, lastReturnedKm);
    const selectedLoanerConflicts =
      selectedWizardLoaner && wizard.plannedPickupDate && wizard.expectedReturnDate
        ? getLoanerConflictsForRange(
            selectedWizardLoaner.id,
            wizard.plannedPickupDate,
            wizard.expectedReturnDate
          )
        : [];
    const wizardLoanerOptions = loaners
      .filter((loaner) => {
        if (!loaner.isActive) return false;
        if (wizardUsageFilter === "all") return true;
        return loaner.usageType === wizardUsageFilter;
      })
      .sort((a, b) => {
        const availableScore = (loaner) => (isLoanerUnavailable(loaner, availabilityMap) ? 1 : 0);
        return availableScore(a) - availableScore(b) || a.plate.localeCompare(b.plate);
      });
    const branchWarningNeeded =
      Boolean(profile?.branch) &&
      Boolean(selectedWizardLoaner?.homeBranch) &&
      profile.branch !== selectedWizardLoaner.homeBranch;
    const wizardEmployeeBranch = getEmployeeBranch(profile, selectedWizardLoaner?.homeBranch);
    const wizardEmployees = getEmployeesForBranchAndTypes(technicians, wizardEmployeeBranch, ["Kundendienstberater"]);
    const currentStep = WIZARD_STEPS[wizardStep];
    const stepStates = {
      employee: Boolean(wizard.handoverEmployeeId),
      vehicle:
        Boolean(wizard.loanerId) &&
        Boolean(wizard.plannedPickupDate) &&
        Boolean(wizard.expectedReturnDate) &&
        wizard.expectedReturnDate >= wizard.plannedPickupDate &&
        selectedLoanerConflicts.length === 0 &&
        (!branchWarningNeeded || wizard.branchWarningAccepted),
      customer:
        Boolean(wizard.customerFirstName) &&
        Boolean(wizard.customerLastName) &&
        Boolean(wizard.customerGender) &&
        (isInternalOrder(cards.find((card) => card.id === wizard.cardId)) || Boolean(wizard.customerEmail)) &&
        Boolean(wizard.plannedPickupDate) &&
        Boolean(wizard.expectedReturnDate) &&
        wizard.expectedReturnDate >= wizard.plannedPickupDate &&
        selectedLoanerConflicts.length === 0,
      license: Boolean(wizard.licenseFrontPhoto) && Boolean(wizard.licenseBackPhoto),
      insurance: Boolean(wizard.insuranceOption) && wizard.termsAccepted,
      walkaround: PHOTO_LABELS.every((entry) => wizard[`${entry.key}Photo`]),
        damage:
          wizard.noOtherDamageConfirmed &&
          wizard.damageEntries.every(
            (entry) => Boolean(entry.url) && Boolean(String(entry.description || "").trim())
          ),
        handover:
          Boolean(wizard.kmStart) &&
          Boolean(wizard.fuelStart) &&
          (isInternalOrder(cards.find((card) => card.id === wizard.cardId)) || Boolean(wizard.signature)) &&
          Boolean(wizard.dashboardPhotoPrimary || wizard.dashboardPhotoSecondary),
      };
    const loanerCompletionChecks = [
      { label: "Mitarbeiter gewählt", ok: stepStates.employee },
      { label: "Fahrzeug und Zeitraum verfügbar", ok: stepStates.vehicle },
      { label: "Kundendaten vollständig", ok: stepStates.customer },
      { label: "Führerschein fotografiert", ok: stepStates.license },
      { label: "Tarif und Bedingungen bestätigt", ok: stepStates.insurance },
      { label: "Fahrzeugfotos vollständig", ok: stepStates.walkaround },
      { label: "Schäden sauber dokumentiert", ok: stepStates.damage },
      { label: "KM/Tank plus Foto erfasst", ok: Boolean(wizard.kmStart && wizard.fuelStart && (wizard.dashboardPhotoPrimary || wizard.dashboardPhotoSecondary)) },
      { label: "Unterschrift / interne Freigabe", ok: isInternalOrder(cards.find((card) => card.id === wizard.cardId)) || Boolean(wizard.signature) },
    ];
    const progressPercent = Math.round(((wizardStep + 1) / WIZARD_STEPS.length) * 100);
    const canGoNext =
      wizardStep < WIZARD_STEPS.length - 1 ? true : true;
    const contextItems = [
      { label: "Auftrag", value: wizard.orderNumber || "-" },
      {
        label: "Kunde",
        value:
          [wizard.customerFirstName, wizard.customerLastName].filter(Boolean).join(" ") || "-",
      },
      { label: "Leihwagen", value: selectedWizardLoaner?.plate || "-" },
      { label: "Mitarbeiter", value: wizard.handoverEmployeeName || "-" },
      { label: "Ab", value: fmtDate(wizard.plannedPickupDate) || "-" },
      { label: "Filiale", value: getBranchLabel(profile?.branch) || "-" },
      { label: "Rückgabe", value: fmtDate(wizard.expectedReturnDate) || "-" },
      { label: "Tarif", value: getInsuranceLabel(wizard.insuranceOption) || "-" },
    ];

    return (
      <div className="modalOverlay">
        <div className="modalCard wizardCard">
          <div className="modalHeader">
            <div>
              <p className="eyebrow">Leihwagen-Übergabe</p>
              <h3>
                {wizard.customerFirstName || wizard.customerLastName
                  ? `${wizard.customerFirstName} ${wizard.customerLastName}`.trim()
                  : "Neuer Vorgang"}
              </h3>
            </div>
            <button
              type="button"
              className="btn dangerSubtle"
              onClick={() => closeWizard({ confirm: true })}
              disabled={wizard.isSubmitting}
            >
              {wizard.cardId ? "Bearbeitung schließen" : "Prozess stornieren"}
            </button>
          </div>

          <div className="wizardContextBar">
            {contextItems.map((item) => (
              <div key={item.label} className="contextTile">
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>

          <div className="wizardStepper">
            {WIZARD_STEPS.map((step, index) => {
              const isActive = index === wizardStep;
              const isComplete = stepStates[step.id];
              const mayJump = index <= wizardStep || stepStates[WIZARD_STEPS[index - 1]?.id];
              return (
                <button
                  type="button"
                  key={step.id}
                  className={`stepPill ${isActive ? "active" : ""} ${isComplete ? "complete" : ""}`}
                  onClick={() => mayJump && setWizardStep(index)}
                >
                  <span>{index + 1}</span>
                  <strong>{step.label}</strong>
                </button>
              );
            })}
          </div>

          <div className="wizardProgressBand">
            <div className="wizardProgressBar" style={{ width: `${progressPercent}%` }} />
          </div>

          <div className="wizardSections">
            {wizardStep === 0 && (
              <div className="wizardSection wizardSectionActive">
                <div className="wizardStepCard">
                  <div className="wizardStepIntro">
                    <span className="sectionTag">Schritt 1</span>
                    <h4>Wer führt die Übergabe durch?</h4>
                    <p className="mutedText">
                      Bitte den Mitarbeiter des Standortes auswählen, der die Dokumentation am Fahrzeug macht.
                    </p>
                  </div>
                  <div className="loanerChoiceGrid employeeChoiceGrid">
                    {wizardEmployees.map((employee) => (
                      <button
                        type="button"
                        key={employee.id}
                        className={`loanerChoiceCard employeeChoiceCard ${
                          wizard.handoverEmployeeId === employee.id ? "selected" : ""
                        }`}
                        onClick={() =>
                          setWizard((prev) => ({
                            ...prev,
                            handoverEmployeeId: employee.id,
                            handoverEmployeeName: employee.name,
                            submissionError: "",
                          }))
                        }
                      >
                        <span className="loanerChoicePlate">{employee.name}</span>
                        <strong>{employee.type}</strong>
                        <span>{getBranchLabel(employee.branch)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {wizardStep === 1 && (
              <div className="wizardSection wizardSectionActive">
                <div className="wizardStepCard">
                  <div className="wizardStepIntro">
                    <span className="sectionTag">Schritt 2</span>
                    <h4>Fahrzeug wählen</h4>
                    <p className="mutedText">
                      Zuerst nur das passende Fahrzeug auswählen. Kundenersatzfahrzeuge stehen immer zuerst.
                    </p>
                  </div>
                  <div className="loanerSelectionHeader">
                    <div className="selectionStatGroup">
                      <div className="summaryBadge">
                        <span>Kundenersatzfahrzeuge</span>
                        <strong>{customerReplacementLoaners.length} verfügbar</strong>
                      </div>
                      <div className="summaryBadge">
                        <span>Sonderkategorien</span>
                        <strong>{secondaryAvailableLoaners.length} verfügbar</strong>
                      </div>
                      <div className="summaryBadge subtle">
                        <span>Aktuell nicht verfügbar</span>
                        <strong>{unavailableCustomerLoaners.length}</strong>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn"
                      onClick={() =>
                        setWizardUsageFilter((prev) =>
                          prev === "customer_loaner" ? "all" : "customer_loaner"
                        )
                      }
                    >
                      {wizardUsageFilter === "all"
                        ? "Nur Kundenersatzfahrzeuge zeigen"
                        : "Dienstwagen / Langzeitvermietung einblenden"}
                    </button>
                  </div>
                  <div className="fleetFilterBar wizardFleetFilter">
                    {[
                      { value: "customer_loaner", label: "Kundenersatz" },
                      { value: "long_term_assignment", label: "Langzeit" },
                      { value: "employee_vehicle", label: "Dienstwagen" },
                      { value: "all", label: "Alle" },
                    ].map((option) => (
                      <button
                        type="button"
                        key={option.value}
                        className={`fleetFilterButton ${wizardUsageFilter === option.value ? "active" : ""}`}
                        onClick={() => setWizardUsageFilter(option.value)}
                      >
                        <span>{option.label}</span>
                      </button>
                    ))}
                  </div>
                  <div className="loanerChoiceSection">
                    {wizardLoanerOptions.length > 0 && (
                      <div className="loanerChoiceGroup">
                        <h5>Fahrzeugauswahl</h5>
                        <div className="loanerChoiceGrid">
                          {wizardLoanerOptions.map((loaner) => {
                            const availableFrom = getLoanerAvailabilityDate(loaner.id);
                            const isUnavailable = isLoanerUnavailable(loaner, availabilityMap);
                            return (
                              <button
                                type="button"
                                key={loaner.id}
                                className={`loanerChoiceCard ${wizard.loanerId === loaner.id ? "selected" : ""} ${isUnavailable ? "disabled" : ""}`}
                                onClick={() => openAvailabilityInspector(loaner.id)}
                              >
                                <span className="loanerChoicePlate">{loaner.plate}</span>
                                <strong>
                                  {loaner.brand} {loaner.model}
                                </strong>
                                <span>{getLoanerUsageLabel(loaner.usageType)}</span>
                                <span>{getBranchLabel(loaner.homeBranch)}</span>
                                <span>
                                  {loaner.permanentlyCheckedOut
                                    ? "Dauerhaft verliehen"
                                    : isUnavailable
                                      ? `Nicht verfügbar bis ${fmtDate(availableFrom)}`
                                      : `Verfügbar ab ${fmtDate(availableFrom)}`}
                                </span>
                                <span className="linkLike">Kalender öffnen</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {wizardLoanerOptions.length === 0 && (
                      <div className="mutedText">Keine Fahrzeuge für diesen Filter vorhanden.</div>
                    )}
                  </div>

                  {selectedWizardLoaner && (
                    <div className="wizardMiniSummary selectedLoanerSummary">
                      <span>Ausgewählt</span>
                      <strong>
                        {selectedWizardLoaner.plate} - {selectedWizardLoaner.brand} {selectedWizardLoaner.model}
                      </strong>
                      <small>
                        {fmtDate(wizard.plannedPickupDate)} bis {fmtDate(wizard.expectedReturnDate)}
                      </small>
                      <div className="actions">
                        <button
                          type="button"
                          className="btn"
                          onClick={() => openAvailabilityInspector(selectedWizardLoaner.id)}
                        >
                          Verfügbarkeitskalender anzeigen
                        </button>
                      </div>
                    </div>
                  )}

                  {!selectedWizardLoaner && (
                    <div className="fieldHint">
                      Auf ein Fahrzeug tippen, dann öffnet sich direkt der Verfügbarkeitskalender.
                    </div>
                  )}

                  {selectedLoanerConflicts.length > 0 && (
                    <div className="authError">
                      Dieses Fahrzeug ist im gewählten Zeitraum nicht verfügbar. Bitte anderen Zeitraum wählen.
                    </div>
                  )}

                  {branchWarningNeeded && (
                    <div className="warnBox">
                      <strong>Achtung Filialzuordnung</strong>
                      <p>
                        Dieses Fahrzeug ist der Filiale {getBranchLabel(selectedWizardLoaner.homeBranch)}
                        zugeordnet, du bist aber aktuell in {getBranchLabel(profile?.branch)}.
                        Bitte bestätigen, falls das Fahrzeug bereits am Standort ist.
                      </p>
                      <label className="checkRow">
                        <input
                          type="checkbox"
                          checked={wizard.branchWarningAccepted}
                          onChange={(event) =>
                            setWizard((prev) => ({
                              ...prev,
                              branchWarningAccepted: event.target.checked,
                            }))
                          }
                        />
                        Ich weiss, dass das Fahrzeug filialfremd ist und bereits an meinem Standort verifiziert wurde.
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}

            {wizardStep === 2 && (
              <div className="wizardSection wizardSectionActive">
                <div className="wizardStepCard">
                  <div className="wizardStepIntro">
                    <span className="sectionTag">Schritt 2</span>
                    <h4>Kundendaten erfassen</h4>
                    <p className="mutedText">
                      Jetzt nur noch die Angaben für die Übergabe eintragen. Den Zeitraum kannst du hier bei Bedarf noch anpassen.
                    </p>
                  </div>
                  <div className="wizardCompactGrid">
                    <label>
                      Vorname
                      <input
                        value={wizard.customerFirstName}
                        onChange={(event) =>
                          setWizard((prev) => ({
                            ...prev,
                            customerFirstName: event.target.value,
                            customerName: `${event.target.value} ${prev.customerLastName}`.trim(),
                          }))
                        }
                      />
                    </label>

                    <label>
                      Nachname
                      <input
                        value={wizard.customerLastName}
                        onChange={(event) =>
                          setWizard((prev) => ({
                            ...prev,
                            customerLastName: event.target.value,
                            customerName: `${prev.customerFirstName} ${event.target.value}`.trim(),
                          }))
                        }
                      />
                    </label>

                    <label>
                      Geschlecht
                      <select
                        value={wizard.customerGender}
                        onChange={(event) =>
                          setWizard((prev) => ({
                            ...prev,
                            customerGender: event.target.value,
                          }))
                        }
                      >
                        <option value="">Bitte wählen</option>
                        {GENDER_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label>
                      Kunden-E-Mail
                      <input
                        type="email"
                        value={wizard.customerEmail}
                        onChange={(event) =>
                          setWizard((prev) => ({
                            ...prev,
                            customerEmail: event.target.value,
                          }))
                        }
                      />
                    </label>

                    <label>
                      Verleihbeginn
                      <input
                        type="date"
                        value={wizard.plannedPickupDate}
                        onChange={(event) =>
                          setWizard((prev) => ({
                            ...prev,
                            plannedPickupDate: event.target.value,
                          }))
                        }
                      />
                    </label>

                    <label>
                      Geplante Rückgabe
                      <input
                        type="date"
                        value={wizard.expectedReturnDate}
                        onChange={(event) =>
                          setWizard((prev) => ({
                            ...prev,
                            expectedReturnDate: event.target.value,
                          }))
                        }
                      />
                    </label>
                  </div>
                  {selectedWizardLoaner && (
                    <div className="wizardMiniSummary">
                      <span>Zeitraum prüfen</span>
                      <strong>
                        {selectedWizardLoaner.plate} - {selectedWizardLoaner.brand} {selectedWizardLoaner.model}
                      </strong>
                      <div className="actions">
                        <button
                          type="button"
                          className="btn"
                          onClick={() => openAvailabilityInspector(selectedWizardLoaner.id)}
                        >
                          Kalender erneut öffnen
                        </button>
                      </div>
                    </div>
                  )}
                  {selectedLoanerConflicts.length > 0 && (
                    <div className="authError">
                      Der geänderte Zeitraum kollidiert mit einer bestehenden Reservierung.
                    </div>
                  )}
                </div>
              </div>
            )}

            {wizardStep === 3 && (
              <div className="wizardSection wizardSectionActive">
                <div className="wizardStepCard">
                  <div className="wizardStepIntro">
                    <span className="sectionTag">Schritt 3</span>
                    <h4>Führerschein fotografieren</h4>
                    <p className="mutedText">
                      Vorderseite und Rückseite nacheinander aufnehmen.
                    </p>
                  </div>
                  <div className="photoGrid">
                    <CameraCaptureButton
                      label="Vorderseite Führerschein"
                      value={wizard.licenseFrontPhoto}
                      sequenceHint="Startet Vorder- und Rückseite automatisch"
                      onOpenCamera={() =>
                        openPhotoCaptureSequence({
                          title: "Führerschein",
                          items: [
                            {
                              field: "licenseFrontPhoto",
                              label: "Vorderseite Führerschein",
                              value: wizard.licenseFrontPhoto,
                            },
                            {
                              field: "licenseBackPhoto",
                              label: "Rückseite Führerschein",
                              value: wizard.licenseBackPhoto,
                            },
                          ],
                          currentField: "licenseFrontPhoto",
                          onSave: (item, dataUrl) =>
                            setWizard((prev) => ({
                              ...prev,
                              [item.field]: dataUrl,
                            })),
                        })
                      }
                      onChange={(event) =>
                        handleWizardPhotoUpload(
                          "licenseFrontPhoto",
                          event.target.files
                        )
                      }
                    />
                    <CameraCaptureButton
                      label="Rückseite Führerschein"
                      value={wizard.licenseBackPhoto}
                      sequenceHint="Setzt die Fotoreihe fort"
                      onOpenCamera={() =>
                        openPhotoCaptureSequence({
                          title: "Führerschein",
                          items: [
                            {
                              field: "licenseFrontPhoto",
                              label: "Vorderseite Führerschein",
                              value: wizard.licenseFrontPhoto,
                            },
                            {
                              field: "licenseBackPhoto",
                              label: "Rückseite Führerschein",
                              value: wizard.licenseBackPhoto,
                            },
                          ],
                          currentField: "licenseBackPhoto",
                          onSave: (item, dataUrl) =>
                            setWizard((prev) => ({
                              ...prev,
                              [item.field]: dataUrl,
                            })),
                        })
                      }
                      onChange={(event) =>
                        handleWizardPhotoUpload(
                          "licenseBackPhoto",
                          event.target.files
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            {wizardStep === 4 && (
              <div className="wizardSection wizardSectionActive">
                <div className="wizardStepCard">
                  <div className="wizardStepIntro">
                    <span className="sectionTag">Schritt 4</span>
                    <h4>Tarif und Bedingungen</h4>
                    <p className="mutedText">
                      Den Selbstbehalt wählen und die Nutzung einmal sauber bestätigen.
                    </p>
                  </div>
                  <div className="wizardCompactGrid">
                    <label>
                      Selbstbehalt
                      <select
                        value={wizard.insuranceOption}
                        onChange={(event) =>
                          setWizard((prev) => ({
                            ...prev,
                            insuranceOption: event.target.value,
                          }))
                        }
                      >
                        {INSURANCE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="termsBox wizardTermsBox">
                    <strong>Hinweise zur Nutzung</strong>
                    <ul className="termsList">
                      {LOANER_TERMS.map((entry) => (
                        <li key={entry}>{entry}</li>
                      ))}
                    </ul>
                  </div>

                  <label className="checkRow">
                    <input
                      type="checkbox"
                      checked={wizard.termsAccepted}
                      onChange={(event) =>
                        setWizard((prev) => ({
                          ...prev,
                          termsAccepted: event.target.checked,
                        }))
                      }
                    />
                    Kunde bestätigt die Bedingungen und die gewählte Selbstbehalt-Option.
                  </label>
                </div>
              </div>
            )}

            {wizardStep === 5 && (
              <div className="wizardSection wizardSectionActive">
                <div className="wizardStepCard">
                  <div className="wizardStepIntro">
                    <span className="sectionTag">Schritt 5</span>
                    <h4>Rundgang Pflichtfotos</h4>
                    <p className="mutedText">
                      Fahrzeug jetzt Seite für Seite dokumentieren.
                    </p>
                  </div>
                  <div className="photoGrid">
                    {PHOTO_LABELS.map((entry) => (
                      <CameraCaptureButton
                        key={entry.key}
                        label={`${entry.label} aufnehmen`}
                        value={wizard[`${entry.key}Photo`]}
                        sequenceHint="Fotoreihe läuft automatisch weiter"
                        onOpenCamera={() =>
                          openPhotoCaptureSequence({
                            title: "Fahrzeugrundgang bei Übergabe",
                            items: PHOTO_LABELS.map((photo) => ({
                              field: `${photo.key}Photo`,
                              label: photo.label,
                              value: wizard[`${photo.key}Photo`],
                            })),
                            currentField: `${entry.key}Photo`,
                            onSave: (item, dataUrl) =>
                              setWizard((prev) => ({
                                ...prev,
                                [item.field]: dataUrl,
                              })),
                          })
                        }
                        onChange={(event) =>
                          handleWizardPhotoUpload(
                            `${entry.key}Photo`,
                            event.target.files
                          )
                        }
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {wizardStep === 6 && (
              <div className="wizardSection wizardSectionActive">
                <div className="wizardStepCard">
                  <div className="wizardStepIntro">
                    <span className="sectionTag">Schritt 6</span>
                    <h4>Dokumentierte Schäden</h4>
                    <p className="mutedText">
                      Pro Schaden bitte Foto und Beschreibung gemeinsam erfassen.
                    </p>
                  </div>
                  {activeVehicleDamages.length > 0 && (
                    <div className="knownDamagePanel">
                      <strong>Bereits dokumentierte aktive Schäden</strong>
                      <span>Diese Schäden sind aus früheren Vorgängen zu diesem Fahrzeug bekannt.</span>
                      <div className="damageHistoryList">
                        {activeVehicleDamages.map((entry, index) => (
                          <div key={`known_${entry.sourceBookingId}_${entry.id || index}`} className="damageHistoryItem">
                            {entry.url ? (
                              <div className="mediaPreview damage compact">
                                <img src={entry.url} alt={`Bekannter Schaden ${index + 1}`} />
                              </div>
                            ) : (
                              <div className="missingMediaBox">Kein Bild</div>
                            )}
                            <div>
                              <strong>Bereits dokumentiert</strong>
                              <span>{entry.description || "Ohne Beschreibung"}</span>
                              <small>{entry.sourceDate ? fmtDateTime(entry.sourceDate) : ""}</small>
                              <div className="actions">
                                <button
                                  type="button"
                                  className="btn"
                                  onClick={() =>
                                    updateActiveDamageLifecycle(entry, "repaired")
                                  }
                                >
                                  Als behoben markieren
                                </button>
                                <button
                                  type="button"
                                  className="btn dangerSubtle"
                                  onClick={() =>
                                    updateActiveDamageLifecycle(entry, "deleted")
                                  }
                                >
                                  Schaden löschen
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="dualUploadGrid">
                    <label className="cameraButtonCard">
                      <div className="cameraButtonText">
                        <strong>Schaden direkt aufnehmen</strong>
                        <span>Kamera auf dem Tablet nutzen</span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        multiple
                        onChange={(event) => addDamageEntry(event.target.files)}
                      />
                    </label>

                    <UploadFallbackCard
                      label="Weitere Schäden hochladen"
                      description="Desktop-Fallback oder Galerie verwenden"
                      multiple
                      onChange={(event) => addDamageEntry(event.target.files)}
                    />
                  </div>

                  <div className="damageEntriesList">
                    {wizard.damageEntries.map((entry, index) => (
                      <div key={entry.id} className="damageEntryCard">
                        <div className="damageEntryHeader">
                          <strong>Schaden {index + 1}</strong>
                          <button
                            type="button"
                            className="btn"
                            onClick={() => removeDamageEntry(entry.id)}
                          >
                            Entfernen
                          </button>
                        </div>
                        <div className="damageEntryBody">
                          <div className="mediaPreview damage">
                            <img src={entry.url} alt={entry.name || `Schaden ${index + 1}`} />
                          </div>
                          <label>
                            Beschreibung Schaden {index + 1}
                            <textarea
                              rows={3}
                              value={entry.description}
                              onChange={(event) =>
                                updateDamageEntry(entry.id, { description: event.target.value })
                              }
                            />
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>

                  <label className="checkRow">
                    <input
                      type="checkbox"
                      checked={wizard.noOtherDamageConfirmed}
                      onChange={(event) =>
                        setWizard((prev) => ({
                          ...prev,
                          noOtherDamageConfirmed: event.target.checked,
                        }))
                      }
                    />
                    Es sind keine weiteren Schäden vorhanden als die oben dokumentierten.
                  </label>
                </div>
              </div>
            )}

            {wizardStep === 7 && (
              <div className="wizardSection wizardSectionActive">
                <div className="wizardStepCard">
                  <div className="wizardStepIntro">
                    <span className="sectionTag">Schritt 7</span>
                    <h4>Übergabedaten</h4>
                    <p className="mutedText">
                      Zum Schluss Kilometerstand, Tankstand und die Kundenunterschrift erfassen.
                    </p>
                  </div>
                    <div className="wizardFinalSummary">
                      <div>
                        <span>Kunde</span>
                        <strong>{[wizard.customerFirstName, wizard.customerLastName].filter(Boolean).join(" ") || "-"}</strong>
                      </div>
                      <div>
                        <span>Leihwagen</span>
                        <strong>{selectedWizardLoaner?.plate || "-"} · {selectedWizardLoaner?.brand || ""} {selectedWizardLoaner?.model || ""}</strong>
                      </div>
                      <div>
                        <span>Zeitraum</span>
                        <strong>{fmtDate(wizard.plannedPickupDate)} bis {fmtDate(wizard.expectedReturnDate)}</strong>
                      </div>
                      <div>
                        <span>Tarif</span>
                        <strong>{INSURANCE_OPTIONS.find((option) => option.value === wizard.insuranceOption)?.label || "-"}</strong>
                      </div>
                      <div>
                        <span>Fotos</span>
                        <strong>{PHOTO_LABELS.filter((entry) => wizard[`${entry.key}Photo`]).length}/{PHOTO_LABELS.length} Fahrzeugfotos</strong>
                      </div>
                      <div>
                        <span>Bestätigung</span>
                        <strong>{isInternalOrder(cards.find((card) => card.id === wizard.cardId)) ? "intern, ohne Kundenlink" : wizard.customerEmail || "E-Mail offen"}</strong>
                      </div>
                    </div>
                    <div className="wizardCompactGrid">
                    <label>
                      Kilometerstand
                      <span className="fieldAssist">
                        {lastReturnedKm
                          ? `Letzter rückgemeldeter KM-Stand: ${lastReturnedKm} km`
                          : "Noch kein rückgemeldeter KM-Stand vorhanden"}
                      </span>
                      <input
                      type="number"
                      value={wizard.kmStart}
                      onChange={(event) =>
                        setWizard((prev) => ({ ...prev, kmStart: event.target.value }))
                      }
                    />
                    {kmLowerThanLast && (
                      <span className="fieldWarning">
                        Neuer KM-Stand ist niedriger als der zuletzt rückgemeldete Wert. Bitte prüfen, du kannst trotzdem fortfahren.
                      </span>
                    )}
                  </label>

                  <label>
                    Tankstand
                    <select
                      value={wizard.fuelStart}
                      onChange={(event) =>
                        setWizard((prev) => ({ ...prev, fuelStart: event.target.value }))
                      }
                    >
                      {FUEL_OPTIONS.map((fuel) => (
                        <option key={fuel} value={fuel}>
                          {fuel}
                        </option>
                        ))}
                        </select>
                      </label>
                    </div>

                    <div className="wizardSectionSubblock">
                      <h4>Foto von Kilometerstand und Tankstand</h4>
                      <p className="mutedText">
                        Mindestens ein Foto ist verpflichtend. Ein zweites Foto kann optional ergänzt werden.
                      </p>
                      <div className="wizardCompactGrid">
                        <CameraCaptureButton
                          label="Tacho / Tank Foto 1"
                          value={wizard.dashboardPhotoPrimary}
                          sequenceHint="Pflichtfoto aufnehmen"
                          onOpenCamera={() =>
                            openPhotoCaptureSequence({
                              title: "Tacho und Tankstand",
                              items: [
                                {
                                  field: "dashboardPhotoPrimary",
                                  label: "Tacho / Tank Foto 1",
                                  value: wizard.dashboardPhotoPrimary,
                                },
                              ],
                              currentField: "dashboardPhotoPrimary",
                              onSave: (item, dataUrl) =>
                                setWizard((prev) => ({
                                  ...prev,
                                  [item.field]: dataUrl,
                                })),
                            })
                          }
                          onChange={(event) =>
                            handleWizardPhotoUpload(
                              "dashboardPhotoPrimary",
                              event.target.files
                            )
                          }
                        />
                        <CameraCaptureButton
                          label="Tacho / Tank Foto 2 optional"
                          value={wizard.dashboardPhotoSecondary}
                          sequenceHint="Optionale Einzelaufnahme"
                          onOpenCamera={() =>
                            openPhotoCaptureSequence({
                              title: "Tacho und Tankstand",
                              items: [
                                {
                                  field: "dashboardPhotoSecondary",
                                  label: "Tacho / Tank Foto 2",
                                  value: wizard.dashboardPhotoSecondary,
                                },
                              ],
                              currentField: "dashboardPhotoSecondary",
                              onSave: (item, dataUrl) =>
                                setWizard((prev) => ({
                                  ...prev,
                                  [item.field]: dataUrl,
                                })),
                            })
                          }
                          onChange={(event) =>
                            handleWizardPhotoUpload(
                              "dashboardPhotoSecondary",
                              event.target.files
                            )
                          }
                        />
                      </div>
                    </div>
  
                    <div className="wizardSignature">
                      <h4>Unterschrift Kunde</h4>
                    <SignaturePad
                      onSave={(signature) =>
                        setWizard((prev) => ({ ...prev, signature }))
                      }
                    />
                    {wizard.signature && (
                      <img src={wizard.signature} alt="Unterschrift" className="signatureImage" />
                    )}
                  </div>

                  <div className="loanerCompletionCheck">
                    <div className="cardHeader">
                      <div>
                        <h4>Abschlusscheck</h4>
                        <p className="mutedText">
                          Erst wenn alle Punkte grün sind, ist die Übergabe belastbar dokumentiert.
                        </p>
                      </div>
                    </div>
                    <div className="completionCheckGrid">
                      {loanerCompletionChecks.map((check) => (
                        <div key={check.label} className={`completionCheckItem ${check.ok ? "ok" : "warn"}`}>
                          <span>{check.ok ? "OK" : "Offen"}</span>
                          <strong>{check.label}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {wizard.submissionError && <div className="authError">{wizard.submissionError}</div>}
          {wizard.submissionSuccess && (
            <div className="customerSuccess">{wizard.submissionSuccess}</div>
          )}
          {canRestoreWizardDraft(wizard) && !wizard.submissionSuccess && (
            <div className="draftSaveHint">
              <span>
                {wizard.cardId
                  ? "Leihwagen-Entwurf wird zentral gesichert und kann auf einem anderen Gerät fortgesetzt werden."
                  : "Leihwagen-Entwurf wird zunächst lokal gesichert. Für den Gerätewechsel zentral speichern."}
              </span>
              <button
                type="button"
                className="btn primary"
                onClick={saveLoanerWizardForDeviceHandover}
                disabled={wizard.isSubmitting}
              >
                {wizard.isSubmitting
                  ? "Speichert zentral ..."
                  : "Am Tablet fortsetzen"}
              </button>
              {!wizard.cardId && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    localStorage.removeItem(WIZARD_DRAFT_STORAGE_KEY);
                    setWizard(getDefaultWizardState());
                    setWizardStep(0);
                  }}
                >
                  Entwurf löschen
                </button>
              )}
            </div>
          )}

          <div className="wizardBottomBar">
            <div className="wizardBottomMeta">
              <strong>
                Schritt {wizardStep + 1} von {WIZARD_STEPS.length}
              </strong>
              <span>{currentStep.label}</span>
            </div>
            <div className="wizardBottomActions">
              <button
                type="button"
                className="btn"
                onClick={() => setWizardStep((prev) => Math.max(0, prev - 1))}
                disabled={wizardStep === 0 || wizard.isSubmitting}
              >
                Zurück
              </button>
              {wizardStep < WIZARD_STEPS.length - 1 ? (
                <button
                  type="button"
                  className="btn primary"
                  onClick={() => setWizardStep((prev) => Math.min(WIZARD_STEPS.length - 1, prev + 1))}
                  disabled={!canGoNext || wizard.isSubmitting}
                >
                  Weiter
                </button>
              ) : (
                <button
                  type="button"
                  className="btn primary"
                  onClick={finalizeLoanerBooking}
                  disabled={wizard.isSubmitting}
                >
                  {wizard.isSubmitting ? "Speichere ..." : "Dokumentation abschließen"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAvailabilityInspector = () => {
    if (!availabilityInspector.open) return null;

    const selectedLoaner = loaners.find((loaner) => loaner.id === availabilityInspector.loanerId);
    const permanentlyCheckedOut = selectedLoaner ? isPermanentlyCheckedOutLoaner(selectedLoaner) : false;
    const scheduledBookings = bookings
      .filter(
        (booking) =>
          booking.loanerId === availabilityInspector.loanerId && isActiveBooking(booking)
      )
      .sort((left, right) => getBookingStartDate(left).localeCompare(getBookingStartDate(right)));
    const vehicleHistoryBookings = bookings
      .filter((booking) => booking.loanerId === availabilityInspector.loanerId)
      .sort((left, right) =>
        String(right.createdAt || getBookingStartDate(right)).localeCompare(
          String(left.createdAt || getBookingStartDate(left))
        )
      );
    const rangeConflicts =
      availabilityInspector.startDate && availabilityInspector.endDate
        ? getLoanerConflictsForRange(
            availabilityInspector.loanerId,
            availabilityInspector.startDate,
            availabilityInspector.endDate
          )
        : [];

    return (
      <div className="modalOverlay">
        <div className="modalCard availabilityInspectorCard">
          <div className="modalHeader">
            <div>
              <p className="eyebrow">Verfügbarkeit</p>
              <h3>
                {selectedLoaner
                  ? `${selectedLoaner.plate} - ${selectedLoaner.brand} ${selectedLoaner.model}`
                  : "Fahrzeugkalender"}
              </h3>
            </div>
            <button type="button" className="btn" onClick={closeAvailabilityInspector}>
              Schließen
            </button>
          </div>

          <div className="availabilityInspectorGrid">
            <label>
              Verleihbeginn
              <input
                type="date"
                value={availabilityInspector.startDate}
                onChange={(event) =>
                  setAvailabilityInspector((prev) => ({
                    ...prev,
                    startDate: event.target.value,
                    error: "",
                  }))
                }
              />
            </label>

            <label>
              Rückgabe geplant
              <input
                type="date"
                value={availabilityInspector.endDate}
                onChange={(event) =>
                  setAvailabilityInspector((prev) => ({
                    ...prev,
                    endDate: event.target.value,
                    error: "",
                  }))
                }
              />
            </label>
          </div>

          <div className="availabilityInspectorHint">
            <span>{permanentlyCheckedOut ? "Status" : "Verfügbar ab"}</span>
            <strong>
              {permanentlyCheckedOut
                ? "Dauerhaft verliehen"
                : fmtDate(getLoanerAvailabilityDate(availabilityInspector.loanerId))}
            </strong>
          </div>

          {permanentlyCheckedOut && (
            <div className="authError">
              Dieses Fahrzeug ist als dauerhaft verliehen markiert. Für neue Übergaben bitte zuerst
              die Checkbox in den Stammdaten entfernen.
            </div>
          )}

          {rangeConflicts.length > 0 && (
            <div className="authError">
              Das Fahrzeug ist im gewählten Zeitraum nicht verfügbar. Diese Reservierung wird
              nicht akzeptiert.
            </div>
          )}
          {availabilityInspector.error && (
            <div className="authError">{availabilityInspector.error}</div>
          )}

          <div className="availabilitySchedule">
            <div className="sectionHeadline">
              <span className="sectionTag">Belegte Zeiträume</span>
              <h4>Kalender für dieses Fahrzeug</h4>
            </div>
            {scheduledBookings.length > 0 ? (
              <div className="availabilityScheduleList">
                {scheduledBookings.map((booking) => (
                  <div
                    key={booking.id}
                    className={`availabilityScheduleItem ${
                      rangeConflicts.some((entry) => entry.id === booking.id) ? "conflict" : ""
                    }`}
                  >
                    <strong>
                      {fmtDate(getBookingStartDate(booking))} bis {fmtDate(getBookingEndDate(booking))}
                    </strong>
                    <span>
                      {booking.customerName || "-"} / {getBookingStatusLabel(booking.status)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mutedText">Für dieses Fahrzeug liegen noch keine Reservierungen vor.</div>
            )}
          </div>

          <div className="availabilitySchedule">
            <div className="sectionHeadline">
              <span className="sectionTag">Historie</span>
              <h4>Alle Vorgänge mit diesem Fahrzeug</h4>
            </div>
            {vehicleHistoryBookings.length > 0 ? (
              <div className="availabilityScheduleList">
                {vehicleHistoryBookings.map((booking) => (
                  <div key={`history_${booking.id}`} className="availabilityScheduleItem">
                    <strong>
                      {booking.customerName || "-"} / {getBookingStatusLabel(booking.status)}
                    </strong>
                    <span>
                      {fmtDate(getBookingStartDate(booking))} bis {fmtDate(getBookingEndDate(booking))}
                      {booking.orderNumber ? ` / Auftrag ${booking.orderNumber}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mutedText">Für dieses Fahrzeug gibt es noch keine Historie.</div>
            )}
          </div>

          <div className="wizardBottomBar availabilityBottomBar">
            <div className="wizardBottomMeta">
              <strong>
                {selectedLoaner ? selectedLoaner.plate : "Fahrzeug"} reservieren
              </strong>
              <span>
                Zeitraum {fmtDate(availabilityInspector.startDate)} bis{" "}
                {fmtDate(availabilityInspector.endDate)}
              </span>
            </div>
            <div className="wizardBottomActions">
              <button type="button" className="btn" onClick={closeAvailabilityInspector}>
                Abbrechen
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={applyAvailabilityInspector}
                disabled={permanentlyCheckedOut || rangeConflicts.length > 0}
              >
                Zeitraum übernehmen
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const openLoanerEditor = (loaner = null) => {
    setLoanerFormMessage("");
    setLoanerDraft(loaner ? { ...getDefaultLoanerDraft(), ...loaner } : getDefaultLoanerDraft());
    setLoanerEditorOpen(true);
  };

  const renderLoanerEditorModal = () => {
    if (!loanerEditorOpen) return null;

    return (
      <div className="modalOverlay">
        <div className="modalCard availabilityInspectorCard loanerEditorModal">
          <div className="modalHeader">
            <div>
              <p className="eyebrow">Leihwagen-Stammdaten</p>
              <h3>{loanerDraft.id ? `${loanerDraft.plate} bearbeiten` : "Fahrzeug anlegen"}</h3>
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setLoanerEditorOpen(false);
                setLoanerDraft(getDefaultLoanerDraft());
              }}
            >
              Schließen
            </button>
          </div>

          <div className="formGrid">
            <label>
              Kennzeichen
              <input
                value={loanerDraft.plate}
                onChange={(event) =>
                  setLoanerDraft((prev) => ({ ...prev, plate: event.target.value }))
                }
              />
            </label>
            <label>
              Marke
              <input
                value={loanerDraft.brand}
                onChange={(event) =>
                  setLoanerDraft((prev) => ({ ...prev, brand: event.target.value }))
                }
              />
            </label>
            <label>
              Modell
              <input
                value={loanerDraft.model}
                onChange={(event) =>
                  setLoanerDraft((prev) => ({ ...prev, model: event.target.value }))
                }
              />
            </label>
            <label>
              Fahrgestellnummer
              <input
                value={loanerDraft.vin}
                onChange={(event) =>
                  setLoanerDraft((prev) => ({ ...prev, vin: event.target.value }))
                }
              />
            </label>
            <label>
              Art
              <input
                value={loanerDraft.vehicleType}
                onChange={(event) =>
                  setLoanerDraft((prev) => ({ ...prev, vehicleType: event.target.value }))
                }
              />
            </label>
            <label>
              Nutzung
              <select
                value={loanerDraft.usageType}
                onChange={(event) =>
                  setLoanerDraft((prev) => ({
                    ...prev,
                    usageType: event.target.value,
                    permanentlyCheckedOut:
                      event.target.value === "customer_loaner" ? prev.permanentlyCheckedOut : true,
                  }))
                }
              >
                {LOANER_USAGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Stammfiliale
              <select
                value={loanerDraft.homeBranch}
                onChange={(event) =>
                  setLoanerDraft((prev) => ({ ...prev, homeBranch: event.target.value }))
                }
              >
                <option value="">Bitte wählen</option>
                {BRANCH_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Verfügbarkeit
              <select
                value={loanerDraft.status}
                onChange={(event) =>
                  setLoanerDraft((prev) => ({ ...prev, status: event.target.value }))
                }
              >
                <option value="available">Verfügbar</option>
                <option value="reserved">Reserviert</option>
                <option value="out">Unterwegs</option>
                <option value="maintenance">Werkstatt / gesperrt</option>
              </select>
            </label>
            <label>
              Motorart
              <input
                value={loanerDraft.engineType}
                onChange={(event) =>
                  setLoanerDraft((prev) => ({ ...prev, engineType: event.target.value }))
                }
              />
            </label>
            <label>
              Bereifung
              <select
                value={loanerDraft.tireType}
                onChange={(event) =>
                  setLoanerDraft((prev) => ({ ...prev, tireType: event.target.value }))
                }
              >
                <option value="">Bitte wählen</option>
                {TIRE_TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Depot
              <select
                value={loanerDraft.hasDepot ? "yes" : "no"}
                onChange={(event) =>
                  setLoanerDraft((prev) => ({ ...prev, hasDepot: event.target.value === "yes" }))
                }
              >
                <option value="yes">Ja</option>
                <option value="no">Nein</option>
              </select>
            </label>
            <label>
              Vignette
              <input
                type="date"
                value={loanerDraft.vignetteDate}
                onChange={(event) =>
                  setLoanerDraft((prev) => ({ ...prev, vignetteDate: event.target.value }))
                }
              />
            </label>
            <label>
              Parkpickerl
              <select
                value={loanerDraft.hasParkingPermit ? "yes" : "no"}
                onChange={(event) =>
                  setLoanerDraft((prev) => ({
                    ...prev,
                    hasParkingPermit: event.target.value === "yes",
                  }))
                }
              >
                <option value="yes">Ja</option>
                <option value="no">Nein</option>
              </select>
            </label>
          </div>

          <label>
            Notizen
            <textarea
              rows={3}
              value={loanerDraft.notes}
              onChange={(event) =>
                setLoanerDraft((prev) => ({ ...prev, notes: event.target.value }))
              }
            />
          </label>

          <label className="checkRow">
            <input
              type="checkbox"
              checked={loanerDraft.isActive}
              onChange={(event) =>
                setLoanerDraft((prev) => ({ ...prev, isActive: event.target.checked }))
              }
            />
            Fahrzeug ist aktiv für die Leihwagenflotte
          </label>

          <label className="checkRow">
            <input
              type="checkbox"
              checked={loanerDraft.permanentlyCheckedOut}
              onChange={(event) =>
                setLoanerDraft((prev) => ({
                  ...prev,
                  permanentlyCheckedOut: event.target.checked,
                }))
              }
            />
            Fahrzeug ist dauerhaft verliehen
          </label>

          {loanerFormMessage && <div className="pill">{loanerFormMessage}</div>}
          <div className="wizardBottomActions">
            <button type="button" className="btn primary" onClick={saveLoanerDraft}>
              {loanerDraft.id ? "Fahrzeug speichern" : "Fahrzeug anlegen"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderLoanerAdminPanel = () => {
    if (!isAdmin) {
      return (
        <main className="board">
          <div className="card">
            <h3>Leihwagen Admin</h3>
            <p>Nur Admins duerfen die Leihwagenflotte pflegen.</p>
          </div>
        </main>
      );
    }

    return (
      <main className="board">
        {renderLoanerEditorModal()}
        <div className="card fullSpan">
          <h3>Leihwagen Stammdaten</h3>
          <p className="mutedText">
            Fahrzeuge über das Popup pflegen. Beim Klick auf ein Fahrzeug öffnet sich der Kalender
            mit der vollständigen Historie dieses Fahrzeugs.
          </p>
          {loanerFormMessage && <div className="pill">{loanerFormMessage}</div>}
          <div className="actions">
            <button type="button" className="btn primary" onClick={() => openLoanerEditor()}>
              Neues Fahrzeug anlegen
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => setShowExtendedLoanerInventory((prev) => !prev)}
            >
              {showExtendedLoanerInventory
                ? "Nur Kundenersatzfahrzeuge zeigen"
                : "Dienstwagen / Langzeitvermietung einblenden"}
            </button>
          </div>
        </div>

        {loaners
          .filter(
            (loaner) =>
              showExtendedLoanerInventory || loaner.usageType === "customer_loaner"
          )
          .sort((a, b) => {
            const score = (loaner) =>
              loaner.usageType === "customer_loaner" && loaner.status === "available" ? 0 : 1;
            return score(a) - score(b) || a.plate.localeCompare(b.plate);
          })
          .map((loaner) => (
          <div key={loaner.id} className="card">
            <div className="cardHeader">
              <h3>{loaner.plate}</h3>
              <span className="pill">Verfügbarkeit: {loaner.status}</span>
            </div>
            <div className="meta">
              <span>{loaner.brand}</span>
              <span>{loaner.model}</span>
              <span>{loaner.isActive ? "aktiv" : "inaktiv"}</span>
              <span>{getLoanerUsageLabel(loaner.usageType)}</span>
              {loaner.permanentlyCheckedOut && <span>Dauerhaft verliehen</span>}
              {loaner.homeBranch && <span>{getBranchLabel(loaner.homeBranch)}</span>}
            </div>
            <div className="meta">
              {loaner.vin && <span>VIN: {loaner.vin}</span>}
              {loaner.vehicleType && <span>Art: {loaner.vehicleType}</span>}
              {loaner.engineType && <span>Motor: {loaner.engineType}</span>}
              {loaner.tireType && <span>Bereifung: {loaner.tireType}</span>}
              {loaner.vignetteDate && <span>Vignette: {fmtDate(loaner.vignetteDate)}</span>}
            </div>
            <div className="meta">
              <span>Depot: {loaner.hasDepot ? "Ja" : "Nein"}</span>
              <span>Parkpickerl: {loaner.hasParkingPermit ? "Ja" : "Nein"}</span>
            </div>
            {loaner.notes && <p>{loaner.notes}</p>}
            <div className="actions">
              <button
                type="button"
                className="btn"
                onClick={() => openAvailabilityInspector(loaner.id)}
              >
                Historie / Kalender
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => openLoanerEditor(loaner)}
              >
                Bearbeiten
              </button>
              <button
                type="button"
                className="btn"
                onClick={() =>
                  persistLoaner({
                    ...loaner,
                    isActive: !loaner.isActive,
                  })
                }
              >
                {loaner.isActive ? "Deaktivieren" : "Aktivieren"}
              </button>
            </div>
          </div>
        ))}

        <div className="card">
          <h3>Blaue Kennzeichen</h3>
          <div className="meta">
            {bluePlates.length === 0 ? (
              <span>Keine blauen Kennzeichen im System.</span>
            ) : (
              bluePlates.map((plate) => (
                <span key={plate.id} className="pill">
                  {plate.plate}
                </span>
              ))
            )}
          </div>
        </div>
      </main>
    );
  };

  const renderProcessAdminPanel = () => {
    if (!isAdmin) {
      return (
        <main className="board">
          <div className="card">
            <h3>Prozess Admin</h3>
            <p>Nur Admins dürfen Standard-To-dos und Abläufe pflegen.</p>
          </div>
        </main>
      );
    }
    const selectedRoute = CASE_TYPE_FLOW[processConfigDraft.caseType] || CASE_TYPE_FLOW.other;
    const currentText = processConfigDraft.text;

    return (
      <main className="board">
        <div className="card fullSpan processAdminHero">
          <div>
            <h3>Prozess Admin</h3>
            <p className="mutedText">
              Standard-To-dos je Falltyp und Abteilung pflegen. Neue Aufträge verwenden diese Liste automatisch.
            </p>
          </div>
          {processConfigDraft.message && <span className="pill">{processConfigDraft.message}</span>}
        </div>

        <div className="card fullSpan processAdminEditor">
          <div className="processAdminSelectors">
            <label>
              Falltyp
              <select
                value={processConfigDraft.caseType}
                onChange={(event) => {
                  const nextCaseType = event.target.value;
                  const nextDepartment = (CASE_TYPE_FLOW[nextCaseType] || CASE_TYPE_FLOW.other)[0] || DEFAULT_DEPARTMENT;
                  setProcessConfigDraft({
                    caseType: nextCaseType,
                    department: nextDepartment,
                    text: processConfig?.[nextCaseType]?.[nextDepartment] || "",
                    message: "",
                  });
                }}
              >
                {CASE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Abteilung
              <select
                value={processConfigDraft.department}
                onChange={(event) => {
                  const nextDepartment = event.target.value;
                  setProcessConfigDraft((prev) => ({
                    ...prev,
                    department: nextDepartment,
                    text: processConfig?.[prev.caseType]?.[nextDepartment] || "",
                    message: "",
                  }));
                }}
              >
                {selectedRoute.map((department) => (
                  <option key={department} value={department}>
                    {department}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label>
            Standard-To-dos
            <textarea
              rows={12}
              value={currentText}
              onChange={(event) =>
                setProcessConfigDraft((prev) => ({
                  ...prev,
                  text: event.target.value,
                  message: "",
                }))
              }
              placeholder={"Ein To-do pro Zeile\nBeispiel: Bremsen prüfen"}
            />
          </label>
          <div className="processPreview">
            <strong>Vorschau</strong>
            {parseSubtasks(currentText.split("\n").filter(Boolean)).map((todo) => (
              <span key={todo.id || todo.text}>{todo.text}</span>
            ))}
          </div>
          <div className="actions">
            <button type="button" className="btn primary" onClick={saveProcessConfigDraft}>
              Standard-To-dos speichern
            </button>
            <button type="button" className="btn" onClick={resetProcessConfigDraft}>
              Auf Standard zurücksetzen
            </button>
          </div>
        </div>
      </main>
    );
  };

  const renderUserAdminPanel = () => {
    if (!isAdmin) {
      return (
        <main className="board">
          <div className="card">
            <h3>Benutzer Admin</h3>
            <p>Nur Admins duerfen Benutzer verwalten.</p>
          </div>
        </main>
      );
    }

    return (
      <main className="board">
        <div className="card">
          <h3>Benutzer vorbereiten</h3>
          <p>
            Legt fehlende Benutzer für Annahme 21, Annahme 22 und Techniker an.
            Bereits vorhandene Benutzer bleiben unverändert.
          </p>
          <div className="actions">
            <button type="button" className="btn primary" onClick={provisionPresetUsers}>
              Team Fraenkel Nutzer anlegen
            </button>
            <button type="button" className="btn" onClick={refreshAdminUsers}>
              Liste aktualisieren
            </button>
            <button
              type="button"
              className="btn dangerSubtle"
              onClick={deleteDisabledUsers}
            >
              Deaktivierte Konten löschen
            </button>
          </div>
          {adminUsersMessage && <div className="pill">{adminUsersMessage}</div>}
        </div>

        <div className="card">
          <h3>Einzelnen Nutzer anlegen</h3>
          <div className="formGrid">
            <label>
              E-Mail
              <input
                type="email"
                placeholder="vorname.nachname@teamfraenkel.at"
                value={userDraft.email}
                onChange={(event) =>
                  setUserDraft((prev) => ({ ...prev, email: event.target.value }))
                }
              />
              <span className="fieldAssist">
                Neuanlagen sind ausschließlich für @teamfraenkel.at zulässig.
              </span>
              {userDraft.email &&
                !isAllowedNewUserEmail(userDraft.email) && (
                  <span className="fieldWarning">
                    {NEW_USER_EMAIL_DOMAIN_ERROR}
                  </span>
                )}
            </label>
            <label>
              Name
              <input
                value={userDraft.fullName}
                onChange={(event) =>
                  setUserDraft((prev) => ({ ...prev, fullName: event.target.value }))
                }
              />
            </label>
            <label>
              Rolle
              <select
                value={userDraft.role}
                onChange={(event) =>
                  setUserDraft((prev) => ({ ...prev, role: event.target.value }))
                }
              >
                {USER_ROLE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Filiale
              <select
                value={userDraft.branch}
                onChange={(event) =>
                  setUserDraft((prev) => ({ ...prev, branch: event.target.value }))
                }
              >
                <option value="">Bitte wählen</option>
                {BRANCH_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Abteilung
              <select
                value={userDraft.department}
                onChange={(event) =>
                  setUserDraft((prev) => ({ ...prev, department: event.target.value }))
                }
              >
                {ROLES.map((department) => (
                  <option key={department} value={department}>
                    {department}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="actions">
            <button
              type="button"
              className="btn primary"
              onClick={createSingleUser}
              disabled={
                Boolean(userDraft.email) &&
                !isAllowedNewUserEmail(userDraft.email)
              }
            >
              Nutzer anlegen und einladen
            </button>
          </div>
        </div>

        <div className="card fullSpan">
          <div className="cardHeader">
            <h3>Benutzerliste</h3>
            <span
              className={`pill adminUsersLoadingStatus ${adminUsersLoading ? "visible" : ""}`}
              role="status"
              aria-live="polite"
              aria-hidden={!adminUsersLoading}
            >
              lädt ...
            </span>
          </div>

          <div className="userTable">
            <div className="userTableHead">
              <span>E-Mail</span>
              <span>Name</span>
              <span>Rolle</span>
              <span>Filiale</span>
              <span>Abteilung</span>
              <span>Techniker</span>
              <span>Status</span>
              <span>Letzter Login</span>
              <span>Aktionen</span>
            </div>
            {adminUsers.map((user) => {
              const mappedTechnicianId = technicianSettings.userMap?.[user.id] || getProfileTechnicianId(user);
              const inviteCooldownKey = `user-invite:${user.id}`;
              const inviteCooldownUntil =
                resendCooldowns[inviteCooldownKey] || 0;
              const inviteCooldownActive =
                inviteCooldownUntil > resendClock;
              const inviteSending = Boolean(
                resendSending[inviteCooldownKey]
              );
              return (
                <div key={user.id} className="userTableRow">
                  <span>{user.email}</span>
                  <span>{user.full_name || "-"}</span>
                  <label className="inlineField">
                    <select
                      value={user.role}
                      onChange={(event) =>
                        updateUserProfile(user, { role: event.target.value })
                      }
                    >
                      {USER_ROLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="inlineField">
                    <select
                      value={user.branch || ""}
                      onChange={(event) =>
                        updateUserProfile(user, { branch: event.target.value })
                      }
                    >
                      <option value="">Bitte wählen</option>
                      {BRANCH_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="inlineField">
                    <select
                      value={user.department || DEFAULT_DEPARTMENT}
                      onChange={(event) =>
                        updateUserProfile(user, { department: event.target.value })
                      }
                    >
                      {ROLES.map((department) => (
                        <option key={department} value={department}>
                          {department}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="inlineField">
                    <select
                      value={mappedTechnicianId}
                      onChange={(event) => updateUserTechnicianMapping(user.id, event.target.value)}
                    >
                      <option value="">Keine Zuordnung</option>
                      {configuredTechnicians
                        .filter((technician) => technician.type === "Techniker")
                        .map((technician) => (
                          <option key={technician.id} value={technician.id}>
                            {technician.name}
                          </option>
                        ))}
                    </select>
                  </label>
                  <span
                    title={
                      user.invite_sent_at
                        ? `Einladung versendet: ${fmtDateTime(user.invite_sent_at)}`
                        : ""
                    }
                  >
                    {user.is_active ? "aktiv" : "deaktiviert"} /{" "}
                    {getUserInviteStatusLabel(user.invite_status)}
                  </span>
                  <span>{fmtDateTime(user.last_login_at)}</span>
                  <div className="actions">
                    <button
                      type="button"
                      className="btn"
                      onClick={() => sendUserInvite(user)}
                      disabled={inviteSending || inviteCooldownActive}
                    >
                      {inviteSending
                        ? "E-Mail wird versendet ..."
                        : inviteCooldownActive
                          ? `Erneut in ${formatResendWait(
                              inviteCooldownUntil,
                              resendClock
                            )}`
                          : user.invite_status === "created"
                            ? "Einladungs-E-Mail aussenden"
                            : "Einladung erneut senden"}
                    </button>
                    {inviteCooldownActive && (
                      <span className="resendCooldownHint">
                        Erneuter Versand in{" "}
                        {formatResendWait(
                          inviteCooldownUntil,
                          resendClock
                        )} möglich.
                      </span>
                    )}
                    <button
                      type="button"
                      className="btn"
                      onClick={() => toggleUserActive(user)}
                    >
                      {user.is_active ? "Nutzer deaktivieren" : "Nutzer aktivieren"}
                    </button>
                  </div>
                </div>
              );
            })}
            {adminUsers.length === 0 && !adminUsersLoading && (
              <div className="empty">Noch keine Benutzer vorbereitet.</div>
            )}
          </div>
        </div>
      </main>
    );
  };

  const renderSystemStatusPanel = () => {
    if (!isAdmin) {
      return (
        <main className="board">
          <div className="card">
            <h3>Systemstatus</h3>
            <p>Nur Admins dürfen den Systemstatus prüfen.</p>
          </div>
        </main>
      );
    }

    const schemaEntries = Object.entries(systemStatus?.schema || {});
    const countEntries = Object.entries(systemStatus?.counts || {});
    const allSchemaOk =
      schemaEntries.length > 0 && schemaEntries.every(([, status]) => status.ok);
    const confirmationHealth = getConfirmationHealth(bookings, activeCards);
    const mailDiagnostics = buildMailOperationalDiagnostics(bookings, activeCards);
    const localQueueTotal = offlineQueueCount + offlineReturnQueueCount;
    const systemTrafficLights = [
      {
        label: "Datenbank",
        state: allSchemaOk ? "grün" : "prüfen",
        tone: allSchemaOk ? "ok" : "warn",
        detail: allSchemaOk ? "Schema vollständig" : "Systemprüfung starten oder Migration prüfen",
      },
      {
        label: "E-Mail",
        state: systemStatus?.smtp?.verified ? "grün" : systemStatus?.smtp?.configured ? "gelb" : "rot",
        tone: systemStatus?.smtp?.verified ? "ok" : "warn",
        detail: systemStatus?.smtp?.verified
          ? "SMTP-Verbindung bestätigt"
          : systemStatus?.smtp?.configured
            ? "Konfiguriert, Verifikation prüfen"
            : "SMTP-Variablen fehlen",
      },
      {
        label: "Fotos",
        state: systemStatus?.storage?.configured ? "grün" : "gelb",
        tone: systemStatus?.storage?.configured ? "ok" : "warn",
        detail: systemStatus?.storage?.configured ? "Storage bereit" : "Fallback aktiv, Storage prüfen",
      },
      {
        label: "Live-Sync",
        state: isOnline ? "aktiv" : "offline",
        tone: isOnline ? "ok" : "warn",
        detail: lastLiveSyncAt ? `Letzter Sync ${fmtDateTime(lastLiveSyncAt)}` : "Realtime plus Polling-Fallback",
      },
      {
        label: "Offline-Queue",
        state: localQueueTotal > 0 ? `${localQueueTotal} offen` : "leer",
        tone: localQueueTotal > 0 ? "warn" : "ok",
        detail:
          localQueueTotal > 0
            ? `${offlineQueueSizeKb} KB lokal gespeichert. Sync startet automatisch oder manuell.`
            : lastOfflineSyncAt
              ? `Zuletzt synchronisiert ${fmtDateTime(lastOfflineSyncAt)}`
              : "Keine lokalen Vorgänge offen",
      },
      {
        label: "Sicherheit",
        state: "aktiv",
        tone: "ok",
        detail: "Session, Rollenprüfung und Rate Limits aktiv",
      },
    ];

    return (
      <main className="board">
        <div className="card fullSpan systemHeroCard">
          <div className="cardHeader">
            <div>
              <h3>Systemstatus</h3>
              <p className="mutedText">
                Prüft Datenbank, E-Mail-Versand, Sicherheitsbremsen und die wichtigsten
                Betriebszahlen.
              </p>
            </div>
            <span className={`pill ${allSchemaOk ? "ok" : ""}`}>
              {systemStatusLoading ? "prüft ..." : allSchemaOk ? "Schema ok" : "Prüfung offen"}
            </span>
          </div>
          <div className="actions">
            <button
              type="button"
              className="btn primary"
              onClick={refreshSystemStatus}
              disabled={systemStatusLoading}
            >
              System prüfen
            </button>
            <button type="button" className="btn" onClick={() => exportBoardCsv("all")}>
              Gesamtexport CSV
            </button>
            <button type="button" className="btn" onClick={() => exportBoardCsv("cards")}>
              Aufträge CSV
            </button>
            <button type="button" className="btn" onClick={() => exportBoardCsv("bookings")}>
              Leihwagenhistorie CSV
            </button>
            <button type="button" className="btn" onClick={() => exportBoardCsv("loaners")}>
              Fahrzeugbestand CSV
            </button>
            <button type="button" className="btn" onClick={runLocalSmokeTest}>
              Testlauf starten
            </button>
            <button type="button" className="btn" onClick={exportE2ETestPackage}>
              E2E-Testpaket
            </button>
          </div>
          {systemStatusMessage && <div className="pill">{systemStatusMessage}</div>}
        </div>

        {systemSmokeTest.length > 0 && (
          <div className="card fullSpan">
            <h3>Admin-Testlauf</h3>
            <div className="systemStatusGrid">
              {systemSmokeTest.map((check) => (
                <div key={check.label} className={`systemStatusTile ${check.ok ? "ok" : "warn"}`}>
                  <strong>{check.label}</strong>
                  <span>{check.ok ? "ok" : "prüfen"}</span>
                  <p>{check.detail}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="card fullSpan">
          <div className="cardHeader">
            <div>
              <h3>Betriebsampel</h3>
              <p className="mutedText">
                Schneller Blick auf die Punkte, die im Alltag wirklich zählen.
              </p>
            </div>
            <span className="summaryBadge">
              <span>Version</span>
              <strong>{APP_BUILD_LABEL}</strong>
            </span>
          </div>
          <div className="opsHealthGrid">
            {systemTrafficLights.map((entry) => (
              <div key={entry.label} className={`opsHealthTile ${entry.tone}`}>
                <span>{entry.label}</span>
                <strong>{entry.state}</strong>
                <p>{entry.detail}</p>
              </div>
            ))}
            {confirmationHealth.map((entry) => (
              <div key={entry.label} className={`opsHealthTile ${entry.tone}`}>
                <span>{entry.label}</span>
                <strong>{entry.value}</strong>
                <p>
                  {entry.label === "Versand prüfen"
                    ? "Offene Bestätigungen ohne gespeicherten Versandzeitpunkt"
                    : "Aktueller Bestätigungsprozess"}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="card fullSpan">
          <div className="cardHeader">
            <div>
              <h3>E-Mail-Zustellung & Bestätigung</h3>
              <p className="mutedText">
                Erkennt typische Spam- und Prozessprobleme: versendet, geöffnet, bestätigt oder Link fehlt.
              </p>
            </div>
            <button type="button" className="btn" onClick={sendSystemTestEmail} disabled={systemStatusLoading}>
              Testmail senden
            </button>
          </div>
          <div className="opsHealthGrid">
            {mailDiagnostics.map((entry) => (
              <div key={entry.label} className={`opsHealthTile ${entry.tone}`}>
                <span>{entry.label}</span>
                <strong>{entry.value}</strong>
                <p>{entry.detail}</p>
              </div>
            ))}
          </div>
          <div className="mailAdviceBox">
            <strong>Spam-Verdacht schnell prüfen</strong>
            <span>Wenn viele Mails gesendet, aber nicht geöffnet werden: Hostinger DNS auf SPF, DKIM und DMARC prüfen, Testmail an Gmail/Outlook senden und im Kundenfall zusätzlich QR-Code vor Ort nutzen.</span>
          </div>
        </div>

        <div className="card fullSpan liveAcceptanceCard">
          <div className="cardHeader">
            <div>
              <h3>Live-Abnahme Tablet / Smartphone</h3>
              <p className="mutedText">
                Geführter Praxischeck für echte Geräte. Jeder Schritt wird lokal auf diesem Gerät gespeichert
                und kann als Abnahmeprotokoll exportiert werden.
              </p>
            </div>
            <span className="summaryBadge">
              <span>Erledigt</span>
              <strong>
                {LIVE_ACCEPTANCE_STEPS.filter((step) => liveAcceptance[step.id]?.checked).length}/
                {LIVE_ACCEPTANCE_STEPS.length}
              </strong>
            </span>
          </div>
          <div className="liveAcceptanceList">
            {LIVE_ACCEPTANCE_STEPS.map((step, index) => {
              const checked = Boolean(liveAcceptance[step.id]?.checked);
              return (
                <label key={step.id} className={`liveAcceptanceStep ${checked ? "done" : ""}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => updateLiveAcceptance(step.id, event.target.checked)}
                  />
                  <span>{index + 1}</span>
                  <div>
                    <strong>{step.title}</strong>
                    <p>{step.detail}</p>
                    {checked && liveAcceptance[step.id]?.checkedAt && (
                      <small>
                        Erledigt am {fmtDateTime(liveAcceptance[step.id].checkedAt)} von{" "}
                        {liveAcceptance[step.id].by || "Benutzer"}
                      </small>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
          <div className="actions">
            <button type="button" className="btn primary" onClick={exportLiveAcceptance}>
              Abnahmeprotokoll exportieren
            </button>
            <button type="button" className="btn" onClick={resetLiveAcceptance}>
              Abnahme zurücksetzen
            </button>
          </div>
        </div>

        <div className="card fullSpan">
          <div className="cardHeader">
            <div>
              <h3>Pilot-Testpaket</h3>
              <p className="mutedText">
                Diese fünf echten Durchläufe sollten vor dem nächsten Produktivtest einmal sauber gespielt werden.
              </p>
            </div>
          </div>
          <div className="pilotTestGrid">
            {PILOT_TEST_CASES.map((testCase, index) => (
              <div key={testCase} className="pilotTestTile">
                <span>{index + 1}</span>
                <strong>{testCase}</strong>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <h3>E-Mail / SMTP</h3>
          <div className="contextSummaryGrid">
            <div className="summaryTile">
              <span>Konfiguration</span>
              <strong>{systemStatus?.smtp?.configured ? "vollständig" : "unvollständig"}</strong>
            </div>
            <div className="summaryTile">
              <span>Verifikation</span>
              <strong>{systemStatus?.smtp?.verified ? "erfolgreich" : "nicht bestätigt"}</strong>
            </div>
            <div className="summaryTile">
              <span>Host</span>
              <strong>{systemStatus?.smtp?.host || "-"}</strong>
            </div>
            <div className="summaryTile">
              <span>Absender</span>
              <strong>{systemStatus?.smtp?.from || "-"}</strong>
            </div>
          </div>
          {systemStatus?.smtp?.error && <div className="authError">{systemStatus.smtp.error}</div>}
          <div className="addRow">
            <label>
              Test-E-Mail an
              <input
                type="email"
                placeholder={session?.user?.email || "empfänger@example.com"}
                value={systemTestEmail}
                onChange={(event) => setSystemTestEmail(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn primary"
              onClick={sendSystemTestEmail}
              disabled={systemStatusLoading}
            >
              Testmail senden
            </button>
          </div>
        </div>

        <div className="card">
          <h3>Betriebszahlen</h3>
          <div className="contextSummaryGrid">
            {countEntries.length > 0 ? (
              countEntries.map(([key, value]) => (
                <div key={key} className="summaryTile">
                  <span>{key}</span>
                  <strong>{value}</strong>
                </div>
              ))
            ) : (
              <div className="mutedText">Noch keine Prüfung geladen.</div>
            )}
          </div>
        </div>

        <div className="card">
          <h3>Storage / Fotos</h3>
          <div className="contextSummaryGrid">
            <div className="summaryTile">
              <span>Bucket</span>
              <strong>{systemStatus?.storage?.bucket || "loaner-media"}</strong>
            </div>
            <div className="summaryTile">
              <span>Status</span>
              <strong>{systemStatus?.storage?.configured ? "bereit" : "Fallback aktiv"}</strong>
            </div>
            <div className="summaryTile">
              <span>Öffentlich</span>
              <strong>{systemStatus?.storage?.public ? "ja" : "nein"}</strong>
            </div>
          </div>
          {systemStatus?.storage?.error && <div className="authError">{systemStatus.storage.error}</div>}
        </div>

        <div className="card fullSpan">
          <h3>Datenbankschema</h3>
          <div className="systemStatusGrid">
            {schemaEntries.length > 0 ? (
              schemaEntries.map(([table, status]) => (
                <div key={table} className={`systemStatusTile ${status.ok ? "ok" : "warn"}`}>
                  <div className="cardHeader">
                    <strong>{table}</strong>
                    <span className="pill">{status.ok ? "ok" : "prüfen"}</span>
                  </div>
                  <p>
                    {status.ok
                      ? `${status.checkedColumns} wichtige Spalten vorhanden.`
                      : `Fehlt / Problem: ${status.missing.join(", ")}`}
                  </p>
                  {status.migrationSql?.length > 0 && (
                    <>
                      <pre className="migrationSnippet">{status.migrationSql.join("\n")}</pre>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => {
                          navigator.clipboard?.writeText(status.migrationSql.join("\n"));
                          setSystemStatusMessage(`SQL-Fix für ${table} wurde in die Zwischenablage kopiert.`);
                        }}
                      >
                        SQL-Fix kopieren
                      </button>
                    </>
                  )}
                </div>
              ))
            ) : (
              <div className="mutedText">Bitte Systemprüfung starten.</div>
            )}
          </div>
        </div>

        <div className="card">
          <h3>Realtime</h3>
          <p className="mutedText">
            {systemStatus?.realtime?.note ||
              "Die App nutzt Supabase Realtime plus Polling-Fallback. Nach der Prüfung erscheinen hier die Tabellen."}
          </p>
          <div className="meta">
            {(systemStatus?.realtime?.expectedTables || []).map((table) => (
              <span key={table} className="pill">
                {table}
              </span>
            ))}
          </div>
        </div>

        <div className="card">
          <h3>Security</h3>
          <div className="contextSummaryGrid">
            {Object.entries(systemStatus?.security || {}).map(([key, value]) => (
              <div key={key} className="summaryTile">
                <span>{key}</span>
                <strong>{value ? "aktiv" : "offen"}</strong>
              </div>
            ))}
          </div>
        </div>

        <div className="card fullSpan">
          <h3>Letzte Systemereignisse</h3>
          <div className="auditList">
            {(systemStatus?.recentAudit || []).map((entry, index) => (
              <div key={`${entry.created_at}_${index}`} className="auditRow">
                <span>{fmtDateTime(entry.created_at)}</span>
                <strong>{entry.action}</strong>
                <span>{entry.actor_email || "System"}</span>
                <span>{entry.entity_type} {entry.entity_id || ""}</span>
              </div>
            ))}
            {(!systemStatus?.recentAudit || systemStatus.recentAudit.length === 0) && (
              <div className="mutedText">Noch keine Audit-Ereignisse geladen.</div>
            )}
          </div>
        </div>
      </main>
    );
  };

  const renderAuditPanel = () => {
    const auditEntries = systemStatus?.recentAudit || [];
    const actionOptions = Array.from(new Set(auditEntries.map((entry) => entry.action).filter(Boolean))).sort();
    const entityOptions = Array.from(new Set(auditEntries.map((entry) => entry.entity_type).filter(Boolean))).sort();
    const query = auditFilters.query.trim().toLowerCase();
    const filteredAudit = auditEntries.filter((entry) => {
      const createdDate = String(entry.created_at || "").slice(0, 10);
      const combined = [
        entry.action,
        entry.actor_email,
        entry.entity_type,
        entry.entity_id,
        JSON.stringify(entry.details || {}),
      ]
        .join(" ")
        .toLowerCase();
      if (query && !combined.includes(query)) return false;
      if (auditFilters.action && entry.action !== auditFilters.action) return false;
      if (auditFilters.entityType && entry.entity_type !== auditFilters.entityType) return false;
      if (auditFilters.dateFrom && createdDate < auditFilters.dateFrom) return false;
      if (auditFilters.dateTo && createdDate > auditFilters.dateTo) return false;
      return true;
    });
    const formatAuditField = (field) =>
      ({
        assignedTech: "Techniker",
        assigned_tech: "Techniker",
        workStatus: "Status",
        work_status: "Status",
        customerName: "Kunde",
        customer_name: "Kunde",
        licensePlate: "Kennzeichen",
        license_plate: "Kennzeichen",
        branch: "Filiale",
        role: "Abteilung",
        due: "Fällig",
        nextActionDue: "Nächste Erinnerung",
        next_action_due: "Nächste Erinnerung",
        estimatedHours: "Dauer",
        estimated_hours: "Dauer",
      }[field] || field);
    const formatAuditValue = (value, field = "") => {
      if (value === null || value === undefined || value === "") return "-";
      if (["assignedTech", "assigned_tech"].includes(field)) {
        return getEmployeeNameById(configuredTechnicians, value) || String(value);
      }
      if (["workStatus", "work_status"].includes(field)) {
        return WORK_STATUS_LABELS[value] || String(value);
      }
      if (field === "branch") return getBranchLabel(value);
      if (["due", "nextActionDue", "next_action_due"].includes(field)) return fmtDate(value);
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
    };
    const renderAuditDiff = (entry) => {
      const diff = Array.isArray(entry.details?.diff) ? entry.details.diff : [];
      if (diff.length === 0) return null;
      return (
        <div className="auditDiffList">
          {diff.map((item) => (
            <div key={`${entry.id || entry.created_at}_${item.field}`} className="auditDiffRow">
              <strong>{formatAuditField(item.field)}</strong>
              <span>{formatAuditValue(item.before, item.field)}</span>
              <b>→</b>
              <span>{formatAuditValue(item.after, item.field)}</span>
            </div>
          ))}
        </div>
      );
    };

    return (
    <main className="board">
      <div className="card fullSpan">
        <div className="cardHeader">
          <div>
            <h3>Protokoll</h3>
            <p className="mutedText">
              Kritische Systemereignisse: E-Mails, Bestätigungen, Benutzeraktionen,
              Abbrüche und Sicherheitsereignisse.
            </p>
          </div>
          <button type="button" className="btn primary" onClick={refreshSystemStatus}>
            Protokoll aktualisieren
          </button>
        </div>
        <div className="auditFilterBar">
          <input
            type="search"
            placeholder="Suche nach E-Mail, Auftrag, Aktion ..."
            value={auditFilters.query}
            onChange={(event) => setAuditFilters((prev) => ({ ...prev, query: event.target.value }))}
          />
          <select
            value={auditFilters.action}
            onChange={(event) => setAuditFilters((prev) => ({ ...prev, action: event.target.value }))}
          >
            <option value="">Aktion: alle</option>
            {actionOptions.map((action) => (
              <option key={action} value={action}>{action}</option>
            ))}
          </select>
          <select
            value={auditFilters.entityType}
            onChange={(event) => setAuditFilters((prev) => ({ ...prev, entityType: event.target.value }))}
          >
            <option value="">Typ: alle</option>
            {entityOptions.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <input
            type="date"
            value={auditFilters.dateFrom}
            onChange={(event) => setAuditFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
          />
          <input
            type="date"
            value={auditFilters.dateTo}
            onChange={(event) => setAuditFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
          />
          <button
            type="button"
            className="btn"
            onClick={() => setAuditFilters({ query: "", action: "", entityType: "", dateFrom: "", dateTo: "" })}
          >
            Filter zurücksetzen
          </button>
        </div>
        <div className="auditList">
          {filteredAudit.map((entry, index) => (
            <div key={`${entry.created_at}_${index}`} className="auditRow auditRowDetailed">
              <span>{fmtDateTime(entry.created_at)}</span>
              <strong>{entry.action}</strong>
              <span>{entry.actor_email || "System/Kunde"}</span>
              <span>{entry.entity_type} {entry.entity_id || ""}</span>
              {renderAuditDiff(entry)}
              {entry.details && Object.keys(entry.details || {}).length > 0 && (
                <details>
                  <summary>Details</summary>
                  <pre>{JSON.stringify(entry.details, null, 2)}</pre>
                </details>
              )}
            </div>
          ))}
          {auditEntries.length === 0 && (
            <div className="mutedText">
              Noch keine Protokolldaten geladen. Bitte oben aktualisieren.
            </div>
          )}
          {auditEntries.length > 0 && filteredAudit.length === 0 && (
            <div className="mutedText">Keine Protokolleinträge für diese Filter.</div>
          )}
        </div>
      </div>
    </main>
    );
  };

  const renderProductionReadinessPanel = () => {
    if (!isAdmin) {
      return (
        <main className="board">
          <div className="card">
            <h3>Produktivtest</h3>
            <p>Nur Admins dürfen den Produktivtest-Assistenten öffnen.</p>
          </div>
        </main>
      );
    }

    const readinessChecks = buildProductionReadinessChecks();
    const readyCount = readinessChecks.filter((check) => check.ok).length;
    const readyForPilot = readyCount === readinessChecks.length;
    const blockingChecks = readinessChecks.filter((check) => !check.ok);

    return (
      <main className="board">
        <div className="card fullSpan productionReadinessHero">
          <div className="cardHeader">
            <div>
              <h3>Produktivtest-Assistent</h3>
              <p className="mutedText">
                Ein Kontrollpunkt vor echten Kundendaten: Schema, Mail, Storage, Testdaten und Rollen sichtbar prüfen.
              </p>
            </div>
            <span className={`summaryBadge ${readyForPilot ? "ok" : "warn"}`}>
              <span>Bereit</span>
              <strong>{readyCount}/{readinessChecks.length}</strong>
            </span>
          </div>
          <div className="actions">
            <button type="button" className="btn primary" onClick={refreshSystemStatus} disabled={systemStatusLoading}>
              Prüfung aktualisieren
            </button>
            <button type="button" className="btn" onClick={() => exportProductionReadiness(readinessChecks)}>
              Protokoll exportieren
            </button>
            <button type="button" className="btn" onClick={() => setUi((prev) => ({ ...prev, activeView: "Systemstatus" }))}>
              Systemstatus öffnen
            </button>
            <button type="button" className="btn" onClick={() => exportBoardCsv("all")}>
              Sicherheits-CSV exportieren
            </button>
            <button type="button" className="btn" onClick={exportEmergencyPackage}>
              Notfall-Export erstellen
            </button>
            <button type="button" className="btn" onClick={healLoanerStatuses}>
              Leihwagenstatus heilen
            </button>
          </div>
        </div>

        <div className={`card fullSpan goNoGoCard ${readyForPilot ? "go" : "noGo"}`}>
          <div className="cardHeader">
            <div>
              <h3>{readyForPilot ? "GO: Produktivtest kann starten" : "NO-GO: Noch nicht starten"}</h3>
              <p className="mutedText">
                {readyForPilot
                  ? "Alle produktivtestrelevanten Prüfpunkte sind grün."
                  : "Bitte zuerst die offenen Punkte schließen, bevor echte Kundendaten verarbeitet werden."}
              </p>
            </div>
            <span className={`summaryBadge ${readyForPilot ? "ok" : "warn"}`}>
              <span>Entscheidung</span>
              <strong>{readyForPilot ? "GO" : "NO-GO"}</strong>
            </span>
          </div>
          {!readyForPilot && (
            <div className="productionStepList">
              {blockingChecks.map((check) => (
                <span key={`blocking_${check.label}`}>{check.label}: {check.detail}</span>
              ))}
            </div>
          )}
        </div>

        <div className="card fullSpan">
          <div className="readinessGrid">
            {readinessChecks.map((check) => (
              <div key={check.label} className={`readinessTile ${check.ok ? "ok" : "warn"}`}>
                <span>{check.ok ? "OK" : "Prüfen"}</span>
                <strong>{check.label}</strong>
                <p>{check.detail}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="card fullSpan deploymentCheckCard">
          <div className="cardHeader">
            <div>
              <h3>Deployment-Kontrolle</h3>
              <p className="mutedText">Schnell prüfen, ob wirklich die erwartete Version auf Vercel läuft.</p>
            </div>
          </div>
          <div className="readinessGrid">
            <div className="readinessTile ok">
              <span>Version</span>
              <strong>{APP_BUILD_LABEL}</strong>
              <p>Diese Nummer muss unten im Footer und in Vercel nach Redeploy sichtbar sein.</p>
            </div>
            <div className="readinessTile ok">
              <span>Env</span>
              <strong>{APP_ENV_LABEL}</strong>
              <p>{APP_MODE_IS_FALLBACK ? "Fallback aktiv. Optional in Vercel setzbar." : "Betriebsmodus kommt aus den Vercel Environment Variables."}</p>
            </div>
            <div className={`readinessTile ${lastLiveSyncAt ? "ok" : "warn"}`}>
              <span>Live-Daten</span>
              <strong>{lastLiveSyncAt ? fmtDateTime(lastLiveSyncAt) : "noch offen"}</strong>
              <p>Letzte erfolgreiche Datenaktualisierung dieses Clients.</p>
            </div>
          </div>
        </div>

        <div className="card fullSpan">
          <h3>Nächste sichere Schritte</h3>
          <div className="dangerNotice">
            <strong>Produktivtest-Reset</strong>
            <span>Löscht Aufträge, Leihwagenvorgänge und Audit-Protokoll. Benutzer, Profile, Fahrzeuge und blaue Kennzeichen bleiben erhalten.</span>
          </div>
          <div className="productionStepList">
            <span>1. SQL `phase_2026_05_11_app_settings.sql` ausführen.</span>
            <span>2. SQL `production_test_data_reset.sql` ausführen, falls Testaufträge gelöscht werden sollen.</span>
            <span>3. Systemstatus aktualisieren und Test-E-Mail prüfen.</span>
            <span>4. Einen echten internen Testauftrag ohne Kundenbestätigung anlegen.</span>
            <span>5. Danach einen externen Auftrag mit E-Mail-Bestätigung testen.</span>
          </div>
          <div className="actions">
            <button type="button" className="btn" onClick={copyProductionResetSql}>
              Produktivtest-Reset SQL kopieren
            </button>
          </div>
        </div>

        <div className="card fullSpan externalTodoCard">
          <div className="cardHeader">
            <div>
              <h3>Externe Todo-Liste</h3>
              <p className="mutedText">
                Diese Punkte können nicht per Git-Push erledigt werden und müssen in Hostinger/DNS bzw. beim Testen geprüft werden.
              </p>
            </div>
            <span className="summaryBadge warn">
              <span>Offen</span>
              <strong>{EXTERNAL_OPERATIONS_TODOS.length}</strong>
            </span>
          </div>
          <div className="productionStepList">
            {EXTERNAL_OPERATIONS_TODOS.map((todo, index) => (
              <span key={todo.title}>{index + 1}. {todo.title}: {todo.detail}</span>
            ))}
          </div>
        </div>
      </main>
    );
  };

  const renderLoanerBoard = () => {
    const waiting = bookings.filter(
      (booking) => booking.status === "awaiting_customer_confirmation"
    );
    const ready = bookings.filter((booking) => booking.status === "ready_for_checkout");
    const onRoad = bookings.filter((booking) => booking.status === "checked_out");
    const todayKey = new Date().toISOString().slice(0, 10);
    const tomorrowDate = new Date();
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    const tomorrowKey = tomorrowDate.toISOString().slice(0, 10);
    const dueToday = onRoad.filter(
      (booking) => String(booking.expectedReturnDate || "").slice(0, 10) === todayKey
    );
    const dueTomorrow = onRoad.filter(
      (booking) => String(booking.expectedReturnDate || "").slice(0, 10) === tomorrowKey
    );
    const unavailableLoanerIds = new Set(
      bookings
        .filter((booking) => isActiveBooking(booking))
        .map((booking) => booking.loanerId)
        .filter(Boolean)
    );
    const availableNow = loaners.filter(
      (loaner) =>
        loaner.isActive &&
        loaner.usageType === "customer_loaner" &&
        !isLoanerUnavailable(loaner, availabilityMap, unavailableLoanerIds)
    );
    const openLoanerDemandCards = visibleCards.filter(
      (card) =>
        card.needsLoaner &&
        !bookings.some((booking) => isBookingLinkedToCard(booking, card) && isActiveBooking(booking))
    );
    const activeLoaners = loaners.filter((loaner) => loaner.isActive);
    const fleetCountByUsage = {
      all: activeLoaners.length,
      customer_loaner: activeLoaners.filter((loaner) => loaner.usageType === "customer_loaner").length,
      long_term_assignment: activeLoaners.filter((loaner) => loaner.usageType === "long_term_assignment").length,
      employee_vehicle: activeLoaners.filter((loaner) => loaner.usageType === "employee_vehicle").length,
    };
    const filteredFleetLoaners = activeLoaners
      .filter((loaner) => {
        if (fleetUsageFilter === "all") return true;
        return loaner.usageType === fleetUsageFilter;
      })
      .sort((left, right) => {
        const leftUnavailable = isLoanerUnavailable(left, availabilityMap, unavailableLoanerIds) ? 1 : 0;
        const rightUnavailable = isLoanerUnavailable(right, availabilityMap, unavailableLoanerIds) ? 1 : 0;
        return leftUnavailable - rightUnavailable || left.plate.localeCompare(right.plate);
      });
    const fleetQualityIssues = buildLoanerDataQuality(activeLoaners);
    const historySearch = loanerHistorySearch.trim().toLowerCase();
    const plateFilter = loanerHistoryFilters.plate.trim().toLowerCase();
    const nameFilter = loanerHistoryFilters.name.trim().toLowerCase();
    const handoverEmployeeFilter = loanerHistoryFilters.handoverEmployee.trim().toLowerCase();
    const returnEmployeeFilter = loanerHistoryFilters.returnEmployee.trim().toLowerCase();
    const dateFromFilter = loanerHistoryFilters.dateFrom;
    const dateToFilter = loanerHistoryFilters.dateTo;
    const statusFilter = loanerHistoryFilters.status;
    const branchFilter = normalizeBranchFilter(loanerHistoryFilters.branch);
    const visibleBookings =
      loanerBoardTab === "handover"
        ? [...waiting, ...ready]
        : loanerBoardTab === "returns"
          ? [...dueToday, ...dueTomorrow]
          : bookings;
    const filteredBookings = visibleBookings.filter((booking) => {
      const combined = [
        booking.customerName,
        booking.loanerPlate,
        booking.orderNumber,
        booking.customerEmail,
      ]
        .join(" ")
        .toLowerCase()
      const bookingStart = getBookingStartDate(booking);
      const bookingEnd = getBookingEndDate(booking);
      const bookingBranch =
        booking.loanerHomeBranch ||
        loaners.find((loaner) => loaner.id === booking.loanerId)?.homeBranch ||
        "";

      if (historySearch && !combined.includes(historySearch)) return false;
      if (plateFilter && !String(booking.loanerPlate || "").toLowerCase().includes(plateFilter)) return false;
      if (nameFilter && !String(booking.customerName || "").toLowerCase().includes(nameFilter)) return false;
      if (
        handoverEmployeeFilter &&
        !getBookingHandoverEmployee(booking).toLowerCase().includes(handoverEmployeeFilter)
      ) return false;
      if (
        returnEmployeeFilter &&
        !getBookingReturnEmployee(booking).toLowerCase().includes(returnEmployeeFilter)
      ) return false;
      if (branchFilter && String(bookingBranch) !== branchFilter) return false;
      if (statusFilter && booking.status !== statusFilter) return false;
      if (dateFromFilter && bookingEnd && bookingEnd < dateFromFilter) return false;
      if (dateToFilter && bookingStart && bookingStart > dateToFilter) return false;
      return true;
    });
    const bookingStatusOptions = Array.from(
      new Set(bookings.map((booking) => booking.status).filter(Boolean))
    ).sort();
    const confirmationHealth = getConfirmationHealth(bookings, visibleCards);

    return (
      <main className="board">
        <div className="card fullSpan loanerWorkspaceHero">
          <h3>Leihwagen-Übersicht</h3>
          <div className="actions loanerHeroActions">
            <button
              type="button"
              className="btn primary"
              onClick={() => startLoanerWizard(null)}
            >
              Neue Verleihung starten
            </button>
          </div>
          <div className="loanerQuickStartGrid">
            {[
              { label: "Neue Ausgabe", value: openLoanerDemandCards.length, action: () => setLoanerBoardTab("overview") },
              { label: "Rücknahme", value: onRoad.length, action: () => setLoanerBoardTab("returns") },
              { label: "Heute fällig", value: dueToday.length, action: () => setLoanerBoardTab("returns") },
              { label: "Aktuell ausgegeben", value: onRoad.length, action: () => setLoanerBoardTab("returns") },
              { label: "Verfügbar", value: availableNow.length, action: () => setFleetUsageFilter("customer_loaner") },
            ].map((entry) => (
              <button type="button" key={entry.label} className="loanerQuickStartTile" onClick={entry.action}>
                <span>{entry.label}</span>
                <strong>{entry.value}</strong>
              </button>
            ))}
          </div>
          <div className="detailTabBar">
            <button
              type="button"
              className={`detailTabButton ${loanerBoardTab === "overview" ? "active" : ""}`}
              onClick={() => setLoanerBoardTab("overview")}
            >
              Übersicht
            </button>
            <button
              type="button"
              className={`detailTabButton ${loanerBoardTab === "handover" ? "active" : ""}`}
              onClick={() => setLoanerBoardTab("handover")}
            >
              Übergaben
            </button>
            <button
              type="button"
              className={`detailTabButton ${loanerBoardTab === "returns" ? "active" : ""}`}
              onClick={() => setLoanerBoardTab("returns")}
            >
              Rücknahmen
            </button>
          </div>
          <div className="dashRow">
            <div className="dashItem">
              <strong>Ausstehende Kundenbestätigung</strong>
              <span>{waiting.length}</span>
            </div>
            <div className="dashItem">
              <strong>Freigegeben zur Übergabe</strong>
              <span>{ready.length}</span>
            </div>
            <div className="dashItem">
              <strong>Aktuell unterwegs</strong>
              <span>{onRoad.length}</span>
            </div>
            <div className="dashItem">
              <strong>Heute fällig</strong>
              <span>{dueToday.length}</span>
            </div>
            <div className="dashItem">
              <strong>Morgen fällig</strong>
              <span>{dueTomorrow.length}</span>
            </div>
            <div className="dashItem">
              <strong>Verfügbar</strong>
              <span>{availableNow.length}</span>
            </div>
            <div className="dashItem">
              <strong>Offline-Warteschlange</strong>
              <span>{offlineQueueCount}</span>
            </div>
          </div>
          <div className="confirmationHealthStrip">
            {confirmationHealth.map((entry) => (
              <div key={entry.label} className={`confirmationHealthTile ${entry.tone}`}>
                <span>{entry.label}</span>
                <strong>{entry.value}</strong>
              </div>
            ))}
          </div>
          {mediaSyncStatus && (
            <div className="syncStatusBar">
              <strong>Sync</strong>
              <span>{mediaSyncStatus}</span>
            </div>
          )}
        </div>

        {loanerBoardTab === "overview" && (
          <div className="card fullSpan loanerDemandOverview">
            <div className="cardHeader">
              <div>
                <h3>Offener Leihwagenbedarf</h3>
                <p className="mutedText">
                  Aus der Annahme und direkt gestartete Leihwagen-Entwürfe. Kundendaten
                  können am PC vorbereitet und Foto- sowie Übergabeschritte am Tablet
                  fortgesetzt werden.
                </p>
              </div>
              <span className="summaryBadge">
                <span>Offen</span>
                <strong>{openLoanerDemandCards.length}</strong>
              </span>
            </div>
            <div className="loanerDemandList">
              {openLoanerDemandCards.map((card) => (
                <div key={card.id} className="loanerDemandRow">
                  <div>
                    <strong>{card.customerName || "Kunde noch offen"}</strong>
                    <span>{card.orderNumber || "ohne Auftragsnummer"}</span>
                  </div>
                  <div>
                    <span>
                      {card.loanerFlow?.standaloneLoanerProcess
                        ? "Vorgesehener Leihwagen"
                        : "Kundenfahrzeug"}
                    </span>
                    <strong>
                      {card.loanerFlow?.standaloneLoanerProcess
                        ? card.loanerFlow?.plannedLoanerPlate || "-"
                        : card.licensePlate || "-"}
                    </strong>
                  </div>
                  <div>
                    <span>Filiale</span>
                    <strong>{card.branch ? getBranchLabel(card.branch) : "-"}</strong>
                  </div>
                  <div>
                    <span>Nächster Schritt</span>
                    <strong>{card.nextAction || "Leihwagen auswählen"}</strong>
                  </div>
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() =>
                      startLoanerWizard(
                        card,
                        card.loanerFlow?.loanerWizardDraft?.wizardStep || 0
                      )
                    }
                  >
                    Verleihung fortsetzen
                  </button>
                </div>
              ))}
              {openLoanerDemandCards.length === 0 && (
                <div className="mutedText">Aktuell ist kein offener Leihwagenbedarf vorgemerkt.</div>
              )}
            </div>
          </div>
        )}

        {(loanerBoardTab === "overview" || loanerBoardTab === "handover") && (
          <div className="card fullSpan readyHandoverPanel">
            <div className="cardHeader">
              <div>
                <h3>Bereit zur Ausgabe</h3>
                <p className="mutedText">
                  Diese Vorgänge wurden vom Kunden bestätigt. Jetzt kann das Fahrzeug
                  tatsächlich ausgegeben werden.
                </p>
              </div>
              <span className="summaryBadge">
                <span>Bereit</span>
                <strong>{ready.length}</strong>
              </span>
            </div>
            <div className="loanerDemandList">
              {ready.map((booking) => (
                <div key={booking.id} className="loanerDemandRow readyRow">
                  <div>
                    <strong>{booking.customerName || "-"}</strong>
                    <span>{booking.orderNumber || "ohne Auftrag"}</span>
                  </div>
                  <div>
                    <span>Leihwagen</span>
                    <strong>{booking.loanerPlate || "-"}</strong>
                  </div>
                  <div>
                    <span>Bestätigt</span>
                    <strong>{booking.confirmationAcceptedAt ? fmtDateTime(booking.confirmationAcceptedAt) : "-"}</strong>
                  </div>
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() => markLoanerHandedOver(booking)}
                  >
                    Fahrzeug jetzt ausgeben
                  </button>
                </div>
              ))}
              {ready.length === 0 && (
                <div className="mutedText">Aktuell ist kein bestätigter Vorgang zur Ausgabe bereit.</div>
              )}
            </div>
          </div>
        )}

        {loanerBoardTab !== "returns" && (
        <div className="card">
          <h3>Gerade verliehen</h3>
          <div className="taskList">
            {onRoad.length > 0 ? (
              onRoad.map((booking) => (
                <div key={booking.id} className="taskListItem highlightedLoanerItem">
                  <strong>{booking.loanerPlate}</strong>
                  <span>{booking.customerName}</span>
                  <span>Verliehen ab {fmtDate(booking.plannedPickupDate)}</span>
                  <span>Rückgabe {fmtDate(booking.expectedReturnDate)}</span>
                </div>
              ))
            ) : (
              <div className="mutedText">Aktuell ist kein Leihwagen verliehen.</div>
            )}
          </div>
        </div>
        )}

        {(loanerBoardTab === "overview" || loanerBoardTab === "returns") && (
        <div className="card">
          <h3>Heute zur Rücknahme</h3>
          <div className="taskList">
            {dueToday.length > 0 ? (
              dueToday.map((booking) => (
                <div key={booking.id} className="taskListItem highlightedLoanerItem dueTodayItem">
                  <strong>{booking.loanerPlate}</strong>
                  <span>{booking.customerName}</span>
                  <span>{booking.loanerHomeBranch ? getBranchLabel(booking.loanerHomeBranch) : "-"}</span>
                </div>
              ))
            ) : (
              <div className="mutedText">Für heute ist aktuell keine Rücknahme geplant.</div>
            )}
          </div>
        </div>
        )}

        {loanerBoardTab === "overview" && (
          <div className="card fullSpan fleetOverviewCard">
            <div className="cardHeader">
              <div>
                <h3>Fahrzeugbestand</h3>
                <p className="mutedText">
                  Alle aktiven Fahrzeuge nach Nutzung gefiltert. Nicht verfügbare oder dauerhaft
                  verliehene Fahrzeuge bleiben sichtbar und werden ausgegraut.
                </p>
              </div>
              <span className="summaryBadge">
                <span>Aktive Fahrzeuge</span>
                <strong>{activeLoaners.length}</strong>
              </span>
            </div>
            <div className="fleetFilterBar">
              {[
                { value: "customer_loaner", label: "Kundenersatz" },
                { value: "long_term_assignment", label: "Langzeit" },
                { value: "employee_vehicle", label: "Dienstwagen" },
                { value: "all", label: "Alle" },
              ].map((option) => (
                <button
                  type="button"
                  key={option.value}
                  className={`fleetFilterButton ${fleetUsageFilter === option.value ? "active" : ""}`}
                  onClick={() => setFleetUsageFilter(option.value)}
                >
                  <span>{option.label}</span>
                  <strong>{fleetCountByUsage[option.value] || 0}</strong>
                </button>
              ))}
            </div>
            {fleetQualityIssues.length > 0 && (
              <div className="fleetQualityBox">
                <div className="cardHeader">
                  <div>
                    <strong>Datenqualität prüfen</strong>
                    <p className="mutedText">
                      {fleetQualityIssues.length} Fahrzeuge haben fehlende oder doppelte Kernfelder.
                    </p>
                  </div>
                  <button type="button" className="btn" onClick={() => exportBoardCsv("loaners")}>
                    Fahrzeugbestand exportieren
                  </button>
                </div>
                <div className="fleetQualityList">
                  {fleetQualityIssues.slice(0, 8).map(({ loaner, issues }) => (
                    <span key={loaner.id}>
                      <strong>{loaner.plate || loaner.id}</strong> {issues.join(", ")}
                    </span>
                  ))}
                  {fleetQualityIssues.length > 8 && (
                    <span>Weitere {fleetQualityIssues.length - 8} Hinweise im CSV prüfen.</span>
                  )}
                </div>
              </div>
            )}
            <div className="fleetOverviewGrid">
              {filteredFleetLoaners.map((loaner) => {
                const isUnavailable =
                  unavailableLoanerIds.has(loaner.id) ||
                  isLoanerUnavailable(loaner, availabilityMap, unavailableLoanerIds);
                return (
                  <button
                    type="button"
                    key={loaner.id}
                    className={`fleetVehicleTile ${isUnavailable ? "disabled" : ""}`}
                    onClick={() => openAvailabilityInspector(loaner.id)}
                  >
                    <strong>{loaner.plate || "-"}</strong>
                    <span>{[loaner.brand, loaner.model].filter(Boolean).join(" ") || "-"}</span>
                    <small>{getLoanerUsageLabel(loaner.usageType)}</small>
                    <small>{loaner.homeBranch ? getBranchLabel(loaner.homeBranch) : "keine Filiale"}</small>
                    <em>
                      {loaner.permanentlyCheckedOut
                        ? "dauerhaft verliehen"
                        : isUnavailable
                          ? "nicht verfügbar"
                          : loaner.status || "-"}
                    </em>
                  </button>
                );
              })}
              {filteredFleetLoaners.length === 0 && (
                <div className="mutedText">Keine Fahrzeuge für diesen Filter verfügbar.</div>
              )}
            </div>
          </div>
        )}

        <div className="card fullSpan compactHistoryCard">
          <div className="cardHeader">
            <div>
              <h3>Leihwagen-Historie</h3>
            </div>
            <InfoHint text="Kompakte Liste. Auf eine Zeile tippen, um Details, Fotos und Aktionen zu öffnen." />
            <input
              type="search"
              className="historySearchInput"
              placeholder="Suchen nach Person, Kennzeichen, Auftrag ..."
              value={loanerHistorySearch}
              onChange={(event) => setLoanerHistorySearch(event.target.value)}
            />
          </div>

          <div className="loanerHistoryFilters">
            <select
              aria-label="Standort der Leihwagen-Historie"
              value={loanerHistoryFilters.branch}
              onChange={(event) =>
                setLoanerHistoryFilters((prev) => ({ ...prev, branch: event.target.value }))
              }
            >
              <option value="">Standort: beide</option>
              {BRANCH_OPTIONS.map((branch) => (
                <option key={branch.value} value={branch.value}>
                  Standort: {branch.label}
                </option>
              ))}
            </select>
            <input
              type="search"
              placeholder="Kennzeichen"
              value={loanerHistoryFilters.plate}
              onChange={(event) =>
                setLoanerHistoryFilters((prev) => ({ ...prev, plate: event.target.value }))
              }
            />
            <input
              type="search"
              placeholder="Name"
              value={loanerHistoryFilters.name}
              onChange={(event) =>
                setLoanerHistoryFilters((prev) => ({ ...prev, name: event.target.value }))
              }
            />
            <input
              type="search"
              placeholder="Ausgabe-Mitarbeiter"
              value={loanerHistoryFilters.handoverEmployee}
              onChange={(event) =>
                setLoanerHistoryFilters((prev) => ({ ...prev, handoverEmployee: event.target.value }))
              }
            />
            <input
              type="search"
              placeholder="Rücknahme-Mitarbeiter"
              value={loanerHistoryFilters.returnEmployee}
              onChange={(event) =>
                setLoanerHistoryFilters((prev) => ({ ...prev, returnEmployee: event.target.value }))
              }
            />
            <input
              type="date"
              value={loanerHistoryFilters.dateFrom}
              onChange={(event) =>
                setLoanerHistoryFilters((prev) => ({ ...prev, dateFrom: event.target.value }))
              }
            />
            <input
              type="date"
              value={loanerHistoryFilters.dateTo}
              onChange={(event) =>
                setLoanerHistoryFilters((prev) => ({ ...prev, dateTo: event.target.value }))
              }
            />
            <select
              value={loanerHistoryFilters.status}
              onChange={(event) =>
                setLoanerHistoryFilters((prev) => ({ ...prev, status: event.target.value }))
              }
            >
              <option value="">Status: alle</option>
              {bookingStatusOptions.map((status) => (
                <option key={status} value={status}>
                  {getBookingStatusLabel(status)}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn"
              onClick={() =>
                setLoanerHistoryFilters(
                  getDefaultLoanerHistoryFilters(profile?.branch)
                )
              }
            >
              Filter zurücksetzen
            </button>
          </div>

          <div className="loanerHistoryList">
            {filteredBookings.slice(0, loanerHistoryLimit).map((booking) => {
              const isExpanded = expandedLoanerHistory === booking.id;
              const handoverPhotos = booking.conditionPhotos || {};
              const confirmationUrl = getLoanerConfirmationUrl(booking);
              const qrUrl = getLoanerConfirmationQrUrl(booking);
              const externalQrUrl = getLoanerExternalQrUrl(booking);
              const canViewDriverLicense = isAdmin;
              const galleryPhotos = getBookingMediaGallery(booking, { includeSensitive: canViewDriverLicense });
              const driverLicensePhotos = [
                { key: "front", label: "Führerschein Vorderseite", url: booking.driverLicenseFront },
                { key: "back", label: "Führerschein Rückseite", url: booking.driverLicenseBack },
              ].filter((entry) => entry.url);
              const damageEntries = getBookingDamageEntries(booking);
              const deletedDamageEntries = getDeletedBookingDamageEntries(booking);
              const returnDamageGroups = getReturnDamageGroups(booking);
              const timelineEvents = getBookingEventTimeline(booking);
              const hasInlineMedia = bookingHasInlineMedia(booking, { includeSensitive: canViewDriverLicense });
              const pendingCorrectionEvents = getPendingCorrectionEvents(booking);
              const latestPendingCorrection = pendingCorrectionEvents[pendingCorrectionEvents.length - 1] || null;
              const canShowConfirmationFallback =
                confirmationUrl &&
                (["awaiting_customer_confirmation", "ready_for_checkout"].includes(booking.status) ||
                  booking.confirmationStatus === "pending");
              const resendCooldownUntil = resendCooldowns[booking.id] || 0;
              const resendCooldownActive = resendCooldownUntil > resendClock;
              const resendInProgress = Boolean(resendSending[booking.id]);
              const openGalleryAt = (url) => {
                const index = Math.max(0, galleryPhotos.findIndex((photo) => photo.url === url));
                setImageViewer({ open: true, photos: galleryPhotos, index });
              };
              return (
                <div key={booking.id} className={`loanerHistoryRow ${isExpanded ? "expanded" : ""}`}>
                  <button
                    type="button"
                    className="loanerHistorySummary"
                    onClick={async () => {
                      const shouldOpen = expandedLoanerHistory !== booking.id;
                      setExpandedLoanerHistory(shouldOpen ? booking.id : null);
                      if (shouldOpen) {
                        await hydrateBookingMedia(booking.id);
                      }
                    }}
                  >
                    <span>{booking.customerName || "-"}</span>
                    <span>{booking.loanerPlate || "-"}</span>
                    <span>{fmtDate(booking.plannedPickupDate)}</span>
                    <span>{fmtDate(booking.expectedReturnDate)}</span>
                    <span>{getBookingStatusLabel(booking.status)}</span>
                    <span className="historyChevron">{isExpanded ? "▾" : "▸"}</span>
                  </button>

                  {isExpanded && (
                    <div className="loanerHistoryDetails">
                      <div className="meta">
                        <span>Auftrag {booking.orderNumber || "-"}</span>
                        <span>{booking.customerEmail || "-"}</span>
                        <span>{getInsuranceLabel(booking.insuranceOption)}</span>
                      </div>
                      <div className="meta">
                        <span>Ausgabe: {getBookingHandoverEmployee(booking)}</span>
                        <span>Zeit Ausgabe: {booking.checkedOutAt ? fmtDateTime(booking.checkedOutAt) : "-"}</span>
                        <span>Rücknahme: {getBookingReturnEmployee(booking)}</span>
                        <span>Zeit Rücknahme: {booking.checkedInAt ? fmtDateTime(booking.checkedInAt) : "-"}</span>
                      </div>

                      <div className="confirmationStepGrid">
                        {buildMailDiagnosticSteps(booking).map((step) => (
                          <div key={`${booking.id}_${step.label}`} className={`confirmationStep ${step.ok ? "done" : ""}`}>
                            <span>{step.label}</span>
                            <strong>{step.value}</strong>
                          </div>
                        ))}
                      </div>

                      {latestPendingCorrection && (
                        <div className="correctionApprovalNotice">
                          <div>
                            <strong>Neue Kundenfreigabe erforderlich</strong>
                            <span>
                              {latestPendingCorrection.reason || "Rechtlich relevante Daten wurden korrigiert."}
                            </span>
                          </div>
                          <div className="correctionMiniDiff">
                            {getCorrectionChangeSummary(latestPendingCorrection).map((change) => (
                              <span key={`${booking.id}_${change.field}`}>
                                {change.field}: <b>{change.before || "-"}</b> → <b>{change.after || "-"}</b>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="loanerAuditStrip">
                        {getBookingPhotoChecklist(booking).map((entry) => (
                          <span key={entry.label}>
                            {entry.label}: <strong>{entry.value}</strong>
                          </span>
                        ))}
                      </div>

                      <div className="loanerAuditTimeline">
                        {[
                          ...getBookingAuditTrail(booking),
                          ...buildConfirmationProtocol({
                            card: cards.find((card) => isBookingLinkedToCard(booking, card)),
                            booking,
                          }),
                        ].map((entry) => (
                          <span key={`${booking.id}_${entry.label}_${entry.value}`}>
                            {entry.label}: <strong>{fmtDateTime(entry.value)}</strong>
                          </span>
                        ))}
                      </div>

                      {(booking.status === "returned" || booking.checkedInAt) && (
                        <div className="returnProtocolBox">
                          <strong>Rücknahmeprotokoll</strong>
                          {getReturnProtocolLines(booking).map((line) => (
                            <span key={`${booking.id}_${line}`}>{line}</span>
                          ))}
                        </div>
                      )}

                      <div className="actions">
                        <button
                          type="button"
                          className="btn"
                          onClick={() => buildBookingPdf(booking)}
                        >
                          PDF
                        </button>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => exportBookingDocumentationPackage(booking)}
                        >
                          Dokumentationspaket
                        </button>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => retryLoanerMediaSync(booking)}
                          disabled={!hasInlineMedia}
                        >
                          Medien erneut synchronisieren
                        </button>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => openLoanerCorrectionModal(booking)}
                        >
                          Daten korrigieren
                        </button>
                        <button
                          type="button"
                          className="btn"
                          onClick={() =>
                            navigator.clipboard.writeText(confirmationUrl)
                          }
                          disabled={!confirmationUrl}
                        >
                          Link kopieren
                        </button>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => window.open(confirmationUrl, "_blank", "noopener,noreferrer")}
                          disabled={!confirmationUrl}
                        >
                          Kundenansicht öffnen
                        </button>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => resendConfirmationEmail(booking)}
                          disabled={resendInProgress || resendCooldownActive}
                        >
                          {resendInProgress
                            ? "E-Mail wird versendet ..."
                            : resendCooldownActive
                              ? "E-Mail wurde versendet"
                              : "E-Mail erneut senden"}
                        </button>
                        {resendCooldownActive && (
                          <span className="resendCooldownHint">
                            Ein erneuter Versand ist in{" "}
                            {formatResendWait(resendCooldownUntil, resendClock)} möglich.
                          </span>
                        )}
                        {canShowConfirmationFallback && (
                          <button
                            type="button"
                            className="btn primary"
                            onClick={() =>
                              setLoanerCompletion({
                                booking,
                                message: "Bestätigungslink ist bereit. Kunde kann jetzt per QR-Code vor Ort bestätigen.",
                                reviewUrl: confirmationUrl,
                              })
                            }
                          >
                            QR-Bestätigung vor Ort
                          </button>
                        )}
                        {booking.status === "ready_for_checkout" && (
                          <button
                            type="button"
                            className="btn primary"
                            onClick={() => markLoanerHandedOver(booking)}
                          >
                            Übergabe starten
                          </button>
                        )}
                        {booking.status === "checked_out" && (
                          <button
                            type="button"
                            className="btn primary"
                            onClick={() => openReturnPanel(booking)}
                          >
                            Rücknahme erfassen
                          </button>
                        )}
                        {["awaiting_customer_confirmation", "ready_for_checkout"].includes(
                          booking.status
                        ) && (
                          <button
                            type="button"
                            className="btn dangerSubtle"
                            onClick={() => cancelLoanerBooking(booking)}
                          >
                            Vorgang abbrechen
                          </button>
                        )}
                      </div>

                      {canShowConfirmationFallback && (
                        <div className="confirmationFallbackPanel">
                          <div>
                            <strong>Fallback, falls die E-Mail nicht ankommt</strong>
                            <span>Link kopieren, direkt öffnen oder QR-Code am Tablet scannen lassen.</span>
                            <code>{confirmationUrl}</code>
                          </div>
                          <div className="qrCodeBox">
                            {qrUrl ? (
                              <img
                                src={qrUrl}
                                alt="QR-Code zum Bestätigungslink"
                                onError={(event) => {
                                  if (externalQrUrl && event.currentTarget.src !== externalQrUrl) {
                                    event.currentTarget.src = externalQrUrl;
                                  }
                                }}
                              />
                            ) : (
                              <span>Kein QR-Code verfügbar</span>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="loanerHistorySection">
                        <div className="historyMediaHeader">
                          <strong>Bestätigung & Verlauf</strong>
                          <span>
                            Status: {booking.confirmationStatus || "-"} · Token {booking.confirmationToken ? "vorhanden" : "fehlt"}
                          </span>
                        </div>
                        <div className="eventTimelineList">
                          {timelineEvents.map((event, index) => (
                            <div key={`${booking.id}_event_${index}_${event.label}`} className="eventTimelineItem">
                              <span>{event.at ? fmtDateTime(event.at) : "-"}</span>
                              <strong>{event.label}</strong>
                              <small>{event.detail || "-"}</small>
                            </div>
                          ))}
                          {timelineEvents.length === 0 && <div className="mutedText">Noch kein Verlauf vorhanden.</div>}
                        </div>
                      </div>

                      <div className="historyMediaHeader">
                        <strong>Fotos und Schäden</strong>
                        <span>
                          Bild antippen, um es groß zu öffnen. {hasInlineMedia ? "Einige Bilder warten noch auf Sync." : "Alle sichtbaren Bilder sind gespeichert."}
                        </span>
                      </div>
                      {canViewDriverLicense && driverLicensePhotos.length > 0 && (
                        <div className="loanerHistorySection sensitiveMediaSection">
                          <div className="historyMediaHeader">
                            <strong>Führerscheinfotos</strong>
                            <span>Nur für Admins sichtbar.</span>
                          </div>
                          <div className="mediaTimelineGrid compactMediaGrid">
                            {driverLicensePhotos.map((entry) => (
                              <button
                                type="button"
                                key={`license_${booking.id}_${entry.key}`}
                                className="mediaPreview compact clickableMedia sensitiveMedia"
                                onClick={() => openGalleryAt(entry.url)}
                              >
                                <img src={entry.url} alt={entry.label} />
                                <span>{entry.label} · {getMediaStorageLabel(entry.url)}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {!canViewDriverLicense && (booking.driverLicenseFront || booking.driverLicenseBack) && (
                        <div className="privacyMediaNotice">
                          Führerscheinfotos sind vorhanden, aber aus Datenschutzgründen nur für Admins sichtbar.
                        </div>
                      )}
                      {damageEntries.length > 0 ? (
                        <div className="damageHistoryList">
                          {damageEntries.map((entry, index) => (
                            <div key={`${booking.id}_damage_${entry.id || index}`} className="damageHistoryItem">
                              {entry.url ? (
                                <button type="button" className="mediaPreview damage compact clickableMedia" onClick={() => openGalleryAt(entry.url)}>
                                  <img src={entry.url} alt={`Schaden ${index + 1}`} />
                                  <span>{getMediaStorageLabel(entry.url)}</span>
                                </button>
                              ) : (
                                <div className="missingMediaBox">Kein Bild synchronisiert</div>
                              )}
                              <div>
                                <strong>Schaden {index + 1}</strong>
                                <span>{entry.description || "Keine Beschreibung hinterlegt"}</span>
                                <button
                                  type="button"
                                  className="btn dangerSubtle"
                                  onClick={() => softDeleteBookingDamageEntry(booking, entry)}
                                >
                                  Schaden entfernen
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="mutedText">Keine Schäden dokumentiert.</div>
                      )}
                      {deletedDamageEntries.length > 0 && (
                        <div className="deletedDamageBox">
                          <strong>Gelöschte Schäden im Verlauf</strong>
                          {deletedDamageEntries.map((entry, index) => (
                            <span key={`deleted_${booking.id}_${entry.id || index}`}>
                              {entry.description || `Schaden ${index + 1}`} gelöscht von {entry.deletedBy || "-"} am{" "}
                              {entry.deletedAt ? fmtDateTime(entry.deletedAt) : "-"}
                              {entry.deleteReason ? ` · Grund: ${entry.deleteReason}` : ""}
                            </span>
                          ))}
                        </div>
                      )}
                      {returnDamageGroups.length > 0 && (
                        <div className="loanerHistorySection">
                          <div className="historyMediaHeader">
                            <strong>Bei Rücknahme dokumentierte Schäden</strong>
                            <span>Alle zugehörigen Fotos dieses Vorgangs.</span>
                          </div>
                          <div className="damageHistoryList">
                            {returnDamageGroups.map((entry, index) => (
                              <div
                                key={`return_damage_${booking.id}_${entry.id || index}`}
                                className="damageHistoryItem"
                              >
                                <div className="mediaTimelineGrid compactMediaGrid">
                                  {entry.photos.map((photo, photoIndex) => (
                                    <button
                                      type="button"
                                      key={`${entry.id}_${photo.id || photoIndex}`}
                                      className="mediaPreview damage compact clickableMedia"
                                      onClick={() => openGalleryAt(photo.url)}
                                    >
                                      <img
                                        src={photo.url}
                                        alt={`Rücknahmeschaden ${index + 1}, Foto ${photoIndex + 1}`}
                                      />
                                    </button>
                                  ))}
                                </div>
                                <div>
                                  <strong>Rücknahmeschaden {index + 1}</strong>
                                  <span>
                                    {entry.description ||
                                      "Keine Beschreibung hinterlegt"}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mediaTimelineGrid compactMediaGrid">
                        {Object.values(handoverPhotos)
                          .filter(Boolean)
                          .map((photo, index) => (
                            <button
                              type="button"
                              key={`condition_${booking.id}_${index}`}
                              className="mediaPreview compact clickableMedia"
                              onClick={() => openGalleryAt(photo)}
                            >
                              <img src={photo} alt={`Übergabe ${index + 1}`} />
                              <span>{getMediaStorageLabel(photo)}</span>
                            </button>
                          ))}
                        {(booking.returnPhotos || [])
                          .filter((entry) => entry?.url)
                          .map((entry, index) => (
                            <button
                              type="button"
                              key={`return_${booking.id}_${index}`}
                              className="mediaPreview compact clickableMedia"
                              onClick={() => openGalleryAt(entry.url)}
                            >
                              <img src={entry.url} alt={`Rücknahme ${index + 1}`} />
                              <span>{getMediaStorageLabel(entry.url)}</span>
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {filteredBookings.length === 0 && (
              <div className="mutedText">Keine passenden Leihwagen-Vorgänge gefunden.</div>
            )}
            {filteredBookings.length > loanerHistoryLimit && (
              <button
                type="button"
                className="btn fullWidth"
                onClick={() => setLoanerHistoryLimit((prev) => prev + 40)}
              >
                Weitere {Math.min(40, filteredBookings.length - loanerHistoryLimit)} laden
              </button>
            )}
          </div>
        </div>

      </main>
    );
  };

  const renderReturnModal = () => {
    if (!returnState.bookingId) return null;

    const booking = bookings.find((item) => item.id === returnState.bookingId);
    if (!booking) return null;

    const loaner = loaners.find((item) => item.id === booking.loanerId);
    const handoverPhotos = booking.conditionPhotos || {};
    const returnTab = returnTabs[booking.id] || "compare";
    const capturedPhotos = PHOTO_LABELS.filter(
      (entry) => returnState.comparisonPhotos?.[entry.key]
    ).length;
    const relevantReturnDamages = getRelevantReturnDamageEntries(
      returnState.returnDamageEntries
    );
    const incompleteReturnDamages = getIncompleteReturnDamageEntries(
      returnState.returnDamageEntries
    );
    const activeReturnVehicleDamages = loaner
      ? getActiveDamagesForLoaner(loaner.id)
      : [];
    const returnKmLowerThanHandover =
      Number(booking.kmStart || 0) > 0 &&
      Number(returnState.kmEnd || 0) > 0 &&
      Number(returnState.kmEnd || 0) < Number(booking.kmStart || 0);
    const returnEmployeeBranch = getEmployeeBranch(profile, booking.loanerHomeBranch);
    const returnEmployees = getEmployeesForBranchAndTypes(technicians, returnEmployeeBranch, ["Kundendienstberater"]);
    const returnSteps = [
      { id: "employee", label: "Mitarbeiter" },
      { id: "compare", label: "Vergleich" },
      { id: "condition", label: "Zustand" },
      { id: "finish", label: "Abschluss" },
    ];
    const activeIndex = Math.max(
      0,
      returnSteps.findIndex((step) => step.id === returnTab)
    );
    const canAdvanceReturn =
      returnTab === "employee" ? Boolean(returnState.returnEmployeeId) : true;
    const returnContextItems = [
      { label: "Kunde", value: booking.customerName || "-" },
      { label: "Leihwagen", value: booking.loanerPlate || "-" },
      { label: "Auftrag", value: booking.orderNumber || "-" },
      { label: "Mitarbeiter", value: returnState.returnEmployeeName || "-" },
      { label: "Rückgabe", value: fmtDate(booking.expectedReturnDate) },
      { label: "Tarif", value: getInsuranceLabel(booking.insuranceOption) },
      { label: "Filiale", value: booking.loanerHomeBranch ? getBranchLabel(booking.loanerHomeBranch) : "-" },
    ];

    return (
      <div className="modalOverlay">
        {returnConflict && (
          <div className="modalCard conflictModal">
            <div className="modalHeader">
              <div>
                <p className="eyebrow">Achtung</p>
                <h3>Rücknahme bereits abgeschlossen</h3>
              </div>
            </div>
            <p>
              Das Fahrzeug wurde bereits von {returnConflict.employee} am {returnConflict.time}
              zurückgenommen. {isAdmin
                ? "Willst du diese Daten wirklich überschreiben?"
                : "Du kannst den Vorgang ohne Speichern schließen. Überschreiben ist nur für Admins freigegeben."}
            </p>
            <div className="actions">
              <button
                type="button"
                className="btn"
              onClick={() => {
                setReturnConflict(null);
                setDbStatus("Rücknahme wurde ohne Speichern beendet, bestehende Daten bleiben erhalten.");
                  closeReturnPanel();
              }}
              >
                Ohne Speichern fortfahren
              </button>
              {isAdmin && (
                <button
                  type="button"
                  className="btn dangerSubtle"
                  onClick={() => {
                    setReturnConflict(null);
                    completeReturn({ forceOverwrite: true });
                  }}
                >
                  Daten überschreiben
                </button>
              )}
            </div>
          </div>
        )}
        <div className="modalCard wizardCard returnWizardCard">
          <div className="modalHeader">
            <div>
              <p className="eyebrow">Leihwagen-Rücknahme</p>
              <h3>
                {booking.loanerPlate} - {booking.customerName || "Rücknahme"}
              </h3>
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => attemptCloseReturnPanel(booking.id)}
            >
              Schließen
            </button>
          </div>

          <div className="wizardContextBar">
            {returnContextItems.map((item) => (
              <div key={item.label} className="contextTile">
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>

          <div className="wizardStepper">
            {returnSteps.map((step, index) => {
              const isComplete =
                step.id === "employee"
                  ? Boolean(returnState.returnEmployeeId)
                  : step.id === "compare"
                  ? capturedPhotos === PHOTO_LABELS.length
                    : step.id === "condition"
                    ? Boolean(
                        returnState.kmEnd &&
                          returnState.fuelEnd &&
                          returnState.fuelReceiptPhoto &&
                          incompleteReturnDamages.length === 0
                      )
                    : capturedPhotos === PHOTO_LABELS.length &&
                      Boolean(
                        returnState.kmEnd &&
                          returnState.fuelEnd &&
                          returnState.fuelReceiptPhoto &&
                          incompleteReturnDamages.length === 0
                      );
              return (
                <button
                  type="button"
                  key={step.id}
                  className={`stepPill ${returnTab === step.id ? "active" : ""} ${isComplete ? "complete" : ""}`}
                  onClick={() => navigateReturnStep(booking.id, step.id)}
                >
                  <span>{index + 1}</span>
                  <strong>{step.label}</strong>
                </button>
              );
            })}
          </div>

          <div className="wizardProgressBand">
            <div
              className="wizardProgressBar"
              style={{ width: `${Math.round(((activeIndex + 1) / returnSteps.length) * 100)}%` }}
            />
          </div>

          <div className="wizardSections">
            {returnTab === "employee" && (
              <div className="wizardSection wizardSectionActive returnStepPanel">
                <div className="wizardStepCard">
                  <div className="wizardStepIntro">
                    <span className="sectionTag">Schritt 1</span>
                    <h4>Wer führt die Rücknahme durch?</h4>
                  </div>
                  <div className="loanerChoiceGrid employeeChoiceGrid">
                    {returnEmployees.map((employee) => (
                      <button
                        type="button"
                        key={employee.id}
                        className={`loanerChoiceCard employeeChoiceCard ${
                          returnState.returnEmployeeId === employee.id ? "selected" : ""
                        }`}
                        onClick={() =>
                          setReturnState((prev) => ({
                            ...prev,
                            returnEmployeeId: employee.id,
                            returnEmployeeName: employee.name,
                            error: "",
                          }))
                        }
                      >
                        <span className="loanerChoicePlate">{employee.name}</span>
                        <strong>{employee.type}</strong>
                        <span>{getBranchLabel(employee.branch)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {returnTab === "compare" && (
              <div className="wizardSection wizardSectionActive returnStepPanel">
                <div className="wizardStepCard">
                  <div className="wizardStepIntro">
                    <span className="sectionTag">Schritt 2</span>
                    <h4>Vorher / Nachher prüfen</h4>
                  </div>
                  <div className="returnComparisonGrid">
                    {PHOTO_LABELS.map((entry) => (
                      <div key={entry.key} className="returnComparisonCard">
                        <div className="returnComparisonHeader">
                          <strong>{entry.label}</strong>
                          <span>Übergabe und Rücknahme</span>
                        </div>
                        <div className="returnComparisonPhotos">
                          <div className="returnReferenceBlock">
                            <span>Übergabe</span>
                            {handoverPhotos[entry.key] ? (
                              <img src={handoverPhotos[entry.key]} alt={`${entry.label} bei Übergabe`} />
                            ) : (
                              <div className="returnPlaceholder">Kein Foto aus der Übergabe vorhanden</div>
                            )}
                          </div>
                          <div className="returnReferenceBlock current">
                            <span>Rückgabe neu aufnehmen</span>
                            <CameraCaptureButton
                              label={`${entry.label} neu fotografieren`}
                              value={returnState.comparisonPhotos?.[entry.key] || ""}
                              sequenceHint="Rücknahme-Fotoreihe läuft automatisch weiter"
                              onOpenCamera={() =>
                                openPhotoCaptureSequence({
                                  title: "Fahrzeugrundgang bei Rücknahme",
                                  items: PHOTO_LABELS.map((photo) => ({
                                    field: photo.key,
                                    label: photo.label,
                                    value:
                                      returnState.comparisonPhotos?.[
                                        photo.key
                                      ] || "",
                                  })),
                                  currentField: entry.key,
                                  onSave: (item, dataUrl) =>
                                    setReturnState((prev) => ({
                                      ...prev,
                                      comparisonPhotos: {
                                        ...prev.comparisonPhotos,
                                        [item.field]: dataUrl,
                                      },
                                      error: "",
                                    })),
                                })
                              }
                              onChange={(event) =>
                                handleReturnComparisonUpload(
                                  entry.key,
                                  event.target.files
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {returnTab === "condition" && (
              <div className="wizardSection wizardSectionActive returnStepPanel">
                <div className="wizardStepCard">
                  <div className="wizardStepIntro">
                    <span className="sectionTag">Schritt 3</span>
                    <h4>Zustand erfassen</h4>
                  </div>
                  <div className="formGrid">
                    <label>
                      KM bei Übergabe
                      <input type="number" value={booking.kmStart || ""} readOnly />
                    </label>
                    <label>
                      KM bei Rückgabe
                      <input
                        type="number"
                        value={returnState.kmEnd}
                        onChange={(event) =>
                          setReturnState((prev) => ({
                            ...prev,
                            kmEnd: event.target.value,
                            error: "",
                          }))
                        }
                      />
                    </label>
                    <label>
                      Tankstand Rückgabe
                      <select
                        value={returnState.fuelEnd}
                        onChange={(event) =>
                          setReturnState((prev) => ({
                            ...prev,
                            fuelEnd: event.target.value,
                          }))
                        }
                      >
                        {FUEL_OPTIONS.map((fuel) => (
                          <option key={fuel} value={fuel}>
                            {fuel}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="returnReceiptPanel">
                    <div>
                      <strong>Tankrechnung (Pflichtfoto)</strong>
                      <span>
                        Bitte fotografieren oder laden Sie die Tankrechnung hoch,
                        bevor Sie fortfahren.
                      </span>
                    </div>
                    <CameraCaptureButton
                      label="Tankrechnung fotografieren"
                      value={returnState.fuelReceiptPhoto}
                      onChange={async (event) => {
                        const file = event.target.files?.[0];
                        if (!file) return;
                        const dataUrl = await fileToDataUrl(file);
                        setReturnState((prev) => ({
                          ...prev,
                          fuelReceiptReceived: true,
                          fuelReceiptPhoto: dataUrl,
                          error: "",
                        }));
                      }}
                    />
                  </div>
                  {returnKmLowerThanHandover && (
                    <div className="authError">
                      Der Rückgabe-KM ist niedriger als der dokumentierte Übergabe-KM.
                      Korrigiere den Rückgabe-KM oder trage hier den korrigierten
                      Übergabe-KM ein. Diese Korrektur wird in der Rücknahme dokumentiert.
                    </div>
                  )}
                  {returnKmLowerThanHandover && (
                    <label>
                      Übergabe-KM korrigieren
                      <input
                        type="number"
                        placeholder="Korrigierter KM-Stand bei Übergabe"
                        value={returnState.correctedKmStart}
                        onChange={(event) =>
                          setReturnState((prev) => ({
                            ...prev,
                            correctedKmStart: event.target.value,
                            error: "",
                          }))
                        }
                      />
                    </label>
                  )}
                  <div className="returnDamageDocumentation">
                    {activeReturnVehicleDamages.length > 0 && (
                      <div className="knownDamagePanel">
                        <div className="historyMediaHeader damageSectionHeader">
                          <div>
                            <strong>Bereits bekannte offene Schäden</strong>
                            <span>
                              Diese Schäden bleiben am Fahrzeug aktiv und müssen nicht
                              erneut dokumentiert werden.
                            </span>
                          </div>
                          <span>{activeReturnVehicleDamages.length} offen</span>
                        </div>
                        <div className="damageHistoryList">
                          {activeReturnVehicleDamages.map((entry, index) => {
                            const photos =
                              Array.isArray(entry.photos) && entry.photos.length > 0
                                ? entry.photos
                                : entry.url
                                  ? [{ id: entry.id, url: entry.url }]
                                  : [];
                            return (
                              <div
                                key={`return_known_${entry.sourceBookingId}_${entry.id || index}`}
                                className="damageHistoryItem"
                              >
                                <div className="mediaTimelineGrid compactMediaGrid">
                                  {photos.map((photo, photoIndex) => (
                                    <div
                                      key={`${entry.id}_${photo.id || photoIndex}`}
                                      className="mediaPreview damage compact"
                                    >
                                      <img
                                        src={photo.url}
                                        alt={`Bekannter Schaden ${index + 1}, Foto ${photoIndex + 1}`}
                                      />
                                    </div>
                                  ))}
                                  {photos.length === 0 && (
                                    <div className="missingMediaBox">Kein Bild</div>
                                  )}
                                </div>
                                <div>
                                  <strong>Bereits dokumentiert</strong>
                                  <span>{entry.description || "Ohne Beschreibung"}</span>
                                  <small>
                                    {entry.sourceDate
                                      ? fmtDateTime(entry.sourceDate)
                                      : ""}
                                  </small>
                                  <div className="actions">
                                    <button
                                      type="button"
                                      className="btn"
                                      onClick={() =>
                                        updateActiveDamageLifecycle(
                                          entry,
                                          "repaired"
                                        )
                                      }
                                    >
                                      Als behoben markieren
                                    </button>
                                    <button
                                      type="button"
                                      className="btn dangerSubtle"
                                      onClick={() =>
                                        updateActiveDamageLifecycle(
                                          entry,
                                          "deleted"
                                        )
                                      }
                                    >
                                      Schaden löschen
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div className="historyMediaHeader damageSectionHeader">
                      <div>
                        <strong>Neue Schäden bei Rückgabe</strong>
                        <span>
                          Zu jedem beschriebenen Schaden ist mindestens ein zusätzliches
                          Foto verpflichtend. Leere Zeilen werden nicht gespeichert.
                        </span>
                      </div>
                      <span>
                        {relevantReturnDamages.length} Schaden
                        {relevantReturnDamages.length === 1 ? "" : "fälle"}
                      </span>
                    </div>

                    <div className="returnDamageEntryList">
                      {(returnState.returnDamageEntries || []).map((entry, index) => {
                        const description = String(entry.description || "");
                        const photos = Array.isArray(entry.photos) ? entry.photos : [];
                        const isRelevant = Boolean(description.trim()) || photos.length > 0;
                        const isIncomplete =
                          isRelevant && (!description.trim() || photos.length === 0);
                        return (
                          <div
                            key={entry.id}
                            className={`returnDamageEntryCard ${isIncomplete ? "incomplete" : ""}`}
                          >
                            <div className="damageEntryHeader">
                              <strong>
                                {isRelevant
                                  ? `Schaden ${index + 1}`
                                  : "Weiteren Schaden dokumentieren (optional)"}
                              </strong>
                              {isRelevant && (
                                <span className={isIncomplete ? "miniBadge warn" : "miniBadge ok"}>
                                  {isIncomplete ? "Foto/Beschreibung fehlt" : "vollständig"}
                                </span>
                              )}
                            </div>

                            <label>
                              Beschreibung
                              <textarea
                                rows={3}
                                placeholder="Schaden eindeutig beschreiben, z. B. Kratzer an der rechten hinteren Tür"
                                value={description}
                                onChange={(event) =>
                                  updateReturnDamageEntry(entry.id, {
                                    description: event.target.value,
                                  })
                                }
                              />
                            </label>

                            <div className="dualUploadGrid">
                              <label className="cameraButtonCard">
                                <div className="cameraButtonText">
                                  <strong>Schaden fotografieren</strong>
                                  <span>Mindestens ein Foto erforderlich</span>
                                </div>
                                <input
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  multiple
                                  onChange={(event) =>
                                    handleReturnDamagePhotoUpload(
                                      entry.id,
                                      event.target.files
                                    )
                                  }
                                />
                              </label>
                              <UploadFallbackCard
                                label="Schadenfotos hochladen"
                                description="Galerie oder Desktop-Datei verwenden"
                                multiple
                                onChange={(event) =>
                                  handleReturnDamagePhotoUpload(
                                    entry.id,
                                    event.target.files
                                  )
                                }
                              />
                            </div>

                            {photos.length > 0 && (
                              <div className="returnDamagePhotoGrid">
                                {photos.map((photo, photoIndex) => (
                                  <div key={photo.id} className="returnDamagePhoto">
                                    <img
                                      src={photo.url}
                                      alt={`Schaden ${index + 1}, Foto ${photoIndex + 1}`}
                                    />
                                    <button
                                      type="button"
                                      className="btn dangerSubtle"
                                      onClick={() =>
                                        removeReturnDamagePhoto(entry.id, photo.id)
                                      }
                                    >
                                      Foto entfernen
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {isIncomplete && (
                              <div className="authError">
                                Zu diesem Schaden sind eine Beschreibung und mindestens
                                ein Foto erforderlich.
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {returnTab === "finish" && (
              <div className="wizardSection wizardSectionActive returnStepPanel returnFinishPanel">
                <div className="wizardStepCard">
                  <div className="wizardStepIntro">
                    <span className="sectionTag">Schritt 4</span>
                    <h4>Rücknahme abschließen</h4>
                  </div>
                    <div className="dashRow">
                      <div className="dashItem">
                        <strong>Pflichtfotos</strong>
                        <span>{capturedPhotos} / 4</span>
                      </div>
                      <div className="dashItem">
                        <strong>KM Übergabe</strong>
                        <span>{returnState.correctedKmStart || booking.kmStart || "-"}</span>
                      </div>
                      <div className="dashItem">
                        <strong>Kilometerstand</strong>
                        <span>{returnState.kmEnd || "-"}</span>
                    </div>
                    <div className="dashItem">
                      <strong>Tankstand</strong>
                      <span>{returnState.fuelEnd || "-"}</span>
                    </div>
                    <div className="dashItem">
                      <strong>Tankrechnung</strong>
                      <span>
                        {returnState.fuelReceiptPhoto
                          ? "Foto vorhanden"
                          : "Pflichtfoto fehlt"}
                      </span>
                    </div>
                    <div className="dashItem">
                      <strong>Neue Schäden</strong>
                      <span>
                        {relevantReturnDamages.length} mit{" "}
                        {relevantReturnDamages.reduce(
                          (sum, damage) => sum + (damage.photos || []).length,
                          0
                        )}{" "}
                        Foto(s)
                      </span>
                    </div>
                    <div className="dashItem">
                      <strong>Flottenstatus</strong>
                      <span>{loaner?.status || "-"}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {returnState.error && <div className="authError">{returnState.error}</div>}
            {canRestoreReturnDraft(returnState) && (
              <div className="draftSaveHint">
                <span>
                  Rücknahme-Entwurf wird lokal gesichert{returnDraftSavedAt ? `: ${fmtDateTime(returnDraftSavedAt)}` : ""}.
                </span>
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    localStorage.removeItem(RETURN_DRAFT_STORAGE_KEY);
                    setReturnDraftSavedAt(null);
                    setReturnState(getDefaultReturnState(booking));
                    setReturnTabs((prev) => ({ ...prev, [booking.id]: "employee" }));
                  }}
                >
                  Entwurf löschen
                </button>
              </div>
            )}
          </div>

          <div className="wizardBottomBar">
            <div className="wizardBottomMeta">
              <strong>Schritt {activeIndex + 1} von {returnSteps.length}</strong>
              <span>{returnSteps[activeIndex]?.label}</span>
            </div>
            <div className="wizardBottomActions">
              <button
                type="button"
                className="btn"
                onClick={() =>
                  navigateReturnStep(
                    booking.id,
                    returnSteps[Math.max(0, activeIndex - 1)].id
                  )
                }
                disabled={activeIndex === 0 || returnState.isSubmitting}
              >
                Zurück
              </button>
              {activeIndex < returnSteps.length - 1 ? (
                <button
                  type="button"
                  className="btn primary"
                  onClick={() =>
                    navigateReturnStep(
                      booking.id,
                      returnSteps[Math.min(returnSteps.length - 1, activeIndex + 1)].id
                    )
                  }
                  disabled={returnState.isSubmitting || !canAdvanceReturn}
                >
                  Weiter
                </button>
              ) : (
                <button
                  type="button"
                  className="btn primary"
                  onClick={completeReturn}
                  disabled={returnState.isSubmitting}
                >
                  {returnState.isSubmitting ? "Speichere ..." : "Rücknahme abschließen"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTaskRow = (card) => {
    const progress = calcProgress(card.subs || []);
    const relatedBooking = bookings.find(
      (booking) => isBookingLinkedToCard(booking, card) && isActiveBooking(booking)
    );
    const escalationLevel = getEscalationLevel(card);
    const customerDisplay =
      [card.customerFirstName, card.customerLastName].filter(Boolean).join(" ") ||
      card.customerName ||
      "-";
    const vehicleDisplay =
      [card.vehicleData?.brand, card.vehicleData?.model].filter(Boolean).join(" ") ||
      card.licensePlate ||
      "-";
    const techName = getEmployeeNameById(technicians, card.assignedTech);
    const isOverdue =
      card.due && new Date(card.due).getTime() < Date.now() && card.workStatus !== WORK_STATUS.DELIVERED;

    return (
      <button
        type="button"
        key={card.id}
        className={`taskRow escalation-${escalationLevel} ${isOverdue ? "overdue" : ""}`}
        onClick={() => openTaskDetail(card.id)}
      >
        <span className="taskRowStatusBar" />
        <div className="taskRowMain">
          <strong>{card.title || "Ohne Titel"}</strong>
          <span>
            #{card.orderNumber || "-"} · {customerDisplay} · {card.licensePlate || "-"}
          </span>
        </div>
        <div className="taskRowMeta">
          <span>{getCaseTypeLabel(card.caseType)}</span>
          <span>{vehicleDisplay}</span>
          <span>{techName || getOperationalStatusLabel(card)}</span>
          <span>{fmtDate(card.due)}</span>
        </div>
        <div className="taskRowBadges">
          {card.needsLoaner && <span className="miniBadge loaner">Leihwagen</span>}
          {relatedBooking && <span className="miniBadge ok">{getBookingStatusLabel(relatedBooking.status)}</span>}
          {isOverdue && <span className="miniBadge danger">überfällig</span>}
          {card.blockerType && <span className="miniBadge warn">Blocker</span>}
          <span className="miniBadge">{progress}%</span>
        </div>
      </button>
    );
  };

  const renderCard = (card, { forceExpanded = false, inModal = false } = {}) => {
    const progress = calcProgress(card.subs || []);
    const relatedBooking = bookings.find(
      (booking) => isBookingLinkedToCard(booking, card) && isActiveBooking(booking)
    );
    const isExpanded = forceExpanded || Boolean(expandedCards[card.id]);
    const currentDetailTab = detailTabs[card.id] || "work";
    const escalationLevel = getEscalationLevel(card);
    const customerDisplay =
      [card.customerFirstName, card.customerLastName].filter(Boolean).join(" ") ||
      card.customerName ||
      "-";
    const gateStatus = getOrderGateStatus(card, relatedBooking);
    const confirmationProtocol = buildConfirmationProtocol({ card, booking: relatedBooking });
    const confirmationSteps = buildOrderConfirmationSteps(card, relatedBooking);
    const isDepartmentFocus = ROLES.includes(activeViewLabel) && !isAdmin;
    const canEditCard = canEditDepartmentCard(card);
    const canRequestTechnicianAssignment =
      canEditCard || (isIntakeStaff && card.role === "Werkstatt");
    const canRequestForeignDepartmentChange =
      isIntakeStaff && !canEditCard && (card.role || DEFAULT_DEPARTMENT) !== DEFAULT_DEPARTMENT;
    const canSeeLoanerAndConfirmation =
      [DEFAULT_DEPARTMENT, "Übergabe"].includes(card.role || DEFAULT_DEPARTMENT) &&
      (isAdmin || [DEFAULT_DEPARTMENT, "Übergabe"].includes(activeViewLabel));
    const visibleTabs = getRelevantDepartmentTabs(card.role, isAdmin || isWorkshopLead);
    const activeDetailTab = visibleTabs.some((tab) => tab.id === currentDetailTab)
      ? currentDetailTab
      : "work";
    const departmentPresets = getDepartmentTodoPresets(card.role);
    const route = getDepartmentRoute(card);
    const handoverIssues = getHandoverIssues(card);
    const workshopMedia = (card.media || []).filter((entry) => entry.group === "Werkstatt" || entry.url);

    return (
      <div
        key={card.id}
        className={`card taskCard ${isExpanded ? "expanded" : ""} escalation-${escalationLevel}`}
      >
        <div className="cardHeader">
          <div>
            <h3>{card.title}</h3>
            <div className="meta">
              <span>#{card.orderNumber}</span>
              <span>{customerDisplay}</span>
              <span>{card.licensePlate}</span>
            </div>
          </div>
          <span className="pill">{card.prio}</span>
        </div>

        {!isExpanded && (
          <>
            <div className="contextSummaryGrid">
              <div className="summaryTile">
                <span>Titel / Anliegen</span>
                <strong>{card.title || "-"}</strong>
              </div>
              <div className="summaryTile">
                <span>Falltyp</span>
                <strong>{getCaseTypeLabel(card.caseType)}</strong>
              </div>
              <div className="summaryTile">
                <span>Fahrzeug</span>
                <strong>
                  {[card.vehicleData?.brand, card.vehicleData?.model].filter(Boolean).join(" ") ||
                    card.licensePlate ||
                    "-"}
                </strong>
              </div>
              <div className="summaryTile">
                <span>Standort</span>
                <strong>{getBranchLabel(card.branch) || "-"}</strong>
              </div>
              <div className="summaryTile">
                <span>Status</span>
                <strong>{WORK_STATUS_LABELS[card.workStatus] || card.workStatus}</strong>
              </div>
              <div className="summaryTile">
                <span>Kundenfreigabe</span>
                <strong>{gateStatus}</strong>
              </div>
              <div className="summaryTile">
                <span>Fällig</span>
                <strong>{fmtDate(card.due)}</strong>
              </div>
              {!isDepartmentFocus && (
                <div className="summaryTile">
                  <span>E-Mail</span>
                  <strong>{getEmailStatusLabel(card, relatedBooking)}</strong>
                </div>
              )}
              <div className="summaryTile">
                <span>Nächste Abteilung</span>
                <strong>{card.nextDepartment || "Abschluss"}</strong>
              </div>
              <div className="summaryTile">
                <span>Eskalation</span>
                <strong>{getEscalationLabel(escalationLevel)}</strong>
              </div>
            </div>

            <div className="progress">
              <div className="bar" style={{ width: `${progress}%` }} />
              <span className="pct">{progress}%</span>
            </div>
          </>
        )}

        {canSeeLoanerAndConfirmation && (
        <div className="loanerPanel">
          <label className="checkRow">
            <input
              type="checkbox"
              checked={card.needsLoaner || false}
              onChange={() => toggleLoanerRequirement(card)}
              disabled={!canEditCard}
            />
            Leihwagen für diesen Auftrag benötigt
          </label>

          {card.needsLoaner && (
            <div className="actions">
              <button
                type="button"
                className="btn primary"
                onClick={() => startLoanerWizard(card)}
                disabled={Boolean(relatedBooking) || !canEditCard}
              >
                {relatedBooking ? "Leihwagen bereits reserviert" : "Leihwagen-Dokumentation starten"}
              </button>
            </div>
          )}

          {relatedBooking && (
            <div className="meta bookingMeta">
              <span className="pill bookingPill">
                {relatedBooking.loanerPlate} bis {fmtDate(relatedBooking.expectedReturnDate)}
              </span>
              <span className="pill">{relatedBooking.status}</span>
            </div>
          )}
          <div className="confirmationStepGrid compact">
            {confirmationSteps.map((step) => (
              <div key={`${card.id}_${step.label}`} className={`confirmationStep ${step.done ? "done" : ""}`}>
                <span>{step.label}</span>
                <strong>{step.value}</strong>
              </div>
            ))}
          </div>
          {confirmationProtocol.length > 0 && (
            <div className="loanerAuditTimeline compactProtocol">
              {confirmationProtocol.map((entry) => (
                <span key={`${card.id}_${entry.label}_${entry.value}`}>
                  {entry.label}: <strong>{fmtDateTime(entry.value)}</strong>
                </span>
              ))}
            </div>
          )}
        </div>
        )}

        <div className="cardActionsBar">
          {inModal ? (
            <button type="button" className="btn" onClick={closeTaskDetail}>
              Übersicht schließen
            </button>
          ) : (
            <button type="button" className="btn" onClick={() => toggleCardExpanded(card.id)}>
              {isExpanded ? "Details reduzieren" : "Details anzeigen"}
            </button>
          )}
          <button
            type="button"
            className="btn primary"
            onClick={() => (card.role === "Werkstatt" ? openWorkshopHandover(card) : doHandover(card))}
          >
            {card.blockerType
              ? "Wartet auf Freigabe"
              : !canEditCard
                ? "Nur Ansicht"
                : card.nextDepartment
                  ? card.role === "Werkstatt"
                    ? "Fertig melden"
                    : `Bereit für ${card.nextDepartment}`
                  : card.role === "Werkstatt"
                    ? "Fertig melden"
                    : "Task abschließen"}
          </button>
        </div>

        {isExpanded && (
          <div className="cardDetailStack">
            <div className="taskDetailHero">
              <div className="summaryBadge">
                <span>Aktuelle Abteilung</span>
                <strong>{card.role}</strong>
              </div>
              <div className="summaryBadge">
                <span>Nächster Schritt</span>
                <strong>{card.nextDepartment || "Abschluss"}</strong>
              </div>
              <div className="summaryBadge">
                <span>Quelle</span>
                <strong>{card.sourceLabel || "manuell"}</strong>
              </div>
              <div className="summaryBadge">
                <span>Referenz</span>
                <strong>{card.externalReference || "-"}</strong>
              </div>
            </div>

            <div className="processRouteBar">
              {route.map((department, index) => {
                const currentIndex = route.indexOf(card.role || DEFAULT_DEPARTMENT);
                const isCurrent = department === card.role;
                const isPast = currentIndex > index;
                return (
                  <div
                    key={`${card.id}_${department}`}
                    className={`processRouteStep ${isCurrent ? "active" : ""} ${isPast ? "done" : ""}`}
                  >
                    <span>{index + 1}</span>
                    <strong>{department}</strong>
                  </div>
                );
              })}
            </div>

            <div className="detailTabBar">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`detailTabButton ${activeDetailTab === tab.id ? "active" : ""}`}
                  onClick={() =>
                    setDetailTabs((prev) => ({
                      ...prev,
                      [card.id]: tab.id,
                    }))
                  }
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeDetailTab === "info" && (
              <div className="detailTabPanel">
                <div className="cardHeader">
                  <div>
                    <h4>Infos und Verlauf</h4>
                    <p className="mutedText">
                      Kontext, Fahrzeugdaten und Historie. Operative Aktionen liegen im To-do-Terminal.
                    </p>
                  </div>
                </div>
                <div className="contextSummaryGrid">
                  <div className="summaryTile">
                    <span>Titel / Anliegen</span>
                    <strong>{card.title || "-"}</strong>
                  </div>
                  <div className="summaryTile">
                    <span>Falltyp</span>
                    <strong>{getCaseTypeLabel(card.caseType)}</strong>
                  </div>
                  <div className="summaryTile">
                    <span>Auftragstyp</span>
                    <strong>{getOrderTypeLabel(card.loanerFlow?.orderType)}</strong>
                  </div>
                  <div className="summaryTile">
                    <span>Kunde</span>
                    <strong>{customerDisplay}</strong>
                  </div>
                  <div className="summaryTile">
                    <span>Kundenfreigabe</span>
                    <strong>{gateStatus}</strong>
                  </div>
                  <div className="summaryTile">
                    <span>Status</span>
                    <strong>{WORK_STATUS_LABELS[card.workStatus] || card.workStatus}</strong>
                  </div>
                  <div className="summaryTile">
                    <span>Standort</span>
                    <strong>{getBranchLabel(card.branch) || "-"}</strong>
                  </div>
                  <div className="summaryTile">
                    <span>Fällig</span>
                    <strong>{fmtDate(card.due)}</strong>
                  </div>
                  <div className="summaryTile">
                    <span>Geplante Dauer</span>
                    <strong>{formatHours(getEstimatedHours(card))}</strong>
                  </div>
                  <div className="summaryTile">
                    <span>Nächste Abteilung</span>
                    <strong>{card.nextDepartment || "Abschluss"}</strong>
                  </div>
                  <div className="summaryTile">
                    <span>Marke / Modell</span>
                    <strong>
                      {[card.vehicleData?.brand, card.vehicleData?.model].filter(Boolean).join(" ") || "-"}
                    </strong>
                  </div>
                  <div className="summaryTile">
                    <span>Erstzulassung</span>
                    <strong>{fmtDate(card.vehicleData?.firstRegistrationDate)}</strong>
                  </div>
                  <div className="summaryTile">
                    <span>VIN</span>
                    <strong>{card.vehicleData?.vin || "-"}</strong>
                  </div>
                  <div className="summaryTile">
                    <span>Motor / Getriebe</span>
                    <strong>
                      {[card.vehicleData?.engineType, card.vehicleData?.transmissionType]
                        .filter(Boolean)
                        .join(" / ") || "-"}
                    </strong>
                  </div>
                  <div className="summaryTile">
                    <span>Kilometerstand</span>
                    <strong>{card.vehicleData?.mileage || "-"}</strong>
                  </div>
                  <div className="summaryTile">
                    <span>Nationaler Code</span>
                    <strong>{card.vehicleData?.nationalCode || "-"}</strong>
                  </div>
                  <div className="summaryTile">
                    <span>Gewicht</span>
                    <strong>
                      {card.vehicleData?.curbWeight || card.vehicleData?.grossWeight
                        ? `${card.vehicleData?.curbWeight || "-"} / ${card.vehicleData?.grossWeight || "-"} kg`
                        : "-"}
                    </strong>
                  </div>
                </div>

                {card.role === "Werkstatt" && (
                  <label>
                    Techniker
                    <select
                      value={card.assignedTech || ""}
                      onChange={(event) => assignTechnician(card.id, event.target.value)}
                      disabled={!canEditCard}
                    >
                      <option value="">keiner</option>
                      {getEmployeesForBranchAndTypes(technicians, card.branch || profile?.branch, ["Techniker"]).map((tech) => (
                        <option key={tech.id} value={tech.id}>
                          {tech.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                {card.invoice && (
                  <div className="meta">
                    <span>Rechnung EUR {Number(card.invoice.total || 0).toFixed(2)}</span>
                    <button type="button" className="btn" onClick={() => toggleInvoicePaid(card.id)} disabled={!canEditCard}>
                      {card.invoice.paid ? "Bezahlt" : "Offen"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeDetailTab === "work" && (
              <div className="detailTabPanel">
                {card.role === "Werkstatt" && (
                  <div className="workshopActionStrip">
                    <button type="button" className="workshopBigAction start" onClick={() => startWorkshopCard(card)} disabled={!canEditCard}>
                      <span>1</span>
                      <strong>{card.workStatus === WORK_STATUS.IN_WORK ? "Arbeit läuft" : "Arbeit starten"}</strong>
                      <small>{card.assignedTechName || getEmployeeNameById(technicians, card.assignedTech) || "Auftrag übernehmen"}</small>
                    </button>
                    <button type="button" className="workshopBigAction photo" onClick={() => openWorkshopPhoto(card)} disabled={!canEditCard}>
                      <span>2</span>
                      <strong>Foto hinzufügen</strong>
                      <small>Schaden, Teil oder erledigte Arbeit</small>
                    </button>
                    <button type="button" className="workshopBigAction blocked" onClick={() => openWorkshopBlocker(card)} disabled={!canEditCard}>
                      <span>3</span>
                      <strong>Ich kann nicht weiter</strong>
                      <small>Teil, Freigabe oder Rückfrage fehlt</small>
                    </button>
                    <button type="button" className="workshopBigAction done" onClick={() => openWorkshopHandover(card)} disabled={!canEditCard}>
                      <span>4</span>
                      <strong>Fertig melden</strong>
                      <small>Weiter an die nächste Stelle</small>
                    </button>
                  </div>
                )}
                <div className="departmentActionTerminal">
                  <div className="cardHeader">
                    <div>
                      <h4>{card.role === "Werkstatt" ? "Rückfrage oder Blocker" : "Arbeits-Terminal"}</h4>
                      <p className="mutedText">
                        {card.role === "Werkstatt"
                          ? "Nur nutzen, wenn du nicht weiterkommst oder eine Rückmeldung brauchst."
                          : "Blocker, Erinnerung oder abweichende Übergabe werden zur Freigabe an Management / Annahme gemeldet."}
                      </p>
                    </div>
                  </div>
                  {handoverIssues.length > 0 && (
                    <div className="handoverCheckBox warn">
                      <strong>Abschluss-Check</strong>
                      {handoverIssues.map((issue) => (
                        <span key={`${card.id}_${issue}`}>{issue}</span>
                      ))}
                    </div>
                  )}
                  {handoverIssues.length === 0 && (
                    <div className="handoverCheckBox ok">
                      <strong>Abschluss-Check</strong>
                      <span>Keine offenen Pflichtpunkte erkannt.</span>
                    </div>
                  )}
                  <div className="addRow">
                    <label>
                      Blocker melden
                      <select
                        value={card.blockerType || ""}
                        onChange={(event) => {
                          const value = event.target.value;
                          updateCard(card.id, (current) => ({
                            ...current,
                            blockerType: value,
                            workStatus: value ? WORK_STATUS.BLOCKED : current.workStatus,
                          }));
                          if (value) {
                            requestDepartmentApproval(card, `Blocker gemeldet: ${getBlockerLabel(value)}.`);
                          }
                        }}
                        disabled={!canEditCard}
                      >
                        {BLOCKER_OPTIONS.map((option) => (
                          <option key={option.value || "none"} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Manuelle Erinnerung
                      <input
                        type="datetime-local"
                        value={card.nextActionDue || ""}
                        onChange={(event) =>
                          updateCard(card.id, (current) => ({
                            ...current,
                            nextActionDue: event.target.value,
                            history: [
                              ...(current.history || []),
                              {
                                ts: new Date().toISOString(),
                                by: profile?.full_name || session?.user?.email || CREATOR,
                                action: `Manuelle Erinnerung gesetzt: ${event.target.value || "-"}`,
                              },
                            ],
                          }))
                        }
                        disabled={!canEditCard}
                      />
                    </label>
                  </div>
                  <div className="addRow">
                    <label>
                      Übergabe vorab anfordern
                      <select
                        value=""
                        onChange={(event) => {
                          forwardCardToDepartment(card, event.target.value);
                          event.target.value = "";
                        }}
                        disabled={!canEditCard}
                      >
                        <option value="">Abteilung wählen</option>
                        {ROLES.filter((role) => role !== card.role).map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Abgabe an anderen Mitarbeiter anfordern
                      <select
                        value=""
                        onChange={(event) => {
                          const nextEmployee = technicians.find((employee) => employee.id === event.target.value);
                          if (nextEmployee) {
                            requestDepartmentApproval(card, `Abgabe an ${nextEmployee.name} angefordert.`);
                          }
                          event.target.value = "";
                        }}
                        disabled={!canRequestTechnicianAssignment}
                      >
                        <option value="">Mitarbeiter wählen</option>
                        {getEmployeesForBranchAndTypes(
                          technicians,
                          card.branch || profile?.branch,
                          card.role === "Fahrzeugaufbereitung"
                            ? ["Aufbereitung"]
                            : card.role === "Spenglerei"
                              ? ["Lackierer"]
                              : card.role === "Werkstatt"
                                ? ["Techniker"]
                                : []
                        ).map((employee) => (
                          <option key={employee.id} value={employee.id}>
                            {employee.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>
                {card.role === "Werkstatt" && (
                  <div className="workshopQuickActions">
                    <div className="cardHeader">
                      <div>
                        <h4>Häufige Werkstattarbeiten</h4>
                        <p className="mutedText">
                          Antippen, wenn diese Arbeit zusätzlich dokumentiert werden soll.
                        </p>
                      </div>
                    </div>
                    <div className="quickActionGrid">
                      {[
                        "Bremsen prüfen",
                        "Flüssigkeiten ergänzen",
                        "Diagnose durchführen",
                        "Probefahrt durchführen",
                      ].map((label) => (
                        <button
                          key={`${card.id}_${label}`}
                          type="button"
                          className="btn"
                          onClick={() => addWorkshopQuickAction(card, label)}
                          disabled={!canEditCard}
                        >
                          {label}
                        </button>
                      ))}
                      <button
                        type="button"
                        className="btn"
                        onClick={() => addWorkshopQuickAction(card, "Zusatzarbeit / Kostenfreigabe erforderlich", { requestApproval: true })}
                        disabled={!canEditCard}
                      >
                        Zusatzfreigabe anfordern
                      </button>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => {
                          updateCard(card.id, (current) => ({
                            ...current,
                            blockerType: "parts",
                            workStatus: WORK_STATUS.BLOCKED,
                          }));
                          requestDepartmentApproval(card, "Blocker gemeldet: Teil / Material fehlt.");
                        }}
                        disabled={!canEditCard}
                      >
                        Teil fehlt melden
                      </button>
                    </div>
                  </div>
                )}
                {card.role === "Werkstatt" && (
                  <div className="workshopPhotoPanel">
                    <div className="cardHeader">
                      <div>
                        <h4>Fotos zum Auftrag</h4>
                        <p className="mutedText">Klein sichtbar, antippen für Detailansicht.</p>
                      </div>
                      <button type="button" className="btn" onClick={() => openWorkshopPhoto(card)} disabled={!canEditCard}>
                        Foto hinzufügen
                      </button>
                    </div>
                    <div className="workshopPhotoGrid">
                      {workshopMedia.slice(0, 12).map((entry, index) => (
                        <button
                          key={entry.id || `${entry.url}_${index}`}
                          type="button"
                          className="mediaPreview compact clickableMedia"
                          onClick={() => setImageViewer({ open: true, photos: workshopMedia, index })}
                        >
                          {entry.url ? <img src={entry.url} alt={entry.label || "Werkstattfoto"} /> : <span>kein Bild</span>}
                          <span>{entry.label || "Foto"}</span>
                        </button>
                      ))}
                      {workshopMedia.length === 0 && <div className="mutedText">Noch keine Werkstattfotos vorhanden.</div>}
                    </div>
                  </div>
                )}
                {canRequestForeignDepartmentChange && (
                  <div className="intakeRequestPanel">
                    <div className="cardHeader">
                      <div>
                        <h4>Annahme-Anforderung</h4>
                        <p className="mutedText">
                          Du siehst diesen Abteilungsauftrag nur lesend. Änderungen werden sauber zur Freigabe vorgemerkt.
                        </p>
                      </div>
                      <span className="miniBadge">nur Anfrage</span>
                    </div>
                    <div className="intakeRequestGrid">
                      <label>
                        Art
                        <select
                          value={partDrafts[`intakeRequestType_${card.id}`] || "change_request"}
                          onChange={(event) =>
                            setPartDrafts((prev) => ({
                              ...prev,
                              [`intakeRequestType_${card.id}`]: event.target.value,
                            }))
                          }
                        >
                          {INTAKE_REQUEST_TYPES.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Zielabteilung
                        <select
                          value={partDrafts[`intakeRequestTarget_${card.id}`] || ""}
                          onChange={(event) =>
                            setPartDrafts((prev) => ({
                              ...prev,
                              [`intakeRequestTarget_${card.id}`]: event.target.value,
                            }))
                          }
                        >
                          <option value="">nur bei Weitergabe nötig</option>
                          {ROLES.filter((role) => role !== card.role).map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Erinnerung
                        <input
                          type="datetime-local"
                          value={partDrafts[`intakeRequestDue_${card.id}`] || ""}
                          onChange={(event) =>
                            setPartDrafts((prev) => ({
                              ...prev,
                              [`intakeRequestDue_${card.id}`]: event.target.value,
                            }))
                          }
                        />
                      </label>
                    </div>
                    <div className="questionComposer">
                      <input
                        type="text"
                        placeholder="Was soll die Abteilung oder das Management prüfen?"
                        value={partDrafts[`intakeRequestText_${card.id}`] || ""}
                        onChange={(event) =>
                          setPartDrafts((prev) => ({
                            ...prev,
                            [`intakeRequestText_${card.id}`]: event.target.value,
                          }))
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter") submitIntakeDepartmentRequest(card);
                        }}
                      />
                      <button type="button" className="btn primary" onClick={() => submitIntakeDepartmentRequest(card)}>
                        Anforderung speichern
                      </button>
                    </div>
                  </div>
                )}
                {departmentPresets.length > 0 && (
                  <div className="departmentTodoComposer">
                    <label>
                      Standard-To-do hinzufügen
                      <select
                        value=""
                        onChange={(event) => {
                          addDepartmentTodoToCard(card.id, event.target.value);
                          event.target.value = "";
                        }}
                        disabled={!canEditCard}
                      >
                        <option value="">Bitte auswählen</option>
                        {departmentPresets.map((preset) => (
                          <option key={preset} value={preset}>
                            {preset}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="questionComposer">
                      <input
                        type="text"
                        placeholder="Eigenes To-do hinzufügen"
                        value={partDrafts[`todo_${card.id}`] || ""}
                        onChange={(event) =>
                          setPartDrafts((prev) => ({
                            ...prev,
                            [`todo_${card.id}`]: event.target.value,
                          }))
                        }
                        disabled={!canEditCard}
                      />
                      <button
                        type="button"
                        className="btn"
                        onClick={() => {
                          addDepartmentTodoToCard(card.id, partDrafts[`todo_${card.id}`]);
                          setPartDrafts((prev) => ({ ...prev, [`todo_${card.id}`]: "" }));
                        }}
                        disabled={!canEditCard}
                      >
                        Hinzufügen
                      </button>
                    </div>
                  </div>
                )}
                <ul className={`subs compactList ${card.role === "Werkstatt" ? "workshopTodoTiles" : ""}`}>
                  {(card.subs || []).length === 0 && (
                    <li className="mutedText">Noch keine To-dos für diese Abteilung angelegt.</li>
                  )}
                  {(card.subs || []).map((sub) => (
                    <li key={sub.id} className={sub.done ? "done" : ""}>
                      <label>
                        <input
                          type="checkbox"
                          checked={sub.done}
                          onChange={() => toggleSub(card.id, sub.id)}
                          disabled={!canEditCard}
                        />
                        {sub.text}
                      </label>
                      {card.role === "Werkstatt" && shouldOfferTodoPhoto(sub.text) && (
                        <button
                          type="button"
                          className="todoPhotoButton"
                          onClick={() => openWorkshopPhoto(card, `Foto zu To-do: ${sub.text}`, sub.id)}
                          disabled={!canEditCard}
                        >
                          Foto hinzufügen
                        </button>
                      )}
                      {canEditCard && (
                        <button
                          type="button"
                          className="todoDeleteButton"
                          onClick={() => deleteDepartmentTodoFromCard(card.id, sub.id)}
                          aria-label={`To-do ${sub.text} löschen`}
                          title="To-do löschen"
                        >
                          ×
                        </button>
                      )}
                    </li>
                  ))}
                </ul>

                {card.parts?.length > 0 && (
                  <div className="partsList">
                    <strong>Teile</strong>
                    {(card.parts || []).map((part) => (
                      <div key={part.id} className="partRow">
                        <span>{part.name}</span>
                        <label>
                          <input
                            type="checkbox"
                            checked={part.delivered || false}
                            onChange={() => togglePartDelivered(card.id, part.id)}
                            disabled={!canEditCard}
                          />
                          geliefert
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={part.installed || false}
                            onChange={() => togglePartInstalled(card.id, part.id)}
                            disabled={!canEditCard}
                          />
                          eingebaut
                        </label>
                      </div>
                    ))}
                    <div className="addRow">
                      <input
                        type="text"
                        placeholder="Teil hinzufügen"
                        value={partDrafts[card.id] || ""}
                        onChange={(event) =>
                          setPartDrafts((prev) => ({
                            ...prev,
                            [card.id]: event.target.value,
                          }))
                        }
                        disabled={!canEditCard}
                      />
                      <button type="button" className="btn" onClick={() => addPartToCard(card.id)} disabled={!canEditCard}>
                        +
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeDetailTab === "work" && (
              <div className="detailTabPanel">
                <div className="questionPanel">
                  <div className="cardHeader">
                    <div>
                      <h4>Rückfragen und Antworten</h4>
                      <p className="mutedText">
                        Offene Rückfragen bleiben direkt beim Task, damit nichts untergeht.
                      </p>
                    </div>
                  </div>
                  <div className="questionComposer">
                    <input
                      type="text"
                      placeholder="Rückfrage an die nächste oder vorige Abteilung"
                      value={questionDrafts[card.id] || ""}
                      onChange={(event) =>
                        setQuestionDrafts((prev) => ({
                          ...prev,
                          [card.id]: event.target.value,
                        }))
                      }
                      disabled={!canEditCard}
                    />
                    <button
                      type="button"
                      className="btn"
                      onClick={() => {
                        addTaskQuestion(card.id, questionDrafts[card.id]);
                        setQuestionDrafts((prev) => ({ ...prev, [card.id]: "" }));
                      }}
                      disabled={!canEditCard}
                    >
                      Rückfrage speichern
                    </button>
                  </div>
                  <div className="questionList">
                    {(card.questions || []).map((question) => (
                      <div key={question.id} className="questionCard">
                        <strong>{question.text}</strong>
                        <span>
                          {question.author || "intern"} · {fmtDateTime(question.createdAt)}
                        </span>
                        {question.answered ? (
                          <p>{question.answer}</p>
                        ) : (
                          <div className="questionAnswerRow">
                            <input
                              type="text"
                              placeholder="Antwort erfassen"
                              disabled={!canEditCard}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                  answerTaskQuestion(card.id, question.id, event.currentTarget.value);
                                  event.currentTarget.value = "";
                                }
                              }}
                            />
                            <button
                              type="button"
                              className="btn"
                              disabled={!canEditCard}
                              onClick={(event) => {
                                const input = event.currentTarget.previousElementSibling;
                                answerTaskQuestion(card.id, question.id, input?.value);
                                if (input) input.value = "";
                              }}
                            >
                              Antworten
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    {(card.questions || []).length === 0 && (
                      <div className="mutedText">Noch keine Rückfragen vorhanden.</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeDetailTab === "info" && (
              <div className="detailTabPanel">
                <div className="historyList">
                  {(card.history || []).length > 0 ? (
                    (card.history || [])
                      .slice()
                      .reverse()
                      .map((entry, index) => (
                        <div key={`${entry.ts || index}_${index}`} className="historyEntry">
                          <strong>{entry.action || "Eintrag"}</strong>
                          <span>
                            {entry.by || "intern"} · {fmtDateTime(entry.ts)}
                          </span>
                        </div>
                      ))
                  ) : (
                    <div className="mutedText">Noch kein Verlauf vorhanden.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderWorkshopBlockerModal = () => {
    if (!workshopBlockerModal.open) return null;
    const card = cards.find((item) => item.id === workshopBlockerModal.cardId);
    if (!card) return null;
    const blockerChoices = [
      { value: "parts", label: "Teil fehlt" },
      { value: "approval", label: "Freigabe fehlt" },
      { value: "customer", label: "Kunde muss gefragt werden" },
      { value: "loaner", label: "Fahrzeug fehlt" },
      { value: "internal", label: "Technische Rückfrage" },
      { value: "other", label: "Sonstiges" },
    ];
    return (
      <div className="modalOverlay" onMouseDown={() => setWorkshopBlockerModal({ open: false, cardId: "", reason: "parts", note: "" })}>
        <div className="modalCard workshopAssistantModal" onMouseDown={(event) => event.stopPropagation()}>
          <div className="modalHeader">
            <div>
              <h3>Ich kann nicht weiter</h3>
              <p className="mutedText">{card.orderNumber || card.title} wird blockiert und sauber dokumentiert.</p>
            </div>
            <button type="button" className="btn" onClick={() => setWorkshopBlockerModal({ open: false, cardId: "", reason: "parts", note: "" })}>
              Schließen
            </button>
          </div>
          <div className="assistantChoiceGrid">
            {blockerChoices.map((choice) => (
              <button
                key={choice.value}
                type="button"
                className={`assistantChoice ${workshopBlockerModal.reason === choice.value ? "active" : ""}`}
                onClick={() => setWorkshopBlockerModal((prev) => ({ ...prev, reason: choice.value }))}
              >
                {choice.label}
              </button>
            ))}
          </div>
          <label>
            Kurze Notiz
            <textarea
              rows={3}
              placeholder="Was fehlt oder wer muss reagieren?"
              value={workshopBlockerModal.note}
              onChange={(event) => setWorkshopBlockerModal((prev) => ({ ...prev, note: event.target.value }))}
            />
          </label>
          <div className="actions">
            <button type="button" className="btn" onClick={() => setWorkshopBlockerModal({ open: false, cardId: "", reason: "parts", note: "" })}>
              Abbrechen
            </button>
            <button type="button" className="btn primary" onClick={submitWorkshopBlocker}>
              Blocker speichern
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderWorkshopHandoverModal = () => {
    if (!workshopHandoverModal.open) return null;
    const card = cards.find((item) => item.id === workshopHandoverModal.cardId);
    if (!card) return null;
    const issues = getHandoverIssues(card);
    const openTodoCount = (card.subs || []).filter((sub) => !sub.done).length;
    const openQuestionCount = (card.questions || []).filter((question) => !question.answered).length;
    const targets = ["Fahrzeugaufbereitung", "Spenglerei", "Rechnung", "Annahme", "Übergabe", "Fertig"];
    return (
      <div className="modalOverlay" onMouseDown={() => setWorkshopHandoverModal({ open: false, cardId: "", target: "", note: "", acknowledgeWarnings: false })}>
        <div className="modalCard workshopAssistantModal" onMouseDown={(event) => event.stopPropagation()}>
          <div className="modalHeader">
            <div>
              <h3>Fertig melden</h3>
              <p className="mutedText">{card.orderNumber || card.title}: kurz prüfen und weitergeben.</p>
            </div>
            <button type="button" className="btn" onClick={() => setWorkshopHandoverModal({ open: false, cardId: "", target: "", note: "", acknowledgeWarnings: false })}>
              Schließen
            </button>
          </div>
          <div className={`handoverCheckBox ${issues.length ? "warn" : "ok"}`}>
            <strong>{issues.length ? "Bitte kurz prüfen" : "Bereit zur Weitergabe"}</strong>
            <span>{openTodoCount ? `${openTodoCount} To-do(s) nicht erledigt` : "Alle To-dos erledigt"}</span>
            <span>{openQuestionCount ? `${openQuestionCount} Rückfrage(n) offen` : "Keine Rückfragen offen"}</span>
            <span>{card.blockerType ? `Blocker aktiv: ${getBlockerLabel(card.blockerType)}` : "Kein Blocker aktiv"}</span>
            {issues.map((issue) => <span key={issue}>{issue}</span>)}
          </div>
          {issues.length > 0 && (
            <label className="checkRow">
              <input
                type="checkbox"
                checked={workshopHandoverModal.acknowledgeWarnings}
                onChange={(event) =>
                  setWorkshopHandoverModal((prev) => ({ ...prev, acknowledgeWarnings: event.target.checked }))
                }
              />
              Trotzdem weitergeben, Warnungen wurden geprüft.
            </label>
          )}
          <label>
            Wohin weitergeben?
            <select
              value={workshopHandoverModal.target}
              onChange={(event) => setWorkshopHandoverModal((prev) => ({ ...prev, target: event.target.value }))}
            >
              {targets.map((target) => (
                <option key={target} value={target}>
                  {target}
                </option>
              ))}
            </select>
          </label>
          <label>
            Notiz optional
            <textarea
              rows={3}
              placeholder="Was soll die nächste Stelle wissen?"
              value={workshopHandoverModal.note}
              onChange={(event) => setWorkshopHandoverModal((prev) => ({ ...prev, note: event.target.value }))}
            />
          </label>
          <div className="actions">
            <button type="button" className="btn" onClick={() => setWorkshopHandoverModal({ open: false, cardId: "", target: "", note: "", acknowledgeWarnings: false })}>
              Abbrechen
            </button>
            <button type="button" className="btn primary" onClick={submitWorkshopHandover} disabled={issues.length > 0 && !workshopHandoverModal.acknowledgeWarnings}>
              Weitergeben
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderWorkshopPhotoModal = () => {
    if (!workshopPhotoModal.open) return null;
    const card = cards.find((item) => item.id === workshopPhotoModal.cardId);
    if (!card) return null;
    const categories = ["Schaden fotografieren", "Teil fotografieren", "Arbeit dokumentieren", "Sonstiges fotografieren"];
    return (
      <div className="modalOverlay" onMouseDown={() => setWorkshopPhotoModal({ open: false, cardId: "", subId: "", category: "Arbeit dokumentiert", note: "", fileName: "", saving: false })}>
        <div className="modalCard workshopAssistantModal" onMouseDown={(event) => event.stopPropagation()}>
          <div className="modalHeader">
            <div>
              <h3>Foto hinzufügen</h3>
              <p className="mutedText">Foto aufnehmen, kurz zuordnen, fertig.</p>
            </div>
            <button type="button" className="btn" onClick={() => setWorkshopPhotoModal({ open: false, cardId: "", subId: "", category: "Arbeit dokumentiert", note: "", fileName: "", saving: false })}>
              Schließen
            </button>
          </div>
          <div className="assistantChoiceGrid">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                className={`assistantChoice ${workshopPhotoModal.category === category ? "active" : ""}`}
                onClick={() => setWorkshopPhotoModal((prev) => ({ ...prev, category }))}
              >
                {category}
              </button>
            ))}
          </div>
          <label>
            Beschreibung optional
            <textarea
              rows={3}
              placeholder="Was ist auf dem Foto zu sehen?"
              value={workshopPhotoModal.note}
              onChange={(event) => setWorkshopPhotoModal((prev) => ({ ...prev, note: event.target.value }))}
            />
          </label>
          <label className="fileDropButton">
            {workshopPhotoModal.saving ? "Foto wird gespeichert ..." : "Foto aufnehmen / auswählen"}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(event) => handleWorkshopPhotoFile(event.target.files)}
              disabled={workshopPhotoModal.saving}
            />
          </label>
          {workshopPhotoModal.fileName && <p className="mutedText">{workshopPhotoModal.fileName}</p>}
        </div>
      </div>
    );
  };

  const renderTaskDetailModal = () => {
    if (!taskDetailCardId) return null;
    const selectedCard = cards.find((card) => card.id === taskDetailCardId);
    if (!selectedCard) return null;

    return (
      <div className="modalOverlay taskDetailOverlay" onMouseDown={closeTaskDetail}>
        <div className="modalCard taskDetailModal" onMouseDown={(event) => event.stopPropagation()}>
          <div className="modalHeader taskDetailModalHeader">
            <div>
              <p className="eyebrow">Auftragsdetails</p>
              <h3>{selectedCard.title || "Auftrag"}</h3>
            </div>
            <button type="button" className="btn" onClick={closeTaskDetail}>
              Schließen
            </button>
          </div>
          {renderCard(selectedCard, { forceExpanded: true, inModal: true })}
        </div>
      </div>
    );
  };

  if (confirmationToken) {
    return <CustomerConfirmationView token={confirmationToken} />;
  }

  if (orderConfirmationToken) {
    return <OrderConfirmationView token={orderConfirmationToken} />;
  }

  if (authLoading) {
    return (
      <div className="authShell">
        <div className="authCard">
          <h1>Team Fraenkel Tool</h1>
          <p>Zugang wird geprüft ...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="authShell">
        <form className="authCard" onSubmit={handleLogin}>
            <p className="eyebrow">Geschützte Demo</p>
          <h1>Team Fraenkel Tool</h1>
          <p>Melde dich mit deinem freigeschalteten Demo-Zugang an.</p>

          <label>
            E-Mail
            <input
              type="email"
              autoComplete="email"
              value={authEmail}
              onChange={(event) => setAuthEmail(event.target.value)}
              required
            />
          </label>

          <label>
            Passwort
            <input
              type="password"
              autoComplete="current-password"
              value={authPassword}
              onChange={(event) => setAuthPassword(event.target.value)}
              required
            />
          </label>

          {authError && <div className="authError">{authError}</div>}

          <button type="submit" className="btn primary" disabled={authSubmitting}>
            {authSubmitting ? "Anmeldung läuft ..." : "Einloggen"}
          </button>
        </form>
      </div>
    );
  }

  const departmentView = effectiveDepartment || DEFAULT_DEPARTMENT;
  const activeStaffView = isWorkshopLead
    ? ui.activeView
    : isTechnicianRole
      ? "Werkstatt"
      : isIntakeStaff
        ? ui.activeView
        : departmentView;
  const viewGroups = isAdmin
    ? [
        { label: "Abteilungen", items: ROLES },
        { label: "Steuerung", items: [...SPECIAL_VIEWS, ...MANAGEMENT_VIEWS] },
        { label: "Masken", items: MASKS.filter((item) => item === "Verkauf") },
        {
          label: "Module",
          items: EXTRA_VIEWS.filter((view) => {
            if (view === "Leihwagen Admin" || view === "Benutzer Admin" || view === "Produktivtest" || view === "Systemstatus" || view === "Protokoll") {
              return isAdmin;
            }
            return true;
          }),
        },
      ]
    : isWorkshopLead
      ? [
          { label: "Steuerung", items: ["Werkstattplanung", "Werkstättenleitung"] },
          { label: "Meine Abteilung", items: ["Werkstatt"] },
        ]
    : isTechnicianRole
      ? [{ label: "Meine Arbeit", items: ["Werkstatt"] }]
    : isIntakeStaff
      ? [
          { label: "Abteilungen", items: ROLES.filter((role) => role !== "Leihwagen") },
          { label: "Module", items: ["Leihwagen"] },
        ]
      : [{ label: "Meine Abteilung", items: [departmentView] }];

  const activeViewLabel = isAdmin ? ui.activeView : activeStaffView;
  const nextStepHint = isAdmin
    ? {
        label: "Produktivtest prüfen",
        detail: "Go/No-Go, Systemstatus und Reset-SQL vor echten Kundendaten prüfen.",
        target: "Produktivtest",
      }
    : isWorkshopLead
      ? {
          label: "Freigaben und Planung prüfen",
          detail: "Offene Techniker-Zuweisungen und Kapazitäten zuerst bearbeiten.",
          target: "Werkstattplanung",
        }
      : isTechnicianRole
        ? {
            label: "Nächsten Auftrag öffnen",
            detail: "Heute fällige und freigegebene Werkstattaufträge abarbeiten.",
            target: "Werkstatt",
          }
        : isIntakeStaff
          ? {
              label: "Neuen Auftrag anlegen",
              detail: "Kunden- und Fahrzeugdaten erfassen, danach Leihwagenbedarf vormerken.",
              target: "Annahme",
            }
          : {
              label: "Arbeitsbereich prüfen",
              detail: "Offene Aufgaben deiner Abteilung bearbeiten.",
              target: activeStaffView,
            };
  const isAdminArea =
    ui.activeView === "Leihwagen Admin" ||
    ui.activeView === "Prozess Admin" ||
    ui.activeView === "Benutzer Admin" ||
    ui.activeView === "Produktivtest" ||
    ui.activeView === "Systemstatus" ||
    ui.activeView === "Protokoll";
  const contentPanel = !isAdmin
    ? isWorkshopLead && ["Werkstattplanung", "Werkstättenleitung"].includes(ui.activeView)
      ? renderWorkshopPlanningPanel()
      : isIntakeStaff && ui.activeView === "Leihwagen"
      ? renderLoanerBoard()
      : activeStaffView === "Leihwagen"
        ? renderLoanerBoard()
      : renderDepartmentWorkspace(activeStaffView)
    : ui.activeView === "Leihwagen"
    ? renderLoanerBoard()
    : ui.activeView === "Leihwagen Admin"
      ? renderLoanerAdminPanel()
      : ui.activeView === "Prozess Admin"
        ? renderProcessAdminPanel()
      : ui.activeView === "Benutzer Admin"
        ? renderUserAdminPanel()
        : ui.activeView === "Produktivtest"
          ? renderProductionReadinessPanel()
          : ui.activeView === "Systemstatus"
            ? renderSystemStatusPanel()
            : ui.activeView === "Protokoll"
              ? renderAuditPanel()
          : ui.activeView === "Management"
            ? renderManagementPanel()
            : ui.activeView === "Werkstättenleitung" || ui.activeView === "Werkstattplanung"
              ? renderWorkshopPlanningPanel()
            : ui.activeView === "Leitstand"
              ? renderLeadstandPanel()
              : ui.activeView === "Tagesboard"
                ? renderKanbanBoard()
              : ROLES.includes(ui.activeView)
                ? renderDepartmentWorkspace(ui.activeView)
        : (
          <main className="board boardStage">
            {renderIntakePanel()}
            <div className="card fullSpan compactTaskListCard">
              <div className="cardHeader">
                <div>
                  <h3>Aufträge</h3>
                  <p className="mutedText">Kompakte Liste. Antippen öffnet alle Details und Aktionen.</p>
                </div>
                <span className="summaryBadge">
                  <span>Gefunden</span>
                  <strong>{cardsView.length}</strong>
                </span>
              </div>
              <div className="kanbanColumnBody compactTaskList">
                {cardsView.map((card) => renderTaskRow(card))}
              </div>
            </div>
            {cardsView.length === 0 && (
              <div className="card empty helpEmpty">
                Keine passenden Aufträge gefunden. Suche oder Filter prüfen, sonst ist hier gerade nichts offen.
              </div>
            )}
          </main>
        );

  return (
    <div className="appShell">
      <div className="container tabletContainer">
        <header className="topBar">
          <div className="brandBlock">
            <span className="topBarChip brandChip">Team Fraenkel</span>
            <span className="topBarChip viewChip">{activeViewLabel}</span>
            <span className="topBarChip">
              {profile?.branch ? getBranchLabel(profile.branch) : "Arbeitsbereich"}
            </span>
            <span className={`topBarChip envChip ${/prod|produktiv/i.test(APP_ENV_LABEL) ? "prod" : "test"}`}>
              {APP_ENV_LABEL}
            </span>
            <span className="topBarChip emailChip">{session.user.email}</span>
          </div>

          <div className="topBarStatus compactStatus">
            <span className="topBarControlChip">{profile?.role || "staff"}</span>
            {profile?.department && <span className="topBarControlChip">{profile.department}</span>}
            {passwordState.success && <span className="topBarControlChip ok">{passwordState.success}</span>}
          </div>

          <div className="topBarActions">
            <div className="profileMenu">
              <button
                type="button"
                className="topBarControlChip profileMenuButton"
                onClick={() => setProfileMenuOpen((prev) => !prev)}
              >
                Profil
              </button>
              {profileMenuOpen && (
                <div className="profileMenuPanel">
                  <div className="profileMenuIdentity">
                    <strong>{profile?.full_name || session.user.email}</strong>
                    <span>{session.user.email}</span>
                    <span>{profile?.branch ? getBranchLabel(profile.branch) : "Keine Filiale"}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setProfileMenuOpen(false);
                      setPasswordState((prev) => ({
                        ...prev,
                        open: true,
                        success: "",
                        required: false,
                        passwordUpdated: false,
                      }));
                    }}
                  >
                    Passwort ändern
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setProfileMenuOpen(false);
                      setUi((prev) => ({ ...prev, activeView: isAdmin ? "Systemstatus" : activeStaffView }));
                    }}
                  >
                    {isAdmin ? "Systemstatus öffnen" : "Arbeitsbereich öffnen"}
                  </button>
                  <button type="button" onClick={handleLogout}>
                    Abmelden
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="tabletWorkspace">
          <aside
            className={`sideRail ${sidebarCollapsed ? "collapsed" : ""}`}
            style={{ width: sidebarCollapsed ? 96 : sidebarWidth }}
          >
            <div className="sideRailInner">
              <section className="railSection">
                <div className="railSectionHeader">
                  <div>
                    <strong>{sidebarCollapsed ? "Menü" : "Arbeitsbereiche"}</strong>
                    {!sidebarCollapsed && <span>{activeViewLabel}</span>}
                  </div>
                  <button
                    type="button"
                    className="sideRailToggle"
                    onClick={() => setSidebarCollapsed((prev) => !prev)}
                    aria-label={sidebarCollapsed ? "Navigation öffnen" : "Navigation einklappen"}
                    title={sidebarCollapsed ? "Navigation öffnen" : "Navigation einklappen"}
                  >
                    <span className="sideRailToggleIcon" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </span>
                    <span className="sideRailToggleChevron">{sidebarCollapsed ? "›" : "‹"}</span>
                  </button>
                </div>
                {viewGroups.map((group) => (
                  <div key={group.label} className="railNavGroup">
                    {!sidebarCollapsed && <p>{group.label}</p>}
                    <div className="railNavList">
                      {group.items.map((item) => (
                        <button
                          type="button"
                          key={item}
                          className={`railNavButton ${ui.activeView === item ? "active" : ""}`}
                          onClick={() => setUi((prev) => ({ ...prev, activeView: item }))}
                          title={item}
                        >
                          <span className="railDot" />
                          {!sidebarCollapsed && <span>{item}</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </section>

              {!sidebarCollapsed && isAdmin && (
                <>
                  {!EXTRA_VIEWS.includes(ui.activeView) && (
                    <section className="railSection">
                      <div className="railSectionHeader">
                        <strong>Filter</strong>
                      </div>
                      <div className="toolbar toolbarStack">
                        <input
                          type="search"
                          placeholder="Auftrag, Kunde, Kennzeichen, VIN, Modell, Techniker"
                          value={ui.search}
                          onChange={(event) =>
                            setUi((prev) => ({ ...prev, search: event.target.value }))
                          }
                        />
                        <select
                          value={ui.statusFilter}
                          onChange={(event) =>
                            setUi((prev) => ({ ...prev, statusFilter: event.target.value }))
                          }
                        >
                          <option value="">Status: alle</option>
                          {Object.entries(WORK_STATUS_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>
                              {label}
                            </option>
                          ))}
                        </select>
                        <select
                          value={ui.prioFilter}
                          onChange={(event) =>
                            setUi((prev) => ({ ...prev, prioFilter: event.target.value }))
                          }
                        >
                          <option value="">Prioritaet: alle</option>
                          <option value="urgent">Dringend</option>
                          <option value="high">Hoch</option>
                          <option value="normal">Normal</option>
                          <option value="low">Niedrig</option>
                        </select>
                        <select
                          value={ui.sortBy}
                          onChange={(event) =>
                            setUi((prev) => ({ ...prev, sortBy: event.target.value }))
                          }
                        >
                          <option value="due_asc">Sortierung: Fälligkeit</option>
                          <option value="prio_desc">Sortierung: Priorität</option>
                          <option value="title_asc">Sortierung: Titel</option>
                        </select>
                      </div>
                    </section>
                  )}
                </>
              )}
            </div>

            <div
              className="sideRailHandle"
              role="separator"
              aria-orientation="vertical"
              aria-label="Seitennavigation verbreitern"
              onMouseDown={() => setSidebarResizing(true)}
              onTouchStart={() => setSidebarResizing(true)}
            />
          </aside>

          <section className="workspaceStage">
            <div className={`workspaceBanner ${isAdminArea ? "adminMode" : "opsMode"}`}>
              <div>
                <strong>{isAdminArea ? "Verwaltungsmodus" : "Arbeitsmodus"}</strong>
                <span>
                  {isAdminArea
                    ? "Stammdaten, Benutzer und Flottenpflege getrennt von der operativen Arbeit."
                    : "Aufträge, Übergaben und Folgeaktionen stehen im Vordergrund."}
                </span>
                {dbStatus && <span className="workspaceStatusText">{getFriendlyStatusText(dbStatus)}</span>}
              </div>
              {!isAdminArea && (
                <div className="workspaceBannerMeta">
                  <span>{cardsView.length} sichtbare Karten</span>
                  <span>{bookings.filter((booking) => isActiveBooking(booking)).length} aktive Leihwagen</span>
                  <span>
                    {dbStatus?.toLowerCase().includes("konnte") || dbStatus?.toLowerCase().includes("fehler")
                      ? "Speichern prüfen"
                      : dbStatus?.toLowerCase().includes("offline") || offlineQueueCount || offlineReturnQueueCount
                        ? "Lokal gespeichert"
                        : "Gespeichert"}
                  </span>
                  <span>Sync {lastLiveSyncAt ? fmtDateTime(lastLiveSyncAt) : "offen"}</span>
                </div>
              )}
            </div>

            {operationAlerts.length > 0 && (
              <div className="operationAlertStrip">
                {operationAlerts.map((alert) => (
                  <button
                    type="button"
                    key={`${alert.target}_${alert.text}`}
                    className={`operationAlert ${alert.tone}`}
                    onClick={() => setUi((prev) => ({ ...prev, activeView: alert.target }))}
                  >
                    {alert.text}
                  </button>
                ))}
              </div>
            )}

            <div className="nextStepBar">
              <div>
                <strong>{nextStepHint.label}</strong>
                <span>{nextStepHint.detail}</span>
              </div>
              <button
                type="button"
                className="btn primary"
                onClick={() => {
                  setUi((prev) => ({ ...prev, activeView: nextStepHint.target }));
                  if (nextStepHint.target === "Annahme") setIntakeWorkspaceTab("new");
                }}
              >
                Öffnen
              </button>
            </div>

            <div className="globalSearchPanel">
              <div>
                <strong>Suchen und direkt öffnen</strong>
                <span>Kennzeichen, Auftrag, Kunde, VIN, Modell oder Leihwagen.</span>
              </div>
              <input
                type="search"
                value={globalSearch}
                onChange={(event) => setGlobalSearch(event.target.value)}
                placeholder="z. B. W-123AB, AU-..., Tucson"
              />
              {globalSearchResults.length > 0 && (
                <div className="globalSearchResults">
                  {globalSearchResults.map((result) => (
                    <button
                      type="button"
                      key={result.id}
                      onClick={() => {
                        result.action();
                        setGlobalSearch("");
                      }}
                    >
                      <span>{result.type}</span>
                      <strong>{result.title}</strong>
                      <small>{result.detail}</small>
                      <b>{result.hint}</b>
                    </button>
                  ))}
                </div>
              )}
              {globalSearch.trim().length >= 2 && globalSearchResults.length === 0 && (
                <div className="globalSearchEmpty">Nichts gefunden. Bitte Kennzeichen, Auftrag oder Namen prüfen.</div>
              )}
            </div>

            {(offlineQueueCount > 0 || offlineReturnQueueCount > 0) && (
              <div className="offlineSyncBanner">
                <div>
                  <strong>Eingaben sind lokal gespeichert.</strong>
                  <span>
                    {isOnline
                      ? `${offlineQueueCount} Übergabe(n), ${offlineReturnQueueCount} Rücknahme(n) werden übertragen. ${offlineQueueSizeKb} KB warten noch.`
                      : "Verbindung fehlt. Nichts geht verloren, die Übertragung startet automatisch im WLAN."}
                  </span>
                  {lastOfflineSyncAt && <small>Zuletzt übertragen: {fmtDateTime(lastOfflineSyncAt)}</small>}
                </div>
                <button type="button" className="btn" onClick={runOfflineSyncNow} disabled={offlineSyncBusy || !isOnline}>
                  {offlineSyncBusy ? "Wird übertragen ..." : "Jetzt übertragen"}
                </button>
              </div>
            )}

            {undoAction && (
              <div className="undoToast">
                <span>{undoAction.label}</span>
                <button type="button" className="btn" onClick={runUndoAction}>
                  Rückgängig
                </button>
              </div>
            )}

            <div className="contentFrame">
              {contentPanel}
            </div>

            <footer className="appFooter">
              <span>Version {APP_BUILD_LABEL}</span>
              <span>Ing. Fraenkel & Kirchner GmbH</span>
              <span>Powered by Evolution Data GmbH</span>
            </footer>

            {session && profileDebug && SHOW_DEBUG_PANEL && (
              <section className="debugPanel">
                <strong>Admin-Debug</strong>
                <div className="debugGrid">
                  <span>Session User ID</span>
                  <code>{profileDebug.sessionUserId || "-"}</code>
                  <span>Session E-Mail</span>
                  <code>{profileDebug.sessionEmail || "-"}</code>
                  <span>Initial Profile ID</span>
                  <code>{profileDebug.initialProfileId || "-"}</code>
                  <span>Initial Role</span>
                  <code>{profileDebug.initialProfileRole || "-"}</code>
                  <span>Initial E-Mail</span>
                  <code>{profileDebug.initialProfileEmail || "-"}</code>
                  <span>Saved Profile ID</span>
                  <code>{profileDebug.savedProfileId || "-"}</code>
                  <span>Saved Role</span>
                  <code>{profileDebug.savedProfileRole || "-"}</code>
                  <span>Saved E-Mail</span>
                  <code>{profileDebug.savedProfileEmail || "-"}</code>
                  <span>Final Role</span>
                  <code>{profileDebug.finalProfileRole || "-"}</code>
                  <span>isAdmin</span>
                  <code>{String(profileDebug.isAdmin)}</code>
                  <span>Profile Upsert Error</span>
                  <code>{profileDebug.profileUpsertError || "-"}</code>
                </div>
              </section>
            )}
          </section>
        </div>

        {renderWizard()}
        {renderLoanerCompletionModal()}
        {renderLoanerCorrectionModal()}
        {renderImageViewerModal()}
        {renderReturnModal()}
        {renderAvailabilityInspector()}
        {renderPasswordPanel()}
        {renderWorkshopBlockerModal()}
        {renderWorkshopHandoverModal()}
        {renderWorkshopPhotoModal()}
        {renderTaskDetailModal()}
        {photoCaptureSequence.open && (
          <PhotoSequenceCamera
            sequence={photoCaptureSequence}
            onCapture={savePhotoSequenceCapture}
            onClose={closePhotoCaptureSequence}
          />
        )}
      </div>
    </div>
  );
}
